export interface LocalQuestionBankStation {
  id: string;
  title: string;
  slug: string;
  order_index: number;
}

export interface LocalQuestionBankAnswer {
  id: string;
  question_id: string;
  answer_text: string;
  option_key: string;
  order_index: number;
  is_correct: boolean;
}

export interface LocalQuestionBankQuestion {
  id: string;
  station_id: string;
  stage: number;
  complexity: number;
  core_concept: string;
  question_type: string;
  status: string;
  human_review: { decision: string };
  languages: Record<
    string,
    {
      question: string;
      options: Record<string, string>;
      correct_answer: string;
      feedback: string;
    }
  >;
  question_text: string;
  explanation: string;
  answers: LocalQuestionBankAnswer[];
}

export interface LocalQuestionBank {
  schema_version: string;
  island: { id: string; title: string };
  stations: LocalQuestionBankStation[];
  questions: LocalQuestionBankQuestion[];
}

export const localQuestionBank = {
  schema_version: "1.1.0",
  island: {
    id: "basic_ai_skills",
    title: "Basic AI Skills Island",
  },
  stations: [
    {
      id: "station_1",
      title: "Reimagining Work with AI",
      slug: "station-1",
      order_index: 1,
    },
    {
      id: "station_2",
      title: "What AI Is and What It Is Not",
      slug: "station-2",
      order_index: 2,
    },
    {
      id: "station_3",
      title: "Everyday Use Cases",
      slug: "station-3",
      order_index: 3,
    },
    {
      id: "station_4",
      title: "Prompting Basics",
      slug: "station-4",
      order_index: 4,
    },
    {
      id: "station_5",
      title: "Finding and Understanding Information with AI",
      slug: "station-5",
      order_index: 5,
    },
    {
      id: "station_6",
      title: "Responsible and Safe Use",
      slug: "station-6",
      order_index: 6,
    },
  ],
  questions: [
    {
      id: "station1_q305",
      station_id: "station_1",
      stage: 1,
      complexity: 1,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "After a meeting, the team often loses actions in long notes. AI could help structure the follow-up. What should you do first?",
          options: {
            A: "Let AI send the follow-up alone.",
            B: "Use AI to draft actions, then assign owners.",
            C: "Keep writing the same notes manually.",
            D: "Ask AI to decide which actions matter.",
          },
          correct_answer: "B",
          feedback:
            "The workflow changes when AI structures actions and people own them. It augments follow-up without giving AI the decision.",
        },
        pt: {
          question:
            "Depois de uma reunião, a equipe costuma perder ações em notas longas. A IA pode estruturar o follow-up. O que fazer primeiro?",
          options: {
            A: "Deixar a IA enviar o follow-up sozinha.",
            B: "Usar IA para rascunhar ações e definir responsáveis.",
            C: "Continuar escrevendo as mesmas notas manualmente.",
            D: "Pedir à IA decidir quais ações importam.",
          },
          correct_answer: "B",
          feedback:
            "O fluxo muda quando a IA estrutura ações e pessoas assumem donos. Isso aumenta o follow-up sem dar decisão à IA.",
        },
        sv: {
          question:
            "Efter ett möte tappar teamet ofta åtgärder i långa anteckningar. AI kan strukturera uppföljningen. Vad gör du först?",
          options: {
            A: "Låt AI skicka uppföljningen ensam.",
            B: "Använd AI för åtgärdsutkast och utse ägare.",
            C: "Fortsätt skriva samma anteckningar manuellt.",
            D: "Be AI avgöra vilka åtgärder som är viktiga.",
          },
          correct_answer: "B",
          feedback:
            "Flödet ändras när AI strukturerar åtgärder och människor äger dem. Det stärker uppföljning utan att AI beslutar. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "Na een meeting raakt het team acties kwijt in lange notities. AI kan de follow-up structureren. Wat doe je eerst?",
          options: {
            A: "Laat AI de follow-up alleen versturen.",
            B: "Gebruik AI voor acties en wijs eigenaren toe.",
            C: "Blijf dezelfde notities handmatig schrijven.",
            D: "Vraag AI te beslissen welke acties tellen.",
          },
          correct_answer: "B",
          feedback:
            "De workflow verandert wanneer AI acties structureert en mensen eigenaar blijven. Dat versterkt follow-up zonder AI te laten beslissen.",
        },
      },
      question_text:
        "After a meeting, the team often loses actions in long notes. AI could help structure the follow-up. What should you do first?",
      explanation:
        "The workflow changes when AI structures actions and people own them. It augments follow-up without giving AI the decision.",
      answers: [
        {
          id: "station1_q305_a",
          question_id: "station1_q305",
          answer_text: "Let AI send the follow-up alone.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q305_b",
          question_id: "station1_q305",
          answer_text: "Use AI to draft actions, then assign owners.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station1_q305_c",
          question_id: "station1_q305",
          answer_text: "Keep writing the same notes manually.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q305_d",
          question_id: "station1_q305",
          answer_text: "Ask AI to decide which actions matter.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q306",
      station_id: "station_1",
      stage: 1,
      complexity: 1,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A colleague fears AI will replace their role, so they avoid trying it. Which response best corrects the misunderstanding?",
          options: {
            A: "Avoid AI until every colleague is trained.",
            B: "Let AI redesign the whole job immediately.",
            C: "Try one low-risk task and discuss learning.",
            D: "Use AI only when the manager orders it.",
          },
          correct_answer: "C",
          feedback:
            "A small experiment reframes AI as support, not replacement. Discussing learning keeps the person active in changing the work.",
        },
        pt: {
          question:
            "Um colega teme que a IA substitua seu papel, então evita experimentar. Que resposta corrige melhor esse entendimento?",
          options: {
            A: "Evitar IA até todo colega ser treinado.",
            B: "Deixar a IA redesenhar o trabalho inteiro imediatamente.",
            C: "Testar uma tarefa simples e discutir aprendizados.",
            D: "Usar IA só quando a gerente mandar.",
          },
          correct_answer: "C",
          feedback:
            "Um experimento pequeno reposiciona a IA como apoio, não substituição. Discutir aprendizados mantém a pessoa ativa na mudança.",
        },
        sv: {
          question:
            "En kollega fruktar att AI ersätter rollen och undviker att testa. Vilket svar rättar missförståndet bäst? Människor använder resultatet i arbetet.",
          options: {
            A: "Undvik AI tills varje kollega är utbildad.",
            B: "Låt AI omforma hela jobbet direkt.",
            C: "Testa en låg risk-uppgift och diskutera lärande.",
            D: "Använd AI bara när chefen kräver det.",
          },
          correct_answer: "C",
          feedback:
            "Ett litet experiment visar AI som stöd, inte ersättning. Lärandet håller personen aktiv i förändringen. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "Een collega vreest dat AI zijn rol vervangt en probeert het niet. Welke reactie corrigeert dat misverstand het best?",
          options: {
            A: "Vermijd AI totdat elke collega getraind is.",
            B: "Laat AI het hele werk direct herontwerpen.",
            C: "Probeer één laag-risico taak en bespreek leren.",
            D: "Gebruik AI alleen wanneer de manager het vraagt.",
          },
          correct_answer: "C",
          feedback:
            "Een klein experiment toont AI als ondersteuning, niet vervanging. Leren bespreken houdt de persoon actief in de verandering.",
        },
      },
      question_text:
        "A colleague fears AI will replace their role, so they avoid trying it. Which response best corrects the misunderstanding?",
      explanation:
        "A small experiment reframes AI as support, not replacement. Discussing learning keeps the person active in changing the work.",
      answers: [
        {
          id: "station1_q306_a",
          question_id: "station1_q306",
          answer_text: "Avoid AI until every colleague is trained.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q306_b",
          question_id: "station1_q306",
          answer_text: "Let AI redesign the whole job immediately.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q306_c",
          question_id: "station1_q306",
          answer_text: "Try one low-risk task and discuss learning.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station1_q306_d",
          question_id: "station1_q306",
          answer_text: "Use AI only when the manager orders it.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q307",
      station_id: "station_1",
      stage: 1,
      complexity: 2,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI helps prepare a weekly status update. The team still needs to choose priorities. Which behaviour shows overtrust?",
          options: {
            A: "Let AI write the update and choose priorities.",
            B: "Use AI to draft sections for review.",
            C: "Ask the team which priorities changed.",
            D: "Compare the draft with the team plan.",
          },
          correct_answer: "A",
          feedback:
            "AI can prepare material, but priority choices shape the work. Letting AI choose them confuses support with ownership.",
        },
        pt: {
          question:
            "A IA ajuda a preparar uma atualização semanal. A equipe ainda precisa escolher prioridades. Qual comportamento mostra confiança excessiva?",
          options: {
            A: "Deixar a IA escrever a atualização e escolher prioridades.",
            B: "Usar IA para rascunhar seções para revisão.",
            C: "Perguntar à equipe quais prioridades mudaram.",
            D: "Comparar o rascunho com o plano da equipe.",
          },
          correct_answer: "A",
          feedback:
            "A IA pode preparar material, mas prioridades moldam o trabalho. Deixar a IA escolhê-las confunde apoio com responsabilidade.",
        },
        sv: {
          question:
            "AI hjälper till att förbereda en veckostatus. Teamet behöver fortfarande välja prioriteringar. Vilket beteende visar övertro? Människor använder resultatet i arbetet.",
          options: {
            A: "Låt AI skriva uppdateringen och välja prioriteringar.",
            B: "Använd AI för avsnittsutkast inför granskning.",
            C: "Fråga teamet vilka prioriteringar som ändrats.",
            D: "Jämför utkastet med teamets plan.",
          },
          correct_answer: "A",
          feedback:
            "AI kan förbereda material, men prioriteringar formar arbetet. Att låta AI välja dem blandar stöd och ägarskap. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "AI helpt een wekelijkse statusupdate voorbereiden. Het team moet nog prioriteiten kiezen. Welk gedrag toont oververtrouwen? Mensen gebruiken het resultaat in hun werk.",
          options: {
            A: "Laat AI de update schrijven en prioriteiten kiezen.",
            B: "Gebruik AI voor conceptsecties ter review.",
            C: "Vraag het team welke prioriteiten veranderden.",
            D: "Vergelijk het concept met het teamplan.",
          },
          correct_answer: "A",
          feedback:
            "AI kan materiaal voorbereiden, maar prioriteiten sturen het werk. AI laten kiezen verwart ondersteuning met eigenaarschap. Dit beïnvloedt de volgende werkstap.",
        },
      },
      question_text:
        "AI helps prepare a weekly status update. The team still needs to choose priorities. Which behaviour shows overtrust?",
      explanation:
        "AI can prepare material, but priority choices shape the work. Letting AI choose them confuses support with ownership.",
      answers: [
        {
          id: "station1_q307_a",
          question_id: "station1_q307",
          answer_text: "Let AI write the update and choose priorities.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q307_b",
          question_id: "station1_q307",
          answer_text: "Use AI to draft sections for review.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q307_c",
          question_id: "station1_q307",
          answer_text: "Ask the team which priorities changed.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q307_d",
          question_id: "station1_q307",
          answer_text: "Compare the draft with the team plan.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q308",
      station_id: "station_1",
      stage: 2,
      complexity: 2,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A meeting agenda is built by copying last week’s format. AI could help prepare it differently. Which option best improves the workflow?",
          options: {
            A: "Make the old agenda longer and prettier.",
            B: "Ask AI to decide meeting outcomes early.",
            C: "Cancel preparation because AI can summarise later.",
            D: "Use AI to group topics, owners and decisions.",
          },
          correct_answer: "D",
          feedback:
            "Grouping topics, owners and decisions changes preparation. AI augments the meeting workflow instead of polishing the old agenda.",
        },
        pt: {
          question:
            "Uma agenda de reunião é feita copiando o formato da semana passada. A IA pode preparar de outro jeito. Qual opção melhora o fluxo?",
          options: {
            A: "Deixar a agenda antiga maior e mais bonita.",
            B: "Pedir à IA para decidir resultados da reunião cedo.",
            C: "Cancelar preparação porque a IA resume depois.",
            D: "Usar IA para agrupar temas, donos e decisões.",
          },
          correct_answer: "D",
          feedback:
            "Agrupar temas, donos e decisões muda a preparação. A IA amplia o fluxo da reunião em vez de polir a agenda antiga.",
        },
        sv: {
          question:
            "En mötesagenda görs genom att kopiera förra veckans format. AI kan förbereda den annorlunda. Vilket alternativ förbättrar flödet?",
          options: {
            A: "Gör den gamla agendan längre och snyggare.",
            B: "Be AI bestämma mötesresultat tidigt.",
            C: "Hoppa över förberedelse eftersom AI summerar senare.",
            D: "Använd AI för att gruppera ämnen, ägare och beslut.",
          },
          correct_answer: "D",
          feedback:
            "Att gruppera ämnen, ägare och beslut ändrar förberedelsen. AI stärker mötesflödet i stället för att putsa agendan. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "Een meetingagenda wordt gemaakt door vorige week te kopiëren. AI kan de voorbereiding anders maken. Welke optie verbetert de workflow?",
          options: {
            A: "Maak de oude agenda langer en mooier.",
            B: "Vraag AI vroeg de meetinguitkomsten te bepalen.",
            C: "Sla voorbereiding over omdat AI later samenvat.",
            D: "Gebruik AI om onderwerpen, eigenaren en besluiten te groeperen.",
          },
          correct_answer: "D",
          feedback:
            "Onderwerpen, eigenaren en besluiten groeperen verandert voorbereiding. AI versterkt de meetingworkflow in plaats van oude agenda’s te polijsten.",
        },
      },
      question_text:
        "A meeting agenda is built by copying last week’s format. AI could help prepare it differently. Which option best improves the workflow?",
      explanation:
        "Grouping topics, owners and decisions changes preparation. AI augments the meeting workflow instead of polishing the old agenda.",
      answers: [
        {
          id: "station1_q308_a",
          question_id: "station1_q308",
          answer_text: "Make the old agenda longer and prettier.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q308_b",
          question_id: "station1_q308",
          answer_text: "Ask AI to decide meeting outcomes early.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q308_c",
          question_id: "station1_q308",
          answer_text: "Cancel preparation because AI can summarise later.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q308_d",
          question_id: "station1_q308",
          answer_text: "Use AI to group topics, owners and decisions.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station1_q309",
      station_id: "station_1",
      stage: 2,
      complexity: 3,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI turns a messy handover into a clear action list. The team changes how follow-up happens. What should remain human-owned?",
          options: {
            A: "AI decides which tasks can wait.",
            B: "People confirm owners and next actions.",
            C: "The longest handover note becomes final.",
            D: "AI sends the handover without discussion.",
          },
          correct_answer: "B",
          feedback:
            "AI can organise the handover, but people must own who does what next. That is role clarity, not automation.",
        },
        pt: {
          question:
            "A IA transforma uma passagem confusa em lista clara de ações. A equipe muda o follow-up. O que deve continuar humano?",
          options: {
            A: "A IA decide quais tarefas podem esperar.",
            B: "Pessoas confirmam responsáveis e próximas ações.",
            C: "A nota de passagem mais longa vira final.",
            D: "A IA envia a passagem sem discussão.",
          },
          correct_answer: "B",
          feedback:
            "A IA organiza a passagem, mas pessoas devem assumir quem faz o quê depois. Isso é clareza de papéis.",
        },
        sv: {
          question:
            "AI gör en rörig överlämning till en tydlig åtgärdslista. Teamet ändrar uppföljningen. Vad ska fortsatt ägas av människor?",
          options: {
            A: "AI avgör vilka uppgifter som kan vänta.",
            B: "Människor bekräftar ägare och nästa åtgärder.",
            C: "Den längsta överlämningen blir slutlig.",
            D: "AI skickar överlämningen utan diskussion.",
          },
          correct_answer: "B",
          feedback:
            "AI kan ordna överlämningen, men människor måste äga vem som gör vad härnäst. Det ger rolltydlighet. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "AI maakt van een rommelige overdracht een duidelijke actielijst. Het team verandert de follow-up. Wat moet menselijk blijven?",
          options: {
            A: "AI beslist welke taken kunnen wachten.",
            B: "Mensen bevestigen eigenaren en volgende acties.",
            C: "De langste overdracht wordt definitief.",
            D: "AI verstuurt de overdracht zonder overleg.",
          },
          correct_answer: "B",
          feedback:
            "AI kan de overdracht ordenen, maar mensen blijven eigenaar van wie wat doet. Dat geeft rolhelderheid. Dit beïnvloedt de volgende werkstap.",
        },
      },
      question_text:
        "AI turns a messy handover into a clear action list. The team changes how follow-up happens. What should remain human-owned?",
      explanation:
        "AI can organise the handover, but people must own who does what next. That is role clarity, not automation.",
      answers: [
        {
          id: "station1_q309_a",
          question_id: "station1_q309",
          answer_text: "AI decides which tasks can wait.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q309_b",
          question_id: "station1_q309",
          answer_text: "People confirm owners and next actions.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station1_q309_c",
          question_id: "station1_q309",
          answer_text: "The longest handover note becomes final.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q309_d",
          question_id: "station1_q309",
          answer_text: "AI sends the handover without discussion.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q310",
      station_id: "station_1",
      stage: 2,
      complexity: 3,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A team wants to try AI in a repeated checklist workflow. They are unsure how much to change. What is the safest next step?",
          options: {
            A: "Test one checklist step with human review.",
            B: "Replace the whole process this week.",
            C: "Wait until every team has perfect rules.",
            D: "Let AI approve the new checklist.",
          },
          correct_answer: "A",
          feedback:
            "One reviewed step creates safe experimentation. The team learns how the workflow changes before scaling it. This matters for the next work step.",
        },
        pt: {
          question:
            "Uma equipe quer testar IA em um fluxo repetido de checklist. Não sabe quanto mudar. Qual é o próximo passo mais seguro?",
          options: {
            A: "Testar uma etapa do checklist com revisão humana.",
            B: "Substituir o processo inteiro esta semana.",
            C: "Esperar até toda equipe ter regras perfeitas.",
            D: "Deixar a IA aprovar o novo checklist.",
          },
          correct_answer: "A",
          feedback:
            "Uma etapa revisada cria experimentação segura. A equipe aprende como o fluxo muda antes de ampliar. Isso importa para o próximo passo do trabalho.",
        },
        sv: {
          question:
            "Ett team vill testa AI i ett återkommande checklistflöde. De är osäkra på hur mycket som ska ändras. Vad är säkrast?",
          options: {
            A: "Testa ett checkliststeg med mänsklig granskning.",
            B: "Ersätt hela processen denna vecka.",
            C: "Vänta tills varje team har perfekta regler.",
            D: "Låt AI godkänna den nya checklistan.",
          },
          correct_answer: "A",
          feedback:
            "Ett granskat steg skapar säker experimentering. Teamet lär sig hur flödet ändras före uppskalning. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "Een team wil AI testen in een herhaalde checklistworkflow. Ze weten niet hoeveel te veranderen. Wat is de veiligste volgende stap?",
          options: {
            A: "Test één checkliststap met menselijke review.",
            B: "Vervang het hele proces deze week.",
            C: "Wacht tot elk team perfecte regels heeft.",
            D: "Laat AI de nieuwe checklist goedkeuren.",
          },
          correct_answer: "A",
          feedback:
            "Eén gereviewde stap maakt veilig experimenteren mogelijk. Het team leert hoe de workflow verandert vóór opschaling. Dit beïnvloedt de volgende werkstap.",
        },
      },
      question_text:
        "A team wants to try AI in a repeated checklist workflow. They are unsure how much to change. What is the safest next step?",
      explanation:
        "One reviewed step creates safe experimentation. The team learns how the workflow changes before scaling it. This matters for the next work step.",
      answers: [
        {
          id: "station1_q310_a",
          question_id: "station1_q310",
          answer_text: "Test one checklist step with human review.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q310_b",
          question_id: "station1_q310",
          answer_text: "Replace the whole process this week.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q310_c",
          question_id: "station1_q310",
          answer_text: "Wait until every team has perfect rules.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q310_d",
          question_id: "station1_q310",
          answer_text: "Let AI approve the new checklist.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q311",
      station_id: "station_1",
      stage: 3,
      complexity: 3,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Several teams use different status formats, so leaders miss patterns. AI could help redesign preparation. Which option best improves the workflow?",
          options: {
            A: "Use AI privately to finish updates faster.",
            B: "Keep every team’s update format separate.",
            C: "Create shared sections for risks, owners and asks.",
            D: "Let AI choose what leaders should see.",
          },
          correct_answer: "C",
          feedback:
            "Shared sections change how teams prepare and compare work. AI supports alignment without deciding what leaders should value.",
        },
        pt: {
          question:
            "Várias equipes usam formatos diferentes de status, então líderes perdem padrões. A IA pode redesenhar a preparação. Qual opção melhora o fluxo?",
          options: {
            A: "Usar IA sozinho para terminar updates mais rápido.",
            B: "Manter separado o formato de cada equipe.",
            C: "Criar seções comuns para riscos, donos e pedidos.",
            D: "Deixar a IA escolher o que líderes veem.",
          },
          correct_answer: "C",
          feedback:
            "Seções comuns mudam como equipes preparam e comparam trabalho. A IA apoia alinhamento sem decidir o que líderes valorizam.",
        },
        sv: {
          question:
            "Flera team använder olika statusformat, så ledare missar mönster. AI kan omforma förberedelsen. Vilket alternativ förbättrar flödet? Människor använder resultatet i arbetet.",
          options: {
            A: "Använd AI privat för snabbare uppdateringar.",
            B: "Behåll varje teams uppdateringsformat separat.",
            C: "Skapa gemensamma delar för risker, ägare och behov.",
            D: "Låt AI välja vad ledare ska se.",
          },
          correct_answer: "C",
          feedback:
            "Gemensamma delar ändrar hur team förbereder och jämför arbete. AI stödjer samsyn utan att besluta värde. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "Meerdere teams gebruiken verschillende statusformats, waardoor leiders patronen missen. AI kan voorbereiding herontwerpen. Welke optie verbetert de workflow?",
          options: {
            A: "Gebruik AI privé om updates sneller af te maken.",
            B: "Houd elk teamformat gescheiden. voor de taak",
            C: "Maak gedeelde secties voor risico’s, eigenaren en vragen.",
            D: "Laat AI kiezen wat leiders zien.",
          },
          correct_answer: "C",
          feedback:
            "Gedeelde secties veranderen voorbereiding en vergelijking. AI ondersteunt afstemming zonder te beslissen wat leiders moeten waarderen. Dit beïnvloedt de volgende werkstap.",
        },
      },
      question_text:
        "Several teams use different status formats, so leaders miss patterns. AI could help redesign preparation. Which option best improves the workflow?",
      explanation:
        "Shared sections change how teams prepare and compare work. AI supports alignment without deciding what leaders should value.",
      answers: [
        {
          id: "station1_q311_a",
          question_id: "station1_q311",
          answer_text: "Use AI privately to finish updates faster.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q311_b",
          question_id: "station1_q311",
          answer_text: "Keep every team’s update format separate.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q311_c",
          question_id: "station1_q311",
          answer_text: "Create shared sections for risks, owners and asks.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station1_q311_d",
          question_id: "station1_q311",
          answer_text: "Let AI choose what leaders should see.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q312",
      station_id: "station_1",
      stage: 3,
      complexity: 4,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI prepares options for a workflow change. Each option affects different teams, and ownership is unclear. What should remain human-owned?",
          options: {
            A: "AI selects the option with clearest wording.",
            B: "The fastest option becomes the decision.",
            C: "Each team follows its own AI advice.",
            D: "People choose the option and accountable owner.",
          },
          correct_answer: "D",
          feedback:
            "AI can structure options, but people must choose the path and owner. That keeps accountability while the workflow changes.",
        },
        pt: {
          question:
            "A IA prepara opções para mudar um fluxo. Cada opção afeta equipes diferentes, e o dono está incerto. O que deve continuar humano?",
          options: {
            A: "A IA seleciona a opção com texto mais claro.",
            B: "A opção mais rápida vira a decisão.",
            C: "Cada equipe segue seu próprio conselho da IA.",
            D: "Pessoas escolhem a opção e o responsável.",
          },
          correct_answer: "D",
          feedback:
            "A IA estrutura opções, mas pessoas escolhem caminho e dono. Isso mantém responsabilidade enquanto o fluxo muda. Isso importa para o próximo passo do trabalho.",
        },
        sv: {
          question:
            "AI förbereder alternativ för en flödesändring. Varje alternativ påverkar olika team, och ägarskap är oklart. Vad ska förbli mänskligt?",
          options: {
            A: "AI väljer alternativet med tydligast formulering.",
            B: "Det snabbaste alternativet blir beslutet.",
            C: "Varje team följer sitt eget AI-råd.",
            D: "Människor väljer alternativ och ansvarig ägare.",
          },
          correct_answer: "D",
          feedback:
            "AI kan strukturera alternativ, men människor väljer väg och ägare. Det behåller ansvar när flödet ändras. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "AI bereidt opties voor een workflowwijziging voor. Elke optie raakt andere teams, en eigenaarschap is onduidelijk. Wat moet menselijk blijven?",
          options: {
            A: "AI kiest de optie met de duidelijkste tekst.",
            B: "De snelste optie wordt het besluit.",
            C: "Elk team volgt zijn eigen AI-advies.",
            D: "Mensen kiezen de optie en verantwoordelijke eigenaar.",
          },
          correct_answer: "D",
          feedback:
            "AI kan opties structureren, maar mensen kiezen richting en eigenaar. Zo blijft accountability tijdens workflowverandering. Dit beïnvloedt de volgende werkstap.",
        },
      },
      question_text:
        "AI prepares options for a workflow change. Each option affects different teams, and ownership is unclear. What should remain human-owned?",
      explanation:
        "AI can structure options, but people must choose the path and owner. That keeps accountability while the workflow changes.",
      answers: [
        {
          id: "station1_q312_a",
          question_id: "station1_q312",
          answer_text: "AI selects the option with clearest wording.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q312_b",
          question_id: "station1_q312",
          answer_text: "The fastest option becomes the decision.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q312_c",
          question_id: "station1_q312",
          answer_text: "Each team follows its own AI advice.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q312_d",
          question_id: "station1_q312",
          answer_text: "People choose the option and accountable owner.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station1_q313",
      station_id: "station_1",
      stage: 3,
      complexity: 4,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI helps redesign a checklist used by several teams. The output looks consistent and complete. Which behaviour shows overtrust?",
          options: {
            A: "Use AI to suggest gaps for team review.",
            B: "Remove human review because AI is consistent.",
            C: "Agree who owns each changed checklist step.",
            D: "Pilot the new checklist with one team.",
          },
          correct_answer: "B",
          feedback:
            "Consistency is useful, but it does not replace human review of roles and workflow impact. Removing review is overtrust.",
        },
        pt: {
          question:
            "A IA ajuda a redesenhar um checklist usado por várias equipes. O resultado parece consistente e completo. Qual comportamento mostra confiança excessiva?",
          options: {
            A: "Usar IA para sugerir lacunas à revisão da equipe.",
            B: "Remover revisão humana porque a IA é consistente.",
            C: "Combinar quem assume cada etapa alterada.",
            D: "Pilotar o novo checklist com uma equipe.",
          },
          correct_answer: "B",
          feedback:
            "Consistência ajuda, mas não substitui revisão humana de papéis e impacto no fluxo. Remover revisão é excesso de confiança.",
        },
        sv: {
          question:
            "AI hjälper till att omforma en checklista för flera team. Resultatet verkar konsekvent och komplett. Vilket beteende visar övertro?",
          options: {
            A: "Använd AI för att föreslå luckor för teamgranskning.",
            B: "Ta bort mänsklig granskning eftersom AI är konsekvent.",
            C: "Kom överens om ägare för varje ändrat steg.",
            D: "Pilottesta nya checklistan med ett team.",
          },
          correct_answer: "B",
          feedback:
            "Konsekvens hjälper, men ersätter inte mänsklig granskning av roller och flödeseffekt. Att ta bort granskning är övertro. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "AI helpt een checklist voor meerdere teams herontwerpen. De output lijkt consistent en compleet. Welk gedrag toont oververtrouwen?",
          options: {
            A: "Gebruik AI om gaten voor teamreview voor te stellen.",
            B: "Verwijder menselijke review omdat AI consistent is.",
            C: "Spreek af wie elke gewijzigde stap bezit.",
            D: "Pilot de nieuwe checklist met één team.",
          },
          correct_answer: "B",
          feedback:
            "Consistentie helpt, maar vervangt geen menselijke review van rollen en workflowimpact. Review verwijderen is oververtrouwen. Dit beïnvloedt de volgende werkstap.",
        },
      },
      question_text:
        "AI helps redesign a checklist used by several teams. The output looks consistent and complete. Which behaviour shows overtrust?",
      explanation:
        "Consistency is useful, but it does not replace human review of roles and workflow impact. Removing review is overtrust.",
      answers: [
        {
          id: "station1_q313_a",
          question_id: "station1_q313",
          answer_text: "Use AI to suggest gaps for team review.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q313_b",
          question_id: "station1_q313",
          answer_text: "Remove human review because AI is consistent.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station1_q313_c",
          question_id: "station1_q313",
          answer_text: "Agree who owns each changed checklist step.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q313_d",
          question_id: "station1_q313",
          answer_text: "Pilot the new checklist with one team.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q314",
      station_id: "station_1",
      stage: 3,
      complexity: 5,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Three teams disagree on how work should flow after AI is introduced. AI can compare their inputs. What makes this use appropriate?",
          options: {
            A: "Use AI to map tensions, then align owners.",
            B: "Let AI merge conflicts into one recommendation.",
            C: "Hide disagreement to make the process faster.",
            D: "Keep each team working in separate files.",
          },
          correct_answer: "A",
          feedback:
            "AI is useful for mapping tensions, not deciding the new workflow alone. Human owners still align the change.",
        },
        pt: {
          question:
            "Três equipes discordam sobre como o trabalho deve fluir após introduzir IA. A IA pode comparar inputs. O que torna esse uso apropriado?",
          options: {
            A: "Usar IA para mapear tensões e alinhar responsáveis.",
            B: "Deixar a IA fundir conflitos em uma recomendação.",
            C: "Esconder divergências para acelerar o processo.",
            D: "Manter cada equipe trabalhando em arquivos separados.",
          },
          correct_answer: "A",
          feedback:
            "A IA é útil para mapear tensões, não decidir sozinha o novo fluxo. Donos humanos ainda alinham a mudança.",
        },
        sv: {
          question:
            "Tre team är oense om hur arbetet ska flöda efter AI införs. AI kan jämföra deras input. Vad gör användningen lämplig?",
          options: {
            A: "Använd AI för att kartlägga spänningar och samordna ägare.",
            B: "Låt AI slå ihop konflikter till en rekommendation.",
            C: "Dölj oenighet för att snabba upp processen.",
            D: "Låt varje team arbeta i separata filer.",
          },
          correct_answer: "A",
          feedback:
            "AI är nyttig för att kartlägga spänningar, inte ensam besluta nytt flöde. Mänskliga ägare samordnar förändringen. Det påverkar nästa steg i arbetet.",
        },
        nl: {
          question:
            "Drie teams verschillen over hoe werk moet lopen na AI-introductie. AI kan hun input vergelijken. Wat maakt dit gebruik passend?",
          options: {
            A: "Gebruik AI om spanningen te mappen en eigenaren af te stemmen.",
            B: "Laat AI conflicten samenvoegen tot één aanbeveling.",
            C: "Verberg verschillen om het proces sneller te maken.",
            D: "Laat elk team in aparte bestanden werken.",
          },
          correct_answer: "A",
          feedback:
            "AI is nuttig om spanningen te mappen, niet om alleen de workflow te beslissen. Menselijke eigenaren stemmen af.",
        },
      },
      question_text:
        "Three teams disagree on how work should flow after AI is introduced. AI can compare their inputs. What makes this use appropriate?",
      explanation:
        "AI is useful for mapping tensions, not deciding the new workflow alone. Human owners still align the change.",
      answers: [
        {
          id: "station1_q314_a",
          question_id: "station1_q314",
          answer_text: "Use AI to map tensions, then align owners.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q314_b",
          question_id: "station1_q314",
          answer_text: "Let AI merge conflicts into one recommendation.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q314_c",
          question_id: "station1_q314",
          answer_text: "Hide disagreement to make the process faster.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q314_d",
          question_id: "station1_q314",
          answer_text: "Keep each team working in separate files.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q315",
      station_id: "station_1",
      stage: 1,
      complexity: 1,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A team wants to learn AI without changing important work yet. What is a better first experiment? The team will use this in daily work.",
          options: {
            A: "Try AI on a low-risk routine task.",
            B: "Replace the whole workflow immediately.",
            C: "Wait until every rule is perfect.",
            D: "Let AI choose the team’s new role.",
          },
          correct_answer: "A",
          feedback:
            "A low-risk task builds confidence without forcing a large redesign. The team learns while staying responsible for the work.",
        },
        pt: {
          question:
            "Uma equipe quer aprender IA sem mudar trabalho importante ainda. Qual é um primeiro experimento melhor? A equipe usará isso no trabalho diário.",
          options: {
            A: "Testar IA em uma tarefa simples de baixo risco.",
            B: "Substituir o fluxo inteiro imediatamente.",
            C: "Esperar até toda regra estar perfeita.",
            D: "Deixar a IA escolher o novo papel da equipe.",
          },
          correct_answer: "A",
          feedback:
            "Uma tarefa de baixo risco cria confiança sem forçar grande redesenho. A equipe aprende mantendo responsabilidade pelo trabalho.",
        },
        sv: {
          question:
            "Ett team vill lära sig AI utan att ändra viktigt arbete ännu. Vad är ett bättre första experiment?",
          options: {
            A: "Testa AI på en låg risk-rutinuppgift.",
            B: "Ersätt hela flödet direkt. i flödet",
            C: "Vänta tills varje regel är perfekt.",
            D: "Låt AI välja teamets nya roll.",
          },
          correct_answer: "A",
          feedback:
            "En låg risk-uppgift bygger trygghet utan stor omformning. Teamet lär sig och behåller ansvar för arbetet. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een team wil AI leren zonder belangrijk werk al te veranderen. Wat is een beter eerste experiment? Het team gebruikt dit in dagelijks werk.",
          options: {
            A: "Test AI op een laag-risico routinetaak.",
            B: "Vervang direct de hele workflow.",
            C: "Wacht tot elke regel perfect is.",
            D: "Laat AI de nieuwe teamrol kiezen.",
          },
          correct_answer: "A",
          feedback:
            "Een laag-risico taak bouwt vertrouwen op zonder groot herontwerp. Het team leert en blijft verantwoordelijk voor het werk.",
        },
      },
      question_text:
        "A team wants to learn AI without changing important work yet. What is a better first experiment? The team will use this in daily work.",
      explanation:
        "A low-risk task builds confidence without forcing a large redesign. The team learns while staying responsible for the work.",
      answers: [
        {
          id: "station1_q315_a",
          question_id: "station1_q315",
          answer_text: "Try AI on a low-risk routine task.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q315_b",
          question_id: "station1_q315",
          answer_text: "Replace the whole workflow immediately.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q315_c",
          question_id: "station1_q315",
          answer_text: "Wait until every rule is perfect.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q315_d",
          question_id: "station1_q315",
          answer_text: "Let AI choose the team’s new role.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q316",
      station_id: "station_1",
      stage: 1,
      complexity: 2,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A colleague says AI is only useful when it fully automates a task. Which response best corrects the misunderstanding?",
          options: {
            A: "Avoid AI unless it replaces the task.",
            B: "Let AI decide which work disappears.",
            C: "Use AI to prepare work people still own.",
            D: "Keep every step manual for safety.",
          },
          correct_answer: "C",
          feedback:
            "Station 1 is about augmentation. AI can prepare, organise or draft work while people still own judgment and outcomes.",
        },
        pt: {
          question:
            "Um colega diz que IA só é útil quando automatiza totalmente uma tarefa. Qual resposta corrige melhor esse entendimento?",
          options: {
            A: "Evitar IA se ela não substituir a tarefa.",
            B: "Deixar a IA decidir qual trabalho desaparece.",
            C: "Usar IA para preparar trabalho que pessoas ainda assumem.",
            D: "Manter toda etapa manual por segurança.",
          },
          correct_answer: "C",
          feedback:
            "A Station 1 trata de aumento, não substituição. A IA prepara ou organiza trabalho enquanto pessoas mantêm julgamento e resultados.",
        },
        sv: {
          question:
            "En kollega säger att AI bara är nyttigt när det helt automatiserar en uppgift. Vilket svar rättar missförståndet?",
          options: {
            A: "Undvik AI om den inte ersätter uppgiften.",
            B: "Låt AI avgöra vilket arbete som försvinner.",
            C: "Använd AI för arbete människor fortsatt äger.",
            D: "Behåll varje steg manuellt för säkerhet.",
          },
          correct_answer: "C",
          feedback:
            "Station 1 handlar om förstärkning. AI kan förbereda eller ordna arbete medan människor äger omdöme och resultat. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een collega zegt dat AI alleen nuttig is als het een taak volledig automatiseert. Welke reactie corrigeert dit misverstand?",
          options: {
            A: "Vermijd AI tenzij het de taak vervangt.",
            B: "Laat AI beslissen welk werk verdwijnt.",
            C: "Gebruik AI om werk voor te bereiden dat mensen blijven bezitten.",
            D: "Houd elke stap handmatig voor veiligheid.",
          },
          correct_answer: "C",
          feedback:
            "Station 1 draait om augmentatie. AI kan werk voorbereiden of ordenen terwijl mensen oordeel en resultaten blijven bezitten.",
        },
      },
      question_text:
        "A colleague says AI is only useful when it fully automates a task. Which response best corrects the misunderstanding?",
      explanation:
        "Station 1 is about augmentation. AI can prepare, organise or draft work while people still own judgment and outcomes.",
      answers: [
        {
          id: "station1_q316_a",
          question_id: "station1_q316",
          answer_text: "Avoid AI unless it replaces the task.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q316_b",
          question_id: "station1_q316",
          answer_text: "Let AI decide which work disappears.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q316_c",
          question_id: "station1_q316",
          answer_text: "Use AI to prepare work people still own.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station1_q316_d",
          question_id: "station1_q316",
          answer_text: "Keep every step manual for safety.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q317",
      station_id: "station_1",
      stage: 1,
      complexity: 2,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI turns a messy handover into a clean list. The team still needs to agree roles. Which behaviour shows overtrust?",
          options: {
            A: "Discuss owners after AI structures the list.",
            B: "Use the list as a starting draft.",
            C: "Add a short team review step.",
            D: "Accept the list as the new ownership model.",
          },
          correct_answer: "D",
          feedback:
            "A clean list can support handover, but it cannot create ownership by itself. Roles still need team agreement.",
        },
        pt: {
          question:
            "A IA transforma uma passagem confusa em lista clara. A equipe ainda precisa combinar papéis. Qual comportamento mostra confiança excessiva?",
          options: {
            A: "Discutir responsáveis depois que a IA estrutura a lista.",
            B: "Usar a lista como rascunho inicial.",
            C: "Adicionar uma breve revisão da equipe.",
            D: "Aceitar a lista como novo modelo de responsabilidades.",
          },
          correct_answer: "D",
          feedback:
            "Uma lista clara apoia a passagem, mas não cria responsabilidades sozinha. Papéis ainda precisam de acordo da equipe.",
        },
        sv: {
          question:
            "AI gör en rörig överlämning till en tydlig lista. Teamet måste fortfarande enas om roller. Vilket beteende visar övertro?",
          options: {
            A: "Diskutera ägare efter att AI strukturerar listan.",
            B: "Använd listan som första utkast.",
            C: "Lägg till en kort teamgranskning.",
            D: "Acceptera listan som ny ägarmodell.",
          },
          correct_answer: "D",
          feedback:
            "En tydlig lista kan stödja överlämning, men skapar inte ägarskap ensam. Roller kräver teamets överenskommelse. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "AI maakt van een rommelige overdracht een duidelijke lijst. Het team moet nog rollen afspreken. Welk gedrag toont oververtrouwen?",
          options: {
            A: "Bespreek eigenaren nadat AI de lijst structureert.",
            B: "Gebruik de lijst als eerste concept.",
            C: "Voeg een korte teamreview toe.",
            D: "Accepteer de lijst als nieuw eigenaarsmodel.",
          },
          correct_answer: "D",
          feedback:
            "Een duidelijke lijst ondersteunt overdracht, maar creëert geen eigenaarschap. Rollen vragen nog teamafstemming. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "AI turns a messy handover into a clean list. The team still needs to agree roles. Which behaviour shows overtrust?",
      explanation:
        "A clean list can support handover, but it cannot create ownership by itself. Roles still need team agreement.",
      answers: [
        {
          id: "station1_q317_a",
          question_id: "station1_q317",
          answer_text: "Discuss owners after AI structures the list.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q317_b",
          question_id: "station1_q317",
          answer_text: "Use the list as a starting draft.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q317_c",
          question_id: "station1_q317",
          answer_text: "Add a short team review step.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q317_d",
          question_id: "station1_q317",
          answer_text: "Accept the list as the new ownership model.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station1_q318",
      station_id: "station_1",
      stage: 2,
      complexity: 2,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A checklist is copied every week, and missed follow-ups repeat. How could AI help reimagine the workflow? The team will use this in daily work.",
          options: {
            A: "Make the same checklist look cleaner.",
            B: "Suggest recurring gaps, then assign owners.",
            C: "Let AI remove steps it dislikes.",
            D: "Keep the process unchanged but faster.",
          },
          correct_answer: "B",
          feedback:
            "The better change uses AI to reveal repeated gaps and clarify ownership. That improves the workflow, not just the document.",
        },
        pt: {
          question:
            "Um checklist é copiado toda semana, e follow-ups perdidos se repetem. Como a IA poderia redesenhar o fluxo?",
          options: {
            A: "Deixar o mesmo checklist mais bonito.",
            B: "Sugerir lacunas recorrentes e definir responsáveis.",
            C: "Deixar a IA remover etapas que não gosta.",
            D: "Manter o processo igual, porém mais rápido.",
          },
          correct_answer: "B",
          feedback:
            "A melhor mudança usa IA para revelar lacunas recorrentes e clarear responsáveis. Isso melhora o fluxo, não só o documento.",
        },
        sv: {
          question:
            "En checklista kopieras varje vecka, och missade uppföljningar återkommer. Hur kan AI omforma flödet? Teamet använder detta i dagligt arbete.",
          options: {
            A: "Gör samma checklista snyggare. i flödet",
            B: "Föreslå återkommande luckor och utse ägare.",
            C: "Låt AI ta bort steg den ogillar.",
            D: "Behåll processen oförändrad men snabbare.",
          },
          correct_answer: "B",
          feedback:
            "Den bättre ändringen använder AI för återkommande luckor och tydligt ägarskap. Det förbättrar flödet, inte bara dokumentet. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een checklist wordt elke week gekopieerd, en gemiste follow-ups keren terug. Hoe kan AI de workflow herontwerpen? Het team gebruikt dit in dagelijks werk.",
          options: {
            A: "Maak dezelfde checklist netter. in de workflow",
            B: "Stel terugkerende gaten voor en wijs eigenaren toe.",
            C: "Laat AI stappen verwijderen die het niet goed vindt.",
            D: "Houd het proces onveranderd maar sneller.",
          },
          correct_answer: "B",
          feedback:
            "De betere verandering gebruikt AI om terugkerende gaten en eigenaarschap zichtbaar te maken. Dat verbetert de workflow. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "A checklist is copied every week, and missed follow-ups repeat. How could AI help reimagine the workflow? The team will use this in daily work.",
      explanation:
        "The better change uses AI to reveal repeated gaps and clarify ownership. That improves the workflow, not just the document.",
      answers: [
        {
          id: "station1_q318_a",
          question_id: "station1_q318",
          answer_text: "Make the same checklist look cleaner.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q318_b",
          question_id: "station1_q318",
          answer_text: "Suggest recurring gaps, then assign owners.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station1_q318_c",
          question_id: "station1_q318",
          answer_text: "Let AI remove steps it dislikes.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q318_d",
          question_id: "station1_q318",
          answer_text: "Keep the process unchanged but faster.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q319",
      station_id: "station_1",
      stage: 2,
      complexity: 2,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI prepares three ways to run a weekly planning meeting. The team wants to try one. What should remain human-owned?",
          options: {
            A: "Choosing the meeting format and owner.",
            B: "Letting AI choose the new routine.",
            C: "Using the option with the shortest text.",
            D: "Running all options without discussion.",
          },
          correct_answer: "A",
          feedback:
            "AI can prepare alternatives, but people choose the format and accountable owner. That keeps redesign practical and owned.",
        },
        pt: {
          question:
            "A IA prepara três formas de conduzir uma reunião semanal de planejamento. A equipe quer testar uma. O que deve continuar humano?",
          options: {
            A: "Escolher o formato da reunião e o responsável.",
            B: "Deixar a IA escolher a nova rotina.",
            C: "Usar a opção com texto mais curto.",
            D: "Rodar todas as opções sem discussão.",
          },
          correct_answer: "A",
          feedback:
            "A IA prepara alternativas, mas pessoas escolhem formato e responsável. Isso mantém o redesenho prático e assumido. Isso mantém a mudança do fluxo clara e com responsabilidade humana.",
        },
        sv: {
          question:
            "AI förbereder tre sätt att hålla ett veckoplaneringsmöte. Teamet vill testa ett. Vad ska förbli mänskligt ägt? Teamet använder detta i dagligt arbete.",
          options: {
            A: "Välja mötesformat och ansvarig ägare.",
            B: "Låta AI välja den nya rutinen.",
            C: "Använda alternativet med kortast text.",
            D: "Köra alla alternativ utan diskussion.",
          },
          correct_answer: "A",
          feedback:
            "AI kan förbereda alternativ, men människor väljer format och ansvarig ägare. Det gör omformningen praktisk och ägd. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "AI bereidt drie manieren voor om een wekelijkse planningmeeting te doen. Het team wil er één testen. Wat moet menselijk blijven?",
          options: {
            A: "Het meetingformat en de eigenaar kiezen.",
            B: "AI de nieuwe routine laten kiezen.",
            C: "De optie met de kortste tekst gebruiken.",
            D: "Alle opties zonder overleg uitvoeren.",
          },
          correct_answer: "A",
          feedback:
            "AI kan alternatieven voorbereiden, maar mensen kiezen format en eigenaar. Zo blijft herontwerp praktisch en gedragen. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "AI prepares three ways to run a weekly planning meeting. The team wants to try one. What should remain human-owned?",
      explanation:
        "AI can prepare alternatives, but people choose the format and accountable owner. That keeps redesign practical and owned.",
      answers: [
        {
          id: "station1_q319_a",
          question_id: "station1_q319",
          answer_text: "Choosing the meeting format and owner.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q319_b",
          question_id: "station1_q319",
          answer_text: "Letting AI choose the new routine.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q319_c",
          question_id: "station1_q319",
          answer_text: "Using the option with the shortest text.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q319_d",
          question_id: "station1_q319",
          answer_text: "Running all options without discussion.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q320",
      station_id: "station_1",
      stage: 2,
      complexity: 3,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A team uses AI to draft stakeholder updates, but follow-up questions still arrive late. What should they change first?",
          options: {
            A: "Ask AI to send updates automatically.",
            B: "Make each update sound more polished.",
            C: "Add AI-drafted questions before sending updates.",
            D: "Stop using AI for stakeholder work.",
          },
          correct_answer: "C",
          feedback:
            "The workflow improves when AI helps anticipate follow-up questions. People still decide what to send and when. This keeps the workflow change clear and human-owned.",
        },
        pt: {
          question:
            "Uma equipe usa IA para rascunhar updates para stakeholders, mas perguntas de follow-up ainda chegam tarde. O que mudar primeiro?",
          options: {
            A: "Pedir à IA para enviar updates automaticamente.",
            B: "Deixar cada update mais polido.",
            C: "Adicionar perguntas rascunhadas pela IA antes do envio.",
            D: "Parar de usar IA para trabalho com stakeholders.",
          },
          correct_answer: "C",
          feedback:
            "O fluxo melhora quando a IA ajuda a antecipar perguntas de follow-up. Pessoas ainda decidem o que enviar e quando.",
        },
        sv: {
          question:
            "Ett team använder AI för intressentuppdateringar, men uppföljningsfrågor kommer fortfarande sent. Vad bör ändras först? Teamet använder detta i dagligt arbete.",
          options: {
            A: "Be AI skicka uppdateringar automatiskt.",
            B: "Gör varje uppdatering mer polerad.",
            C: "Lägg till AI-utkast till frågor före utskick.",
            D: "Sluta använda AI för intressentarbete.",
          },
          correct_answer: "C",
          feedback:
            "Flödet förbättras när AI hjälper till att förutse uppföljningsfrågor. Människor beslutar fortfarande vad som skickas. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een team gebruikt AI voor stakeholderupdates, maar follow-upvragen komen nog laat. Wat moeten ze eerst veranderen? Het team gebruikt dit in dagelijks werk.",
          options: {
            A: "Vraag AI updates automatisch te versturen.",
            B: "Maak elke update meer gepolijst.",
            C: "Voeg AI-conceptvragen toe vóór verzending.",
            D: "Stop met AI voor stakeholderwerk.",
          },
          correct_answer: "C",
          feedback:
            "De workflow verbetert wanneer AI follow-upvragen helpt voorzien. Mensen blijven beslissen wat en wanneer ze sturen. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "A team uses AI to draft stakeholder updates, but follow-up questions still arrive late. What should they change first?",
      explanation:
        "The workflow improves when AI helps anticipate follow-up questions. People still decide what to send and when. This keeps the workflow change clear and human-owned.",
      answers: [
        {
          id: "station1_q320_a",
          question_id: "station1_q320",
          answer_text: "Ask AI to send updates automatically.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q320_b",
          question_id: "station1_q320",
          answer_text: "Make each update sound more polished.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q320_c",
          question_id: "station1_q320",
          answer_text: "Add AI-drafted questions before sending updates.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station1_q320_d",
          question_id: "station1_q320",
          answer_text: "Stop using AI for stakeholder work.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q321",
      station_id: "station_1",
      stage: 2,
      complexity: 3,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI organises a cross-team discussion into themes and next steps. The output feels complete. Which behaviour shows overtrust?",
          options: {
            A: "Use the themes to guide team review.",
            B: "Treat the output as agreed team alignment.",
            C: "Ask owners to confirm next steps.",
            D: "Use AI to spot missing follow-up topics.",
          },
          correct_answer: "B",
          feedback:
            "Organised notes are not the same as agreement. Treating them as alignment gives AI too much authority over team commitments.",
        },
        pt: {
          question:
            "A IA organiza uma discussão entre equipes em temas e próximos passos. O resultado parece completo. Qual comportamento mostra confiança excessiva?",
          options: {
            A: "Usar os temas para orientar revisão da equipe.",
            B: "Tratar o resultado como alinhamento acordado pela equipe.",
            C: "Pedir que responsáveis confirmem próximos passos.",
            D: "Usar IA para achar temas de follow-up ausentes.",
          },
          correct_answer: "B",
          feedback:
            "Notas organizadas não são acordo. Tratá-las como alinhamento dá autoridade demais à IA sobre compromissos da equipe. Isso mantém a mudança do fluxo clara e com responsabilidade humana.",
        },
        sv: {
          question:
            "AI ordnar en diskussion mellan team i teman och nästa steg. Resultatet känns komplett. Vilket beteende visar övertro?",
          options: {
            A: "Använd teman för att styra teamgranskning.",
            B: "Behandla resultatet som överenskommen samsyn.",
            C: "Be ägare bekräfta nästa steg.",
            D: "Använd AI för saknade uppföljningsteman.",
          },
          correct_answer: "B",
          feedback:
            "Organiserade anteckningar är inte samma som samsyn. Att se dem som avtal ger AI för mycket mandat. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "AI ordent een gesprek tussen teams in thema’s en volgende stappen. De output voelt compleet. Welk gedrag toont oververtrouwen?",
          options: {
            A: "Gebruik thema’s om teamreview te sturen.",
            B: "Behandel de output als afgesproken teamafstemming.",
            C: "Vraag eigenaren volgende stappen te bevestigen.",
            D: "Gebruik AI om ontbrekende follow-upthema’s te vinden.",
          },
          correct_answer: "B",
          feedback:
            "Geordende notities zijn geen akkoord. Ze als afstemming behandelen geeft AI te veel autoriteit over teamafspraken. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "AI organises a cross-team discussion into themes and next steps. The output feels complete. Which behaviour shows overtrust?",
      explanation:
        "Organised notes are not the same as agreement. Treating them as alignment gives AI too much authority over team commitments.",
      answers: [
        {
          id: "station1_q321_a",
          question_id: "station1_q321",
          answer_text: "Use the themes to guide team review.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q321_b",
          question_id: "station1_q321",
          answer_text: "Treat the output as agreed team alignment.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station1_q321_c",
          question_id: "station1_q321",
          answer_text: "Ask owners to confirm next steps.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q321_d",
          question_id: "station1_q321",
          answer_text: "Use AI to spot missing follow-up topics.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q322",
      station_id: "station_1",
      stage: 3,
      complexity: 3,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Leaders receive five different status updates and miss shared blockers. AI could change preparation. Which option best improves the workflow?",
          options: {
            A: "Make every update longer and detailed.",
            B: "Let AI rank teams by performance.",
            C: "Keep formats separate to preserve habits.",
            D: "Create shared sections for blockers and owners.",
          },
          correct_answer: "D",
          feedback:
            "Shared sections help leaders compare work and see blockers. AI augments preparation while teams keep ownership of content.",
        },
        pt: {
          question:
            "Líderes recebem cinco updates diferentes e perdem bloqueios comuns. A IA pode mudar a preparação. Qual opção melhora o fluxo?",
          options: {
            A: "Deixar todo update mais longo e detalhado.",
            B: "Deixar a IA ranquear equipes por desempenho.",
            C: "Manter formatos separados para preservar hábitos.",
            D: "Criar seções comuns para bloqueios e responsáveis.",
          },
          correct_answer: "D",
          feedback:
            "Seções comuns ajudam líderes a comparar trabalho e ver bloqueios. A IA amplia a preparação com donos humanos.",
        },
        sv: {
          question:
            "Ledare får fem olika statusuppdateringar och missar gemensamma hinder. AI kan ändra förberedelsen. Vilket alternativ förbättrar flödet? Teamet använder detta i dagligt arbete.",
          options: {
            A: "Gör varje uppdatering längre och detaljerad.",
            B: "Låt AI rangordna team efter prestation.",
            C: "Behåll separata format för vanans skull.",
            D: "Skapa gemensamma delar för hinder och ägare.",
          },
          correct_answer: "D",
          feedback:
            "Gemensamma delar hjälper ledare jämföra arbete och se hinder. AI stärker förberedelsen medan team äger innehåll. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Leiders krijgen vijf verschillende statusupdates en missen gedeelde blokkades. AI kan voorbereiding veranderen. Welke optie verbetert de workflow?",
          options: {
            A: "Maak elke update langer en gedetailleerd.",
            B: "Laat AI teams op prestatie rangschikken.",
            C: "Houd formats apart om gewoonten te bewaren.",
            D: "Maak gedeelde secties voor blokkades en eigenaren.",
          },
          correct_answer: "D",
          feedback:
            "Gedeelde secties helpen leiders werk vergelijken en blokkades zien. AI versterkt voorbereiding terwijl teams eigenaar blijven. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "Leaders receive five different status updates and miss shared blockers. AI could change preparation. Which option best improves the workflow?",
      explanation:
        "Shared sections help leaders compare work and see blockers. AI augments preparation while teams keep ownership of content.",
      answers: [
        {
          id: "station1_q322_a",
          question_id: "station1_q322",
          answer_text: "Make every update longer and detailed.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q322_b",
          question_id: "station1_q322",
          answer_text: "Let AI rank teams by performance.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q322_c",
          question_id: "station1_q322",
          answer_text: "Keep formats separate to preserve habits.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q322_d",
          question_id: "station1_q322",
          answer_text: "Create shared sections for blockers and owners.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station1_q323",
      station_id: "station_1",
      stage: 3,
      complexity: 4,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI prepares two workflow redesign options. One saves time, another improves team clarity. What should remain human-owned? The team will use this in daily work.",
          options: {
            A: "Choosing the trade-off and accountable owner.",
            B: "Letting AI select the faster option.",
            C: "Using the option with the best wording.",
            D: "Skipping discussion to keep momentum.",
          },
          correct_answer: "A",
          feedback:
            "The trade-off changes how people work, so people must choose and own it. AI can clarify options, not decide.",
        },
        pt: {
          question:
            "A IA prepara duas opções de redesenho de fluxo. Uma economiza tempo, outra melhora clareza da equipe. O que deve continuar humano?",
          options: {
            A: "Escolher o trade-off e o responsável.",
            B: "Deixar a IA selecionar a opção mais rápida.",
            C: "Usar a opção com melhor texto.",
            D: "Pular discussão para manter ritmo.",
          },
          correct_answer: "A",
          feedback:
            "O trade-off muda como pessoas trabalham, então pessoas devem escolher e assumir. A IA esclarece opções, não decide.",
        },
        sv: {
          question:
            "AI förbereder två alternativ för flödesdesign. Ett sparar tid, ett ger tydligare team. Vad ska förbli mänskligt ägt?",
          options: {
            A: "Välja trade-off och ansvarig ägare.",
            B: "Låta AI välja det snabbare alternativet.",
            C: "Använda alternativet med bäst formulering.",
            D: "Hoppa över diskussion för tempo.",
          },
          correct_answer: "A",
          feedback:
            "Trade-offen ändrar hur människor arbetar, så människor måste välja och äga den. AI klargör alternativ, inte beslutar. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "AI bereidt twee workflowopties voor. Eén bespaart tijd, één verbetert teamduidelijkheid. Wat moet menselijk blijven? Het team gebruikt dit in dagelijks werk.",
          options: {
            A: "De trade-off en eigenaar kiezen.",
            B: "AI de snellere optie laten kiezen.",
            C: "De optie met beste formulering gebruiken.",
            D: "Overleg overslaan om tempo te houden.",
          },
          correct_answer: "A",
          feedback:
            "De trade-off verandert hoe mensen werken, dus mensen kiezen en dragen eigenaarschap. AI verduidelijkt opties, beslist niet. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "AI prepares two workflow redesign options. One saves time, another improves team clarity. What should remain human-owned? The team will use this in daily work.",
      explanation:
        "The trade-off changes how people work, so people must choose and own it. AI can clarify options, not decide.",
      answers: [
        {
          id: "station1_q323_a",
          question_id: "station1_q323",
          answer_text: "Choosing the trade-off and accountable owner.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q323_b",
          question_id: "station1_q323",
          answer_text: "Letting AI select the faster option.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q323_c",
          question_id: "station1_q323",
          answer_text: "Using the option with the best wording.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q323_d",
          question_id: "station1_q323",
          answer_text: "Skipping discussion to keep momentum.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q324",
      station_id: "station_1",
      stage: 3,
      complexity: 4,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Three teams disagree on a new AI-supported workflow. AI can map tensions and assumptions. What makes this use appropriate?",
          options: {
            A: "AI chooses the final workflow alone.",
            B: "Teams hide disagreement to move faster.",
            C: "People use the map to align decisions.",
            D: "Each team follows its own AI summary.",
          },
          correct_answer: "C",
          feedback:
            "Mapping tensions is appropriate when people use it for alignment. AI structures the discussion without owning the workflow decision.",
        },
        pt: {
          question:
            "Três equipes discordam sobre um novo fluxo com IA. A IA pode mapear tensões e premissas. O que torna esse uso apropriado?",
          options: {
            A: "A IA escolhe sozinha o fluxo final.",
            B: "Equipes escondem divergência para avançar rápido.",
            C: "Pessoas usam o mapa para alinhar decisões.",
            D: "Cada equipe segue seu próprio resumo da IA.",
          },
          correct_answer: "C",
          feedback:
            "Mapear tensões é apropriado quando pessoas usam para alinhamento. A IA estrutura a discussão sem assumir a decisão.",
        },
        sv: {
          question:
            "Tre team är oense om ett nytt AI-stött flöde. AI kan kartlägga spänningar och antaganden. Vad gör användningen lämplig?",
          options: {
            A: "AI väljer slutligt flöde ensam.",
            B: "Team döljer oenighet för fart.",
            C: "Människor använder kartan för beslutssamsyn.",
            D: "Varje team följer sin AI-sammanfattning.",
          },
          correct_answer: "C",
          feedback:
            "Att kartlägga spänningar passar när människor använder det för samsyn. AI strukturerar diskussionen utan att äga beslutet. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Drie teams verschillen over een nieuwe AI-ondersteunde workflow. AI kan spanningen en aannames mappen. Wat maakt dit passend?",
          options: {
            A: "AI kiest alleen de uiteindelijke workflow.",
            B: "Teams verbergen verschil om sneller te gaan.",
            C: "Mensen gebruiken de map voor besluitafstemming.",
            D: "Elk team volgt zijn eigen AI-samenvatting.",
          },
          correct_answer: "C",
          feedback:
            "Spanningen mappen past wanneer mensen het gebruiken voor afstemming. AI structureert discussie zonder de workflowbeslissing te bezitten. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "Three teams disagree on a new AI-supported workflow. AI can map tensions and assumptions. What makes this use appropriate?",
      explanation:
        "Mapping tensions is appropriate when people use it for alignment. AI structures the discussion without owning the workflow decision.",
      answers: [
        {
          id: "station1_q324_a",
          question_id: "station1_q324",
          answer_text: "AI chooses the final workflow alone.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q324_b",
          question_id: "station1_q324",
          answer_text: "Teams hide disagreement to move faster.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q324_c",
          question_id: "station1_q324",
          answer_text: "People use the map to align decisions.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station1_q324_d",
          question_id: "station1_q324",
          answer_text: "Each team follows its own AI summary.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q325",
      station_id: "station_1",
      stage: 1,
      complexity: 1,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A team spends time finding decisions in long email threads. What change shows AI augmenting the workflow? People will use this change in daily work.",
          options: {
            A: "Use AI to list decisions for team review.",
            B: "Let AI decide which emails matter.",
            C: "Keep searching threads manually every week.",
            D: "Make the thread summary sound friendlier.",
          },
          correct_answer: "A",
          feedback:
            "The workflow improves when AI prepares decisions for review. People still confirm what matters and what follows. This keeps the workflow change clear and human-owned.",
        },
        pt: {
          question:
            "Uma equipe gasta tempo achando decisões em longas conversas de email. Que mudança mostra IA ampliando o fluxo?",
          options: {
            A: "Usar IA para listar decisões para revisão da equipe.",
            B: "Deixar a IA decidir quais emails importam.",
            C: "Continuar buscando conversas manualmente toda semana.",
            D: "Deixar o resumo da conversa mais amigável.",
          },
          correct_answer: "A",
          feedback:
            "O fluxo melhora quando a IA prepara decisões para revisão. Pessoas ainda confirmam o que importa e o próximo passo.",
        },
        sv: {
          question:
            "Ett team lägger tid på beslut i långa mejltrådar. Vilken ändring visar AI som stöd i flödet? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "Använd AI för beslutslista till teamgranskning.",
            B: "Låt AI avgöra vilka mejl som räknas.",
            C: "Fortsätt söka trådar manuellt varje vecka.",
            D: "Gör trådsammanfattningen vänligare. i flödet",
          },
          correct_answer: "A",
          feedback:
            "Flödet förbättras när AI förbereder beslut för granskning. Människor bekräftar vad som är viktigt och nästa steg. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een team zoekt veel naar besluiten in lange mailthreads. Welke verandering toont AI als workflowondersteuning? Mensen gebruiken deze verandering in dagelijks werk.",
          options: {
            A: "Gebruik AI voor besluitenlijst voor teamreview.",
            B: "Laat AI bepalen welke mails tellen.",
            C: "Blijf threads elke week handmatig zoeken.",
            D: "Maak de threadsamenvatting vriendelijker. in de workflow",
          },
          correct_answer: "A",
          feedback:
            "De workflow verbetert wanneer AI besluiten voorbereidt voor review. Mensen bevestigen wat telt en wat volgt. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "A team spends time finding decisions in long email threads. What change shows AI augmenting the workflow? People will use this change in daily work.",
      explanation:
        "The workflow improves when AI prepares decisions for review. People still confirm what matters and what follows. This keeps the workflow change clear and human-owned.",
      answers: [
        {
          id: "station1_q325_a",
          question_id: "station1_q325",
          answer_text: "Use AI to list decisions for team review.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q325_b",
          question_id: "station1_q325",
          answer_text: "Let AI decide which emails matter.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q325_c",
          question_id: "station1_q325",
          answer_text: "Keep searching threads manually every week.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q325_d",
          question_id: "station1_q325",
          answer_text: "Make the thread summary sound friendlier.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q326",
      station_id: "station_1",
      stage: 1,
      complexity: 2,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A specialist explains work in technical language, and colleagues disengage. How can AI augment the specialist’s role? People will use this change in daily work.",
          options: {
            A: "Replace the specialist in future sessions.",
            B: "Keep the explanation unchanged for accuracy.",
            C: "Ask AI to choose what colleagues need.",
            D: "Use AI to draft plainer versions for review.",
          },
          correct_answer: "D",
          feedback:
            "AI augments the specialist by preparing clearer versions. The specialist still owns meaning, nuance and final explanation. This keeps the workflow change clear and human-owned.",
        },
        pt: {
          question:
            "Uma especialista explica o trabalho em linguagem técnica, e colegas se afastam. Como a IA pode ampliar o papel dela?",
          options: {
            A: "Substituir a especialista em sessões futuras.",
            B: "Manter a explicação igual por precisão.",
            C: "Pedir à IA escolher o que colegas precisam.",
            D: "Usar IA para rascunhar versões simples para revisão.",
          },
          correct_answer: "D",
          feedback:
            "A IA amplia a especialista ao preparar versões mais claras. Ela ainda assume sentido, nuance e explicação final.",
        },
        sv: {
          question:
            "En specialist förklarar arbetet tekniskt, och kollegor tappar intresse. Hur kan AI förstärka specialistens roll? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "Ersätt specialisten i framtida pass.",
            B: "Behåll förklaringen oförändrad för precision.",
            C: "Be AI välja vad kollegor behöver.",
            D: "Använd AI för enklare utkast till granskning.",
          },
          correct_answer: "D",
          feedback:
            "AI förstärker specialisten genom tydligare utkast. Specialisten äger fortsatt innebörd, nyans och slutlig förklaring. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een specialist legt werk technisch uit, en collega’s haken af. Hoe kan AI haar rol versterken? Mensen gebruiken deze verandering in dagelijks werk.",
          options: {
            A: "Vervang de specialist in toekomstige sessies.",
            B: "Houd de uitleg onveranderd voor nauwkeurigheid.",
            C: "Vraag AI te kiezen wat collega’s nodig hebben.",
            D: "Gebruik AI voor eenvoudigere concepten ter review.",
          },
          correct_answer: "D",
          feedback:
            "AI versterkt de specialist met duidelijkere concepten. De specialist blijft eigenaar van betekenis, nuance en einduitleg. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "A specialist explains work in technical language, and colleagues disengage. How can AI augment the specialist’s role? People will use this change in daily work.",
      explanation:
        "AI augments the specialist by preparing clearer versions. The specialist still owns meaning, nuance and final explanation. This keeps the workflow change clear and human-owned.",
      answers: [
        {
          id: "station1_q326_a",
          question_id: "station1_q326",
          answer_text: "Replace the specialist in future sessions.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q326_b",
          question_id: "station1_q326",
          answer_text: "Keep the explanation unchanged for accuracy.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q326_c",
          question_id: "station1_q326",
          answer_text: "Ask AI to choose what colleagues need.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q326_d",
          question_id: "station1_q326",
          answer_text: "Use AI to draft plainer versions for review.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station1_q327",
      station_id: "station_1",
      stage: 1,
      complexity: 2,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A team waits for a perfect AI strategy before trying anything. What is the safest next step? People will use this change in daily work.",
          options: {
            A: "Agree and pause all learning for now.",
            B: "Run a small trial with clear human ownership.",
            C: "Let AI design the full strategy first.",
            D: "Use AI everywhere to build momentum.",
          },
          correct_answer: "B",
          feedback:
            "A small owned trial builds practical learning without uncontrolled rollout. It treats adoption as guided experimentation. This keeps the workflow change clear and human-owned.",
        },
        pt: {
          question:
            "Uma equipe espera uma estratégia perfeita de IA antes de tentar algo. Qual é o próximo passo mais seguro? Pessoas usarão esta mudança no trabalho diário.",
          options: {
            A: "Concordar e pausar todo aprendizado por enquanto.",
            B: "Rodar um teste pequeno com dono humano claro.",
            C: "Deixar a IA desenhar toda a estratégia primeiro.",
            D: "Usar IA em tudo para criar ritmo.",
          },
          correct_answer: "B",
          feedback:
            "Um teste pequeno com dono cria aprendizado prático sem rollout descontrolado. A adoção vira experimentação guiada. Isso mantém a mudança do fluxo clara e com responsabilidade humana.",
        },
        sv: {
          question:
            "Ett team väntar på en perfekt AI-strategi före varje test. Vad är säkrast som nästa steg? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "Håll med och pausa allt lärande nu.",
            B: "Kör ett litet test med tydligt mänskligt ägarskap.",
            C: "Låt AI utforma hela strategin först.",
            D: "Använd AI överallt för fart.",
          },
          correct_answer: "B",
          feedback:
            "Ett litet ägt test skapar praktiskt lärande utan okontrollerad utrullning. Införandet blir styrd experimentering. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een team wacht op een perfecte AI-strategie voordat het iets probeert. Wat is de veiligste volgende stap? Mensen gebruiken deze verandering in dagelijks werk.",
          options: {
            A: "Ga akkoord en pauzeer al het leren.",
            B: "Doe een kleine proef met helder menselijk eigenaarschap.",
            C: "Laat AI eerst de hele strategie ontwerpen.",
            D: "Gebruik AI overal om tempo te maken.",
          },
          correct_answer: "B",
          feedback:
            "Een kleine proef met eigenaar bouwt praktisch leren zonder ongecontroleerde uitrol. Adoptie wordt begeleid experimenteren. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "A team waits for a perfect AI strategy before trying anything. What is the safest next step? People will use this change in daily work.",
      explanation:
        "A small owned trial builds practical learning without uncontrolled rollout. It treats adoption as guided experimentation. This keeps the workflow change clear and human-owned.",
      answers: [
        {
          id: "station1_q327_a",
          question_id: "station1_q327",
          answer_text: "Agree and pause all learning for now.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q327_b",
          question_id: "station1_q327",
          answer_text: "Run a small trial with clear human ownership.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station1_q327_c",
          question_id: "station1_q327",
          answer_text: "Let AI design the full strategy first.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q327_d",
          question_id: "station1_q327",
          answer_text: "Use AI everywhere to build momentum.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q328",
      station_id: "station_1",
      stage: 1,
      complexity: 2,
      core_concept: "AI support with human responsibility",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI suggests two ways to split a routine task between people and tools. What should remain human-owned? People will use this change in daily work.",
          options: {
            A: "AI chooses the new team responsibilities.",
            B: "The shortest proposal becomes the plan.",
            C: "People decide roles and review points.",
            D: "Everyone keeps the old routine unchanged.",
          },
          correct_answer: "C",
          feedback:
            "Role changes affect accountability. AI can propose options, but people must decide roles and review points. This keeps the workflow change clear and human-owned.",
        },
        pt: {
          question:
            "A IA sugere duas formas de dividir uma rotina entre pessoas e ferramentas. O que deve continuar humano?",
          options: {
            A: "A IA escolhe as novas responsabilidades da equipe.",
            B: "A proposta mais curta vira o plano.",
            C: "Pessoas decidem papéis e pontos de revisão.",
            D: "Todos mantêm a rotina antiga sem mudança.",
          },
          correct_answer: "C",
          feedback:
            "Mudanças de papel afetam responsabilidade. A IA propõe opções, mas pessoas decidem papéis e pontos de revisão. Isso mantém a mudança do fluxo clara e com responsabilidade humana.",
        },
        sv: {
          question:
            "AI föreslår två sätt att dela en rutin mellan människor och verktyg. Vad ska förbli mänskligt? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "AI väljer teamets nya ansvar.",
            B: "Det kortaste förslaget blir planen.",
            C: "Människor beslutar roller och granskningspunkter.",
            D: "Alla behåller den gamla rutinen oförändrad.",
          },
          correct_answer: "C",
          feedback:
            "Rolländringar påverkar ansvar. AI kan föreslå alternativ, men människor beslutar roller och granskningspunkter. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "AI stelt twee manieren voor om routinewerk tussen mensen en tools te verdelen. Wat moet menselijk blijven? Mensen gebruiken deze verandering in dagelijks werk.",
          options: {
            A: "AI kiest de nieuwe teamverantwoordelijkheden.",
            B: "Het kortste voorstel wordt het plan.",
            C: "Mensen beslissen rollen en reviewmomenten.",
            D: "Iedereen houdt de oude routine onveranderd.",
          },
          correct_answer: "C",
          feedback:
            "Rolveranderingen raken verantwoordelijkheid. AI kan opties voorstellen, maar mensen beslissen rollen en reviewmomenten. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "AI suggests two ways to split a routine task between people and tools. What should remain human-owned? People will use this change in daily work.",
      explanation:
        "Role changes affect accountability. AI can propose options, but people must decide roles and review points. This keeps the workflow change clear and human-owned.",
      answers: [
        {
          id: "station1_q328_a",
          question_id: "station1_q328",
          answer_text: "AI chooses the new team responsibilities.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q328_b",
          question_id: "station1_q328",
          answer_text: "The shortest proposal becomes the plan.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q328_c",
          question_id: "station1_q328",
          answer_text: "People decide roles and review points.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station1_q328_d",
          question_id: "station1_q328",
          answer_text: "Everyone keeps the old routine unchanged.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q329",
      station_id: "station_1",
      stage: 2,
      complexity: 2,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "After meetings, actions are captured but rarely followed up. Which option shows AI augmenting the team? People will use this change in daily work.",
          options: {
            A: "Draft follow-up lists for owner confirmation.",
            B: "Let AI chase people automatically.",
            C: "Keep action tracking in private notes.",
            D: "Make the minutes longer after each meeting.",
          },
          correct_answer: "A",
          feedback:
            "AI augments follow-up by preparing owner lists for confirmation. The team still owns commitments and accountability. This keeps the workflow change clear and human-owned.",
        },
        pt: {
          question:
            "Depois das reuniões, ações são registradas mas raramente acompanhadas. Qual opção mostra IA ampliando a equipe? Pessoas usarão esta mudança no trabalho diário.",
          options: {
            A: "Rascunhar listas de follow-up para confirmação dos responsáveis.",
            B: "Deixar a IA cobrar pessoas automaticamente.",
            C: "Manter controle de ações em notas privadas.",
            D: "Deixar atas mais longas após cada reunião.",
          },
          correct_answer: "A",
          feedback:
            "A IA amplia o follow-up ao preparar listas para confirmação dos responsáveis. A equipe ainda assume compromissos. Isso mantém a mudança do fluxo clara e com responsabilidade humana.",
        },
        sv: {
          question:
            "Efter möten fångas åtgärder men följs sällan upp. Vilket alternativ visar AI som förstärker teamet? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "Skapa uppföljningslistor för ägarbekräftelse. i flödet",
            B: "Låt AI jaga människor automatiskt.",
            C: "Behåll åtgärdsspårning i privata anteckningar.",
            D: "Gör protokollen längre efter varje möte.",
          },
          correct_answer: "A",
          feedback:
            "AI stärker uppföljning genom listor för ägarbekräftelse. Teamet äger fortsatt åtaganden och ansvar. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Na meetings worden acties genoteerd maar zelden opgevolgd. Welke optie toont AI als teamversterking? Mensen gebruiken deze verandering in dagelijks werk.",
          options: {
            A: "Maak follow-uplijsten voor bevestiging door eigenaren.",
            B: "Laat AI mensen automatisch najagen.",
            C: "Houd actieopvolging in privénotities. in de workflow",
            D: "Maak notulen langer na elke meeting.",
          },
          correct_answer: "A",
          feedback:
            "AI versterkt follow-up met lijsten voor eigenaarsbevestiging. Het team blijft eigenaar van afspraken en verantwoordelijkheid. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "After meetings, actions are captured but rarely followed up. Which option shows AI augmenting the team? People will use this change in daily work.",
      explanation:
        "AI augments follow-up by preparing owner lists for confirmation. The team still owns commitments and accountability. This keeps the workflow change clear and human-owned.",
      answers: [
        {
          id: "station1_q329_a",
          question_id: "station1_q329",
          answer_text: "Draft follow-up lists for owner confirmation.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q329_b",
          question_id: "station1_q329",
          answer_text: "Let AI chase people automatically.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q329_c",
          question_id: "station1_q329",
          answer_text: "Keep action tracking in private notes.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q329_d",
          question_id: "station1_q329",
          answer_text: "Make the minutes longer after each meeting.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q330",
      station_id: "station_1",
      stage: 2,
      complexity: 3,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A manager says AI should only make the current checklist faster. Which response better reimagines the workflow? People will use this change in daily work.",
          options: {
            A: "Agree and keep every step the same.",
            B: "Let AI remove review from the process.",
            C: "Make the checklist wording more formal.",
            D: "Use AI to find gaps and clarify owners.",
          },
          correct_answer: "D",
          feedback:
            "Station 1 asks whether work should change. Finding gaps and owners redesigns the workflow instead of only speeding it up.",
        },
        pt: {
          question:
            "Uma gerente diz que IA só deve acelerar o checklist atual. Qual resposta redesenha melhor o fluxo? Pessoas usarão esta mudança no trabalho diário.",
          options: {
            A: "Concordar e manter toda etapa igual.",
            B: "Deixar a IA remover revisão do processo.",
            C: "Deixar o texto do checklist mais formal.",
            D: "Usar IA para achar lacunas e clarear responsáveis.",
          },
          correct_answer: "D",
          feedback:
            "A Station 1 pergunta se o trabalho deve mudar. Achar lacunas e donos redesenha o fluxo, não só acelera.",
        },
        sv: {
          question:
            "En chef säger att AI bara ska snabba upp nuvarande checklista. Vilket svar omformar flödet bättre? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "Håll med och behåll varje steg.",
            B: "Låt AI ta bort granskning ur processen.",
            C: "Gör checklistans text mer formell.",
            D: "Använd AI för luckor och tydliga ägare.",
          },
          correct_answer: "D",
          feedback:
            "Station 1 frågar om arbetet ska ändras. Luckor och ägare omformar flödet, inte bara hastigheten. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een manager zegt dat AI alleen de huidige checklist sneller moet maken. Welke reactie herontwerpt de workflow beter?",
          options: {
            A: "Ga akkoord en houd elke stap gelijk.",
            B: "Laat AI review uit het proces halen.",
            C: "Maak de checklisttekst formeler. in de workflow",
            D: "Gebruik AI om gaten en eigenaren te verduidelijken.",
          },
          correct_answer: "D",
          feedback:
            "Station 1 vraagt of werk moet veranderen. Gaten en eigenaren vinden herontwerpt de workflow, niet alleen snelheid. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "A manager says AI should only make the current checklist faster. Which response better reimagines the workflow? People will use this change in daily work.",
      explanation:
        "Station 1 asks whether work should change. Finding gaps and owners redesigns the workflow instead of only speeding it up.",
      answers: [
        {
          id: "station1_q330_a",
          question_id: "station1_q330",
          answer_text: "Agree and keep every step the same.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q330_b",
          question_id: "station1_q330",
          answer_text: "Let AI remove review from the process.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q330_c",
          question_id: "station1_q330",
          answer_text: "Make the checklist wording more formal.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q330_d",
          question_id: "station1_q330",
          answer_text: "Use AI to find gaps and clarify owners.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station1_q331",
      station_id: "station_1",
      stage: 2,
      complexity: 3,
      core_concept: "Practical AI use in everyday work",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A team wants AI to challenge assumptions before changing a routine. What makes this use appropriate for Station 1?",
          options: {
            A: "AI decides which routine disappears.",
            B: "People use AI questions to discuss the change.",
            C: "The team skips discussion because AI is neutral.",
            D: "Every assumption becomes an automatic rule.",
          },
          correct_answer: "B",
          feedback:
            "The use is appropriate when AI improves the team discussion. People still decide how the routine should change.",
        },
        pt: {
          question:
            "Uma equipe quer que a IA desafie premissas antes de mudar uma rotina. O que torna esse uso apropriado para Station 1?",
          options: {
            A: "A IA decide qual rotina desaparece.",
            B: "Pessoas usam perguntas da IA para discutir a mudança.",
            C: "A equipe pula discussão porque a IA é neutra.",
            D: "Toda premissa vira regra automática.",
          },
          correct_answer: "B",
          feedback:
            "O uso é apropriado quando a IA melhora a discussão da equipe. Pessoas ainda decidem como a rotina deve mudar.",
        },
        sv: {
          question:
            "Ett team vill att AI utmanar antaganden före en rutinändring. Vad gör användningen lämplig för Station 1? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "AI avgör vilken rutin som försvinner.",
            B: "Människor använder AI-frågor för ändringsdialog.",
            C: "Teamet hoppar över dialog eftersom AI är neutral.",
            D: "Varje antagande blir automatisk regel.",
          },
          correct_answer: "B",
          feedback:
            "Användningen passar när AI förbättrar teamdialogen. Människor beslutar fortfarande hur rutinen ska ändras. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Een team wil dat AI aannames uitdaagt vóór een routinewijziging. Wat maakt dit passend voor Station 1? Mensen gebruiken deze verandering in dagelijks werk.",
          options: {
            A: "AI beslist welke routine verdwijnt.",
            B: "Mensen gebruiken AI-vragen om verandering te bespreken.",
            C: "Het team slaat overleg over omdat AI neutraal is.",
            D: "Elke aanname wordt een automatische regel.",
          },
          correct_answer: "B",
          feedback:
            "Het gebruik past wanneer AI het teamgesprek verbetert. Mensen beslissen nog steeds hoe de routine verandert. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "A team wants AI to challenge assumptions before changing a routine. What makes this use appropriate for Station 1?",
      explanation:
        "The use is appropriate when AI improves the team discussion. People still decide how the routine should change.",
      answers: [
        {
          id: "station1_q331_a",
          question_id: "station1_q331",
          answer_text: "AI decides which routine disappears.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q331_b",
          question_id: "station1_q331",
          answer_text: "People use AI questions to discuss the change.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station1_q331_c",
          question_id: "station1_q331",
          answer_text: "The team skips discussion because AI is neutral.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q331_d",
          question_id: "station1_q331",
          answer_text: "Every assumption becomes an automatic rule.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q332",
      station_id: "station_1",
      stage: 3,
      complexity: 3,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Different teams send separate stakeholder updates, so messages conflict. AI could help prepare one flow. What should you do first?",
          options: {
            A: "Let AI publish one combined message.",
            B: "Keep separate updates to avoid discussion.",
            C: "Map common themes, owners and gaps.",
            D: "Ask each team to write faster.",
          },
          correct_answer: "C",
          feedback:
            "Mapping themes, owners and gaps prepares alignment before messages are sent. AI supports the workflow without owning communication.",
        },
        pt: {
          question:
            "Equipes diferentes enviam updates separados para stakeholders, então mensagens conflitam. A IA pode preparar um fluxo único. O que fazer primeiro?",
          options: {
            A: "Deixar a IA publicar uma mensagem combinada.",
            B: "Manter updates separados para evitar discussão.",
            C: "Mapear temas comuns, responsáveis e lacunas.",
            D: "Pedir que cada equipe escreva mais rápido.",
          },
          correct_answer: "C",
          feedback:
            "Mapear temas, donos e lacunas prepara alinhamento antes do envio. A IA apoia o fluxo sem assumir comunicação.",
        },
        sv: {
          question:
            "Olika team skickar separata intressentuppdateringar och budskap krockar. AI kan förbereda ett flöde. Vad gör du först? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "Låt AI publicera ett samlat meddelande.",
            B: "Behåll separata uppdateringar för att undvika dialog.",
            C: "Kartlägg gemensamma teman, ägare och luckor.",
            D: "Be varje team skriva snabbare.",
          },
          correct_answer: "C",
          feedback:
            "Kartläggning av teman, ägare och luckor förbereder samsyn före utskick. AI stödjer flödet utan att äga kommunikationen. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "Verschillende teams sturen aparte stakeholderupdates, waardoor boodschappen botsen. AI kan één flow voorbereiden. Wat doe je eerst? Mensen gebruiken deze verandering in dagelijks werk.",
          options: {
            A: "Laat AI één gecombineerd bericht publiceren.",
            B: "Houd updates apart om overleg te vermijden.",
            C: "Map gemeenschappelijke thema’s, eigenaren en gaten.",
            D: "Vraag elk team sneller te schrijven.",
          },
          correct_answer: "C",
          feedback:
            "Thema’s, eigenaren en gaten mappen bereidt afstemming voor. AI ondersteunt de workflow zonder communicatie te bezitten. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "Different teams send separate stakeholder updates, so messages conflict. AI could help prepare one flow. What should you do first?",
      explanation:
        "Mapping themes, owners and gaps prepares alignment before messages are sent. AI supports the workflow without owning communication.",
      answers: [
        {
          id: "station1_q332_a",
          question_id: "station1_q332",
          answer_text: "Let AI publish one combined message.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q332_b",
          question_id: "station1_q332",
          answer_text: "Keep separate updates to avoid discussion.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q332_c",
          question_id: "station1_q332",
          answer_text: "Map common themes, owners and gaps.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station1_q332_d",
          question_id: "station1_q332",
          answer_text: "Ask each team to write faster.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station1_q333",
      station_id: "station_1",
      stage: 3,
      complexity: 4,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI creates a new handover flow from several examples. It looks clear and practical. Which behaviour shows overtrust?",
          options: {
            A: "Pilot it with one team first.",
            B: "Ask users where ownership is unclear.",
            C: "Use it as a draft for redesign.",
            D: "Roll it out because it looks complete.",
          },
          correct_answer: "D",
          feedback:
            "A clear AI draft is still a draft. Rolling it out without testing role clarity confuses appearance with readiness.",
        },
        pt: {
          question:
            "A IA cria um novo fluxo de passagem a partir de vários exemplos. Parece claro e prático. Qual comportamento mostra excesso de confiança?",
          options: {
            A: "Pilotar com uma equipe primeiro.",
            B: "Perguntar onde responsabilidades estão incertas.",
            C: "Usar como rascunho para redesenho.",
            D: "Fazer rollout porque parece completo.",
          },
          correct_answer: "D",
          feedback:
            "Um rascunho claro da IA ainda é rascunho. Fazer rollout sem testar papéis confunde aparência com prontidão. Isso mantém a mudança do fluxo clara e com responsabilidade humana.",
        },
        sv: {
          question:
            "AI skapar ett nytt överlämningsflöde från flera exempel. Det verkar tydligt och praktiskt. Vilket beteende visar övertro? Människor använder ändringen i dagligt arbete.",
          options: {
            A: "Pilottesta med ett team först.",
            B: "Fråga användare var ägarskap är oklart.",
            C: "Använd det som utkast för omformning.",
            D: "Rulla ut det eftersom det verkar komplett.",
          },
          correct_answer: "D",
          feedback:
            "Ett tydligt AI-utkast är fortfarande utkast. Utrullning utan rolltest blandar utseende med beredskap. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "AI maakt een nieuwe overdrachtsflow uit meerdere voorbeelden. Het lijkt duidelijk en praktisch. Welk gedrag toont oververtrouwen? Mensen gebruiken deze verandering in dagelijks werk.",
          options: {
            A: "Pilot het eerst met één team.",
            B: "Vraag gebruikers waar eigenaarschap onduidelijk is.",
            C: "Gebruik het als concept voor herontwerp.",
            D: "Rol het uit omdat het compleet lijkt.",
          },
          correct_answer: "D",
          feedback:
            "Een duidelijk AI-concept blijft een concept. Uitrollen zonder rolhelderheid te testen verwart uiterlijk met gereedheid. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "AI creates a new handover flow from several examples. It looks clear and practical. Which behaviour shows overtrust?",
      explanation:
        "A clear AI draft is still a draft. Rolling it out without testing role clarity confuses appearance with readiness.",
      answers: [
        {
          id: "station1_q333_a",
          question_id: "station1_q333",
          answer_text: "Pilot it with one team first.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station1_q333_b",
          question_id: "station1_q333",
          answer_text: "Ask users where ownership is unclear.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q333_c",
          question_id: "station1_q333",
          answer_text: "Use it as a draft for redesign.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q333_d",
          question_id: "station1_q333",
          answer_text: "Roll it out because it looks complete.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station1_q334",
      station_id: "station_1",
      stage: 3,
      complexity: 5,
      core_concept: "Responsible AI judgment under ambiguity",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI shows two possible future workflows, but teams disagree on who owns the new review step. What should you ask AI to do next?",
          options: {
            A: "Map ownership options and trade-offs for discussion.",
            B: "Choose the owner with the least workload.",
            C: "Hide ownership conflict until rollout.",
            D: "Merge both workflows without team decision.",
          },
          correct_answer: "A",
          feedback:
            "AI should help structure the ownership discussion, not decide it. The human decision is the owner and trade-off.",
        },
        pt: {
          question:
            "A IA mostra dois fluxos futuros possíveis, mas equipes discordam sobre quem assume a nova revisão. O que pedir à IA agora?",
          options: {
            A: "Mapear opções de responsabilidade e trade-offs para discussão.",
            B: "Escolher o responsável com menor carga.",
            C: "Esconder conflito de responsabilidade até o rollout.",
            D: "Unir os dois fluxos sem decisão da equipe.",
          },
          correct_answer: "A",
          feedback:
            "A IA deve estruturar a discussão de responsabilidade, não decidir. A decisão humana é dono e trade-off. Isso mantém a mudança do fluxo clara e com responsabilidade humana.",
        },
        sv: {
          question:
            "AI visar två möjliga framtida flöden, men team är oense om ägare för ny granskning. Vad ber du AI göra nu?",
          options: {
            A: "Kartlägg ägaralternativ och trade-offs för dialog.",
            B: "Välj ägaren med minst arbetsbelastning.",
            C: "Dölj ägarkonflikten till utrullning. i flödet",
            D: "Slå ihop båda flöden utan teambeslut.",
          },
          correct_answer: "A",
          feedback:
            "AI ska strukturera ägardialogen, inte besluta. Det mänskliga beslutet gäller ägare och trade-off. Det håller flödesändringen tydlig och mänskligt ägd.",
        },
        nl: {
          question:
            "AI toont twee mogelijke toekomstige workflows, maar teams verschillen over eigenaar van de nieuwe reviewstap. Wat vraag je AI nu?",
          options: {
            A: "Map eigenaarsopties en trade-offs voor overleg.",
            B: "Kies de eigenaar met de minste werkdruk.",
            C: "Verberg eigenaarsconflict tot uitrol. in de workflow",
            D: "Voeg beide workflows samen zonder teambesluit.",
          },
          correct_answer: "A",
          feedback:
            "AI moet het eigenaarsgesprek structureren, niet beslissen. De menselijke beslissing gaat over eigenaar en trade-off. Dit houdt de workflowverandering helder en menselijk eigendom.",
        },
      },
      question_text:
        "AI shows two possible future workflows, but teams disagree on who owns the new review step. What should you ask AI to do next?",
      explanation:
        "AI should help structure the ownership discussion, not decide it. The human decision is the owner and trade-off.",
      answers: [
        {
          id: "station1_q334_a",
          question_id: "station1_q334",
          answer_text: "Map ownership options and trade-offs for discussion.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station1_q334_b",
          question_id: "station1_q334",
          answer_text: "Choose the owner with the least workload.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station1_q334_c",
          question_id: "station1_q334",
          answer_text: "Hide ownership conflict until rollout.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station1_q334_d",
          question_id: "station1_q334",
          answer_text: "Merge both workflows without team decision.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q001",
      station_id: "station_2",
      stage: 1,
      complexity: 1,
      core_concept:
        "AI is useful pattern-based generation, not magic or human expertise",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A colleague says AI must be magic because it writes useful text quickly. Which response best corrects this?",
          options: {
            A: "AI understands work like a human expert.",
            B: "AI is just a normal calculator.",
            C: "AI generates likely outputs from patterns and context.",
            D: "AI always knows why an answer is right.",
          },
          correct_answer: "C",
          feedback:
            "Useful speed does not make AI magic. A realistic model is that it generates likely outputs from patterns, context and instructions.",
        },
        pt: {
          question:
            "Um colega diz que IA deve ser mágica porque escreve textos úteis rapidamente. Qual resposta corrige melhor isso?",
          options: {
            A: "A IA entende o trabalho como especialista humano.",
            B: "A IA é só uma calculadora comum.",
            C: "A IA gera saídas prováveis a partir de padrões e contexto.",
            D: "A IA sempre sabe por que uma resposta está certa.",
          },
          correct_answer: "C",
          feedback:
            "Velocidade útil não torna a IA mágica. O modelo realista é gerar saídas prováveis por padrões, contexto e instruções.",
        },
        sv: {
          question:
            "En kollega säger att AI måste vara magi eftersom den skriver användbar text snabbt. Vilket svar rättar detta bäst?",
          options: {
            A: "AI förstår arbete som en mänsklig expert.",
            B: "AI är bara en vanlig miniräknare.",
            C: "AI skapar sannolika svar från mönster och kontext.",
            D: "AI vet alltid varför ett svar är rätt.",
          },
          correct_answer: "C",
          feedback:
            "Användbar hastighet gör inte AI magisk. En realistisk modell är sannolika svar från mönster, kontext och instruktioner. Det hjälper deltagaren använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "Een collega zegt dat AI magie moet zijn omdat het snel nuttige tekst schrijft. Welke reactie corrigeert dit het best?",
          options: {
            A: "AI begrijpt werk als een menselijke expert.",
            B: "AI is gewoon een normale calculator.",
            C: "AI maakt waarschijnlijke output uit patronen en context.",
            D: "AI weet altijd waarom een antwoord klopt.",
          },
          correct_answer: "C",
          feedback:
            "Nuttige snelheid maakt AI geen magie. Realistisch is dat AI waarschijnlijke output maakt uit patronen, context en instructies.",
        },
      },
      question_text:
        "A colleague says AI must be magic because it writes useful text quickly. Which response best corrects this?",
      explanation:
        "Useful speed does not make AI magic. A realistic model is that it generates likely outputs from patterns, context and instructions.",
      answers: [
        {
          id: "station2_q001_a",
          question_id: "station2_q001",
          answer_text: "AI understands work like a human expert.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q001_b",
          question_id: "station2_q001",
          answer_text: "AI is just a normal calculator.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q001_c",
          question_id: "station2_q001",
          answer_text: "AI generates likely outputs from patterns and context.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station2_q001_d",
          question_id: "station2_q001",
          answer_text: "AI always knows why an answer is right.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q002",
      station_id: "station_2",
      stage: 1,
      complexity: 1,
      core_concept:
        "AI is useful pattern-based generation, not magic or human expertise",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A learner compares AI with a spreadsheet formula. Which option shows the best basic understanding?",
          options: {
            A: "Both always return one fixed answer.",
            B: "AI can generate varied outputs from context.",
            C: "AI follows only visible spreadsheet rules.",
            D: "A formula has human judgment.",
          },
          correct_answer: "B",
          feedback:
            "Traditional formulas are deterministic. Generative AI can vary because context and instructions shape the output.",
        },
        pt: {
          question:
            "Um aprendiz compara IA com uma fórmula de planilha. Qual opção mostra o melhor entendimento básico?",
          options: {
            A: "Ambas sempre retornam uma resposta fixa.",
            B: "A IA pode gerar saídas variadas pelo contexto.",
            C: "A IA segue só regras visíveis da planilha.",
            D: "Uma fórmula tem julgamento humano.",
          },
          correct_answer: "B",
          feedback:
            "Fórmulas tradicionais são determinísticas. IA generativa pode variar porque contexto e instruções moldam a saída.",
        },
        sv: {
          question:
            "En deltagare jämför AI med en kalkylbladsformel. Vilket alternativ visar bäst grundförståelse?",
          options: {
            A: "Båda ger alltid ett fast svar.",
            B: "AI kan skapa varierade svar från kontext.",
            C: "AI följer bara synliga kalkylbladsregler.",
            D: "En formel har mänskligt omdöme.",
          },
          correct_answer: "B",
          feedback:
            "Traditionella formler är deterministiska. Generativ AI kan variera eftersom kontext och instruktioner formar svaret.",
        },
        nl: {
          question:
            "Een lerende vergelijkt AI met een spreadsheetformule. Welke optie toont het beste basisbegrip?",
          options: {
            A: "Beide geven altijd één vast antwoord.",
            B: "AI kan gevarieerde output maken uit context.",
            C: "AI volgt alleen zichtbare spreadsheetregels.",
            D: "Een formule heeft menselijk oordeel.",
          },
          correct_answer: "B",
          feedback:
            "Traditionele formules zijn deterministisch. Generatieve AI kan variëren doordat context en instructies de output vormen.",
        },
      },
      question_text:
        "A learner compares AI with a spreadsheet formula. Which option shows the best basic understanding?",
      explanation:
        "Traditional formulas are deterministic. Generative AI can vary because context and instructions shape the output.",
      answers: [
        {
          id: "station2_q002_a",
          question_id: "station2_q002",
          answer_text: "Both always return one fixed answer.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q002_b",
          question_id: "station2_q002",
          answer_text: "AI can generate varied outputs from context.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station2_q002_c",
          question_id: "station2_q002",
          answer_text: "AI follows only visible spreadsheet rules.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q002_d",
          question_id: "station2_q002",
          answer_text: "A formula has human judgment.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q003",
      station_id: "station_2",
      stage: 1,
      complexity: 2,
      core_concept:
        "AI is useful pattern-based generation, not magic or human expertise",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI writes a clear summary of a topic you barely know. Which behaviour shows overtrust?",
          options: {
            A: "Treat clarity as proof the summary is true.",
            B: "Use it as a starting point for learning.",
            C: "Ask what assumptions may be missing.",
            D: "Remember AI may be useful but imperfect.",
          },
          correct_answer: "A",
          feedback:
            "Fluent wording is not proof of truth. The useful habit is to treat the output as support, not proof.",
        },
        pt: {
          question:
            "A IA escreve um resumo claro sobre um tema que você mal conhece. Qual comportamento mostra confiança excessiva?",
          options: {
            A: "Tratar clareza como prova de que o resumo é verdadeiro.",
            B: "Usar como ponto de partida para aprender.",
            C: "Perguntar quais premissas podem faltar.",
            D: "Lembrar que IA pode ser útil, mas imperfeita.",
          },
          correct_answer: "A",
          feedback:
            "Texto fluente não prova verdade. O hábito útil é tratar a saída como apoio, não como prova.",
        },
        sv: {
          question:
            "AI skriver en tydlig sammanfattning av ett ämne du knappt kan. Vilket beteende visar övertro?",
          options: {
            A: "Se tydlighet som bevis för att sammanfattningen är sann.",
            B: "Använd den som startpunkt för lärande.",
            C: "Fråga vilka antaganden som kan saknas.",
            D: "Kom ihåg att AI kan vara nyttig men ofullkomlig.",
          },
          correct_answer: "A",
          feedback:
            "Flytande formulering bevisar inte sanning. Den användbara vanan är att se svaret som stöd, inte bevis.",
        },
        nl: {
          question:
            "AI schrijft een duidelijke samenvatting van een onderwerp dat je nauwelijks kent. Welk gedrag toont oververtrouwen?",
          options: {
            A: "Zie duidelijkheid als bewijs dat de samenvatting waar is.",
            B: "Gebruik het als startpunt om te leren.",
            C: "Vraag welke aannames kunnen ontbreken.",
            D: "Onthoud dat AI nuttig maar imperfect kan zijn.",
          },
          correct_answer: "A",
          feedback:
            "Vloeiende tekst bewijst geen waarheid. De nuttige gewoonte is output als steun te behandelen, niet als bewijs.",
        },
      },
      question_text:
        "AI writes a clear summary of a topic you barely know. Which behaviour shows overtrust?",
      explanation:
        "Fluent wording is not proof of truth. The useful habit is to treat the output as support, not proof.",
      answers: [
        {
          id: "station2_q003_a",
          question_id: "station2_q003",
          answer_text: "Treat clarity as proof the summary is true.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station2_q003_b",
          question_id: "station2_q003",
          answer_text: "Use it as a starting point for learning.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q003_c",
          question_id: "station2_q003",
          answer_text: "Ask what assumptions may be missing.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q003_d",
          question_id: "station2_q003",
          answer_text: "Remember AI may be useful but imperfect.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q004",
      station_id: "station_2",
      stage: 1,
      complexity: 2,
      core_concept:
        "AI is useful pattern-based generation, not magic or human expertise",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A colleague says AI is just a search engine with nicer wording. Which response best corrects this?",
          options: {
            A: "Yes, AI only lists existing pages.",
            B: "AI never uses information from context.",
            C: "Search and AI are always identical.",
            D: "AI can generate new wording from context and patterns.",
          },
          correct_answer: "D",
          feedback:
            "Some tools combine search and AI, but they are not the same. Generative AI creates outputs from context and patterns.",
        },
        pt: {
          question:
            "Um colega diz que IA é só um buscador com texto mais bonito. Qual resposta corrige melhor isso?",
          options: {
            A: "Sim, IA só lista páginas existentes.",
            B: "A IA nunca usa informação de contexto.",
            C: "Busca e IA são sempre idênticas.",
            D: "A IA pode gerar novo texto por contexto e padrões.",
          },
          correct_answer: "D",
          feedback:
            "Algumas ferramentas combinam busca e IA, mas não são iguais. IA generativa cria saídas por contexto e padrões.",
        },
        sv: {
          question:
            "En kollega säger att AI bara är en sökmotor med snyggare text. Vilket svar rättar detta bäst?",
          options: {
            A: "Ja, AI listar bara befintliga sidor.",
            B: "AI använder aldrig information från kontext.",
            C: "Sökning och AI är alltid identiska.",
            D: "AI kan skapa ny formulering från kontext och mönster.",
          },
          correct_answer: "D",
          feedback:
            "Vissa verktyg kombinerar sökning och AI, men de är inte samma. Generativ AI skapar svar från kontext och mönster.",
        },
        nl: {
          question:
            "Een collega zegt dat AI alleen een zoekmachine met mooiere tekst is. Welke reactie corrigeert dit het best?",
          options: {
            A: "Ja, AI somt alleen bestaande pagina’s op.",
            B: "AI gebruikt nooit informatie uit context.",
            C: "Zoeken en AI zijn altijd hetzelfde.",
            D: "AI kan nieuwe formulering maken uit context en patronen.",
          },
          correct_answer: "D",
          feedback:
            "Sommige tools combineren zoeken en AI, maar ze zijn niet hetzelfde. Generatieve AI maakt output uit context en patronen.",
        },
      },
      question_text:
        "A colleague says AI is just a search engine with nicer wording. Which response best corrects this?",
      explanation:
        "Some tools combine search and AI, but they are not the same. Generative AI creates outputs from context and patterns.",
      answers: [
        {
          id: "station2_q004_a",
          question_id: "station2_q004",
          answer_text: "Yes, AI only lists existing pages.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q004_b",
          question_id: "station2_q004",
          answer_text: "AI never uses information from context.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q004_c",
          question_id: "station2_q004",
          answer_text: "Search and AI are always identical.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q004_d",
          question_id: "station2_q004",
          answer_text: "AI can generate new wording from context and patterns.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station2_q005",
      station_id: "station_2",
      stage: 2,
      complexity: 2,
      core_concept:
        "AI output can be a useful draft or starting point when context matters",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI creates a neat summary of a long report. You need to brief your manager. How should you treat this output?",
          options: {
            A: "As a final expert conclusion.",
            B: "As useless because AI is imperfect.",
            C: "As a useful starting point for briefing.",
            D: "As proof the report was understood.",
          },
          correct_answer: "C",
          feedback:
            "AI can structure a useful starting point, but the briefing should be checked against the report before use.",
        },
        pt: {
          question:
            "A IA cria um resumo organizado de um relatório longo. Você precisa preparar sua gerente. Como tratar essa saída?",
          options: {
            A: "Como conclusão final de especialista.",
            B: "Como inútil porque IA é imperfeita.",
            C: "Como ponto de partida útil para briefing.",
            D: "Como prova de que o relatório foi entendido.",
          },
          correct_answer: "C",
          feedback:
            "A IA pode estruturar um ponto de partida útil, mas o briefing deve ser comparado ao relatório antes do uso.",
        },
        sv: {
          question:
            "AI skapar en tydlig sammanfattning av en lång rapport. Du ska briefa din chef. Hur bör du behandla svaret?",
          options: {
            A: "Som en slutlig expertbedömning.",
            B: "Som värdelös eftersom AI är ofullkomlig.",
            C: "Som en användbar startpunkt för briefing.",
            D: "Som bevis för att rapporten förståtts.",
          },
          correct_answer: "C",
          feedback:
            "AI kan strukturera en användbar startpunkt, men briefingen bör jämföras med rapporten före användning.",
        },
        nl: {
          question:
            "AI maakt een nette samenvatting van een lang rapport. Je moet je manager briefen. Hoe behandel je deze output?",
          options: {
            A: "Als definitieve expertconclusie.",
            B: "Als nutteloos omdat AI imperfect is.",
            C: "Als nuttig startpunt voor briefing.",
            D: "Als bewijs dat het rapport begrepen is.",
          },
          correct_answer: "C",
          feedback:
            "AI kan een nuttig startpunt structureren, maar de briefing moet vóór gebruik met het rapport worden vergeleken.",
        },
      },
      question_text:
        "AI creates a neat summary of a long report. You need to brief your manager. How should you treat this output?",
      explanation:
        "AI can structure a useful starting point, but the briefing should be checked against the report before use.",
      answers: [
        {
          id: "station2_q005_a",
          question_id: "station2_q005",
          answer_text: "As a final expert conclusion.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q005_b",
          question_id: "station2_q005",
          answer_text: "As useless because AI is imperfect.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q005_c",
          question_id: "station2_q005",
          answer_text: "As a useful starting point for briefing.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station2_q005_d",
          question_id: "station2_q005",
          answer_text: "As proof the report was understood.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q006",
      station_id: "station_2",
      stage: 2,
      complexity: 2,
      core_concept:
        "AI output can be a useful draft or starting point when context matters",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI gives a generic answer to a workplace question with little context. Which option shows the best understanding of AI?",
          options: {
            A: "AI failed because it is never useful.",
            B: "The output reflects limited context and instructions.",
            C: "AI should know the workplace automatically.",
            D: "Generic wording proves the answer is safe.",
          },
          correct_answer: "B",
          feedback:
            "AI output depends strongly on context and instructions. A generic answer often reflects a thin input, not magic knowledge.",
        },
        pt: {
          question:
            "A IA dá uma resposta genérica a uma pergunta de trabalho com pouco contexto. Qual opção mostra melhor entendimento da IA?",
          options: {
            A: "A IA falhou porque nunca é útil.",
            B: "A saída reflete contexto e instruções limitados.",
            C: "A IA deveria conhecer o trabalho automaticamente.",
            D: "Texto genérico prova que a resposta é segura.",
          },
          correct_answer: "B",
          feedback:
            "A saída da IA depende de contexto e instruções. Resposta genérica costuma refletir entrada fraca, não conhecimento mágico.",
        },
        sv: {
          question:
            "AI ger ett generiskt svar på en arbetsfråga med lite kontext. Vilket alternativ visar bäst AI-förståelse?",
          options: {
            A: "AI misslyckades eftersom den aldrig är nyttig.",
            B: "Svaret speglar begränsad kontext och instruktioner.",
            C: "AI borde automatiskt känna arbetet.",
            D: "Generisk text bevisar att svaret är säkert.",
          },
          correct_answer: "B",
          feedback:
            "AI-svar beror starkt på kontext och instruktioner. Ett generiskt svar speglar ofta tunn input, inte magisk kunskap. Det hjälper deltagaren använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "AI geeft een generiek antwoord op een werkvraag met weinig context. Welke optie toont het beste AI-begrip?",
          options: {
            A: "AI faalde omdat het nooit nuttig is.",
            B: "De output weerspiegelt beperkte context en instructies.",
            C: "AI zou het werk automatisch moeten kennen.",
            D: "Generieke tekst bewijst dat het antwoord veilig is.",
          },
          correct_answer: "B",
          feedback:
            "AI-output hangt sterk af van context en instructies. Een generiek antwoord weerspiegelt vaak dunne input, geen magische kennis.",
        },
      },
      question_text:
        "AI gives a generic answer to a workplace question with little context. Which option shows the best understanding of AI?",
      explanation:
        "AI output depends strongly on context and instructions. A generic answer often reflects a thin input, not magic knowledge.",
      answers: [
        {
          id: "station2_q006_a",
          question_id: "station2_q006",
          answer_text: "AI failed because it is never useful.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q006_b",
          question_id: "station2_q006",
          answer_text: "The output reflects limited context and instructions.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station2_q006_c",
          question_id: "station2_q006",
          answer_text: "AI should know the workplace automatically.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q006_d",
          question_id: "station2_q006",
          answer_text: "Generic wording proves the answer is safe.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q007",
      station_id: "station_2",
      stage: 2,
      complexity: 3,
      core_concept:
        "AI output can be a useful draft or starting point when context matters",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI prepares three options for a meeting and explains them confidently. Which behaviour shows overtrust?",
          options: {
            A: "Use the options to prepare discussion.",
            B: "Notice what information may be missing.",
            C: "Treat them as possible starting points.",
            D: "Assume confidence means the options are best.",
          },
          correct_answer: "D",
          feedback:
            "Confident explanations can support meeting preparation, but confidence does not make the options best or complete.",
        },
        pt: {
          question:
            "A IA prepara três opções para uma reunião e as explica com confiança. Qual comportamento mostra excesso de confiança?",
          options: {
            A: "Usar as opções para preparar discussão.",
            B: "Notar quais informações podem faltar.",
            C: "Tratá-las como possíveis pontos de partida.",
            D: "Supor que confiança significa melhores opções.",
          },
          correct_answer: "D",
          feedback:
            "Explicações confiantes podem apoiar a preparação da reunião, mas confiança não torna as opções melhores ou completas.",
        },
        sv: {
          question:
            "AI förbereder tre alternativ för ett möte och förklarar dem säkert. Vilket beteende visar övertro?",
          options: {
            A: "Använd alternativen för att förbereda dialog.",
            B: "Notera vilken information som kan saknas.",
            C: "Se dem som möjliga startpunkter.",
            D: "Anta att säkerhet betyder bäst alternativ.",
          },
          correct_answer: "D",
          feedback:
            "Säkra förklaringar kan stödja mötesförberedelse, men säkerhet gör inte alternativen bäst eller kompletta.",
        },
        nl: {
          question:
            "AI bereidt drie opties voor een meeting voor en legt ze zelfverzekerd uit. Welk gedrag toont oververtrouwen?",
          options: {
            A: "Gebruik de opties om overleg voor te bereiden.",
            B: "Let op welke informatie kan ontbreken.",
            C: "Behandel ze als mogelijke startpunten.",
            D: "Neem aan dat vertrouwen betekent dat opties het best zijn.",
          },
          correct_answer: "D",
          feedback:
            "Zekere uitleg kan meetingvoorbereiding steunen, maar zekerheid maakt opties niet automatisch het best of compleet.",
        },
      },
      question_text:
        "AI prepares three options for a meeting and explains them confidently. Which behaviour shows overtrust?",
      explanation:
        "Confident explanations can support meeting preparation, but confidence does not make the options best or complete.",
      answers: [
        {
          id: "station2_q007_a",
          question_id: "station2_q007",
          answer_text: "Use the options to prepare discussion.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q007_b",
          question_id: "station2_q007",
          answer_text: "Notice what information may be missing.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q007_c",
          question_id: "station2_q007",
          answer_text: "Treat them as possible starting points.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q007_d",
          question_id: "station2_q007",
          answer_text: "Assume confidence means the options are best.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station2_q008",
      station_id: "station_2",
      stage: 3,
      complexity: 3,
      core_concept:
        "Fluent AI output can support reasoning but does not own truth or accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI helps compare options for a decision memo. The answer is fluent, balanced and useful. What remains outside AI’s responsibility?",
          options: {
            A: "Owning the final business judgment.",
            B: "Making text easier to read.",
            C: "Structuring possible pros and cons.",
            D: "Suggesting useful comparison categories.",
          },
          correct_answer: "A",
          feedback:
            "AI can support comparison, but accountability for the final judgment stays human. Fluent balance does not create responsibility.",
        },
        pt: {
          question:
            "A IA ajuda a comparar opções para uma nota de decisão. A resposta é fluente, equilibrada e útil. O que fica fora da responsabilidade da IA?",
          options: {
            A: "Assumir o julgamento de negócio final.",
            B: "Deixar o texto mais fácil de ler.",
            C: "Estruturar possíveis prós e contras.",
            D: "Sugerir categorias úteis de comparação.",
          },
          correct_answer: "A",
          feedback:
            "A IA apoia comparação, mas a responsabilidade pelo julgamento final continua humana. Fluência não cria responsabilidade. Isso ajuda o aprendiz a usar IA com expectativas realistas.",
        },
        sv: {
          question:
            "AI hjälper jämföra alternativ för ett beslutsunderlag. Svaret är flytande, balanserat och nyttigt. Vad ligger utanför AI:s ansvar?",
          options: {
            A: "Äga det slutliga affärsomdömet.",
            B: "Göra texten lättare att läsa.",
            C: "Strukturera möjliga för- och nackdelar.",
            D: "Föreslå användbara jämförelsekategorier.",
          },
          correct_answer: "A",
          feedback:
            "AI kan stödja jämförelse, men ansvar för slutligt omdöme är mänskligt. Flytande balans skapar inte ansvar. Det hjälper deltagaren använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "AI helpt opties vergelijken voor een beslisnotitie. Het antwoord is vloeiend, gebalanceerd en nuttig. Wat valt buiten AI-verantwoordelijkheid?",
          options: {
            A: "Het uiteindelijke zakelijke oordeel bezitten.",
            B: "Tekst makkelijker leesbaar maken.",
            C: "Mogelijke voor- en nadelen structureren.",
            D: "Nuttige vergelijkingscategorieën voorstellen.",
          },
          correct_answer: "A",
          feedback:
            "AI kan vergelijking ondersteunen, maar accountability voor het eindoordeel blijft menselijk. Vloeiende balans creëert geen verantwoordelijkheid. Dit helpt de lerende AI met realistische verwachtingen te gebruiken.",
        },
      },
      question_text:
        "AI helps compare options for a decision memo. The answer is fluent, balanced and useful. What remains outside AI’s responsibility?",
      explanation:
        "AI can support comparison, but accountability for the final judgment stays human. Fluent balance does not create responsibility.",
      answers: [
        {
          id: "station2_q008_a",
          question_id: "station2_q008",
          answer_text: "Owning the final business judgment.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station2_q008_b",
          question_id: "station2_q008",
          answer_text: "Making text easier to read.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q008_c",
          question_id: "station2_q008",
          answer_text: "Structuring possible pros and cons.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q008_d",
          question_id: "station2_q008",
          answer_text: "Suggesting useful comparison categories.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q009",
      station_id: "station_2",
      stage: 3,
      complexity: 4,
      core_concept:
        "Fluent AI output can support reasoning but does not own truth or accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI combines several team inputs into one polished explanation. Some inputs were incomplete. What is the main risk?",
          options: {
            A: "The text may sound too informal.",
            B: "The answer may be slower to read.",
            C: "The polished output may hide missing context.",
            D: "The teams may prefer bullet points.",
          },
          correct_answer: "C",
          feedback:
            "A polished AI output can look complete while carrying gaps from its inputs. Usefulness and reliability are different.",
        },
        pt: {
          question:
            "A IA combina inputs de várias equipes em uma explicação polida. Alguns inputs estavam incompletos. Qual é o risco principal?",
          options: {
            A: "O texto pode soar informal demais.",
            B: "A resposta pode demorar mais para ler.",
            C: "A saída polida pode esconder contexto ausente.",
            D: "As equipes podem preferir bullet points.",
          },
          correct_answer: "C",
          feedback:
            "Uma saída polida da IA pode parecer completa mesmo com lacunas dos inputs. Utilidade e confiabilidade são diferentes.",
        },
        sv: {
          question:
            "AI kombinerar flera teams input till en polerad förklaring. Vissa input var ofullständiga. Vilken är huvudrisken?",
          options: {
            A: "Texten kan låta för informell.",
            B: "Svaret kan ta längre tid att läsa.",
            C: "Det polerade svaret kan dölja saknad kontext.",
            D: "Team kan föredra punktlistor.",
          },
          correct_answer: "C",
          feedback:
            "Ett polerat AI-svar kan verka komplett trots luckor i input. Nytta och tillförlitlighet är olika saker. Det hjälper deltagaren använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "AI combineert input van meerdere teams tot één gepolijste uitleg. Sommige input was onvolledig. Wat is het grootste risico?",
          options: {
            A: "De tekst kan te informeel klinken.",
            B: "Het antwoord kan langer duren om te lezen.",
            C: "De gepolijste output kan ontbrekende context verbergen.",
            D: "Teams kunnen bulletpoints verkiezen.",
          },
          correct_answer: "C",
          feedback:
            "Gepolijste AI-output kan compleet lijken terwijl input gaten bevat. Bruikbaarheid en betrouwbaarheid zijn verschillend. Dit helpt de lerende AI met realistische verwachtingen te gebruiken.",
        },
      },
      question_text:
        "AI combines several team inputs into one polished explanation. Some inputs were incomplete. What is the main risk?",
      explanation:
        "A polished AI output can look complete while carrying gaps from its inputs. Usefulness and reliability are different.",
      answers: [
        {
          id: "station2_q009_a",
          question_id: "station2_q009",
          answer_text: "The text may sound too informal.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q009_b",
          question_id: "station2_q009",
          answer_text: "The answer may be slower to read.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q009_c",
          question_id: "station2_q009",
          answer_text: "The polished output may hide missing context.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station2_q009_d",
          question_id: "station2_q009",
          answer_text: "The teams may prefer bullet points.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q010",
      station_id: "station_2",
      stage: 3,
      complexity: 4,
      core_concept:
        "Fluent AI output can support reasoning but does not own truth or accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "One AI tool is connected to internal documents; another is general. Both answer the same question confidently. What expectation is realistic?",
          options: {
            A: "Both tools have the same work context.",
            B: "Their answers may differ because context differs.",
            C: "The more confident answer is always better.",
            D: "General AI automatically knows internal decisions.",
          },
          correct_answer: "B",
          feedback:
            "AI output depends on available context. A connected tool may use internal material, while general AI may not.",
        },
        pt: {
          question:
            "Uma ferramenta de IA está conectada a documentos internos; outra é geral. Ambas respondem com confiança. Qual expectativa é realista?",
          options: {
            A: "Ambas ferramentas têm o mesmo contexto de trabalho.",
            B: "As respostas podem diferir porque o contexto difere.",
            C: "A resposta mais confiante é sempre melhor.",
            D: "IA geral conhece automaticamente decisões internas.",
          },
          correct_answer: "B",
          feedback:
            "A saída da IA depende do contexto disponível. Uma ferramenta conectada pode usar material interno; IA geral talvez não.",
        },
        sv: {
          question:
            "Ett AI-verktyg är kopplat till interna dokument; ett annat är generellt. Båda svarar säkert. Vilken förväntan är realistisk?",
          options: {
            A: "Båda verktygen har samma arbetskontext.",
            B: "Svaren kan skilja sig eftersom kontexten skiljer sig.",
            C: "Det säkrare svaret är alltid bättre.",
            D: "Generell AI känner automatiskt interna beslut.",
          },
          correct_answer: "B",
          feedback:
            "AI-svar beror på tillgänglig kontext. Ett kopplat verktyg kan använda internt material, medan generell AI kanske inte kan.",
        },
        nl: {
          question:
            "Eén AI-tool is verbonden met interne documenten; een andere is algemeen. Beide antwoorden zelfverzekerd. Welke verwachting is realistisch?",
          options: {
            A: "Beide tools hebben dezelfde werkcontext.",
            B: "Hun antwoorden kunnen verschillen omdat context verschilt.",
            C: "Het meest zelfverzekerde antwoord is altijd beter.",
            D: "Algemene AI kent automatisch interne besluiten.",
          },
          correct_answer: "B",
          feedback:
            "AI-output hangt af van beschikbare context. Een verbonden tool kan intern materiaal gebruiken; algemene AI mogelijk niet. Dit helpt de lerende AI met realistische verwachtingen te gebruiken.",
        },
      },
      question_text:
        "One AI tool is connected to internal documents; another is general. Both answer the same question confidently. What expectation is realistic?",
      explanation:
        "AI output depends on available context. A connected tool may use internal material, while general AI may not.",
      answers: [
        {
          id: "station2_q010_a",
          question_id: "station2_q010",
          answer_text: "Both tools have the same work context.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q010_b",
          question_id: "station2_q010",
          answer_text: "Their answers may differ because context differs.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station2_q010_c",
          question_id: "station2_q010",
          answer_text: "The more confident answer is always better.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q010_d",
          question_id: "station2_q010",
          answer_text: "General AI automatically knows internal decisions.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q011",
      station_id: "station_2",
      stage: 1,
      complexity: 1,
      core_concept:
        "AI is useful pattern-based generation, not magic or human expertise",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A learner expects AI to give the same answer every time like a fixed rule. Which view is more realistic?",
          options: {
            A: "AI can vary output based on context.",
            B: "AI always follows one visible rule.",
            C: "AI has human understanding of the task.",
            D: "AI only copies search results exactly.",
          },
          correct_answer: "A",
          feedback:
            "Generative AI is not a fixed rule like traditional software. It can produce varied outputs from patterns, context and instructions.",
        },
        pt: {
          question:
            "Um aprendiz espera que a IA dê sempre a mesma resposta como regra fixa. Qual visão é mais realista?",
          options: {
            A: "A IA pode variar a saída conforme o contexto.",
            B: "A IA sempre segue uma regra visível.",
            C: "A IA tem entendimento humano da tarefa.",
            D: "A IA só copia resultados de busca exatamente.",
          },
          correct_answer: "A",
          feedback:
            "IA generativa não é regra fixa como software tradicional. Ela pode variar saídas por padrões, contexto e instruções.",
        },
        sv: {
          question:
            "En deltagare förväntar sig samma AI-svar varje gång som en fast regel. Vilken syn är mer realistisk?",
          options: {
            A: "AI kan variera svar utifrån kontext.",
            B: "AI följer alltid en synlig regel.",
            C: "AI har mänsklig förståelse av uppgiften.",
            D: "AI kopierar bara sökresultat exakt.",
          },
          correct_answer: "A",
          feedback:
            "Generativ AI är inte en fast regel som traditionell mjukvara. Den kan variera svar från mönster, kontext och instruktioner.",
        },
        nl: {
          question:
            "Een lerende verwacht telkens hetzelfde AI-antwoord zoals een vaste regel. Welke kijk is realistischer?",
          options: {
            A: "AI kan output variëren op basis van context.",
            B: "AI volgt altijd één zichtbare regel.",
            C: "AI heeft menselijk begrip van de taak.",
            D: "AI kopieert alleen zoekresultaten exact.",
          },
          correct_answer: "A",
          feedback:
            "Generatieve AI is geen vaste regel zoals traditionele software. Het kan output variëren vanuit patronen, context en instructies.",
        },
      },
      question_text:
        "A learner expects AI to give the same answer every time like a fixed rule. Which view is more realistic?",
      explanation:
        "Generative AI is not a fixed rule like traditional software. It can produce varied outputs from patterns, context and instructions.",
      answers: [
        {
          id: "station2_q011_a",
          question_id: "station2_q011",
          answer_text: "AI can vary output based on context.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station2_q011_b",
          question_id: "station2_q011",
          answer_text: "AI always follows one visible rule.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q011_c",
          question_id: "station2_q011",
          answer_text: "AI has human understanding of the task.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q011_d",
          question_id: "station2_q011",
          answer_text: "AI only copies search results exactly.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q012",
      station_id: "station_2",
      stage: 1,
      complexity: 1,
      core_concept:
        "AI is useful pattern-based generation, not magic or human expertise",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI creates a quick summary of an unfamiliar document. What is a realistic first way to use it?",
          options: {
            A: "Treat it as a certified expert view.",
            B: "Ignore it because AI is imperfect.",
            C: "Assume it proves full understanding.",
            D: "Use it to orient your reading.",
          },
          correct_answer: "D",
          feedback:
            "A summary can orient your reading without proving full understanding or accuracy. That is useful but realistic. This helps learners use AI with realistic expectations.",
        },
        pt: {
          question:
            "A IA cria um resumo rápido de um documento desconhecido. Qual é uma primeira forma realista de usá-lo?",
          options: {
            A: "Tratá-lo como visão certificada de especialista.",
            B: "Ignorá-lo porque IA é imperfeita.",
            C: "Supor que prova entendimento total.",
            D: "Usá-lo para orientar sua leitura.",
          },
          correct_answer: "D",
          feedback:
            "Um resumo pode orientar a leitura sem provar entendimento total ou precisão. Isso é útil e realista. Isso ajuda aprendizes a usar IA com expectativas realistas.",
        },
        sv: {
          question:
            "AI skapar en snabb sammanfattning av ett okänt dokument. Vad är ett realistiskt första sätt att använda den?",
          options: {
            A: "Se den som ett certifierat expertutlåtande.",
            B: "Ignorera den eftersom AI är ofullkomlig.",
            C: "Anta att den bevisar full förståelse.",
            D: "Använd den för att orientera läsningen.",
          },
          correct_answer: "D",
          feedback:
            "En sammanfattning kan orientera läsningen utan att bevisa full förståelse eller precision. Det är nyttigt och realistiskt. Det hjälper deltagare använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "AI maakt een snelle samenvatting van een onbekend document. Wat is een realistische eerste manier om die te gebruiken?",
          options: {
            A: "Behandel het als gecertificeerd expertoordeel.",
            B: "Negeer het omdat AI imperfect is.",
            C: "Neem aan dat het volledig begrip bewijst.",
            D: "Gebruik het om je lezing te oriënteren.",
          },
          correct_answer: "D",
          feedback:
            "Een samenvatting kan je lezing oriënteren zonder volledig begrip of nauwkeurigheid te bewijzen. Dat is nuttig en realistisch.",
        },
      },
      question_text:
        "AI creates a quick summary of an unfamiliar document. What is a realistic first way to use it?",
      explanation:
        "A summary can orient your reading without proving full understanding or accuracy. That is useful but realistic. This helps learners use AI with realistic expectations.",
      answers: [
        {
          id: "station2_q012_a",
          question_id: "station2_q012",
          answer_text: "Treat it as a certified expert view.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q012_b",
          question_id: "station2_q012",
          answer_text: "Ignore it because AI is imperfect.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q012_c",
          question_id: "station2_q012",
          answer_text: "Assume it proves full understanding.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q012_d",
          question_id: "station2_q012",
          answer_text: "Use it to orient your reading.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station2_q013",
      station_id: "station_2",
      stage: 1,
      complexity: 2,
      core_concept:
        "AI is useful pattern-based generation, not magic or human expertise",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI explains a technical topic in simple language. The explanation sounds smooth. Which behaviour shows overtrust?",
          options: {
            A: "Use it as an introduction to the topic.",
            B: "Ask what important nuance may be missing.",
            C: "Assume smooth wording means it is correct.",
            D: "Treat it as useful but incomplete.",
          },
          correct_answer: "C",
          feedback:
            "Smooth language can make weak content feel convincing. Check whether important nuance may be missing.",
        },
        pt: {
          question:
            "A IA explica um tema técnico em linguagem simples. A explicação soa fluida. Qual comportamento mostra confiança excessiva?",
          options: {
            A: "Usá-la como introdução ao tema.",
            B: "Perguntar que nuance importante pode faltar.",
            C: "Supor que texto fluido significa correção.",
            D: "Tratá-la como útil, mas incompleta.",
          },
          correct_answer: "C",
          feedback:
            "Linguagem fluida pode fazer conteúdo fraco parecer convincente. Veja se nuance importante pode estar ausente.",
        },
        sv: {
          question:
            "AI förklarar ett tekniskt ämne enkelt. Förklaringen låter flytande. Vilket beteende visar övertro?",
          options: {
            A: "Använd den som introduktion till ämnet.",
            B: "Fråga vilken viktig nyans som kan saknas.",
            C: "Anta att flytande text betyder korrekthet.",
            D: "Se den som nyttig men ofullständig.",
          },
          correct_answer: "C",
          feedback:
            "Smidigt språk kan få svagt innehåll att verka övertygande. Kontrollera om viktig nyans saknas.",
        },
        nl: {
          question:
            "AI legt een technisch onderwerp eenvoudig uit. De uitleg klinkt vloeiend. Welk gedrag toont oververtrouwen?",
          options: {
            A: "Gebruik het als introductie op het onderwerp.",
            B: "Vraag welke belangrijke nuance kan ontbreken.",
            C: "Neem aan dat vloeiende tekst correctheid betekent.",
            D: "Behandel het als nuttig maar onvolledig.",
          },
          correct_answer: "C",
          feedback:
            "Soepele taal kan zwakke inhoud overtuigend laten lijken. Controleer of belangrijke nuance ontbreekt.",
        },
      },
      question_text:
        "AI explains a technical topic in simple language. The explanation sounds smooth. Which behaviour shows overtrust?",
      explanation:
        "Smooth language can make weak content feel convincing. Check whether important nuance may be missing.",
      answers: [
        {
          id: "station2_q013_a",
          question_id: "station2_q013",
          answer_text: "Use it as an introduction to the topic.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q013_b",
          question_id: "station2_q013",
          answer_text: "Ask what important nuance may be missing.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q013_c",
          question_id: "station2_q013",
          answer_text: "Assume smooth wording means it is correct.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station2_q013_d",
          question_id: "station2_q013",
          answer_text: "Treat it as useful but incomplete.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q014",
      station_id: "station_2",
      stage: 1,
      complexity: 2,
      core_concept:
        "AI is useful pattern-based generation, not magic or human expertise",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A teammate says an AI answer must come from one exact webpage. What expectation is more realistic at work?",
          options: {
            A: "AI and search always work identically.",
            B: "AI may generate wording from patterns and context.",
            C: "AI has human memory of every webpage.",
            D: "AI never uses any available information.",
          },
          correct_answer: "B",
          feedback:
            "Generative AI is not simply a list of webpages. Some tools use search, but generated wording can come from patterns and context.",
        },
        pt: {
          question:
            "Um colega diz que uma resposta da IA deve vir de uma página exata. Qual expectativa é mais realista?",
          options: {
            A: "IA e busca sempre funcionam igual.",
            B: "A IA pode gerar texto por padrões e contexto.",
            C: "A IA tem memória humana de toda página.",
            D: "A IA nunca usa nenhuma informação disponível.",
          },
          correct_answer: "B",
          feedback:
            "IA generativa não é só lista de páginas. Algumas ferramentas usam busca, mas o texto pode vir de padrões e contexto.",
        },
        sv: {
          question:
            "En kollega säger att ett AI-svar måste komma från en exakt webbsida. Vilken förväntan är mer realistisk på jobbet?",
          options: {
            A: "AI och sökning fungerar alltid likadant.",
            B: "AI kan skapa formulering från mönster och kontext.",
            C: "AI har mänskligt minne av varje webbsida.",
            D: "AI använder aldrig någon tillgänglig information.",
          },
          correct_answer: "B",
          feedback:
            "Generativ AI är inte bara en lista webbsidor. Vissa verktyg söker, men formulering kan komma från mönster och kontext.",
        },
        nl: {
          question:
            "Een teamgenoot zegt dat een AI-antwoord van één exacte webpagina moet komen. Welke verwachting is realistischer op het werk?",
          options: {
            A: "AI en zoeken werken altijd hetzelfde.",
            B: "AI kan formulering maken uit patronen en context.",
            C: "AI heeft menselijk geheugen van elke webpagina.",
            D: "AI gebruikt nooit beschikbare informatie.",
          },
          correct_answer: "B",
          feedback:
            "Generatieve AI is niet simpelweg een lijst webpagina’s. Sommige tools zoeken, maar formulering kan uit patronen en context komen.",
        },
      },
      question_text:
        "A teammate says an AI answer must come from one exact webpage. What expectation is more realistic at work?",
      explanation:
        "Generative AI is not simply a list of webpages. Some tools use search, but generated wording can come from patterns and context.",
      answers: [
        {
          id: "station2_q014_a",
          question_id: "station2_q014",
          answer_text: "AI and search always work identically.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q014_b",
          question_id: "station2_q014",
          answer_text: "AI may generate wording from patterns and context.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station2_q014_c",
          question_id: "station2_q014",
          answer_text: "AI has human memory of every webpage.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q014_d",
          question_id: "station2_q014",
          answer_text: "AI never uses any available information.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q015",
      station_id: "station_2",
      stage: 2,
      complexity: 2,
      core_concept:
        "AI output can be a useful draft or starting point when context matters",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI answers a workplace request with a generic suggestion. The user gave only one sentence. What is the best interpretation?",
          options: {
            A: "The thin context likely shaped the output.",
            B: "AI is useless for all work requests.",
            C: "AI should know the full workplace automatically.",
            D: "Polished wording proves the answer understands the request.",
          },
          correct_answer: "A",
          feedback:
            "AI often reflects the context it receives. A generic output can mean the input was too thin, not that AI is useless.",
        },
        pt: {
          question:
            "A IA responde a um pedido de trabalho com sugestão genérica. O usuário deu só uma frase. Qual é a melhor interpretação?",
          options: {
            A: "O contexto fraco provavelmente moldou a saída.",
            B: "A IA é inútil para todo pedido de trabalho.",
            C: "A IA deveria conhecer todo o trabalho automaticamente.",
            D: "Texto polido prova que a resposta entende o pedido.",
          },
          correct_answer: "A",
          feedback:
            "A IA costuma refletir o contexto recebido. Saída genérica pode indicar entrada fraca, não inutilidade. Isso ajuda aprendizes a usar IA com expectativas realistas.",
        },
        sv: {
          question:
            "AI svarar på en arbetsfråga med ett generiskt förslag. Användaren gav bara en mening. Vad är bästa tolkningen?",
          options: {
            A: "Den tunna kontexten formade troligen svaret.",
            B: "AI är värdelös för alla arbetsfrågor.",
            C: "AI borde automatiskt känna hela arbetsplatsen.",
            D: "Polerad text bevisar att svaret förstår frågan.",
          },
          correct_answer: "A",
          feedback:
            "AI speglar ofta kontexten den får. Ett generiskt svar kan betyda tunn input, inte att AI är värdelös.",
        },
        nl: {
          question:
            "AI beantwoordt een werkverzoek met een generieke suggestie. De gebruiker gaf maar één zin. Wat is de beste interpretatie?",
          options: {
            A: "De dunne context vormde waarschijnlijk de output.",
            B: "AI is nutteloos voor alle werkverzoeken.",
            C: "AI zou automatisch de hele werkplek moeten kennen.",
            D: "Verzorgde tekst bewijst dat het antwoord het verzoek begrijpt.",
          },
          correct_answer: "A",
          feedback:
            "AI weerspiegelt vaak de context die het krijgt. Generieke output kan dunne input betekenen, niet nutteloosheid. Dit helpt lerenden AI met realistische verwachtingen te gebruiken.",
        },
      },
      question_text:
        "AI answers a workplace request with a generic suggestion. The user gave only one sentence. What is the best interpretation?",
      explanation:
        "AI often reflects the context it receives. A generic output can mean the input was too thin, not that AI is useless.",
      answers: [
        {
          id: "station2_q015_a",
          question_id: "station2_q015",
          answer_text: "The thin context likely shaped the output.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station2_q015_b",
          question_id: "station2_q015",
          answer_text: "AI is useless for all work requests.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q015_c",
          question_id: "station2_q015",
          answer_text: "AI should know the full workplace automatically.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q015_d",
          question_id: "station2_q015",
          answer_text:
            "Polished wording proves the answer understands the request.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q016",
      station_id: "station_2",
      stage: 2,
      complexity: 2,
      core_concept:
        "AI output can be a useful draft or starting point when context matters",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI suggests discussion points for a team meeting. The list is useful and balanced. What remains outside AI’s responsibility?",
          options: {
            A: "Making a list easier to scan.",
            B: "Suggesting possible discussion categories for the team.",
            C: "Grouping similar points before the meeting.",
            D: "Owning what the team decides.",
          },
          correct_answer: "D",
          feedback:
            "AI can prepare useful material, but it does not own meeting decisions. Accountability remains with people. This helps learners use AI with realistic expectations.",
        },
        pt: {
          question:
            "A IA sugere pontos de discussão para uma reunião da equipe. A lista é útil e equilibrada. O que fica fora da responsabilidade da IA?",
          options: {
            A: "Tornar uma lista mais fácil de ler.",
            B: "Sugerir possíveis categorias de discussão.",
            C: "Agrupar pontos parecidos antes da reunião.",
            D: "Assumir o que a equipe decide.",
          },
          correct_answer: "D",
          feedback:
            "A IA prepara material útil, mas não assume decisões da reunião. A responsabilidade continua com pessoas. Isso ajuda aprendizes a usar IA com expectativas realistas.",
        },
        sv: {
          question:
            "AI föreslår diskussionspunkter för ett teammöte. Listan är nyttig och balanserad. Vad ligger utanför AI:s ansvar?",
          options: {
            A: "Göra listan lättare att överblicka.",
            B: "Föreslå möjliga diskussionskategorier för teamet.",
            C: "Gruppera liknande punkter före mötet.",
            D: "Äga vad teamet beslutar efter mötet.",
          },
          correct_answer: "D",
          feedback:
            "AI kan förbereda användbart material, men äger inte mötesbeslut. Ansvar ligger hos människor. Det hjälper deltagare använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "AI stelt gesprekspunten voor een teammeeting voor. De lijst is nuttig en gebalanceerd. Wat valt buiten AI-verantwoordelijkheid?",
          options: {
            A: "Een lijst makkelijker scanbaar maken.",
            B: "Mogelijke gesprekscategorieën voor het team voorstellen.",
            C: "Vergelijkbare punten vóór de meeting groeperen.",
            D: "Eigenaarschap nemen over wat het team beslist.",
          },
          correct_answer: "D",
          feedback:
            "AI kan nuttig materiaal voorbereiden, maar bezit geen meetingbesluiten. Accountability blijft bij mensen. Dit helpt lerenden AI met realistische verwachtingen te gebruiken.",
        },
      },
      question_text:
        "AI suggests discussion points for a team meeting. The list is useful and balanced. What remains outside AI’s responsibility?",
      explanation:
        "AI can prepare useful material, but it does not own meeting decisions. Accountability remains with people. This helps learners use AI with realistic expectations.",
      answers: [
        {
          id: "station2_q016_a",
          question_id: "station2_q016",
          answer_text: "Making a list easier to scan.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q016_b",
          question_id: "station2_q016",
          answer_text:
            "Suggesting possible discussion categories for the team.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q016_c",
          question_id: "station2_q016",
          answer_text: "Grouping similar points before the meeting.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q016_d",
          question_id: "station2_q016",
          answer_text: "Owning what the team decides.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station2_q017",
      station_id: "station_2",
      stage: 2,
      complexity: 3,
      core_concept:
        "AI output can be a useful draft or starting point when context matters",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI rewrites a complex update so it sounds confident and clear. Which behaviour shows overtrust?",
          options: {
            A: "Use it as a clearer draft.",
            B: "Assume confidence means the content is accurate.",
            C: "Notice that style and truth differ.",
            D: "Treat it as useful but imperfect.",
          },
          correct_answer: "B",
          feedback:
            "Clear confident language can improve communication, but it does not prove accuracy. Style and truth are different checks.",
        },
        pt: {
          question:
            "A IA reescreve um update complexo para soar confiante e claro. Qual comportamento mostra confiança excessiva?",
          options: {
            A: "Usá-lo como rascunho mais claro.",
            B: "Supor que confiança significa conteúdo preciso.",
            C: "Notar que estilo e verdade diferem.",
            D: "Tratá-lo como útil, mas imperfeito.",
          },
          correct_answer: "B",
          feedback:
            "Linguagem clara e confiante pode melhorar comunicação, mas não prova precisão. Estilo e verdade são checagens diferentes.",
        },
        sv: {
          question:
            "AI skriver om en komplex uppdatering så den låter säker och tydlig. Vilket beteende visar övertro?",
          options: {
            A: "Använd den som tydligare utkast.",
            B: "Anta att säker ton betyder korrekt innehåll.",
            C: "Notera att stil och sanning skiljer sig.",
            D: "Se den som nyttig men ofullkomlig.",
          },
          correct_answer: "B",
          feedback:
            "Tydligt och säkert språk kan förbättra kommunikation, men bevisar inte korrekthet. Stil och sanning är olika kontroller.",
        },
        nl: {
          question:
            "AI herschrijft een complexe update zodat die zelfverzekerd en duidelijk klinkt. Welk gedrag toont oververtrouwen?",
          options: {
            A: "Gebruik het als duidelijker concept.",
            B: "Neem aan dat zekerheid betekent dat inhoud klopt.",
            C: "Merk op dat stijl en waarheid verschillen.",
            D: "Behandel het als nuttig maar imperfect.",
          },
          correct_answer: "B",
          feedback:
            "Heldere, zekere taal kan communicatie verbeteren, maar bewijst geen juistheid. Stijl en waarheid zijn aparte controles.",
        },
      },
      question_text:
        "AI rewrites a complex update so it sounds confident and clear. Which behaviour shows overtrust?",
      explanation:
        "Clear confident language can improve communication, but it does not prove accuracy. Style and truth are different checks.",
      answers: [
        {
          id: "station2_q017_a",
          question_id: "station2_q017",
          answer_text: "Use it as a clearer draft.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q017_b",
          question_id: "station2_q017",
          answer_text: "Assume confidence means the content is accurate.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station2_q017_c",
          question_id: "station2_q017",
          answer_text: "Notice that style and truth differ.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q017_d",
          question_id: "station2_q017",
          answer_text: "Treat it as useful but imperfect.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q018",
      station_id: "station_2",
      stage: 3,
      complexity: 3,
      core_concept:
        "Fluent AI output can support reasoning but does not own truth or accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI compares two options and gives a balanced recommendation. You have incomplete context. How should you treat this output?",
          options: {
            A: "As a final decision you can own less.",
            B: "As useless because context is incomplete.",
            C: "As useful reasoning support, not a conclusion.",
            D: "As proof AI understands the business problem.",
          },
          correct_answer: "C",
          feedback:
            "AI can support reasoning even with imperfect context. It should not own the conclusion or reduce human accountability.",
        },
        pt: {
          question:
            "A IA compara duas opções e dá recomendação equilibrada. Você tem contexto incompleto. Como tratar essa saída?",
          options: {
            A: "Como decisão final com menos responsabilidade sua.",
            B: "Como inútil porque o contexto está incompleto.",
            C: "Como apoio útil de raciocínio, não conclusão.",
            D: "Como prova de que a IA entende o problema.",
          },
          correct_answer: "C",
          feedback:
            "A IA pode apoiar raciocínio mesmo com contexto imperfeito. Ela não deve assumir conclusão nem reduzir responsabilidade humana.",
        },
        sv: {
          question:
            "AI jämför två alternativ och ger en balanserad rekommendation. Du har ofullständig kontext. Hur bör du behandla svaret?",
          options: {
            A: "Som ett slutbeslut med mindre eget ansvar.",
            B: "Som värdelöst eftersom kontexten är ofullständig.",
            C: "Som nyttigt resonemangsstöd, inte slutsats.",
            D: "Som bevis att AI förstår affärsproblemet.",
          },
          correct_answer: "C",
          feedback:
            "AI kan stödja resonemang även med ofullständig kontext. Den ska inte äga slutsats eller minska mänskligt ansvar. Det hjälper deltagare använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "AI vergelijkt twee opties en geeft een gebalanceerde aanbeveling. Je hebt onvolledige context. Hoe behandel je deze output?",
          options: {
            A: "Als eindbesluit met minder eigen verantwoordelijkheid.",
            B: "Als nutteloos omdat context onvolledig is.",
            C: "Als nuttige redeneersteun, niet als conclusie.",
            D: "Als bewijs dat AI het bedrijfsprobleem begrijpt.",
          },
          correct_answer: "C",
          feedback:
            "AI kan redeneren ondersteunen met onvolledige context. Het bezit de conclusie niet en vermindert menselijke verantwoordelijkheid niet. Dit helpt lerenden AI met realistische verwachtingen te gebruiken.",
        },
      },
      question_text:
        "AI compares two options and gives a balanced recommendation. You have incomplete context. How should you treat this output?",
      explanation:
        "AI can support reasoning even with imperfect context. It should not own the conclusion or reduce human accountability.",
      answers: [
        {
          id: "station2_q018_a",
          question_id: "station2_q018",
          answer_text: "As a final decision you can own less.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q018_b",
          question_id: "station2_q018",
          answer_text: "As useless because context is incomplete.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q018_c",
          question_id: "station2_q018",
          answer_text: "As useful reasoning support, not a conclusion.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station2_q018_d",
          question_id: "station2_q018",
          answer_text: "As proof AI understands the business problem.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q019",
      station_id: "station_2",
      stage: 3,
      complexity: 4,
      core_concept:
        "Fluent AI output can support reasoning but does not own truth or accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A general AI tool summarises a situation involving internal decisions it cannot access. What is the main risk?",
          options: {
            A: "The summary may use too many bullets.",
            B: "The wording may sound too neutral.",
            C: "The answer may be shorter than expected.",
            D: "It may sound informed while missing internal context.",
          },
          correct_answer: "D",
          feedback:
            "General AI may produce fluent wording without access to internal context. That makes the answer useful-looking but limited.",
        },
        pt: {
          question:
            "Uma ferramenta geral de IA resume uma situação com decisões internas que não consegue acessar. Qual é o risco principal?",
          options: {
            A: "O resumo pode usar bullets demais.",
            B: "O texto pode soar neutro demais.",
            C: "A resposta pode ser mais curta que esperado.",
            D: "Pode soar informado sem contexto interno.",
          },
          correct_answer: "D",
          feedback:
            "IA geral pode produzir texto fluente sem acesso a contexto interno. A resposta parece útil, mas é limitada.",
        },
        sv: {
          question:
            "Ett generellt AI-verktyg sammanfattar en situation med interna beslut det inte når. Vilken är huvudrisken?",
          options: {
            A: "Sammanfattningen kan ha för många punkter.",
            B: "Formuleringen kan låta för neutral.",
            C: "Svaret kan vara kortare än väntat.",
            D: "Det kan låta insatt men sakna intern kontext.",
          },
          correct_answer: "D",
          feedback:
            "Generell AI kan skapa flytande text utan intern kontext. Svaret kan verka nyttigt men vara begränsat. Det hjälper deltagare använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "Een algemene AI-tool vat een situatie samen met interne besluiten waar het geen toegang toe heeft. Wat is het grootste risico?",
          options: {
            A: "De samenvatting kan te veel bullets gebruiken.",
            B: "De formulering kan te neutraal klinken.",
            C: "Het antwoord kan korter zijn dan verwacht.",
            D: "Het kan geïnformeerd klinken zonder interne context.",
          },
          correct_answer: "D",
          feedback:
            "Algemene AI kan vloeiende tekst maken zonder interne context. Het antwoord lijkt bruikbaar maar is beperkt. Dit helpt lerenden AI met realistische verwachtingen te gebruiken.",
        },
      },
      question_text:
        "A general AI tool summarises a situation involving internal decisions it cannot access. What is the main risk?",
      explanation:
        "General AI may produce fluent wording without access to internal context. That makes the answer useful-looking but limited.",
      answers: [
        {
          id: "station2_q019_a",
          question_id: "station2_q019",
          answer_text: "The summary may use too many bullets.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q019_b",
          question_id: "station2_q019",
          answer_text: "The wording may sound too neutral.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q019_c",
          question_id: "station2_q019",
          answer_text: "The answer may be shorter than expected.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q019_d",
          question_id: "station2_q019",
          answer_text: "It may sound informed while missing internal context.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station2_q020",
      station_id: "station_2",
      stage: 3,
      complexity: 4,
      core_concept:
        "Fluent AI output can support reasoning but does not own truth or accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI challenges assumptions in a risk review and finds weak logic. The team likes the critique. What expectation is realistic?",
          options: {
            A: "AI can support reasoning, not own conclusions.",
            B: "AI has become accountable for the risk decision.",
            C: "Any critique from AI is automatically correct.",
            D: "Humans no longer need to judge assumptions.",
          },
          correct_answer: "A",
          feedback:
            "AI can expose weak reasoning, which is useful. Responsibility for conclusions and action still stays with people. This helps learners use AI with realistic expectations.",
        },
        pt: {
          question:
            "A IA desafia premissas em uma revisão de riscos e encontra lógica fraca. A equipe gosta da crítica. Qual expectativa é realista?",
          options: {
            A: "A IA apoia raciocínio, não assume conclusões.",
            B: "A IA virou responsável pela decisão de risco.",
            C: "Toda crítica da IA é automaticamente correta.",
            D: "Humanos não precisam mais julgar premissas.",
          },
          correct_answer: "A",
          feedback:
            "A IA pode expor raciocínio fraco, o que é útil. A responsabilidade por conclusões e ações continua humana.",
        },
        sv: {
          question:
            "AI utmanar antaganden i en riskgenomgång och hittar svag logik. Teamet gillar kritiken. Vilken förväntan är realistisk?",
          options: {
            A: "AI kan stödja resonemang, inte äga slutsatser.",
            B: "AI har blivit ansvarig för riskbeslutet.",
            C: "All AI-kritik är automatiskt korrekt.",
            D: "Människor behöver inte längre bedöma antaganden.",
          },
          correct_answer: "A",
          feedback:
            "AI kan visa svagt resonemang, vilket är nyttigt. Ansvar för slutsatser och åtgärder ligger kvar hos människor. Det hjälper deltagare använda AI med realistiska förväntningar.",
        },
        nl: {
          question:
            "AI daagt aannames uit in een risicoreview en vindt zwakke logica. Het team waardeert de kritiek. Welke verwachting is realistisch?",
          options: {
            A: "AI kan redeneren ondersteunen, niet conclusies bezitten.",
            B: "AI is verantwoordelijk geworden voor het risicobesluit.",
            C: "Elke AI-kritiek is automatisch correct.",
            D: "Mensen hoeven aannames niet meer te beoordelen.",
          },
          correct_answer: "A",
          feedback:
            "AI kan zwakke redenering blootleggen, wat nuttig is. Verantwoordelijkheid voor conclusies en actie blijft menselijk. Dit helpt lerenden AI met realistische verwachtingen te gebruiken.",
        },
      },
      question_text:
        "AI challenges assumptions in a risk review and finds weak logic. The team likes the critique. What expectation is realistic?",
      explanation:
        "AI can expose weak reasoning, which is useful. Responsibility for conclusions and action still stays with people. This helps learners use AI with realistic expectations.",
      answers: [
        {
          id: "station2_q020_a",
          question_id: "station2_q020",
          answer_text: "AI can support reasoning, not own conclusions.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station2_q020_b",
          question_id: "station2_q020",
          answer_text: "AI has become accountable for the risk decision.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q020_c",
          question_id: "station2_q020",
          answer_text: "Any critique from AI is automatically correct.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q020_d",
          question_id: "station2_q020",
          answer_text: "Humans no longer need to judge assumptions.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q021",
      station_id: "station_2",
      stage: 1,
      complexity: 1,
      core_concept: "AI is useful technology, not magic or human intelligence",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI summarises a long email thread in seconds. Which view shows the best basic understanding?",
          options: {
            A: "AI must understand the team like a person.",
            B: "AI generated useful text from patterns and context.",
            C: "AI only stored the email in shorter form.",
            D: "AI proved the answer is fully true.",
          },
          correct_answer: "B",
          feedback:
            "AI can create useful summaries from context, but that does not mean it understands the team like a human.",
        },
        pt: {
          question:
            "A IA resume uma longa troca de emails em segundos. Qual visão mostra o melhor entendimento básico?",
          options: {
            A: "A IA entende a equipe como uma pessoa.",
            B: "A IA gerou texto útil com padrões e contexto.",
            C: "A IA só guardou o email em forma curta.",
            D: "A IA provou que a resposta é totalmente verdadeira.",
          },
          correct_answer: "B",
          feedback:
            "A IA pode criar resumos úteis a partir do contexto, mas isso não significa entendimento humano da equipe.",
        },
        sv: {
          question:
            "AI sammanfattar en lång mejltråd på några sekunder. Vilken syn visar bäst grundförståelse?",
          options: {
            A: "AI förstår teamet som en person.",
            B: "AI skapade användbar text från mönster och kontext.",
            C: "AI lagrade bara mejlet i kortare form.",
            D: "AI bevisade att svaret är helt sant.",
          },
          correct_answer: "B",
          feedback:
            "AI kan skapa användbara sammanfattningar från kontext, men det betyder inte mänsklig förståelse av teamet.",
        },
        nl: {
          question:
            "AI vat een lange mailthread in seconden samen. Welke kijk toont het beste basisbegrip?",
          options: {
            A: "AI begrijpt het team als een persoon.",
            B: "AI maakte bruikbare tekst uit patronen en context.",
            C: "AI sloeg de mail alleen korter op.",
            D: "AI bewees dat het antwoord volledig waar is.",
          },
          correct_answer: "B",
          feedback:
            "AI kan nuttige samenvattingen maken uit context, maar dat betekent geen menselijk begrip van het team.",
        },
      },
      question_text:
        "AI summarises a long email thread in seconds. Which view shows the best basic understanding?",
      explanation:
        "AI can create useful summaries from context, but that does not mean it understands the team like a human.",
      answers: [
        {
          id: "station2_q021_a",
          question_id: "station2_q021",
          answer_text: "AI must understand the team like a person.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q021_b",
          question_id: "station2_q021",
          answer_text: "AI generated useful text from patterns and context.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station2_q021_c",
          question_id: "station2_q021",
          answer_text: "AI only stored the email in shorter form.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q021_d",
          question_id: "station2_q021",
          answer_text: "AI proved the answer is fully true.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q022",
      station_id: "station_2",
      stage: 1,
      complexity: 2,
      core_concept:
        "Generative AI behaves differently from deterministic rule-based software",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI extracts action items from meeting notes. A spreadsheet formula would always follow fixed rules. What expectation is realistic?",
          options: {
            A: "AI should decide the final owners automatically.",
            B: "AI must return identical wording every time.",
            C: "AI may vary when context or wording changes.",
            D: "AI works best with no instructions.",
          },
          correct_answer: "C",
          feedback:
            "Generative AI is not a fixed formula. Its output can change when context, wording or instructions change.",
        },
        pt: {
          question:
            "A IA extrai ações de notas de reunião. Uma fórmula seguiria regras fixas. Qual expectativa é realista?",
          options: {
            A: "A IA deve decidir os responsáveis finais sozinha.",
            B: "A IA deve retornar texto idêntico sempre.",
            C: "A IA pode variar com contexto ou formulação.",
            D: "A IA funciona melhor sem instruções.",
          },
          correct_answer: "C",
          feedback:
            "IA generativa não é uma fórmula fixa. A saída pode mudar quando contexto, texto ou instruções mudam.",
        },
        sv: {
          question:
            "AI tar ut åtgärder ur mötesanteckningar. En formel följer fasta regler. Vilken förväntan är realistisk?",
          options: {
            A: "AI ska automatiskt bestämma slutliga ägare.",
            B: "AI måste ge identisk text varje gång.",
            C: "AI kan variera med kontext eller formulering.",
            D: "AI fungerar bäst utan instruktioner.",
          },
          correct_answer: "C",
          feedback:
            "Generativ AI är inte en fast formel. Svaret kan ändras när kontext, formulering eller instruktioner ändras.",
        },
        nl: {
          question:
            "AI haalt acties uit vergadernotities. Een formule volgt vaste regels. Welke verwachting is realistisch?",
          options: {
            A: "AI moet de uiteindelijke eigenaars automatisch kiezen.",
            B: "AI moet elke keer identieke tekst geven.",
            C: "AI kan variëren door context of formulering.",
            D: "AI werkt het best zonder instructies.",
          },
          correct_answer: "C",
          feedback:
            "Generatieve AI is geen vaste formule. De output kan veranderen door context, formulering of instructies.",
        },
      },
      question_text:
        "AI extracts action items from meeting notes. A spreadsheet formula would always follow fixed rules. What expectation is realistic?",
      explanation:
        "Generative AI is not a fixed formula. Its output can change when context, wording or instructions change.",
      answers: [
        {
          id: "station2_q022_a",
          question_id: "station2_q022",
          answer_text: "AI should decide the final owners automatically.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q022_b",
          question_id: "station2_q022",
          answer_text: "AI must return identical wording every time.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q022_c",
          question_id: "station2_q022",
          answer_text: "AI may vary when context or wording changes.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station2_q022_d",
          question_id: "station2_q022",
          answer_text: "AI works best with no instructions.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q023",
      station_id: "station_2",
      stage: 2,
      complexity: 2,
      core_concept:
        "AI output is often a useful starting point, not final truth",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI turns messy project notes into a clear status update. How should you treat the result?",
          options: {
            A: "As a useful draft to confirm.",
            B: "As the final project record.",
            C: "Too weak to use for any status work.",
            D: "As reliable because it is well organised.",
          },
          correct_answer: "A",
          feedback:
            "A clear structure can help, but the project facts still need human confirmation before the update is used.",
        },
        pt: {
          question:
            "A IA transforma notas confusas de projeto em status claro. Como você deve tratar o resultado?",
          options: {
            A: "Como um rascunho útil para confirmar.",
            B: "Como o registro final do projeto.",
            C: "Fraco demais para qualquer status de trabalho.",
            D: "Como confiável porque está bem organizado.",
          },
          correct_answer: "A",
          feedback:
            "Uma estrutura clara ajuda, mas os fatos do projeto ainda precisam de confirmação humana antes do uso.",
        },
        sv: {
          question:
            "AI gör röriga projektnoter till en tydlig status. Hur bör du behandla resultatet?",
          options: {
            A: "Som ett användbart utkast att bekräfta.",
            B: "Som projektets slutliga dokumentation i arbetet.",
            C: "För svagt för något statusarbete.",
            D: "Som pålitligt eftersom det är välordnat.",
          },
          correct_answer: "A",
          feedback:
            "En tydlig struktur kan hjälpa, men projektfakta behöver fortfarande mänsklig bekräftelse innan statusen används.",
        },
        nl: {
          question:
            "AI maakt van rommelige projectnotities een duidelijke statusupdate. Hoe behandel je het resultaat?",
          options: {
            A: "Als een nuttig concept om te bevestigen.",
            B: "Als het definitieve projectverslag voor werk.",
            C: "Te zwak voor enig statuswerk.",
            D: "Als betrouwbaar omdat het goed geordend is.",
          },
          correct_answer: "A",
          feedback:
            "Een duidelijke structuur helpt, maar de projectfeiten hebben nog menselijke bevestiging nodig vóór gebruik.",
        },
      },
      question_text:
        "AI turns messy project notes into a clear status update. How should you treat the result?",
      explanation:
        "A clear structure can help, but the project facts still need human confirmation before the update is used.",
      answers: [
        {
          id: "station2_q023_a",
          question_id: "station2_q023",
          answer_text: "As a useful draft to confirm.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station2_q023_b",
          question_id: "station2_q023",
          answer_text: "As the final project record.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q023_c",
          question_id: "station2_q023",
          answer_text: "Too weak to use for any status work.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q023_d",
          question_id: "station2_q023",
          answer_text: "As reliable because it is well organised.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q024",
      station_id: "station_2",
      stage: 2,
      complexity: 2,
      core_concept:
        "AI is different from search, although tools may combine both",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A tool answers a question and also shows search results. Which view shows the best understanding?",
          options: {
            A: "AI and search are always the same thing.",
            B: "AI remembers every past decision like a person.",
            C: "Generated text is automatically a source.",
            D: "Some tools combine generation with search.",
          },
          correct_answer: "D",
          feedback:
            "Some products combine AI and search. Generated text itself is still not the same as a source record.",
        },
        pt: {
          question:
            "Uma ferramenta responde a uma pergunta e também mostra resultados de busca. Qual visão mostra melhor entendimento?",
          options: {
            A: "IA e busca são sempre a mesma coisa.",
            B: "A IA lembra toda decisão como uma pessoa.",
            C: "Texto gerado é automaticamente uma fonte.",
            D: "Algumas ferramentas combinam geração e busca.",
          },
          correct_answer: "D",
          feedback:
            "Alguns produtos combinam IA e busca. O texto gerado ainda não é o mesmo que uma fonte.",
        },
        sv: {
          question:
            "Ett verktyg svarar på en fråga och visar även sökresultat. Vilken syn visar bäst förståelse?",
          options: {
            A: "AI och sökning är alltid samma sak.",
            B: "AI minns varje beslut som en person.",
            C: "Genererad text är automatiskt en källa.",
            D: "Vissa verktyg kombinerar generering och sökning.",
          },
          correct_answer: "D",
          feedback:
            "Vissa produkter kombinerar AI och sökning. Genererad text är ändå inte samma sak som en källpost.",
        },
        nl: {
          question:
            "Een tool beantwoordt een vraag en toont ook zoekresultaten. Welke kijk toont het beste begrip?",
          options: {
            A: "AI en zoeken zijn altijd hetzelfde.",
            B: "AI onthoudt elke beslissing als een persoon.",
            C: "Gegenereerde tekst is automatisch een bron.",
            D: "Sommige tools combineren generatie met zoeken.",
          },
          correct_answer: "D",
          feedback:
            "Sommige producten combineren AI en zoeken. Gegenereerde tekst is nog steeds niet hetzelfde als een bronrecord.",
        },
      },
      question_text:
        "A tool answers a question and also shows search results. Which view shows the best understanding?",
      explanation:
        "Some products combine AI and search. Generated text itself is still not the same as a source record.",
      answers: [
        {
          id: "station2_q024_a",
          question_id: "station2_q024",
          answer_text: "AI and search are always the same thing.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q024_b",
          question_id: "station2_q024",
          answer_text: "AI remembers every past decision like a person.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q024_c",
          question_id: "station2_q024",
          answer_text: "Generated text is automatically a source.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q024_d",
          question_id: "station2_q024",
          answer_text: "Some tools combine generation with search.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station2_q025",
      station_id: "station_2",
      stage: 2,
      complexity: 3,
      core_concept:
        "Fluent AI output is not proof of accuracy or understanding",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI rewrites a stakeholder message so it sounds calm and confident. Which behaviour shows overtrust?",
          options: {
            A: "Use the wording, while checking the facts separately.",
            B: "Pause because the message changes important claims.",
            C: "Treat the polished tone as proof the claims are correct.",
            D: "Adapt the message to the audience.",
          },
          correct_answer: "C",
          feedback:
            "Confident wording can improve tone, but it does not prove the underlying claims are accurate.",
        },
        pt: {
          question:
            "A IA reescreve uma mensagem para stakeholders com tom calmo e confiante. Qual comportamento mostra confiança excessiva?",
          options: {
            A: "Usar o texto, checando fatos separadamente.",
            B: "Pausar porque a mensagem muda afirmações importantes.",
            C: "Tratar o tom calmo como prova de que as afirmações estão corretas.",
            D: "Adaptar a mensagem ao público.",
          },
          correct_answer: "C",
          feedback:
            "Texto confiante pode melhorar o tom, mas não prova que as afirmações por trás estão corretas.",
        },
        sv: {
          question:
            "AI skriver om ett intressentmeddelande så det låter lugnt och säkert. Vilket beteende visar övertro?",
          options: {
            A: "Använda texten, men kontrollera fakta separat.",
            B: "Pausa eftersom meddelandet ändrar viktiga påståenden.",
            C: "Behandla säker ton som bevis på att påståendena stämmer.",
            D: "Anpassa meddelandet till rätt publik.",
          },
          correct_answer: "C",
          feedback:
            "Säker formulering kan förbättra tonen, men den bevisar inte att påståendena är korrekta.",
        },
        nl: {
          question:
            "AI herschrijft een stakeholderbericht zodat het rustig en zeker klinkt. Welk gedrag toont oververtrouwen?",
          options: {
            A: "De tekst gebruiken, maar feiten apart controleren.",
            B: "Pauzeren omdat het bericht belangrijke claims wijzigt.",
            C: "Rustige toon zien als bewijs dat claims kloppen.",
            D: "Het bericht aanpassen aan het publiek.",
          },
          correct_answer: "C",
          feedback:
            "Zekere formulering kan de toon verbeteren, maar bewijst niet dat de onderliggende claims kloppen.",
        },
      },
      question_text:
        "AI rewrites a stakeholder message so it sounds calm and confident. Which behaviour shows overtrust?",
      explanation:
        "Confident wording can improve tone, but it does not prove the underlying claims are accurate.",
      answers: [
        {
          id: "station2_q025_a",
          question_id: "station2_q025",
          answer_text: "Use the wording, while checking the facts separately.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q025_b",
          question_id: "station2_q025",
          answer_text: "Pause because the message changes important claims.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q025_c",
          question_id: "station2_q025",
          answer_text: "Treat the polished tone as proof the claims are correct.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station2_q025_d",
          question_id: "station2_q025",
          answer_text: "Adapt the message to the audience.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q026",
      station_id: "station_2",
      stage: 2,
      complexity: 3,
      core_concept: "AI can support reasoning, but humans remain accountable",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI lists risks for a small change and ranks them clearly. What remains outside AI’s responsibility?",
          options: {
            A: "Finding possible risks in the notes.",
            B: "Owning the final risk judgment.",
            C: "Organising risks by likely impact.",
            D: "Formatting the ranked list clearly.",
          },
          correct_answer: "B",
          feedback:
            "AI can support risk thinking, but people remain accountable for the final judgment and chosen actions.",
        },
        pt: {
          question:
            "A IA lista riscos de uma pequena mudança e os ranqueia claramente. O que fica fora da responsabilidade da IA?",
          options: {
            A: "Encontrar possíveis riscos nas notas.",
            B: "Assumir o julgamento final do risco.",
            C: "Organizar riscos por impacto provável.",
            D: "Formatar claramente a lista ranqueada.",
          },
          correct_answer: "B",
          feedback:
            "A IA pode apoiar o raciocínio sobre riscos, mas pessoas continuam responsáveis pelo julgamento final e ações escolhidas.",
        },
        sv: {
          question:
            "AI listar risker för en liten ändring och rangordnar dem tydligt. Vad ligger utanför AI:s ansvar?",
          options: {
            A: "Hitta möjliga risker i anteckningarna.",
            B: "Äga den slutliga riskbedömningen efter genomgången.",
            C: "Ordna risker efter sannolik påverkan.",
            D: "Formatera den rangordnade listan tydligt.",
          },
          correct_answer: "B",
          feedback:
            "AI kan stödja risktänkande, men människor är fortsatt ansvariga för slutlig bedömning och valda åtgärder.",
        },
        nl: {
          question:
            "AI noemt risico’s voor een kleine wijziging en rangschikt ze duidelijk. Wat valt buiten AI’s verantwoordelijkheid?",
          options: {
            A: "Mogelijke risico’s in de notities vinden.",
            B: "Het uiteindelijke risico-oordeel na bespreking dragen.",
            C: "Risico’s ordenen op waarschijnlijke impact.",
            D: "De gerangschikte lijst duidelijk opmaken.",
          },
          correct_answer: "B",
          feedback:
            "AI kan risicodenken ondersteunen, maar mensen blijven verantwoordelijk voor het eindoordeel en gekozen acties.",
        },
      },
      question_text:
        "AI lists risks for a small change and ranks them clearly. What remains outside AI’s responsibility?",
      explanation:
        "AI can support risk thinking, but people remain accountable for the final judgment and chosen actions.",
      answers: [
        {
          id: "station2_q026_a",
          question_id: "station2_q026",
          answer_text: "Finding possible risks in the notes.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q026_b",
          question_id: "station2_q026",
          answer_text: "Owning the final risk judgment.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station2_q026_c",
          question_id: "station2_q026",
          answer_text: "Organising risks by likely impact.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q026_d",
          question_id: "station2_q026",
          answer_text: "Formatting the ranked list clearly.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q027",
      station_id: "station_2",
      stage: 3,
      complexity: 3,
      core_concept: "AI can be useful even when imperfect",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI groups supplier emails by topic, but some messages are ambiguous. What expectation is realistic?",
          options: {
            A: "The grouping can help, but may need correction.",
            B: "The categories prove each message was understood.",
            C: "The result is useless if one email is wrong.",
            D: "The tool simply found exact past categories.",
          },
          correct_answer: "A",
          feedback:
            "AI can make sorting easier while still misreading ambiguous messages. Useful does not always mean fully reliable.",
        },
        pt: {
          question:
            "A IA agrupa emails de fornecedores por tema, mas algumas mensagens são ambíguas. Qual expectativa é realista?",
          options: {
            A: "O agrupamento ajuda, mas pode precisar correção.",
            B: "As categorias provam entendimento de cada mensagem.",
            C: "O resultado é inútil se um email estiver errado.",
            D: "A ferramenta só encontrou categorias passadas exatas.",
          },
          correct_answer: "A",
          feedback:
            "A IA pode facilitar a organização e ainda interpretar mal mensagens ambíguas. Útil nem sempre significa totalmente confiável.",
        },
        sv: {
          question:
            "AI grupperar leverantörsmejl efter ämne, men vissa meddelanden är tvetydiga. Vilken förväntan är realistisk?",
          options: {
            A: "Grupperingen kan hjälpa, men behöva korrigering.",
            B: "Kategorierna bevisar att varje meddelande förståtts.",
            C: "Resultatet är värdelöst om ett mejl är fel.",
            D: "Verktyget hittade bara exakta gamla kategorier.",
          },
          correct_answer: "A",
          feedback:
            "AI kan göra sortering enklare och ändå misstolka tvetydiga meddelanden. Användbart betyder inte alltid fullt pålitligt.",
        },
        nl: {
          question:
            "AI groepeert leveranciersmails per onderwerp, maar sommige berichten zijn dubbelzinnig. Welke verwachting is realistisch?",
          options: {
            A: "De groepering helpt, maar kan correctie vragen.",
            B: "De categorieën bewijzen begrip van elk bericht.",
            C: "Het resultaat is waardeloos bij één fout.",
            D: "De tool vond alleen exacte oude categorieën.",
          },
          correct_answer: "A",
          feedback:
            "AI kan sorteren makkelijker maken en toch dubbelzinnige berichten verkeerd lezen. Nuttig betekent niet altijd volledig betrouwbaar.",
        },
      },
      question_text:
        "AI groups supplier emails by topic, but some messages are ambiguous. What expectation is realistic?",
      explanation:
        "AI can make sorting easier while still misreading ambiguous messages. Useful does not always mean fully reliable.",
      answers: [
        {
          id: "station2_q027_a",
          question_id: "station2_q027",
          answer_text: "The grouping can help, but may need correction.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station2_q027_b",
          question_id: "station2_q027",
          answer_text: "The categories prove each message was understood.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q027_c",
          question_id: "station2_q027",
          answer_text: "The result is useless if one email is wrong.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q027_d",
          question_id: "station2_q027",
          answer_text: "The tool simply found exact past categories.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q028",
      station_id: "station_2",
      stage: 3,
      complexity: 4,
      core_concept:
        "General AI may lack internal context that connected systems might have",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A general AI tool compares two team inputs but cannot access the latest internal decision. What is the main risk?",
          options: {
            A: "The comparison may use too many bullets.",
            B: "The wording may sound overly formal.",
            C: "The comparison may skip a key dependency.",
            D: "It may sound informed while missing context.",
          },
          correct_answer: "D",
          feedback:
            "A general tool may produce a confident comparison without the latest internal context needed for a reliable view.",
        },
        pt: {
          question:
            "Uma IA geral compara dois inputs de equipes, mas não acessa a decisão interna mais recente. Qual é o principal risco?",
          options: {
            A: "A comparação pode usar muitos bullets.",
            B: "O texto pode soar formal demais.",
            C: "A comparação pode pular uma dependência-chave.",
            D: "Pode soar informada sem ter contexto.",
          },
          correct_answer: "D",
          feedback:
            "Uma ferramenta geral pode produzir comparação confiante sem o contexto interno recente necessário para uma visão confiável.",
        },
        sv: {
          question:
            "Ett generellt AI-verktyg jämför två teaminput men saknar senaste interna beslut. Vad är huvudrisken?",
          options: {
            A: "Jämförelsen kan använda för många punkter.",
            B: "Formuleringen kan låta för formell.",
            C: "Jämförelsen kan missa ett viktigt beroende.",
            D: "Det kan låta informerat utan kontext.",
          },
          correct_answer: "D",
          feedback:
            "Ett generellt verktyg kan skapa en säker jämförelse utan den senaste interna kontexten som krävs för tillförlitlighet.",
        },
        nl: {
          question:
            "Een algemene AI-tool vergelijkt twee teaminputs, maar mist het nieuwste interne besluit. Wat is het belangrijkste risico?",
          options: {
            A: "De vergelijking gebruikt misschien te veel bullets.",
            B: "De formulering klinkt misschien te formeel.",
            C: "De vergelijking kan een belangrijke afhankelijkheid missen.",
            D: "Het klinkt geïnformeerd zonder context.",
          },
          correct_answer: "D",
          feedback:
            "Een algemene tool kan zeker klinken zonder de nieuwste interne context die nodig is voor een betrouwbaar beeld.",
        },
      },
      question_text:
        "A general AI tool compares two team inputs but cannot access the latest internal decision. What is the main risk?",
      explanation:
        "A general tool may produce a confident comparison without the latest internal context needed for a reliable view.",
      answers: [
        {
          id: "station2_q028_a",
          question_id: "station2_q028",
          answer_text: "The comparison may use too many bullets.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q028_b",
          question_id: "station2_q028",
          answer_text: "The wording may sound overly formal.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q028_c",
          question_id: "station2_q028",
          answer_text: "The comparison may skip a key dependency.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q028_d",
          question_id: "station2_q028",
          answer_text: "It may sound informed while missing context.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station2_q029",
      station_id: "station_2",
      stage: 3,
      complexity: 4,
      core_concept: "Simplified AI output can lose nuance while sounding clear",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI simplifies a technical explanation for non-specialists. The result is clear, but the topic has important exceptions. What is the main risk?",
          options: {
            A: "The audience may dislike simple language.",
            B: "The explanation may become too short.",
            C: "Important nuance may disappear from the explanation.",
            D: "AI will always refuse technical topics.",
          },
          correct_answer: "C",
          feedback:
            "Generated AI output can sound clear while losing important exceptions that matter.",
        },
        pt: {
          question:
            "A IA simplifica uma explicação técnica para não especialistas. O resultado é claro, mas o tema tem exceções importantes. Qual é o principal risco?",
          options: {
            A: "O público pode não gostar de linguagem simples.",
            B: "A explicação pode ficar curta demais.",
            C: "Nuance importante pode desaparecer da explicação.",
            D: "A IA sempre recusará temas técnicos.",
          },
          correct_answer: "C",
          feedback:
            "Texto gerado por IA pode soar claro enquanto perde exceções importantes que importam.",
        },
        sv: {
          question:
            "AI förenklar en teknisk förklaring för icke-specialister. Resultatet är tydligt, men ämnet har viktiga undantag. Vad är huvudrisken?",
          options: {
            A: "Publiken kan ogilla enkelt språk.",
            B: "Förklaringen kan bli för kort.",
            C: "Viktig nyans kan försvinna ur förklaringen.",
            D: "AI vägrar alltid tekniska ämnen.",
          },
          correct_answer: "C",
          feedback:
            "Genererad AI-text kan låta tydlig men förlora viktiga undantag som spelar roll.",
        },
        nl: {
          question:
            "AI vereenvoudigt een technische uitleg voor niet-specialisten. Het resultaat is duidelijk, maar het onderwerp heeft belangrijke uitzonderingen. Wat is het risico?",
          options: {
            A: "Het publiek houdt niet van eenvoudige taal.",
            B: "De uitleg wordt misschien te kort.",
            C: "Belangrijke nuance kan uit de uitleg verdwijnen.",
            D: "AI weigert altijd technische onderwerpen.",
          },
          correct_answer: "C",
          feedback:
            "Gegenereerde AI-output kan duidelijk klinken terwijl belangrijke uitzonderingen verdwijnen.",
        },
      },
      question_text:
        "AI simplifies a technical explanation for non-specialists. The result is clear, but the topic has important exceptions. What is the main risk?",
      explanation:
        "Generated AI output can sound clear while losing important exceptions that matter.",
      answers: [
        {
          id: "station2_q029_a",
          question_id: "station2_q029",
          answer_text: "The audience may dislike simple language.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q029_b",
          question_id: "station2_q029",
          answer_text: "The explanation may become too short.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station2_q029_c",
          question_id: "station2_q029",
          answer_text: "Important nuance may disappear from the explanation.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station2_q029_d",
          question_id: "station2_q029",
          answer_text: "AI will always refuse technical topics.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station2_q030",
      station_id: "station_2",
      stage: 3,
      complexity: 5,
      core_concept:
        "AI can challenge thinking but cannot replace human accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI challenges the assumptions in a decision memo. The critique is strong, but the business context is incomplete. What remains human-owned?",
          options: {
            A: "Listing weak assumptions for discussion.",
            B: "Deciding what conclusion to stand behind.",
            C: "Suggesting questions for the next meeting.",
            D: "Ignoring the critique until context is perfect.",
          },
          correct_answer: "B",
          feedback:
            "AI can improve the discussion, but a person must still own the conclusion and its consequences.",
        },
        pt: {
          question:
            "A IA questiona premissas em um memo de decisão. A crítica é forte, mas o contexto de negócio está incompleto. O que continua humano?",
          options: {
            A: "Listar premissas fracas para discussão.",
            B: "Decidir qual conclusão sustentar depois da análise.",
            C: "Sugerir perguntas para a próxima reunião.",
            D: "Ignorar a crítica até o contexto ser perfeito.",
          },
          correct_answer: "B",
          feedback:
            "A IA pode melhorar a discussão, mas uma pessoa ainda precisa assumir a conclusão e suas consequências.",
        },
        sv: {
          question:
            "AI utmanar antaganden i ett beslutsunderlag. Kritiken är stark, men affärskontexten är ofullständig. Vad förblir mänskligt ägt?",
          options: {
            A: "Lista svaga antaganden för diskussion.",
            B: "Besluta vilken slutsats man står bakom.",
            C: "Föreslå frågor till nästa möte.",
            D: "Ignorera kritiken tills kontexten är perfekt.",
          },
          correct_answer: "B",
          feedback:
            "AI kan förbättra diskussionen, men en människa måste fortfarande äga slutsatsen och dess konsekvenser.",
        },
        nl: {
          question:
            "AI bevraagt aannames in een beslismemo. De kritiek is sterk, maar de bedrijfscontext is onvolledig. Wat blijft menselijk eigenaarschap?",
          options: {
            A: "Zwakke aannames voor discussie noemen.",
            B: "Beslissen welke conclusie je draagt.",
            C: "Vragen voor de volgende meeting voorstellen.",
            D: "De kritiek negeren tot context perfect is.",
          },
          correct_answer: "B",
          feedback:
            "AI kan de discussie verbeteren, maar een mens moet de conclusie en gevolgen blijven dragen.",
        },
      },
      question_text:
        "AI challenges the assumptions in a decision memo. The critique is strong, but the business context is incomplete. What remains human-owned?",
      explanation:
        "AI can improve the discussion, but a person must still own the conclusion and its consequences.",
      answers: [
        {
          id: "station2_q030_a",
          question_id: "station2_q030",
          answer_text: "Listing weak assumptions for discussion.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station2_q030_b",
          question_id: "station2_q030",
          answer_text: "Deciding what conclusion to stand behind.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station2_q030_c",
          question_id: "station2_q030",
          answer_text: "Suggesting questions for the next meeting.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station2_q030_d",
          question_id: "station2_q030",
          answer_text: "Ignoring the critique until context is perfect.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q001",
      station_id: "station_3",
      stage: 1,
      complexity: 1,
      core_concept:
        "Good first use cases are low-risk, reviewable and preparation-oriented",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You face a long email thread before a meeting. Which first AI use case makes most sense?",
          options: {
            A: "Let AI choose and send the final reply.",
            B: "Ask AI for a short summary to review.",
            C: "Avoid AI because the thread is long.",
            D: "Use AI only to make the reply faster.",
          },
          correct_answer: "B",
          feedback:
            "Summarising a long thread is useful and reviewable. You still decide what matters before using it safely later.",
        },
        pt: {
          question:
            "Você tem uma longa troca de emails antes de uma reunião. Qual primeiro uso de IA faz mais sentido?",
          options: {
            A: "Deixar a IA escolher e enviar a resposta final.",
            B: "Pedir à IA um resumo curto para revisar.",
            C: "Evitar IA porque a troca é longa.",
            D: "Usar IA só para responder mais rápido.",
          },
          correct_answer: "B",
          feedback:
            "Resumir uma longa troca é útil e revisável. Você ainda decide o que importa antes de usar com segurança.",
        },
        sv: {
          question:
            "Du har en lång mejltråd före ett möte. Vilket första AI-användningsfall är mest rimligt?",
          options: {
            A: "Låta AI välja och skicka slutligt svar.",
            B: "Be AI om en kort sammanfattning att granska.",
            C: "Undvika AI eftersom tråden är lång.",
            D: "Använda AI bara för snabbare svar.",
          },
          correct_answer: "B",
          feedback:
            "Att sammanfatta en lång tråd är användbart och granskningsbart. Du avgör fortfarande vad som är viktigt före säker användning.",
        },
        nl: {
          question:
            "Je hebt een lange mailthread vóór een meeting. Welke eerste AI-toepassing is het meest logisch?",
          options: {
            A: "AI de definitieve reactie laten kiezen en sturen.",
            B: "AI om een korte samenvatting vragen om te beoordelen.",
            C: "AI vermijden omdat de thread lang is.",
            D: "AI alleen gebruiken om sneller te antwoorden.",
          },
          correct_answer: "B",
          feedback:
            "Een lange thread samenvatten is nuttig en controleerbaar. Jij beslist nog steeds wat belangrijk is vóór veilig gebruik.",
        },
      },
      question_text:
        "You face a long email thread before a meeting. Which first AI use case makes most sense?",
      explanation:
        "Summarising a long thread is useful and reviewable. You still decide what matters before using it safely later.",
      answers: [
        {
          id: "station3_q001_a",
          question_id: "station3_q001",
          answer_text: "Let AI choose and send the final reply.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q001_b",
          question_id: "station3_q001",
          answer_text: "Ask AI for a short summary to review.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station3_q001_c",
          question_id: "station3_q001",
          answer_text: "Avoid AI because the thread is long.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q001_d",
          question_id: "station3_q001",
          answer_text: "Use AI only to make the reply faster.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q002",
      station_id: "station_3",
      stage: 1,
      complexity: 1,
      core_concept:
        "AI is useful for turning messy notes into reviewable structure",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "After a meeting, your notes are messy and need follow-up. What is a good first AI use case?",
          options: {
            A: "Extract actions, owners and deadlines for review.",
            B: "Decide which commitments the team accepts.",
            C: "Avoid AI until the notes are perfect.",
            D: "Rewrite the notes in a friendlier tone.",
          },
          correct_answer: "A",
          feedback:
            "AI can structure messy notes into actions, owners and deadlines. People still confirm the follow-up before it is used.",
        },
        pt: {
          question:
            "Depois de uma reunião, suas notas estão confusas e precisam de follow-up. Qual é um bom primeiro uso de IA?",
          options: {
            A: "Extrair ações, responsáveis e prazos para revisar.",
            B: "Decidir quais compromissos a equipe aceita.",
            C: "Evitar IA até as notas ficarem perfeitas.",
            D: "Reescrever as notas em tom mais amigável.",
          },
          correct_answer: "A",
          feedback:
            "A IA pode estruturar notas confusas em ações, responsáveis e prazos. Pessoas ainda confirmam o follow-up antes do uso.",
        },
        sv: {
          question:
            "Efter ett möte är dina anteckningar röriga och behöver tydlig uppföljning. Vad är ett bra första AI-användningsfall här?",
          options: {
            A: "Ta ut åtgärder, ägare och deadlines för granskning.",
            B: "Besluta vilka åtaganden teamet accepterar.",
            C: "Undvika AI tills anteckningarna är perfekta.",
            D: "Skriva om anteckningarna i vänligare ton.",
          },
          correct_answer: "A",
          feedback:
            "AI kan strukturera röriga anteckningar till åtgärder, ägare och deadlines. Människor bekräftar fortfarande uppföljningen före användning sedan tillsammans.",
        },
        nl: {
          question:
            "Na een meeting zijn je notities rommelig en vragen ze duidelijke opvolging. Wat is een goede eerste AI-toepassing?",
          options: {
            A: "Acties, eigenaars en deadlines extraheren voor controle.",
            B: "Beslissen welke afspraken het team accepteert.",
            C: "AI vermijden tot de notities perfect zijn.",
            D: "Geen AI-hulp omdat notities informeel zijn.",
          },
          correct_answer: "A",
          feedback:
            "AI kan rommelige notities structureren in acties, eigenaars en deadlines. Mensen bevestigen nog steeds de opvolging vóór gebruik.",
        },
      },
      question_text:
        "After a meeting, your notes are messy and need follow-up. What is a good first AI use case?",
      explanation:
        "AI can structure messy notes into actions, owners and deadlines. People still confirm the follow-up before it is used.",
      answers: [
        {
          id: "station3_q002_a",
          question_id: "station3_q002",
          answer_text: "Extract actions, owners and deadlines for review.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station3_q002_b",
          question_id: "station3_q002",
          answer_text: "Decide which commitments the team accepts.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q002_c",
          question_id: "station3_q002",
          answer_text: "Avoid AI until the notes are perfect.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q002_d",
          question_id: "station3_q002",
          answer_text: "Rewrite the notes in a friendlier tone.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q003",
      station_id: "station_3",
      stage: 1,
      complexity: 2,
      core_concept:
        "Good first use cases should be easy to review and low-risk",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You want to simplify a dense training paragraph for beginners. How should AI support this first use case?",
          options: {
            A: "Trust simple wording as automatically correct.",
            B: "Simplify only for speed, even if meaning changes.",
            C: "Let AI publish the final training text.",
            D: "Ask for a simpler version, then compare meaning.",
          },
          correct_answer: "D",
          feedback:
            "Simplifying text is a good use case when the result is reviewable and the original meaning is protected.",
        },
        pt: {
          question:
            "Você quer simplificar um parágrafo denso de treinamento para iniciantes. Como a IA deve apoiar esse primeiro uso?",
          options: {
            A: "Confiar que texto simples é automaticamente correto.",
            B: "Simplificar só por velocidade, mesmo mudando sentido.",
            C: "Deixar a IA publicar o texto final.",
            D: "Pedir versão simples e comparar o sentido.",
          },
          correct_answer: "D",
          feedback:
            "Simplificar texto é um bom uso quando o resultado é revisável e o sentido original fica protegido antes do uso.",
        },
        sv: {
          question:
            "Du vill förenkla ett tätt utbildningsstycke för nybörjare. Hur bör AI stödja detta första användningsfall före mänsklig granskning?",
          options: {
            A: "Lita på att enkel text automatiskt är korrekt.",
            B: "Förenkla bara för hastighet, även om betydelsen ändras.",
            C: "Låta AI publicera den slutliga utbildningstexten.",
            D: "Be om enklare version och jämför betydelsen.",
          },
          correct_answer: "D",
          feedback:
            "Att förenkla text är bra när resultatet kan granskas och den ursprungliga betydelsen skyddas före användning sedan tillsammans.",
        },
        nl: {
          question:
            "Je wilt een compacte trainingsalinea vereenvoudigen voor beginners. Hoe moet AI deze eerste toepassing ondersteunen vóór menselijke controle?",
          options: {
            A: "Eenvoudige tekst als automatisch correct vertrouwen.",
            B: "Alleen voor snelheid vereenvoudigen, ook met betekenisverlies.",
            C: "AI de definitieve trainingstekst laten publiceren.",
            D: "Een eenvoudige versie vragen en betekenis vergelijken.",
          },
          correct_answer: "D",
          feedback:
            "Tekst vereenvoudigen is goed wanneer het resultaat controleerbaar is en de oorspronkelijke betekenis behouden blijft vóór gebruik door mensen.",
        },
      },
      question_text:
        "You want to simplify a dense training paragraph for beginners. How should AI support this first use case?",
      explanation:
        "Simplifying text is a good use case when the result is reviewable and the original meaning is protected.",
      answers: [
        {
          id: "station3_q003_a",
          question_id: "station3_q003",
          answer_text: "Trust simple wording as automatically correct.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q003_b",
          question_id: "station3_q003",
          answer_text: "Simplify only for speed, even if meaning changes.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q003_c",
          question_id: "station3_q003",
          answer_text: "Let AI publish the final training text.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q003_d",
          question_id: "station3_q003",
          answer_text: "Ask for a simpler version, then compare meaning.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station3_q004",
      station_id: "station_3",
      stage: 1,
      complexity: 2,
      core_concept: "High-risk or unclear tasks are poor first AI use cases",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A beginner wants AI to rate employee performance from short comments. What makes this a poor first use case?",
          options: {
            A: "It would save time for the manager.",
            B: "The comments may need cleaner formatting.",
            C: "The task involves people-sensitive judgment.",
            D: "It resembles an existing manual task.",
          },
          correct_answer: "C",
          feedback:
            "Good first use cases are low-risk and reviewable. Rating employee performance is too sensitive for a beginner task.",
        },
        pt: {
          question:
            "Um iniciante quer usar IA para avaliar desempenho por comentários curtos. O que torna isso um mau primeiro uso?",
          options: {
            A: "Isso economizaria tempo do gestor.",
            B: "Os comentários podem precisar de formatação.",
            C: "A tarefa envolve julgamento sensível sobre pessoas.",
            D: "Parece uma tarefa manual existente.",
          },
          correct_answer: "C",
          feedback:
            "Bons primeiros usos são de baixo risco e revisáveis. Avaliar desempenho é sensível demais para iniciantes nessa fase.",
        },
        sv: {
          question:
            "En nybörjare vill låta AI bedöma prestation från korta kommentarer. Vad gör detta till ett dåligt första användningsfall?",
          options: {
            A: "Det skulle spara tid för chefen.",
            B: "Kommentarerna kan behöva bättre format.",
            C: "Uppgiften rör personkänslig bedömning för nybörjare.",
            D: "Den liknar en befintlig manuell uppgift.",
          },
          correct_answer: "C",
          feedback:
            "Bra första användningsfall är lågrisk och granskningsbara. Prestationsbedömning är för känsligt för en nybörjaruppgift här idag. Välj enklare uppgifter först.",
        },
        nl: {
          question:
            "Een beginner wil AI prestaties laten beoordelen op basis van korte opmerkingen. Waarom is dit geen goede eerste toepassing?",
          options: {
            A: "Het zou de manager tijd besparen.",
            B: "De opmerkingen hebben misschien betere opmaak nodig.",
            C: "De taak vraagt mensgevoelig oordeel.",
            D: "Het lijkt op een bestaande handmatige taak.",
          },
          correct_answer: "C",
          feedback:
            "Goede eerste toepassingen zijn laag risico en controleerbaar. Prestaties beoordelen is te gevoelig voor beginners in deze fase.",
        },
      },
      question_text:
        "A beginner wants AI to rate employee performance from short comments. What makes this a poor first use case?",
      explanation:
        "Good first use cases are low-risk and reviewable. Rating employee performance is too sensitive for a beginner task.",
      answers: [
        {
          id: "station3_q004_a",
          question_id: "station3_q004",
          answer_text: "It would save time for the manager.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q004_b",
          question_id: "station3_q004",
          answer_text: "The comments may need cleaner formatting.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q004_c",
          question_id: "station3_q004",
          answer_text: "The task involves people-sensitive judgment.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station3_q004_d",
          question_id: "station3_q004",
          answer_text: "It resembles an existing manual task.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q005",
      station_id: "station_3",
      stage: 2,
      complexity: 2,
      core_concept:
        "Different everyday tasks need different kinds of AI support",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You have 15 minutes before a stakeholder meeting and only rough notes. What AI preparation support fits best?",
          options: {
            A: "Ask AI to decide the meeting outcome.",
            B: "Ask for likely questions and concerns.",
            C: "Ask only for a warmer tone.",
            D: "Use AI’s answer as the final position.",
          },
          correct_answer: "B",
          feedback:
            "For this everyday use case, AI is strongest when it helps you anticipate questions, risks and concerns to consider.",
        },
        pt: {
          question:
            "Você tem 15 minutos antes de uma reunião e só notas rascunhadas. Que apoio da IA combina melhor?",
          options: {
            A: "Pedir à IA que decida o resultado.",
            B: "Pedir perguntas e preocupações prováveis.",
            C: "Pedir apenas um tom mais cordial.",
            D: "Usar a resposta como posição final.",
          },
          correct_answer: "B",
          feedback:
            "Nesse caso de uso cotidiano, a IA ajuda mais ao antecipar perguntas, riscos e preocupações que você deve considerar.",
        },
        sv: {
          question:
            "Du har 15 minuter före ett intressentmöte och bara grova anteckningar. Vilket AI-stöd för förberedelse passar bäst nu?",
          options: {
            A: "Be AI besluta mötets resultat.",
            B: "Be om sannolika frågor och farhågor.",
            C: "Be bara om en varmare ton.",
            D: "Använd AI-svaret som slutlig position.",
          },
          correct_answer: "B",
          feedback:
            "I detta vardagliga användningsfall är AI starkast när den hjälper dig förutse frågor, risker och farhågor inför mötet.",
        },
        nl: {
          question:
            "Je hebt 15 minuten vóór een stakeholdermeeting en alleen ruwe notities. Welke AI-steun voor voorbereiding past het best?",
          options: {
            A: "AI vragen de uitkomst te beslissen.",
            B: "Vragen om waarschijnlijke vragen en zorgen.",
            C: "Alleen om een warmere toon vragen.",
            D: "AI’s antwoord als eindstandpunt gebruiken.",
          },
          correct_answer: "B",
          feedback:
            "Voor deze dagelijkse toepassing helpt AI vooral door vragen, risico’s en zorgen vooraf zichtbaar te maken voor beoordeling.",
        },
      },
      question_text:
        "You have 15 minutes before a stakeholder meeting and only rough notes. What AI preparation support fits best?",
      explanation:
        "For this everyday use case, AI is strongest when it helps you anticipate questions, risks and concerns to consider.",
      answers: [
        {
          id: "station3_q005_a",
          question_id: "station3_q005",
          answer_text: "Ask AI to decide the meeting outcome.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q005_b",
          question_id: "station3_q005",
          answer_text: "Ask for likely questions and concerns.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station3_q005_c",
          question_id: "station3_q005",
          answer_text: "Ask only for a warmer tone.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q005_d",
          question_id: "station3_q005",
          answer_text: "Use AI’s answer as the final position.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q006",
      station_id: "station_3",
      stage: 2,
      complexity: 2,
      core_concept:
        "Choose summarising, action extraction or drafting based on the work need",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Your manager asks what was decided, who owns actions and when they are due. Which AI output fits best?",
          options: {
            A: "A shorter summary of the whole meeting.",
            B: "A final commitment list sent automatically.",
            C: "A table of decisions, owners and deadlines.",
            D: "No AI help because notes are informal.",
          },
          correct_answer: "C",
          feedback:
            "The task asks for structure. A table of decisions, owners and deadlines is more useful than a general summary.",
        },
        pt: {
          question:
            "Seu gestor pergunta o que foi decidido, quem é responsável e quais são os prazos. Qual saída da IA combina melhor?",
          options: {
            A: "Um resumo mais curto da reunião inteira.",
            B: "Uma lista final enviada automaticamente.",
            C: "Uma tabela com decisões, responsáveis e prazos.",
            D: "Sem IA porque as notas são informais.",
          },
          correct_answer: "C",
          feedback:
            "A tarefa pede estrutura. Uma tabela com decisões, responsáveis e prazos é mais útil que um resumo geral.",
        },
        sv: {
          question:
            "Din chef frågar vad som beslutades, vem som äger åtgärder och när de ska vara klara. Vilket AI-resultat passar bäst?",
          options: {
            A: "En kortare sammanfattning av hela mötet.",
            B: "En slutlig åtagandelista som skickas automatiskt.",
            C: "En tabell med beslut, ägare och deadlines.",
            D: "Ingen AI-hjälp eftersom noterna är informella.",
          },
          correct_answer: "C",
          feedback:
            "Uppgiften kräver struktur. En tabell med beslut, ägare och deadlines är mer användbar än en allmän sammanfattning här.",
        },
        nl: {
          question:
            "Je manager vraagt wat is besloten, wie acties bezit en wanneer ze af moeten zijn. Welke AI-output past het best?",
          options: {
            A: "Een kortere samenvatting van de hele meeting.",
            B: "Een definitieve actielijst die automatisch wordt verstuurd.",
            C: "Een tabel met besluiten, eigenaars en deadlines.",
            D: "AI overslaan omdat de notities niet netjes zijn.",
          },
          correct_answer: "C",
          feedback:
            "De taak vraagt om structuur. Een tabel met besluiten, eigenaars en deadlines is nuttiger dan een algemene samenvatting.",
        },
      },
      question_text:
        "Your manager asks what was decided, who owns actions and when they are due. Which AI output fits best?",
      explanation:
        "The task asks for structure. A table of decisions, owners and deadlines is more useful than a general summary.",
      answers: [
        {
          id: "station3_q006_a",
          question_id: "station3_q006",
          answer_text: "A shorter summary of the whole meeting.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q006_b",
          question_id: "station3_q006",
          answer_text: "A final commitment list sent automatically.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q006_c",
          question_id: "station3_q006",
          answer_text: "A table of decisions, owners and deadlines.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station3_q006_d",
          question_id: "station3_q006",
          answer_text: "No AI help because notes are informal.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q007",
      station_id: "station_3",
      stage: 2,
      complexity: 3,
      core_concept:
        "AI can adapt tone and format, but meaning must remain intact",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A technical update must go to non-specialists without losing key meaning. Which AI use is strongest for the audience?",
          options: {
            A: "Keep the technical version unchanged.",
            B: "Remove complex points so it reads easily.",
            C: "Let AI send the adapted message.",
            D: "Ask for a plain-language version, then check the meaning.",
          },
          correct_answer: "D",
          feedback:
            "Tone and complexity adaptation is useful when you check that the simpler version still carries the same meaning.",
        },
        pt: {
          question:
            "Uma atualização técnica deve ir para não especialistas sem perder sentido essencial. Qual uso de IA é mais forte?",
          options: {
            A: "Manter a versão técnica sem mudança.",
            B: "Remover pontos complexos para facilitar leitura.",
            C: "Deixar a IA enviar a mensagem adaptada.",
            D: "Pedir uma versão clara e verificar se mantém o sentido.",
          },
          correct_answer: "D",
          feedback:
            "Adaptar tom e complexidade é útil quando você checa se a versão simples mantém o mesmo sentido para o público.",
        },
        sv: {
          question:
            "En teknisk uppdatering ska till icke-specialister utan att tappa viktig betydelse. Vilken AI-användning är starkast för publiken nu?",
          options: {
            A: "Behåll den tekniska versionen oförändrad.",
            B: "Ta bort komplexa punkter så den läses lätt.",
            C: "Låt AI skicka det anpassade meddelandet.",
            D: "Be om klarspråk och kontrollera betydelsen.",
          },
          correct_answer: "D",
          feedback:
            "Att anpassa ton och komplexitet är användbart när du kontrollerar att den enklare versionen behåller betydelsen för publiken.",
        },
        nl: {
          question:
            "Een technische update moet naar niet-specialisten zonder kernbetekenis te verliezen. Welke AI-toepassing is het sterkst voor het publiek?",
          options: {
            A: "De technische versie onveranderd houden.",
            B: "Complexe punten verwijderen voor makkelijke lezing.",
            C: "AI het aangepaste bericht laten versturen.",
            D: "Een heldere versie vragen en betekenis controleren.",
          },
          correct_answer: "D",
          feedback:
            "Toon en complexiteit aanpassen is nuttig wanneer je controleert dat de eenvoudige versie dezelfde betekenis behoudt voor het publiek.",
        },
      },
      question_text:
        "A technical update must go to non-specialists without losing key meaning. Which AI use is strongest for the audience?",
      explanation:
        "Tone and complexity adaptation is useful when you check that the simpler version still carries the same meaning.",
      answers: [
        {
          id: "station3_q007_a",
          question_id: "station3_q007",
          answer_text: "Keep the technical version unchanged.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q007_b",
          question_id: "station3_q007",
          answer_text: "Remove complex points so it reads easily.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q007_c",
          question_id: "station3_q007",
          answer_text: "Let AI send the adapted message.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q007_d",
          question_id: "station3_q007",
          answer_text: "Ask for a plain-language version, then check the meaning.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station3_q008",
      station_id: "station_3",
      stage: 3,
      complexity: 3,
      core_concept: "Mature everyday use balances speed with review",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You need a project status update in ten minutes. The facts are known, but the wording is rough. What is the safest next step?",
          options: {
            A: "Ask AI for a draft, then review it.",
            B: "Send the AI draft as soon as it appears.",
            C: "Avoid AI because time is short.",
            D: "Let AI choose what stakeholders need.",
          },
          correct_answer: "A",
          feedback:
            "A draft is useful under time pressure when the facts are known and you still review before sharing.",
        },
        pt: {
          question:
            "Você precisa de um status de projeto em dez minutos. Os fatos são conhecidos, mas o texto está bruto. Qual próximo passo é mais seguro?",
          options: {
            A: "Pedir um rascunho à IA e revisar.",
            B: "Enviar o rascunho assim que aparecer.",
            C: "Evitar IA porque o tempo é curto.",
            D: "Deixar a IA escolher o que stakeholders precisam.",
          },
          correct_answer: "A",
          feedback:
            "Um rascunho ajuda sob pressão quando os fatos são conhecidos e você ainda revisa antes de compartilhar com outros.",
        },
        sv: {
          question:
            "Du behöver en projektstatus på tio minuter. Fakta är kända, men texten är rå. Vad är säkrast nästa steg?",
          options: {
            A: "Be AI om ett utkast och granska det.",
            B: "Skicka AI-utkastet så snart det visas.",
            C: "Undvika AI eftersom tiden är kort.",
            D: "Låta AI välja vad intressenter behöver.",
          },
          correct_answer: "A",
          feedback:
            "Ett utkast hjälper under tidspress när fakta är kända och du fortfarande granskar innan du delar med andra.",
        },
        nl: {
          question:
            "Je hebt binnen tien minuten een projectstatus nodig. De feiten zijn bekend, maar de tekst is ruw. Wat is het veiligste?",
          options: {
            A: "AI om een concept vragen en het controleren.",
            B: "Het AI-concept sturen zodra het verschijnt.",
            C: "AI vermijden omdat tijd kort is.",
            D: "AI laten kiezen wat stakeholders nodig hebben.",
          },
          correct_answer: "A",
          feedback:
            "Een concept helpt bij tijdsdruk wanneer de feiten bekend zijn en je nog controleert vóór delen met anderen.",
        },
      },
      question_text:
        "You need a project status update in ten minutes. The facts are known, but the wording is rough. What is the safest next step?",
      explanation:
        "A draft is useful under time pressure when the facts are known and you still review before sharing.",
      answers: [
        {
          id: "station3_q008_a",
          question_id: "station3_q008",
          answer_text: "Ask AI for a draft, then review it.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station3_q008_b",
          question_id: "station3_q008",
          answer_text: "Send the AI draft as soon as it appears.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q008_c",
          question_id: "station3_q008",
          answer_text: "Avoid AI because time is short.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q008_d",
          question_id: "station3_q008",
          answer_text: "Let AI choose what stakeholders need.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q009",
      station_id: "station_3",
      stage: 3,
      complexity: 4,
      core_concept:
        "Some technically possible uses are too risky as everyday first use cases",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A customer asks for a delivery promise, and details are incomplete. AI can prepare options quickly. What makes full automation inappropriate?",
          options: {
            A: "The response may not be short enough.",
            B: "The tone may not match the customer.",
            C: "A commitment needs human ownership.",
            D: "AI cannot help prepare any option.",
          },
          correct_answer: "C",
          feedback:
            "AI can prepare options, but a delivery promise affects others. A person must own the commitment before it is shared.",
        },
        pt: {
          question:
            "Um cliente pede promessa de entrega, e detalhes estão incompletos. A IA prepara opções rápido. Por que automação total é inadequada?",
          options: {
            A: "A resposta pode não ser curta o bastante.",
            B: "O tom pode não combinar com o cliente.",
            C: "Um compromisso exige responsabilidade humana.",
            D: "A IA não pode ajudar a preparar opções.",
          },
          correct_answer: "C",
          feedback:
            "A IA pode preparar opções, mas uma promessa de entrega afeta outros. Uma pessoa deve assumir o compromisso.",
        },
        sv: {
          question:
            "En kund ber om ett leveranslöfte, och detaljer saknas. AI kan snabbt förbereda alternativ. Varför är full automatisering olämplig?",
          options: {
            A: "Svaret kanske inte är tillräckligt kort.",
            B: "Tonen kanske inte passar kunden.",
            C: "Ett åtagande kräver mänskligt ägarskap.",
            D: "AI kan inte hjälpa till med alternativ.",
          },
          correct_answer: "C",
          feedback:
            "AI kan förbereda alternativ, men ett leveranslöfte påverkar andra. En människa måste äga åtagandet före delning sedan tydligt.",
        },
        nl: {
          question:
            "Een klant vraagt om een leveringsbelofte, en details ontbreken. AI kan snel opties voorbereiden. Waarom is volledige automatisering ongeschikt?",
          options: {
            A: "Het antwoord is misschien niet kort genoeg.",
            B: "De toon past misschien niet bij de klant.",
            C: "Een belofte vraagt menselijk eigenaarschap.",
            D: "AI kan geen enkele optie voorbereiden.",
          },
          correct_answer: "C",
          feedback:
            "AI kan opties voorbereiden, maar een leveringsbelofte raakt anderen. Een mens moet de toezegging dragen vóór delen later.",
        },
      },
      question_text:
        "A customer asks for a delivery promise, and details are incomplete. AI can prepare options quickly. What makes full automation inappropriate?",
      explanation:
        "AI can prepare options, but a delivery promise affects others. A person must own the commitment before it is shared.",
      answers: [
        {
          id: "station3_q009_a",
          question_id: "station3_q009",
          answer_text: "The response may not be short enough.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q009_b",
          question_id: "station3_q009",
          answer_text: "The tone may not match the customer.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q009_c",
          question_id: "station3_q009",
          answer_text: "A commitment needs human ownership.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station3_q009_d",
          question_id: "station3_q009",
          answer_text: "AI cannot help prepare any option.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q010",
      station_id: "station_3",
      stage: 3,
      complexity: 5,
      core_concept:
        "Mature use recognises when AI should prepare, not replace judgment under constraints",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Three teams send conflicting inputs before a planning call. AI can compare them and show gaps. What should remain human-owned?",
          options: {
            A: "Creating a comparison table for discussion.",
            B: "Resolving which position the group will use.",
            C: "Letting AI choose the least conflicting input.",
            D: "Avoiding AI because the inputs disagree.",
          },
          correct_answer: "B",
          feedback:
            "AI can prepare the comparison as a use case, but people must resolve disagreement and own the position used.",
        },
        pt: {
          question:
            "Três equipes enviam inputs conflitantes antes do planejamento. A IA pode comparar e mostrar lacunas. O que deve continuar humano?",
          options: {
            A: "Criar uma tabela de comparação para discussão.",
            B: "Resolver qual posição o grupo usará.",
            C: "Deixar a IA escolher o input menos conflitante.",
            D: "Evitar IA porque os inputs discordam.",
          },
          correct_answer: "B",
          feedback:
            "A IA pode preparar a comparação como caso de uso, mas pessoas precisam resolver divergências e assumir a posição usada.",
        },
        sv: {
          question:
            "Tre team skickar motstridiga input före planering. AI kan jämföra dem och visa gap. Vad ska förbli mänskligt ägt?",
          options: {
            A: "Skapa en jämförelsetabell för diskussion.",
            B: "Avgöra vilken position gruppen ska använda.",
            C: "Låta AI välja minst konfliktfylld input.",
            D: "Undvika AI eftersom inputen inte stämmer.",
          },
          correct_answer: "B",
          feedback:
            "AI kan förbereda jämförelsen som användningsfall, men människor måste lösa oenighet och äga positionen som används sedan tydligt.",
        },
        nl: {
          question:
            "Drie teams sturen tegenstrijdige input vóór planning. AI kan vergelijken en gaten tonen. Wat moet menselijk eigenaarschap blijven?",
          options: {
            A: "Een vergelijkingstabel maken voor discussie.",
            B: "Beslissen welke positie de groep gebruikt.",
            C: "AI de minst conflicterende input laten kiezen.",
            D: "AI vermijden omdat de input botst.",
          },
          correct_answer: "B",
          feedback:
            "AI kan de vergelijking als toepassing voorbereiden, maar mensen moeten verschil oplossen en de gekozen positie dragen daarna.",
        },
      },
      question_text:
        "Three teams send conflicting inputs before a planning call. AI can compare them and show gaps. What should remain human-owned?",
      explanation:
        "AI can prepare the comparison as a use case, but people must resolve disagreement and own the position used.",
      answers: [
        {
          id: "station3_q010_a",
          question_id: "station3_q010",
          answer_text: "Creating a comparison table for discussion.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q010_b",
          question_id: "station3_q010",
          answer_text: "Resolving which position the group will use.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station3_q010_c",
          question_id: "station3_q010",
          answer_text: "Letting AI choose the least conflicting input.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q010_d",
          question_id: "station3_q010",
          answer_text: "Avoiding AI because the inputs disagree.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q011",
      station_id: "station_3",
      stage: 1,
      complexity: 1,
      core_concept:
        "AI is useful for creating first drafts when the result is easy to review",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You need a routine supplier email but do not know how to start. What should you do first?",
          options: {
            A: "Let AI send the final supplier message.",
            B: "Avoid AI until the wording is perfect.",
            C: "Ask AI for a first draft to review.",
            D: "Use AI only to make it shorter.",
          },
          correct_answer: "C",
          feedback:
            "A first draft is useful when the task is routine and easy to review. You still own the final message.",
        },
        pt: {
          question:
            "Você precisa escrever um email rotineiro a fornecedor, mas não sabe como começar. O que fazer primeiro?",
          options: {
            A: "Deixar a IA enviar a mensagem final.",
            B: "Evitar IA até o texto ficar perfeito.",
            C: "Pedir um primeiro rascunho para revisar.",
            D: "Usar IA só para encurtar o texto.",
          },
          correct_answer: "C",
          feedback:
            "Um primeiro rascunho é útil quando a tarefa é rotineira e fácil de revisar. Você ainda assume a mensagem final.",
        },
        sv: {
          question:
            "Du behöver skriva ett rutinmejl till en leverantör men vet inte hur du ska börja. Vad gör du först?",
          options: {
            A: "Låta AI skicka slutligt leverantörsmejl.",
            B: "Undvika AI tills texten är perfekt.",
            C: "Be AI om ett första utkast att granska.",
            D: "Använda AI bara för att korta texten.",
          },
          correct_answer: "C",
          feedback:
            "Ett första utkast är användbart när uppgiften är rutinmässig och lätt att granska. Du äger fortfarande slutmeddelandet.",
        },
        nl: {
          question:
            "Je moet een routine-mail aan een leverancier schrijven, maar weet niet hoe te starten. Wat doe je eerst?",
          options: {
            A: "AI het definitieve leveranciersbericht laten sturen.",
            B: "AI vermijden tot de tekst perfect is.",
            C: "AI om een eerste concept vragen.",
            D: "AI alleen gebruiken om tekst te verkorten.",
          },
          correct_answer: "C",
          feedback:
            "Een eerste concept is nuttig wanneer de taak routine is en makkelijk te controleren. Jij bezit nog steeds het eindbericht.",
        },
      },
      question_text:
        "You need a routine supplier email but do not know how to start. What should you do first?",
      explanation:
        "A first draft is useful when the task is routine and easy to review. You still own the final message.",
      answers: [
        {
          id: "station3_q011_a",
          question_id: "station3_q011",
          answer_text: "Let AI send the final supplier message.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q011_b",
          question_id: "station3_q011",
          answer_text: "Avoid AI until the wording is perfect.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q011_c",
          question_id: "station3_q011",
          answer_text: "Ask AI for a first draft to review.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station3_q011_d",
          question_id: "station3_q011",
          answer_text: "Use AI only to make it shorter.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q012",
      station_id: "station_3",
      stage: 1,
      complexity: 1,
      core_concept:
        "Repetitive formatting work can be a low-risk first AI use case",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Your team repeats the same small task every week. What is the safest first AI use case?",
          options: {
            A: "Draft a checklist of repeated steps.",
            B: "Let AI own the full process.",
            C: "Delay until the checklist is fully documented.",
            D: "Use the checklist without reading it.",
          },
          correct_answer: "A",
          feedback:
            "A checklist is low-risk and easy to review. It helps with repetitive work without handing over control.",
        },
        pt: {
          question:
            "Sua equipe repete a mesma pequena tarefa toda semana. Qual primeiro uso de IA é mais seguro?",
          options: {
            A: "Rascunhar uma checklist dos passos repetidos.",
            B: "Deixar a IA assumir todo o processo.",
            C: "Esperar até a checklist estar totalmente documentada.",
            D: "Usar a checklist sem ler.",
          },
          correct_answer: "A",
          feedback:
            "Uma checklist é de baixo risco e fácil de revisar. Ela ajuda no trabalho repetitivo sem entregar controle.",
        },
        sv: {
          question:
            "Ditt team upprepar samma lilla uppgift varje vecka. Vilket första AI-användningsfall är säkrast?",
          options: {
            A: "Skapa ett utkast till checklista över stegen.",
            B: "Låta AI äga hela processen.",
            C: "Vänta tills checklistan är helt dokumenterad.",
            D: "Använda checklistan utan att läsa.",
          },
          correct_answer: "A",
          feedback:
            "En checklista är lågrisk och lätt att granska. Den hjälper med repetitivt arbete utan att lämna över kontroll.",
        },
        nl: {
          question:
            "Je team herhaalt elke week dezelfde kleine taak. Welke eerste AI-toepassing is het veiligst?",
          options: {
            A: "Een checklist met herhaalde stappen opstellen.",
            B: "AI het volledige proces laten bezitten.",
            C: "Wachten tot de checklist volledig is gedocumenteerd.",
            D: "De checklist gebruiken zonder te lezen.",
          },
          correct_answer: "A",
          feedback:
            "Een checklist is laag risico en makkelijk te controleren. Het helpt repetitief werk zonder controle over te dragen.",
        },
      },
      question_text:
        "Your team repeats the same small task every week. What is the safest first AI use case?",
      explanation:
        "A checklist is low-risk and easy to review. It helps with repetitive work without handing over control.",
      answers: [
        {
          id: "station3_q012_a",
          question_id: "station3_q012",
          answer_text: "Draft a checklist of repeated steps.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station3_q012_b",
          question_id: "station3_q012",
          answer_text: "Let AI own the full process.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q012_c",
          question_id: "station3_q012",
          answer_text: "Delay until the checklist is fully documented.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q012_d",
          question_id: "station3_q012",
          answer_text: "Use the checklist without reading it.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q013",
      station_id: "station_3",
      stage: 1,
      complexity: 2,
      core_concept:
        "AI can help choose a useful output format for messy information",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A handover note is long and hard to scan. Which AI output would help most?",
          options: {
            A: "A shorter paragraph with the same order.",
            B: "A final decision on what matters.",
            C: "No AI help because the note is messy.",
            D: "A table of topics, status and open items.",
          },
          correct_answer: "D",
          feedback:
            "The best use case is structuring messy information into a format the next person can quickly review.",
        },
        pt: {
          question:
            "Uma nota de handover está longa e difícil de ler. Qual saída da IA ajudaria mais?",
          options: {
            A: "Um parágrafo menor na mesma ordem.",
            B: "Uma decisão final sobre o que importa.",
            C: "Nenhuma IA porque a nota é confusa.",
            D: "Uma tabela com temas, status e pendências.",
          },
          correct_answer: "D",
          feedback:
            "O melhor uso é estruturar informação confusa em um formato que a próxima pessoa possa revisar rapidamente.",
        },
        sv: {
          question:
            "En överlämningsnotering är lång och svår att överblicka. Vilket AI-resultat hjälper mest?",
          options: {
            A: "Ett kortare stycke i samma ordning.",
            B: "Ett slutligt beslut om vad som är viktigt.",
            C: "Ingen AI-hjälp eftersom noten är rörig.",
            D: "En tabell med ämnen, status och öppna punkter.",
          },
          correct_answer: "D",
          feedback:
            "Det bästa användningsfallet är att strukturera rörig information så nästa person snabbt kan granska den.",
        },
        nl: {
          question:
            "Een overdrachtsnotitie is lang en lastig te scannen. Welke AI-output helpt het meest?",
          options: {
            A: "Een kortere alinea in dezelfde volgorde.",
            B: "Een definitief besluit over wat belangrijk is.",
            C: "Geen AI-hulp omdat de notitie rommelig is.",
            D: "Een tabel met onderwerpen, status en open punten.",
          },
          correct_answer: "D",
          feedback:
            "De beste toepassing is rommelige informatie structureren in een vorm die de volgende persoon snel kan controleren.",
        },
      },
      question_text:
        "A handover note is long and hard to scan. Which AI output would help most?",
      explanation:
        "The best use case is structuring messy information into a format the next person can quickly review.",
      answers: [
        {
          id: "station3_q013_a",
          question_id: "station3_q013",
          answer_text: "A shorter paragraph with the same order.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q013_b",
          question_id: "station3_q013",
          answer_text: "A final decision on what matters.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q013_c",
          question_id: "station3_q013",
          answer_text: "No AI help because the note is messy.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q013_d",
          question_id: "station3_q013",
          answer_text: "A table of topics, status and open items.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station3_q014",
      station_id: "station_3",
      stage: 1,
      complexity: 2,
      core_concept:
        "A good first use case has a reviewable output and low consequence if adjusted",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You have two draft documents and need to see what changed. What should you do first?",
          options: {
            A: "Ask AI to make both drafts shorter.",
            B: "Ask AI to compare the main differences.",
            C: "Let AI choose the final document.",
            D: "Avoid AI because drafts are unfinished.",
          },
          correct_answer: "B",
          feedback:
            "Comparing draft differences is reviewable and useful. You still decide which changes matter.",
        },
        pt: {
          question:
            "Você tem dois documentos rascunho e precisa ver o que mudou. O que fazer primeiro?",
          options: {
            A: "Pedir à IA para encurtar os dois rascunhos.",
            B: "Pedir à IA para comparar diferenças principais.",
            C: "Deixar a IA escolher o documento final.",
            D: "Evitar IA porque são rascunhos.",
          },
          correct_answer: "B",
          feedback:
            "Comparar diferenças entre rascunhos é útil e revisável. Você ainda decide quais mudanças importam.",
        },
        sv: {
          question:
            "Du har två dokumentutkast och behöver se vad som ändrats. Vad gör du först?",
          options: {
            A: "Be AI göra båda utkasten kortare.",
            B: "Be AI jämföra de viktigaste skillnaderna.",
            C: "Låta AI välja slutligt dokument.",
            D: "Undvika AI eftersom de är utkast.",
          },
          correct_answer: "B",
          feedback:
            "Att jämföra skillnader mellan utkast är användbart och granskningsbart. Du avgör fortfarande vilka ändringar som spelar roll.",
        },
        nl: {
          question:
            "Je hebt twee conceptdocumenten en moet zien wat is veranderd. Wat doe je eerst?",
          options: {
            A: "AI vragen beide concepten korter te maken.",
            B: "AI de belangrijkste verschillen laten vergelijken.",
            C: "AI het definitieve document laten kiezen.",
            D: "AI vermijden omdat het concepten zijn.",
          },
          correct_answer: "B",
          feedback:
            "Verschillen tussen concepten vergelijken is nuttig en controleerbaar. Jij beslist nog steeds welke wijzigingen belangrijk zijn.",
        },
      },
      question_text:
        "You have two draft documents and need to see what changed. What should you do first?",
      explanation:
        "Comparing draft differences is reviewable and useful. You still decide which changes matter.",
      answers: [
        {
          id: "station3_q014_a",
          question_id: "station3_q014",
          answer_text: "Ask AI to make both drafts shorter.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q014_b",
          question_id: "station3_q014",
          answer_text: "Ask AI to compare the main differences.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station3_q014_c",
          question_id: "station3_q014",
          answer_text: "Let AI choose the final document.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q014_d",
          question_id: "station3_q014",
          answer_text: "Avoid AI because drafts are unfinished.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q015",
      station_id: "station_3",
      stage: 1,
      complexity: 2,
      core_concept:
        "Poor first use cases are unclear, high-impact or hard to review",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A beginner wants AI to choose between two costly project options with unclear inputs. What is the main risk?",
          options: {
            A: "The answer may use too many bullets.",
            B: "The wording may not fit the audience.",
            C: "The task is high-impact and hard to review.",
            D: "The process may stay too manual.",
          },
          correct_answer: "C",
          feedback:
            "Good first use cases are low-risk and easy to review. A costly decision with unclear inputs is not one.",
        },
        pt: {
          question:
            "Um iniciante quer que a IA escolha entre duas opções caras de projeto com inputs incertos. Qual é o principal risco?",
          options: {
            A: "A resposta pode usar muitos bullets.",
            B: "O texto pode não servir ao público.",
            C: "A tarefa tem alto impacto e difícil revisão.",
            D: "O processo pode continuar manual demais.",
          },
          correct_answer: "C",
          feedback:
            "Bons primeiros usos são de baixo risco e fáceis de revisar. Uma decisão cara com inputs incertos não é.",
        },
        sv: {
          question:
            "En nybörjare vill att AI väljer mellan två dyra projektalternativ med oklara input. Vad är huvudrisken?",
          options: {
            A: "Svaret kan använda för många punkter.",
            B: "Formuleringen kanske inte passar publiken.",
            C: "Uppgiften har hög påverkan och svår granskning.",
            D: "Processen kan förbli för manuell.",
          },
          correct_answer: "C",
          feedback:
            "Bra första användningsfall är lågrisk och lätta att granska. Ett dyrt beslut med oklara input är inte det.",
        },
        nl: {
          question:
            "Een beginner wil AI laten kiezen tussen twee dure projectopties met onduidelijke input. Wat is het grootste risico?",
          options: {
            A: "Het antwoord gebruikt misschien te veel bullets.",
            B: "De formulering past niet bij het publiek.",
            C: "De taak heeft hoge impact en is lastig controleerbaar.",
            D: "Het proces blijft misschien te handmatig.",
          },
          correct_answer: "C",
          feedback:
            "Goede eerste toepassingen zijn laag risico en makkelijk te controleren. Een dure beslissing met onduidelijke input is dat niet.",
        },
      },
      question_text:
        "A beginner wants AI to choose between two costly project options with unclear inputs. What is the main risk?",
      explanation:
        "Good first use cases are low-risk and easy to review. A costly decision with unclear inputs is not one.",
      answers: [
        {
          id: "station3_q015_a",
          question_id: "station3_q015",
          answer_text: "The answer may use too many bullets.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q015_b",
          question_id: "station3_q015",
          answer_text: "The wording may not fit the audience.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q015_c",
          question_id: "station3_q015",
          answer_text: "The task is high-impact and hard to review.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station3_q015_d",
          question_id: "station3_q015",
          answer_text: "The process may stay too manual.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q016",
      station_id: "station_3",
      stage: 1,
      complexity: 2,
      core_concept: "AI can help rewrite routine text for a specific audience",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A routine update sounds too technical for a broad audience. What is the safest next step?",
          options: {
            A: "Ask AI for a clearer version to review.",
            B: "Keep the technical wording unchanged.",
            C: "Send the AI rewrite without reading it.",
            D: "Avoid AI for any audience adjustment.",
          },
          correct_answer: "A",
          feedback:
            "Rewriting for audience is a useful everyday use case when the result is reviewed before sending.",
        },
        pt: {
          question:
            "Uma atualização rotineira soa técnica demais para um público amplo. Qual próximo passo é mais seguro?",
          options: {
            A: "Pedir uma versão mais clara para revisar.",
            B: "Manter o texto técnico sem mudança.",
            C: "Enviar a reescrita da IA sem ler.",
            D: "Evitar IA para qualquer ajuste de público.",
          },
          correct_answer: "A",
          feedback:
            "Reescrever para o público é um uso cotidiano útil quando o resultado é revisado antes do envio.",
        },
        sv: {
          question:
            "En rutinuppdatering låter för teknisk för en bred publik. Vad är säkrast nästa steg?",
          options: {
            A: "Be om en tydligare version att granska.",
            B: "Behålla den tekniska formuleringen oförändrad.",
            C: "Skicka AI-omskrivningen utan att läsa.",
            D: "Undvika AI för all publikanpassning.",
          },
          correct_answer: "A",
          feedback:
            "Att skriva om för publiken är ett användbart vardagsfall när resultatet granskas före utskick.",
        },
        nl: {
          question:
            "Een routine-update klinkt te technisch voor een breed publiek. Wat is de veiligste volgende stap?",
          options: {
            A: "Om een duidelijkere versie vragen om te controleren.",
            B: "De technische formulering onveranderd houden.",
            C: "De AI-herschrijving sturen zonder te lezen.",
            D: "AI vermijden voor elke publieksaanpassing.",
          },
          correct_answer: "A",
          feedback:
            "Herschrijven voor het publiek is een nuttige dagelijkse toepassing wanneer het resultaat vóór verzending wordt gecontroleerd.",
        },
      },
      question_text:
        "A routine update sounds too technical for a broad audience. What is the safest next step?",
      explanation:
        "Rewriting for audience is a useful everyday use case when the result is reviewed before sending.",
      answers: [
        {
          id: "station3_q016_a",
          question_id: "station3_q016",
          answer_text: "Ask AI for a clearer version to review.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station3_q016_b",
          question_id: "station3_q016",
          answer_text: "Keep the technical wording unchanged.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q016_c",
          question_id: "station3_q016",
          answer_text: "Send the AI rewrite without reading it.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q016_d",
          question_id: "station3_q016",
          answer_text: "Avoid AI for any audience adjustment.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q017",
      station_id: "station_3",
      stage: 2,
      complexity: 2,
      core_concept:
        "The best AI support depends on whether the user needs a summary or extracted actions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A long email thread contains requests, owners and due dates. Which AI output fits the task best?",
          options: {
            A: "A general summary of the thread.",
            B: "A final priority decision from AI.",
            C: "A friendlier version of the emails.",
            D: "A list of requests, owners and due dates.",
          },
          correct_answer: "D",
          feedback:
            "When follow-up matters, extracting actions, owners and due dates is more useful than a broad summary.",
        },
        pt: {
          question:
            "Uma longa troca de emails contém pedidos, responsáveis e prazos. Qual saída da IA combina melhor?",
          options: {
            A: "Um resumo geral da troca.",
            B: "Uma decisão final de prioridade pela IA.",
            C: "Uma versão mais amigável dos emails.",
            D: "Uma lista de pedidos, responsáveis e prazos.",
          },
          correct_answer: "D",
          feedback:
            "Quando o follow-up importa, extrair ações, responsáveis e prazos é mais útil que um resumo amplo.",
        },
        sv: {
          question:
            "En lång mejltråd innehåller begäranden, ägare och deadlines. Vilket AI-resultat passar uppgiften bäst?",
          options: {
            A: "En allmän sammanfattning av tråden.",
            B: "Ett slutligt prioriteringsbeslut från AI.",
            C: "En vänligare version av mejlen.",
            D: "En lista med begäranden, ägare och deadlines.",
          },
          correct_answer: "D",
          feedback:
            "När uppföljning är viktig är åtgärder, ägare och deadlines mer användbart än en bred sammanfattning.",
        },
        nl: {
          question:
            "Een lange mailthread bevat verzoeken, eigenaars en deadlines. Welke AI-output past het best?",
          options: {
            A: "Een algemene samenvatting van de thread.",
            B: "Een definitieve prioriteitskeuze door AI.",
            C: "Een vriendelijkere versie van de mails.",
            D: "Een lijst met verzoeken, eigenaars en deadlines.",
          },
          correct_answer: "D",
          feedback:
            "Wanneer opvolging belangrijk is, zijn acties, eigenaars en deadlines nuttiger dan een brede samenvatting.",
        },
      },
      question_text:
        "A long email thread contains requests, owners and due dates. Which AI output fits the task best?",
      explanation:
        "When follow-up matters, extracting actions, owners and due dates is more useful than a broad summary.",
      answers: [
        {
          id: "station3_q017_a",
          question_id: "station3_q017",
          answer_text: "A general summary of the thread.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q017_b",
          question_id: "station3_q017",
          answer_text: "A final priority decision from AI.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q017_c",
          question_id: "station3_q017",
          answer_text: "A friendlier version of the emails.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q017_d",
          question_id: "station3_q017",
          answer_text: "A list of requests, owners and due dates.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station3_q018",
      station_id: "station_3",
      stage: 2,
      complexity: 2,
      core_concept:
        "AI can help prepare interview or discussion questions from material",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You read a project brief and need to prepare for discussion. What should you ask AI to do next?",
          options: {
            A: "Make the brief shorter only.",
            B: "Suggest questions and unclear points.",
            C: "Decide the discussion conclusion.",
            D: "Avoid AI because the brief is important.",
          },
          correct_answer: "B",
          feedback:
            "This is a practical meeting-preparation use case: AI surfaces questions and unclear points without deciding the outcome.",
        },
        pt: {
          question:
            "Você leu um brief de projeto e precisa se preparar para discussão. O que pedir à IA em seguida?",
          options: {
            A: "Apenas encurtar o brief.",
            B: "Sugerir perguntas e pontos incertos.",
            C: "Decidir a conclusão da discussão.",
            D: "Evitar IA porque o brief é importante.",
          },
          correct_answer: "B",
          feedback:
            "Este é um uso prático de preparação: a IA mostra perguntas e pontos incertos sem decidir o resultado.",
        },
        sv: {
          question:
            "Du har läst en projektbrief och behöver förbereda diskussion. Vad bör du be AI göra härnäst?",
          options: {
            A: "Bara göra briefen kortare.",
            B: "Föreslå frågor och oklara punkter.",
            C: "Besluta diskussionens slutsats.",
            D: "Undvika AI eftersom briefen är viktig.",
          },
          correct_answer: "B",
          feedback:
            "Detta är ett praktiskt förberedelsefall: AI visar frågor och oklara punkter utan att besluta resultatet.",
        },
        nl: {
          question:
            "Je hebt een projectbrief gelezen en moet een discussie voorbereiden. Wat vraag je AI daarna?",
          options: {
            A: "De brief alleen korter maken.",
            B: "Vragen en onduidelijke punten voorstellen.",
            C: "De conclusie van de discussie bepalen.",
            D: "AI vermijden omdat de brief belangrijk is.",
          },
          correct_answer: "B",
          feedback:
            "Dit is een praktische voorbereidingstoepassing: AI toont vragen en onduidelijke punten zonder de uitkomst te bepalen.",
        },
      },
      question_text:
        "You read a project brief and need to prepare for discussion. What should you ask AI to do next?",
      explanation:
        "This is a practical meeting-preparation use case: AI surfaces questions and unclear points without deciding the outcome.",
      answers: [
        {
          id: "station3_q018_a",
          question_id: "station3_q018",
          answer_text: "Make the brief shorter only.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q018_b",
          question_id: "station3_q018",
          answer_text: "Suggest questions and unclear points.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station3_q018_c",
          question_id: "station3_q018",
          answer_text: "Decide the discussion conclusion.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q018_d",
          question_id: "station3_q018",
          answer_text: "Avoid AI because the brief is important.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q019",
      station_id: "station_3",
      stage: 2,
      complexity: 2,
      core_concept: "AI can convert unstructured notes into a useful format",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A process note mixes steps, exceptions and tips. Which AI output would improve the workflow most?",
          options: {
            A: "A shorter note with fewer words.",
            B: "A final process decided by AI.",
            C: "A checklist with steps and exceptions.",
            D: "No AI help because the note is informal.",
          },
          correct_answer: "C",
          feedback:
            "AI can turn mixed notes into a reviewable checklist. The team still confirms the steps and exceptions.",
        },
        pt: {
          question:
            "Uma nota de processo mistura passos, exceções e dicas. Qual saída da IA melhoraria mais o fluxo?",
          options: {
            A: "Uma nota mais curta com menos palavras.",
            B: "Um processo final decidido pela IA.",
            C: "Uma checklist com passos e exceções.",
            D: "Sem IA porque a nota é informal.",
          },
          correct_answer: "C",
          feedback:
            "A IA pode transformar notas misturadas em checklist revisável. A equipe ainda confirma passos e exceções.",
        },
        sv: {
          question:
            "En processnot blandar steg, undantag och tips. Vilket AI-resultat skulle förbättra flödet mest?",
          options: {
            A: "En kortare not med färre ord.",
            B: "En slutlig process beslutad av AI.",
            C: "En checklista med steg och undantag.",
            D: "Ingen AI-hjälp eftersom noten är informell.",
          },
          correct_answer: "C",
          feedback:
            "AI kan göra blandade anteckningar till en granskningsbar checklista. Teamet bekräftar fortfarande steg och undantag.",
        },
        nl: {
          question:
            "Een procesnotitie mengt stappen, uitzonderingen en tips. Welke AI-output verbetert de workflow het meest?",
          options: {
            A: "Een kortere notitie met minder woorden.",
            B: "Een definitief proces beslist door AI.",
            C: "Een checklist met stappen en uitzonderingen.",
            D: "Geen AI-hulp omdat de notitie informeel is.",
          },
          correct_answer: "C",
          feedback:
            "AI kan gemengde notities omzetten in een controleerbare checklist. Het team bevestigt nog steeds stappen en uitzonderingen.",
        },
      },
      question_text:
        "A process note mixes steps, exceptions and tips. Which AI output would improve the workflow most?",
      explanation:
        "AI can turn mixed notes into a reviewable checklist. The team still confirms the steps and exceptions.",
      answers: [
        {
          id: "station3_q019_a",
          question_id: "station3_q019",
          answer_text: "A shorter note with fewer words.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q019_b",
          question_id: "station3_q019",
          answer_text: "A final process decided by AI.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q019_c",
          question_id: "station3_q019",
          answer_text: "A checklist with steps and exceptions.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station3_q019_d",
          question_id: "station3_q019",
          answer_text: "No AI help because the note is informal.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q020",
      station_id: "station_3",
      stage: 2,
      complexity: 3,
      core_concept:
        "AI support should match the output the user actually needs",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A policy paragraph must be understandable for new joiners. Which AI use fits the task best?",
          options: {
            A: "Create a plain-language version to compare.",
            B: "Remove all conditions to make it simple.",
            C: "Let AI publish the final explanation.",
            D: "Keep the original wording unchanged.",
          },
          correct_answer: "A",
          feedback:
            "AI can help adapt language for an audience. The comparison protects the original meaning.",
        },
        pt: {
          question:
            "Um parágrafo de política precisa ser entendido por novos colegas. Qual uso de IA combina melhor?",
          options: {
            A: "Criar versão simples para comparar.",
            B: "Remover todas as condições para simplificar.",
            C: "Deixar a IA publicar a explicação final.",
            D: "Manter o texto original sem mudança.",
          },
          correct_answer: "A",
          feedback:
            "A IA pode ajudar a adaptar linguagem para o público. A comparação protege o sentido original.",
        },
        sv: {
          question:
            "Ett policystycke behöver förstås av nya kollegor. Vilken AI-användning passar uppgiften bäst?",
          options: {
            A: "Skapa en enkel version att jämföra.",
            B: "Ta bort alla villkor för enkelhet.",
            C: "Låta AI publicera slutlig förklaring.",
            D: "Behålla originalformuleringen oförändrad.",
          },
          correct_answer: "A",
          feedback:
            "AI kan hjälpa till att anpassa språk för publiken. Jämförelsen skyddar den ursprungliga betydelsen.",
        },
        nl: {
          question:
            "Een beleidsalinea moet begrijpelijk zijn voor nieuwe collega’s. Welke AI-toepassing past het best?",
          options: {
            A: "Een eenvoudige versie maken om te vergelijken.",
            B: "Alle voorwaarden verwijderen voor eenvoud.",
            C: "AI de definitieve uitleg laten publiceren.",
            D: "De oorspronkelijke tekst onveranderd houden.",
          },
          correct_answer: "A",
          feedback:
            "AI kan taal aanpassen voor het publiek. De vergelijking beschermt de oorspronkelijke betekenis.",
        },
      },
      question_text:
        "A policy paragraph must be understandable for new joiners. Which AI use fits the task best?",
      explanation:
        "AI can help adapt language for an audience. The comparison protects the original meaning.",
      answers: [
        {
          id: "station3_q020_a",
          question_id: "station3_q020",
          answer_text: "Create a plain-language version to compare.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station3_q020_b",
          question_id: "station3_q020",
          answer_text: "Remove all conditions to make it simple.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q020_c",
          question_id: "station3_q020",
          answer_text: "Let AI publish the final explanation.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q020_d",
          question_id: "station3_q020",
          answer_text: "Keep the original wording unchanged.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q021",
      station_id: "station_3",
      stage: 2,
      complexity: 3,
      core_concept:
        "AI can support comparison without choosing the final recommendation",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Two approaches look similar, and you need to brief your lead. What is the safest next step?",
          options: {
            A: "Ask AI to choose the recommendation.",
            B: "Avoid AI because the choice matters.",
            C: "Ask only for the shorter approach.",
            D: "Ask AI to compare trade-offs for review.",
          },
          correct_answer: "D",
          feedback:
            "This is a practical comparison use case: AI prepares trade-offs, while you decide what to recommend.",
        },
        pt: {
          question:
            "Duas abordagens parecem parecidas, e você precisa informar seu líder. Qual próximo passo é mais seguro?",
          options: {
            A: "Pedir à IA que escolha a recomendação.",
            B: "Evitar IA porque a escolha importa.",
            C: "Pedir apenas a abordagem mais curta.",
            D: "Pedir comparação de trade-offs para revisar.",
          },
          correct_answer: "D",
          feedback:
            "Este é um uso prático de comparação: a IA prepara trade-offs, enquanto você decide o que recomendar.",
        },
        sv: {
          question:
            "Två arbetssätt verkar lika, och du behöver briefa din chef. Vad är säkrast nästa steg?",
          options: {
            A: "Be AI välja rekommendationen.",
            B: "Undvika AI eftersom valet spelar roll.",
            C: "Be bara om det kortare arbetssättet.",
            D: "Be AI jämföra avvägningar för granskning.",
          },
          correct_answer: "D",
          feedback:
            "Detta är ett praktiskt jämförelsefall: AI förbereder avvägningar medan du beslutar rekommendationen.",
        },
        nl: {
          question:
            "Twee aanpakken lijken op elkaar, en je moet je lead briefen. Wat is de veiligste volgende stap?",
          options: {
            A: "AI de aanbeveling laten kiezen.",
            B: "AI vermijden omdat de keuze ertoe doet.",
            C: "Alleen om de kortere aanpak vragen.",
            D: "AI trade-offs laten vergelijken voor controle.",
          },
          correct_answer: "D",
          feedback:
            "Dit is een praktische vergelijkingstoepassing: AI bereidt trade-offs voor terwijl jij de aanbeveling kiest.",
        },
      },
      question_text:
        "Two approaches look similar, and you need to brief your lead. What is the safest next step?",
      explanation:
        "This is a practical comparison use case: AI prepares trade-offs, while you decide what to recommend.",
      answers: [
        {
          id: "station3_q021_a",
          question_id: "station3_q021",
          answer_text: "Ask AI to choose the recommendation.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q021_b",
          question_id: "station3_q021",
          answer_text: "Avoid AI because the choice matters.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q021_c",
          question_id: "station3_q021",
          answer_text: "Ask only for the shorter approach.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q021_d",
          question_id: "station3_q021",
          answer_text: "Ask AI to compare trade-offs for review.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station3_q022",
      station_id: "station_3",
      stage: 2,
      complexity: 3,
      core_concept:
        "AI can classify or group work items when the grouping is reviewable",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You receive many internal requests and need to triage them. How should you treat AI grouping?",
          options: {
            A: "As final categories for immediate action.",
            B: "As a first grouping to review.",
            C: "As useless because some requests are unclear.",
            D: "As only a way to rewrite requests.",
          },
          correct_answer: "B",
          feedback:
            "Grouping requests is useful for triage when the categories remain reviewable before action.",
        },
        pt: {
          question:
            "Você recebe muitos pedidos internos e precisa fazer triagem. Como tratar o agrupamento da IA?",
          options: {
            A: "Como categorias finais para ação imediata.",
            B: "Como primeiro agrupamento para revisar.",
            C: "Como inútil porque alguns pedidos são incertos.",
            D: "Apenas como forma de reescrever pedidos.",
          },
          correct_answer: "B",
          feedback:
            "Agrupar pedidos é útil para triagem quando as categorias continuam revisáveis antes da ação.",
        },
        sv: {
          question:
            "Du får många interna förfrågningar och behöver triagera dem. Hur bör du behandla AI-gruppering?",
          options: {
            A: "Som slutliga kategorier för omedelbar åtgärd.",
            B: "Som en första gruppering att granska.",
            C: "Som värdelös eftersom vissa förfrågningar är oklara.",
            D: "Bara som ett sätt att skriva om förfrågningar.",
          },
          correct_answer: "B",
          feedback:
            "Att gruppera förfrågningar är användbart för triage när kategorierna kan granskas före åtgärd.",
        },
        nl: {
          question:
            "Je ontvangt veel interne verzoeken en moet ze triëren. Hoe behandel je AI-groepering?",
          options: {
            A: "Als definitieve categorieën voor directe actie.",
            B: "Als een eerste groepering om te controleren.",
            C: "Als nutteloos omdat sommige verzoeken onduidelijk zijn.",
            D: "Alleen als manier om verzoeken te herschrijven.",
          },
          correct_answer: "B",
          feedback:
            "Verzoeken groeperen is nuttig voor triage wanneer categorieën vóór actie controleerbaar blijven.",
        },
      },
      question_text:
        "You receive many internal requests and need to triage them. How should you treat AI grouping?",
      explanation:
        "Grouping requests is useful for triage when the categories remain reviewable before action.",
      answers: [
        {
          id: "station3_q022_a",
          question_id: "station3_q022",
          answer_text: "As final categories for immediate action.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q022_b",
          question_id: "station3_q022",
          answer_text: "As a first grouping to review.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station3_q022_c",
          question_id: "station3_q022",
          answer_text: "As useless because some requests are unclear.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q022_d",
          question_id: "station3_q022",
          answer_text: "As only a way to rewrite requests.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q023",
      station_id: "station_3",
      stage: 2,
      complexity: 3,
      core_concept:
        "AI can prepare a useful first version but not replace context-aware communication",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A senior leader needs a short version of a detailed update. Which option shows the best AI use?",
          options: {
            A: "Keep the detailed version unchanged.",
            B: "Let AI send the short version.",
            C: "Draft a concise version, then review emphasis.",
            D: "Remove all caveats to make it simpler.",
          },
          correct_answer: "C",
          feedback:
            "AI can help tailor length and tone, but you should review what the shorter version emphasizes.",
        },
        pt: {
          question:
            "Uma liderança precisa de versão curta de uma atualização detalhada. Qual opção mostra melhor uso de IA?",
          options: {
            A: "Manter a versão detalhada sem mudança.",
            B: "Deixar a IA enviar a versão curta.",
            C: "Rascunhar versão concisa e revisar ênfase.",
            D: "Remover ressalvas para simplificar.",
          },
          correct_answer: "C",
          feedback:
            "A IA pode ajustar tamanho e tom, mas você deve revisar o que a versão curta enfatiza.",
        },
        sv: {
          question:
            "En senior ledare behöver en kort version av en detaljerad uppdatering. Vilket alternativ visar bäst AI-användning?",
          options: {
            A: "Behålla den detaljerade versionen oförändrad.",
            B: "Låta AI skicka den korta versionen.",
            C: "Skapa kort utkast och granska betoning.",
            D: "Ta bort alla förbehåll för enkelhet.",
          },
          correct_answer: "C",
          feedback:
            "AI kan hjälpa med längd och ton, men du bör granska vad den korta versionen betonar.",
        },
        nl: {
          question:
            "Een senior leider heeft een korte versie van een gedetailleerde update nodig. Welke optie toont het beste AI-gebruik?",
          options: {
            A: "De gedetailleerde versie onveranderd houden.",
            B: "AI de korte versie laten sturen.",
            C: "Een beknopte versie maken en nadruk controleren.",
            D: "Alle nuances verwijderen voor eenvoud.",
          },
          correct_answer: "C",
          feedback:
            "AI kan helpen met lengte en toon, maar je moet controleren wat de korte versie benadrukt.",
        },
      },
      question_text:
        "A senior leader needs a short version of a detailed update. Which option shows the best AI use?",
      explanation:
        "AI can help tailor length and tone, but you should review what the shorter version emphasizes.",
      answers: [
        {
          id: "station3_q023_a",
          question_id: "station3_q023",
          answer_text: "Keep the detailed version unchanged.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q023_b",
          question_id: "station3_q023",
          answer_text: "Let AI send the short version.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q023_c",
          question_id: "station3_q023",
          answer_text: "Draft a concise version, then review emphasis.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station3_q023_d",
          question_id: "station3_q023",
          answer_text: "Remove all caveats to make it simpler.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q024",
      station_id: "station_3",
      stage: 3,
      complexity: 3,
      core_concept:
        "AI can prepare options under time pressure when the user owns the choice",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You must prepare a quick discussion plan from scattered notes. Time is short. What should you do first?",
          options: {
            A: "Ask AI for options and questions to review.",
            B: "Ask AI for a short summary first.",
            C: "Avoid AI because the notes are scattered.",
            D: "Ask only for a shorter note.",
          },
          correct_answer: "A",
          feedback:
            "This is a useful preparation use case: AI drafts options and questions. A summary alone may not support the discussion plan enough.",
        },
        pt: {
          question:
            "Você precisa preparar um plano rápido de discussão com notas espalhadas. O tempo é curto. O que fazer primeiro?",
          options: {
            A: "Pedir opções e perguntas para revisar.",
            B: "Pedir primeiro um resumo curto.",
            C: "Evitar IA porque as notas estão espalhadas.",
            D: "Pedir apenas uma nota mais curta.",
          },
          correct_answer: "A",
          feedback:
            "Este é um uso útil de preparação: a IA rascunha opções e perguntas. Só um resumo pode não apoiar suficientemente a discussão.",
        },
        sv: {
          question:
            "Du måste snabbt förbereda en diskussionsplan från spridda anteckningar. Tiden är kort. Vad gör du först?",
          options: {
            A: "Be AI om alternativ och frågor att granska.",
            B: "Be först om en kort sammanfattning.",
            C: "Undvika AI eftersom noterna är spridda.",
            D: "Be bara om en kortare not.",
          },
          correct_answer: "A",
          feedback:
            "Detta är ett användbart förberedelsefall: AI skapar alternativ och frågor. En sammanfattning räcker kanske inte för diskussionen.",
        },
        nl: {
          question:
            "Je moet snel een discussieplan maken uit verspreide notities. De tijd is kort. Wat doe je eerst?",
          options: {
            A: "AI om opties en vragen vragen om te controleren.",
            B: "Eerst om een korte samenvatting vragen.",
            C: "AI vermijden omdat notities verspreid zijn.",
            D: "Alleen om een kortere notitie vragen.",
          },
          correct_answer: "A",
          feedback:
            "Dit is een nuttige voorbereidingstoepassing: AI maakt opties en vragen. Alleen een samenvatting ondersteunt de discussie mogelijk onvoldoende.",
        },
      },
      question_text:
        "You must prepare a quick discussion plan from scattered notes. Time is short. What should you do first?",
      explanation:
        "This is a useful preparation use case: AI drafts options and questions. A summary alone may not support the discussion plan enough.",
      answers: [
        {
          id: "station3_q024_a",
          question_id: "station3_q024",
          answer_text: "Ask AI for options and questions to review.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station3_q024_b",
          question_id: "station3_q024",
          answer_text: "Ask AI for a short summary first.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q024_c",
          question_id: "station3_q024",
          answer_text: "Avoid AI because the notes are scattered.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q024_d",
          question_id: "station3_q024",
          answer_text: "Ask only for a shorter note.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q025",
      station_id: "station_3",
      stage: 3,
      complexity: 3,
      core_concept:
        "AI is useful for creating a draft when facts are known and review is possible",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Facts are confirmed, but a project update needs clear wording for several audiences. How should you treat AI help?",
          options: {
            A: "Use the AI draft as final.",
            B: "Avoid AI because audiences differ.",
            C: "Change only the tone for everyone.",
            D: "Draft versions, then review fit and accuracy.",
          },
          correct_answer: "D",
          feedback:
            "AI can draft audience versions when facts are known. Review still decides what fits and stays accurate.",
        },
        pt: {
          question:
            "Os fatos estão confirmados, mas um status de projeto precisa de clareza para vários públicos. Como tratar a ajuda da IA?",
          options: {
            A: "Usar o rascunho da IA como final.",
            B: "Evitar IA porque públicos diferem.",
            C: "Mudar apenas o tom para todos.",
            D: "Rascunhar versões e revisar adequação e precisão.",
          },
          correct_answer: "D",
          feedback:
            "A IA pode rascunhar versões por público quando fatos são conhecidos. A revisão decide adequação e precisão.",
        },
        sv: {
          question:
            "Fakta är bekräftade, men en projektstatus behöver tydligt språk för flera publiker. Hur bör du behandla AI-hjälp?",
          options: {
            A: "Använda AI-utkastet som slutversion.",
            B: "Undvika AI eftersom publiker skiljer sig.",
            C: "Bara ändra tonen för alla.",
            D: "Skapa versioner och granska passform och korrekthet.",
          },
          correct_answer: "D",
          feedback:
            "AI kan skapa publikversioner när fakta är kända. Granskning avgör fortfarande passform och korrekthet.",
        },
        nl: {
          question:
            "Feiten zijn bevestigd, maar een projectupdate moet helder zijn voor meerdere doelgroepen. Hoe behandel je AI-hulp?",
          options: {
            A: "Het AI-concept als definitief gebruiken.",
            B: "AI vermijden omdat doelgroepen verschillen.",
            C: "Alleen de toon voor iedereen wijzigen.",
            D: "Versies maken en fit en juistheid controleren.",
          },
          correct_answer: "D",
          feedback:
            "AI kan versies per doelgroep opstellen wanneer feiten bekend zijn. Controle bepaalt nog steeds fit en juistheid.",
        },
      },
      question_text:
        "Facts are confirmed, but a project update needs clear wording for several audiences. How should you treat AI help?",
      explanation:
        "AI can draft audience versions when facts are known. Review still decides what fits and stays accurate.",
      answers: [
        {
          id: "station3_q025_a",
          question_id: "station3_q025",
          answer_text: "Use the AI draft as final.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q025_b",
          question_id: "station3_q025",
          answer_text: "Avoid AI because audiences differ.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q025_c",
          question_id: "station3_q025",
          answer_text: "Change only the tone for everyone.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q025_d",
          question_id: "station3_q025",
          answer_text: "Draft versions, then review fit and accuracy.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station3_q026",
      station_id: "station_3",
      stage: 3,
      complexity: 4,
      core_concept:
        "Mature everyday use recognises when a useful AI task is not a good first task",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A long document looks easy to summarise, but you are unsure whether it is allowed for a first AI trial. What is the main use-case risk?",
          options: {
            A: "The summary may omit the document purpose.",
            B: "The document may contain restricted content.",
            C: "The task may take too long to review.",
            D: "The output may need a clearer format.",
          },
          correct_answer: "B",
          feedback:
            "Summarising is useful, but a first AI trial needs content you know is allowed and appropriate.",
        },
        pt: {
          question:
            "Um documento longo parece fácil de resumir, mas você não sabe se ele é permitido para um primeiro teste com IA. Qual é o principal risco?",
          options: {
            A: "O resumo pode omitir o objetivo do documento.",
            B: "O documento pode conter conteúdo restrito.",
            C: "A tarefa pode demorar para revisar.",
            D: "A saída pode precisar de formato mais claro.",
          },
          correct_answer: "B",
          feedback:
            "Resumir é útil, mas um primeiro teste com IA precisa usar conteúdo permitido e apropriado.",
        },
        sv: {
          question:
            "Ett långt dokument verkar lätt att sammanfatta, men du vet inte om det passar ett första AI-test. Vad är huvudrisken?",
          options: {
            A: "Sammanfattningen kan missa dokumentets syfte.",
            B: "Dokumentet kan innehålla begränsat innehåll.",
            C: "Uppgiften kan ta lång tid att granska.",
            D: "Resultatet kan behöva tydligare format.",
          },
          correct_answer: "B",
          feedback:
            "Sammanfattning är användbart, men ett första AI-test behöver innehåll som du vet är tillåtet och lämpligt.",
        },
        nl: {
          question:
            "Een lang document lijkt makkelijk samen te vatten, maar je weet niet of het mag voor een eerste AI-test. Wat is het grootste risico?",
          options: {
            A: "De samenvatting kan het doel missen.",
            B: "Het document kan beperkte inhoud bevatten.",
            C: "De taak kan lang duren om te beoordelen.",
            D: "De output heeft mogelijk duidelijker vorm nodig.",
          },
          correct_answer: "B",
          feedback:
            "Samenvatten is nuttig, maar een eerste AI-test vraagt inhoud waarvan je weet dat die toegestaan en passend is.",
        },
      },
      question_text:
        "A long document looks easy to summarise, but you are unsure whether it is allowed for a first AI trial. What is the main use-case risk?",
      explanation:
        "Summarising is useful, but a first AI trial needs content you know is allowed and appropriate.",
      answers: [
        {
          id: "station3_q026_a",
          question_id: "station3_q026",
          answer_text: "The summary may omit the document purpose.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q026_b",
          question_id: "station3_q026",
          answer_text: "The document may contain restricted content.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station3_q026_c",
          question_id: "station3_q026",
          answer_text: "The task may take too long to review.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q026_d",
          question_id: "station3_q026",
          answer_text: "The output may need a clearer format.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q027",
      station_id: "station_3",
      stage: 3,
      complexity: 4,
      core_concept:
        "AI can help structure competing inputs, but the team must align on the final view",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Three teams give different updates for the same topic. AI can structure the differences. What is the safest next step?",
          options: {
            A: "Let AI decide which team is right.",
            B: "Create one story and skip alignment.",
            C: "Use the comparison to align with teams.",
            D: "Avoid AI because inputs conflict.",
          },
          correct_answer: "C",
          feedback:
            "AI can make differences visible. The useful next step is team alignment before the result is used.",
        },
        pt: {
          question:
            "Três equipes dão atualizações diferentes sobre o mesmo tema. A IA pode estruturar diferenças. Qual próximo passo é mais seguro?",
          options: {
            A: "Deixar a IA decidir qual equipe está certa.",
            B: "Criar uma história única e pular alinhamento.",
            C: "Usar a comparação para alinhar equipes.",
            D: "Evitar IA porque inputs conflitam.",
          },
          correct_answer: "C",
          feedback:
            "A IA pode tornar diferenças visíveis. O próximo passo útil é alinhar equipes antes do uso.",
        },
        sv: {
          question:
            "Tre team ger olika uppdateringar om samma ämne. AI kan strukturera skillnaderna. Vad är säkrast nästa steg?",
          options: {
            A: "Låta AI avgöra vilket team som har rätt.",
            B: "Skapa en berättelse och hoppa över förankring.",
            C: "Använda jämförelsen för att förankra med team.",
            D: "Undvika AI eftersom inputen konfliktar.",
          },
          correct_answer: "C",
          feedback:
            "AI kan synliggöra skillnader. Nästa användbara steg är förankring med team innan resultatet används.",
        },
        nl: {
          question:
            "Drie teams geven verschillende updates over hetzelfde onderwerp. AI kan verschillen structureren. Wat is de veiligste volgende stap?",
          options: {
            A: "AI laten bepalen welk team gelijk heeft.",
            B: "Eén verhaal maken en afstemming overslaan.",
            C: "De vergelijking gebruiken voor teamafstemming.",
            D: "AI vermijden omdat input botst.",
          },
          correct_answer: "C",
          feedback:
            "AI kan verschillen zichtbaar maken. De nuttige volgende stap is afstemming met teams vóór gebruik.",
        },
      },
      question_text:
        "Three teams give different updates for the same topic. AI can structure the differences. What is the safest next step?",
      explanation:
        "AI can make differences visible. The useful next step is team alignment before the result is used.",
      answers: [
        {
          id: "station3_q027_a",
          question_id: "station3_q027",
          answer_text: "Let AI decide which team is right.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q027_b",
          question_id: "station3_q027",
          answer_text: "Create one story and skip alignment.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q027_c",
          question_id: "station3_q027",
          answer_text: "Use the comparison to align with teams.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station3_q027_d",
          question_id: "station3_q027",
          answer_text: "Avoid AI because inputs conflict.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q028",
      station_id: "station_3",
      stage: 3,
      complexity: 4,
      core_concept:
        "AI can support prioritisation preparation without owning the priority decision",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You have many requests with mixed urgency. AI could group them quickly. Which option shows the best use?",
          options: {
            A: "Group requests by urgency for review.",
            B: "Let AI set the final priorities.",
            C: "Avoid AI because urgency matters.",
            D: "Make every request shorter first.",
          },
          correct_answer: "A",
          feedback:
            "AI can prepare useful urgency groups. People still confirm priorities before acting.",
        },
        pt: {
          question:
            "Você tem muitos pedidos com urgência variada. A IA poderia agrupá-los rápido. Qual opção mostra melhor uso?",
          options: {
            A: "Agrupar pedidos por urgência para revisar.",
            B: "Deixar a IA definir prioridades finais.",
            C: "Evitar IA porque urgência importa.",
            D: "Encurtar todos os pedidos primeiro.",
          },
          correct_answer: "A",
          feedback:
            "A IA pode preparar grupos úteis por urgência. Pessoas ainda confirmam prioridades antes de agir.",
        },
        sv: {
          question:
            "Du har många förfrågningar med blandad brådska. AI kan gruppera dem snabbt. Vilket alternativ visar bäst användning?",
          options: {
            A: "Gruppera förfrågningar efter brådska för granskning.",
            B: "Låta AI sätta slutliga prioriteringar.",
            C: "Undvika AI eftersom brådska spelar roll.",
            D: "Göra varje förfrågan kortare först.",
          },
          correct_answer: "A",
          feedback:
            "AI kan förbereda användbara brådskandegrupper. Människor bekräftar fortfarande prioriteringar före åtgärd.",
        },
        nl: {
          question:
            "Je hebt veel verzoeken met gemengde urgentie. AI kan ze snel groeperen. Welke optie toont het beste gebruik?",
          options: {
            A: "Verzoeken op urgentie groeperen voor controle.",
            B: "AI de definitieve prioriteiten laten bepalen.",
            C: "AI vermijden omdat urgentie ertoe doet.",
            D: "Elk verzoek eerst korter maken.",
          },
          correct_answer: "A",
          feedback:
            "AI kan nuttige urgentiegroepen voorbereiden. Mensen bevestigen nog steeds prioriteiten vóór actie.",
        },
      },
      question_text:
        "You have many requests with mixed urgency. AI could group them quickly. Which option shows the best use?",
      explanation:
        "AI can prepare useful urgency groups. People still confirm priorities before acting.",
      answers: [
        {
          id: "station3_q028_a",
          question_id: "station3_q028",
          answer_text: "Group requests by urgency for review.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station3_q028_b",
          question_id: "station3_q028",
          answer_text: "Let AI set the final priorities.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q028_c",
          question_id: "station3_q028",
          answer_text: "Avoid AI because urgency matters.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q028_d",
          question_id: "station3_q028",
          answer_text: "Make every request shorter first.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station3_q029",
      station_id: "station_3",
      stage: 3,
      complexity: 5,
      core_concept:
        "Mature use balances useful AI preparation with risk from moderate uncertainty",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A quick risk review is needed, but the inputs include assumptions. AI can identify possible risks fast. What is the safest next step?",
          options: {
            A: "Use the AI risk list as final.",
            B: "Avoid AI because assumptions exist.",
            C: "Ask only for a cleaner risk table.",
            D: "Use the list, then challenge assumptions.",
          },
          correct_answer: "D",
          feedback:
            "This is a practical risk-preparation use case: AI speeds up the first list, while people challenge assumptions.",
        },
        pt: {
          question:
            "Uma revisão rápida de riscos é necessária, mas os inputs incluem premissas. A IA identifica riscos rápido. Qual próximo passo é mais seguro?",
          options: {
            A: "Usar a lista da IA como final.",
            B: "Evitar IA porque há premissas.",
            C: "Pedir apenas tabela de riscos mais limpa.",
            D: "Usar a lista e questionar premissas.",
          },
          correct_answer: "D",
          feedback:
            "Este é um uso prático de preparação de riscos: a IA acelera a primeira lista, e pessoas questionam premissas.",
        },
        sv: {
          question:
            "En snabb riskgenomgång behövs, men inputen innehåller antaganden. AI kan snabbt hitta möjliga risker. Vad är säkrast nästa steg?",
          options: {
            A: "Använda AI:s risklista som slutlig.",
            B: "Undvika AI eftersom antaganden finns.",
            C: "Be bara om en renare risktabell.",
            D: "Använda listan och utmana antaganden.",
          },
          correct_answer: "D",
          feedback:
            "Detta är ett praktiskt riskförberedelsefall: AI snabbar upp första listan, medan människor utmanar antaganden.",
        },
        nl: {
          question:
            "Een snelle risicoreview is nodig, maar de input bevat aannames. AI kan snel risico’s vinden. Wat is het veiligst?",
          options: {
            A: "De AI-risicolijst als definitief gebruiken.",
            B: "AI vermijden omdat er aannames zijn.",
            C: "Alleen om een schonere risicotabel vragen.",
            D: "De lijst gebruiken en aannames bevragen.",
          },
          correct_answer: "D",
          feedback:
            "Dit is een praktische risicovoorbereiding: AI versnelt de eerste lijst, terwijl mensen aannames bevragen.",
        },
      },
      question_text:
        "A quick risk review is needed, but the inputs include assumptions. AI can identify possible risks fast. What is the safest next step?",
      explanation:
        "This is a practical risk-preparation use case: AI speeds up the first list, while people challenge assumptions.",
      answers: [
        {
          id: "station3_q029_a",
          question_id: "station3_q029",
          answer_text: "Use the AI risk list as final.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q029_b",
          question_id: "station3_q029",
          answer_text: "Avoid AI because assumptions exist.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station3_q029_c",
          question_id: "station3_q029",
          answer_text: "Ask only for a cleaner risk table.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q029_d",
          question_id: "station3_q029",
          answer_text: "Use the list, then challenge assumptions.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station3_q030",
      station_id: "station_3",
      stage: 3,
      complexity: 5,
      core_concept:
        "AI can prepare a useful view when audiences conflict, but humans own trade-offs",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Two stakeholder groups need different levels of detail in the same update. AI can prepare versions. What should remain human-owned?",
          options: {
            A: "Drafting two versions for comparison.",
            B: "Choosing the trade-off between audiences.",
            C: "Letting AI decide who gets detail.",
            D: "Avoiding AI because needs differ.",
          },
          correct_answer: "B",
          feedback:
            "AI can prepare versions, but people must own the trade-off between audience needs.",
        },
        pt: {
          question:
            "Dois grupos precisam de níveis diferentes de detalhe na mesma atualização. A IA pode preparar versões. O que deve continuar humano?",
          options: {
            A: "Rascunhar duas versões para comparar.",
            B: "Escolher o trade-off entre públicos.",
            C: "Deixar a IA decidir quem recebe detalhe.",
            D: "Evitar IA porque necessidades diferem.",
          },
          correct_answer: "B",
          feedback:
            "A IA pode preparar versões, mas pessoas devem assumir o trade-off entre necessidades dos públicos.",
        },
        sv: {
          question:
            "Två intressentgrupper behöver olika detaljnivå i samma uppdatering. AI kan förbereda versioner. Vad ska förbli mänskligt ägt?",
          options: {
            A: "Skapa två versioner för jämförelse.",
            B: "Välja avvägningen mellan publiker.",
            C: "Låta AI besluta vem som får detalj.",
            D: "Undvika AI eftersom behoven skiljer sig.",
          },
          correct_answer: "B",
          feedback:
            "AI kan förbereda versioner, men människor måste äga avvägningen mellan publikernas behov.",
        },
        nl: {
          question:
            "Twee stakeholdergroepen hebben verschillende detailniveaus nodig in dezelfde update. AI kan versies voorbereiden. Wat blijft menselijk eigenaarschap?",
          options: {
            A: "Twee versies maken ter vergelijking.",
            B: "De trade-off tussen doelgroepen kiezen.",
            C: "AI laten beslissen wie details krijgt.",
            D: "AI vermijden omdat behoeften verschillen.",
          },
          correct_answer: "B",
          feedback:
            "AI kan versies voorbereiden, maar mensen moeten de trade-off tussen doelgroepen dragen.",
        },
      },
      question_text:
        "Two stakeholder groups need different levels of detail in the same update. AI can prepare versions. What should remain human-owned?",
      explanation:
        "AI can prepare versions, but people must own the trade-off between audience needs.",
      answers: [
        {
          id: "station3_q030_a",
          question_id: "station3_q030",
          answer_text: "Drafting two versions for comparison.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station3_q030_b",
          question_id: "station3_q030",
          answer_text: "Choosing the trade-off between audiences.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station3_q030_c",
          question_id: "station3_q030",
          answer_text: "Letting AI decide who gets detail.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station3_q030_d",
          question_id: "station3_q030",
          answer_text: "Avoiding AI because needs differ.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q001",
      station_id: "station_4",
      stage: 1,
      complexity: 1,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You want AI to summarise a long email thread before a meeting. Which prompt gives the clearest summary task?",
          options: {
            A: "Summarise this thread in any way.",
            B: "Make the thread shorter and faster to read.",
            C: "Summarise key decisions, open questions and owners.",
            D: "Tell me what the team should do next.",
          },
          correct_answer: "C",
          feedback:
            "This prompt names the output needed for the meeting: decisions, questions and owners. The others are vague or give AI too much authority.",
        },
        pt: {
          question:
            "Você quer que a IA resuma uma longa troca de e-mails antes de uma reunião. Qual prompt dá a tarefa mais clara?",
          options: {
            A: "Resuma esta troca de qualquer forma.",
            B: "Deixe a troca mais curta e rápida.",
            C: "Resuma decisões, dúvidas abertas e responsáveis.",
            D: "Diga o que a equipe deve fazer.",
          },
          correct_answer: "C",
          feedback:
            "Esse prompt define o resultado necessário para a reunião: decisões, dúvidas e responsáveis. Os outros são vagos ou dão autoridade demais.",
        },
        sv: {
          question:
            "Du vill att AI-verktyget sammanfattar en lång mejltråd inför ett möte. Vilken prompt ger tydligast uppgift?",
          options: {
            A: "Sammanfatta tråden på valfritt sätt.",
            B: "Gör tråden kortare och snabbare.",
            C: "Sammanfatta beslut, öppna frågor och ansvariga.",
            D: "Säg vad teamet ska göra.",
          },
          correct_answer: "C",
          feedback:
            "Prompten anger vad mötet behöver: beslut, frågor och ansvariga. De andra är vaga eller ger verktyget för mycket ansvar.",
        },
        nl: {
          question:
            "Je wilt een lange e-mailreeks laten samenvatten vóór een overleg. Welke prompt geeft de duidelijkste taak?",
          options: {
            A: "Vat deze reeks op elke manier samen.",
            B: "Maak de reeks korter en sneller.",
            C: "Vat besluiten, open vragen en eigenaars samen.",
            D: "Zeg wat het team moet doen.",
          },
          correct_answer: "C",
          feedback:
            "Deze prompt noemt de output voor het overleg: besluiten, vragen en eigenaars. De andere opties zijn vaag of geven te veel gezag.",
        },
      },
      question_text:
        "You want AI to summarise a long email thread before a meeting. Which prompt gives the clearest summary task?",
      explanation:
        "This prompt names the output needed for the meeting: decisions, questions and owners. The others are vague or give AI too much authority.",
      answers: [
        {
          id: "station4_q001_a",
          question_id: "station4_q001",
          answer_text: "Summarise this thread in any way.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q001_b",
          question_id: "station4_q001",
          answer_text: "Make the thread shorter and faster to read.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q001_c",
          question_id: "station4_q001",
          answer_text: "Summarise key decisions, open questions and owners.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station4_q001_d",
          question_id: "station4_q001",
          answer_text: "Tell me what the team should do next.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q002",
      station_id: "station_4",
      stage: 1,
      complexity: 1,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Rough meeting notes mix decisions, tasks and dates. Which prompt would help AI organise the follow-up?",
          options: {
            A: "Create a table with actions, owners and deadlines.",
            B: "Decide which tasks matter most for the team.",
            C: "Make these notes look cleaner and shorter.",
            D: "Read this and finish the follow-up quickly.",
          },
          correct_answer: "A",
          feedback:
            "The prompt asks for a useful structure: actions, owners and deadlines. It improves follow-up without asking AI to decide priorities.",
        },
        pt: {
          question:
            "Notas de reunião misturam decisões, tarefas e datas. Qual prompt ajudaria a IA a organizar o acompanhamento?",
          options: {
            A: "Crie uma tabela com ações, responsáveis e prazos.",
            B: "Decida quais tarefas importam mais para a equipe.",
            C: "Deixe estas notas mais limpas e curtas.",
            D: "Leia isto e finalize o acompanhamento rápido.",
          },
          correct_answer: "A",
          feedback:
            "O prompt pede uma estrutura útil: ações, responsáveis e prazos. Ele melhora o acompanhamento sem pedir que a IA decida prioridades.",
        },
        sv: {
          question:
            "Mötesanteckningar blandar beslut, uppgifter och datum. Vilken prompt hjälper AI-verktyget att ordna uppföljningen?",
          options: {
            A: "Skapa en tabell med åtgärder, ansvariga och datum.",
            B: "Avgör vilka uppgifter som är viktigast för teamet.",
            C: "Gör anteckningarna renare och kortare.",
            D: "Läs detta och slutför uppföljningen snabbt.",
          },
          correct_answer: "A",
          feedback:
            "Prompten ber om en användbar struktur: åtgärder, ansvariga och datum. Den förbättrar uppföljning utan att låta verktyget prioritera.",
        },
        nl: {
          question:
            "Ruwe vergadernotities mengen besluiten, taken en datums. Welke prompt helpt de AI-tool de opvolging te ordenen?",
          options: {
            A: "Maak een tabel met acties, eigenaars en deadlines.",
            B: "Bepaal welke taken het belangrijkst zijn.",
            C: "Maak deze notities netter en korter.",
            D: "Lees dit en rond de opvolging snel af.",
          },
          correct_answer: "A",
          feedback:
            "De prompt vraagt om een bruikbare structuur: acties, eigenaars en deadlines. Dat helpt opvolging zonder AI prioriteiten te laten bepalen.",
        },
      },
      question_text:
        "Rough meeting notes mix decisions, tasks and dates. Which prompt would help AI organise the follow-up?",
      explanation:
        "The prompt asks for a useful structure: actions, owners and deadlines. It improves follow-up without asking AI to decide priorities.",
      answers: [
        {
          id: "station4_q002_a",
          question_id: "station4_q002",
          answer_text: "Create a table with actions, owners and deadlines.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q002_b",
          question_id: "station4_q002",
          answer_text: "Decide which tasks matter most for the team.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q002_c",
          question_id: "station4_q002",
          answer_text: "Make these notes look cleaner and shorter.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q002_d",
          question_id: "station4_q002",
          answer_text: "Read this and finish the follow-up quickly.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q003",
      station_id: "station_4",
      stage: 1,
      complexity: 1,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A technical update must be easier for non-specialists to read. Which prompt sets the clearest instruction?",
          options: {
            A: "Make this sound less technical somehow.",
            B: "Remove the hard parts from this update.",
            C: "Rewrite it so everyone likes the tone.",
            D: "Explain it simply without losing key meaning.",
          },
          correct_answer: "D",
          feedback:
            "The best prompt names the audience and protects meaning. It asks for simpler language without removing important content.",
        },
        pt: {
          question:
            "Uma atualização técnica precisa ficar mais fácil para não especialistas. Qual prompt define melhor a instrução?",
          options: {
            A: "Deixe isto menos técnico de algum jeito.",
            B: "Remova as partes difíceis desta atualização.",
            C: "Reescreva para todos gostarem do tom.",
            D: "Explique de forma simples sem perder significado.",
          },
          correct_answer: "D",
          feedback:
            "O melhor prompt considera o público e protege o significado. Ele pede linguagem mais simples sem remover conteúdo importante.",
        },
        sv: {
          question:
            "En teknisk uppdatering behöver bli lättare för icke-specialister. Vilken prompt ger tydligast instruktion?",
          options: {
            A: "Gör detta mindre tekniskt på något sätt.",
            B: "Ta bort de svåra delarna ur uppdateringen.",
            C: "Skriv om så att tonen passar alla.",
            D: "Förklara enkelt utan att tappa viktig betydelse.",
          },
          correct_answer: "D",
          feedback:
            "Den bästa prompten tar hänsyn till målgruppen och skyddar betydelsen. Den ber om enklare språk utan att ta bort viktigt innehåll.",
        },
        nl: {
          question:
            "Een technische update moet makkelijker leesbaar worden voor niet-specialisten. Welke prompt geeft de duidelijkste instructie?",
          options: {
            A: "Maak dit op een manier minder technisch.",
            B: "Haal de moeilijke delen uit deze update.",
            C: "Herschrijf het zodat iedereen de toon prettig vindt.",
            D: "Leg het eenvoudig uit zonder kernbetekenis te verliezen.",
          },
          correct_answer: "D",
          feedback:
            "De beste prompt noemt de doelgroep en bewaart betekenis. Hij vraagt eenvoudiger taal zonder belangrijke inhoud weg te halen.",
        },
      },
      question_text:
        "A technical update must be easier for non-specialists to read. Which prompt sets the clearest instruction?",
      explanation:
        "The best prompt names the audience and protects meaning. It asks for simpler language without removing important content.",
      answers: [
        {
          id: "station4_q003_a",
          question_id: "station4_q003",
          answer_text: "Make this sound less technical somehow.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q003_b",
          question_id: "station4_q003",
          answer_text: "Remove the hard parts from this update.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q003_c",
          question_id: "station4_q003",
          answer_text: "Rewrite it so everyone likes the tone.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q003_d",
          question_id: "station4_q003",
          answer_text: "Explain it simply without losing key meaning.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station4_q004",
      station_id: "station_4",
      stage: 1,
      complexity: 2,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You have bullet points for a project update to managers. Which prompt gives AI enough direction?",
          options: {
            A: "Write a good update from these bullets.",
            B: "Draft a brief manager update with progress, risks and next steps.",
            C: "Make the update confident and professional.",
            D: "Turn these bullets into something impressive.",
          },
          correct_answer: "B",
          feedback:
            "The prompt gives audience, format and content priorities. That is stronger than asking for a generic or polished update.",
        },
        pt: {
          question:
            "Você tem tópicos para uma atualização de projeto para gestores. Qual prompt dá direção suficiente para a IA?",
          options: {
            A: "Escreva uma boa atualização com estes tópicos.",
            B: "Rascunhe atualização breve para gestores com progresso, riscos e próximos passos.",
            C: "Deixe a atualização confiante e profissional.",
            D: "Transforme estes tópicos em algo impressionante.",
          },
          correct_answer: "B",
          feedback:
            "O prompt define público, formato e prioridades de conteúdo. Isso é mais forte que pedir uma atualização genérica ou polida.",
        },
        sv: {
          question:
            "Du har punkter till en projektuppdatering för chefer. Vilken prompt ger AI-verktyget tillräcklig riktning?",
          options: {
            A: "Skriv en bra uppdatering från dessa punkter.",
            B: "Skapa kort chefsuppdatering med framsteg, risker och nästa steg.",
            C: "Gör uppdateringen säker och professionell.",
            D: "Gör punkterna till något imponerande.",
          },
          correct_answer: "B",
          feedback:
            "Prompten anger målgrupp, format och innehållsprioritering. Det är starkare än att be om en generell eller polerad uppdatering.",
        },
        nl: {
          question:
            "Je hebt bullets voor een projectupdate aan managers. Welke prompt geeft de AI-tool genoeg richting?",
          options: {
            A: "Schrijf een goede update met deze bullets.",
            B: "Maak korte managerupdate met voortgang, risico’s en volgende stappen.",
            C: "Maak de update zelfverzekerd en professioneel.",
            D: "Maak van deze bullets iets indrukwekkends.",
          },
          correct_answer: "B",
          feedback:
            "De prompt geeft doelgroep, vorm en inhoudsprioriteiten. Dat is sterker dan vragen om een algemene of opgepoetste update.",
        },
      },
      question_text:
        "You have bullet points for a project update to managers. Which prompt gives AI enough direction?",
      explanation:
        "The prompt gives audience, format and content priorities. That is stronger than asking for a generic or polished update.",
      answers: [
        {
          id: "station4_q004_a",
          question_id: "station4_q004",
          answer_text: "Write a good update from these bullets.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q004_b",
          question_id: "station4_q004",
          answer_text:
            "Draft a brief manager update with progress, risks and next steps.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station4_q004_c",
          question_id: "station4_q004",
          answer_text: "Make the update confident and professional.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q004_d",
          question_id: "station4_q004",
          answer_text: "Turn these bullets into something impressive.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q005",
      station_id: "station_4",
      stage: 1,
      complexity: 2,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A message sounds too sharp, but the facts must stay unchanged. Which prompt gives the right rewrite task?",
          options: {
            A: "Make this message nicer for the reader.",
            B: "Change the facts so the message feels softer.",
            C: "Rewrite in a neutral tone without changing facts.",
            D: "Remove details that could create questions.",
          },
          correct_answer: "C",
          feedback:
            "The strongest prompt separates tone from facts. It improves the message while protecting the meaning of the original.",
        },
        pt: {
          question:
            "Uma mensagem soa dura demais, mas os fatos devem ficar iguais. Qual prompt dá a tarefa correta?",
          options: {
            A: "Deixe esta mensagem mais simpática para o leitor.",
            B: "Mude os fatos para a mensagem soar mais suave.",
            C: "Reescreva em tom neutro sem mudar os fatos.",
            D: "Remova detalhes que possam gerar perguntas.",
          },
          correct_answer: "C",
          feedback:
            "O prompt mais forte separa tom e fatos. Ele melhora a mensagem enquanto preserva o significado original.",
        },
        sv: {
          question:
            "Ett meddelande låter för hårt, men fakta måste vara oförändrade. Vilken prompt ger rätt omskrivning?",
          options: {
            A: "Gör meddelandet trevligare för läsaren.",
            B: "Ändra fakta så att tonen blir mjukare.",
            C: "Skriv om neutralt utan att ändra fakta.",
            D: "Ta bort detaljer som kan väcka frågor.",
          },
          correct_answer: "C",
          feedback:
            "Den starkaste prompten skiljer ton från fakta. Den förbättrar meddelandet och bevarar samtidigt ursprunglig innebörd.",
        },
        nl: {
          question:
            "Een bericht klinkt te scherp, maar de feiten moeten gelijk blijven. Welke prompt geeft de juiste herschrijftaak?",
          options: {
            A: "Maak dit bericht vriendelijker voor de lezer.",
            B: "Verander feiten zodat het zachter klinkt.",
            C: "Herschrijf neutraal zonder de feiten te veranderen.",
            D: "Verwijder details die vragen kunnen oproepen.",
          },
          correct_answer: "C",
          feedback:
            "De sterkste prompt scheidt toon van feiten. Hij verbetert het bericht terwijl de oorspronkelijke betekenis behouden blijft.",
        },
      },
      question_text:
        "A message sounds too sharp, but the facts must stay unchanged. Which prompt gives the right rewrite task?",
      explanation:
        "The strongest prompt separates tone from facts. It improves the message while protecting the meaning of the original.",
      answers: [
        {
          id: "station4_q005_a",
          question_id: "station4_q005",
          answer_text: "Make this message nicer for the reader.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q005_b",
          question_id: "station4_q005",
          answer_text: "Change the facts so the message feels softer.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q005_c",
          question_id: "station4_q005",
          answer_text: "Rewrite in a neutral tone without changing facts.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station4_q005_d",
          question_id: "station4_q005",
          answer_text: "Remove details that could create questions.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q006",
      station_id: "station_4",
      stage: 1,
      complexity: 2,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A handover note mixes topics, status and open questions. Which instruction would create the most useful structure?",
          options: {
            A: "Group by topic, status and open question.",
            B: "Make the note shorter for handover.",
            C: "Find the most important handover topic.",
            D: "Rewrite everything in a smoother style.",
          },
          correct_answer: "A",
          feedback:
            "The prompt asks AI to organise the handover into useful categories. It does not merely shorten or polish the note.",
        },
        pt: {
          question:
            "Uma nota de transição mistura temas, status e dúvidas abertas. Qual instrução criaria a estrutura mais útil?",
          options: {
            A: "Agrupe por tema, status e dúvida aberta.",
            B: "Deixe a nota mais curta para transição.",
            C: "Encontre o tema mais importante da transição.",
            D: "Reescreva tudo em estilo mais fluido.",
          },
          correct_answer: "A",
          feedback:
            "O prompt pede que a IA organize a transição em categorias úteis. Ele não apenas encurta ou melhora o estilo.",
        },
        sv: {
          question:
            "En överlämningsanteckning blandar ämnen, status och öppna frågor. Vilken instruktion skapar mest användbar struktur?",
          options: {
            A: "Gruppera efter ämne, status och öppen fråga.",
            B: "Gör anteckningen kortare för överlämning.",
            C: "Hitta det viktigaste överlämningsämnet.",
            D: "Skriv om allt med mjukare stil.",
          },
          correct_answer: "A",
          feedback:
            "Prompten ber AI-verktyget ordna överlämningen i användbara kategorier. Den kortar eller polerar inte bara texten.",
        },
        nl: {
          question:
            "Een overdrachtsnotitie mengt onderwerpen, status en open vragen. Welke instructie maakt de nuttigste structuur?",
          options: {
            A: "Groepeer per onderwerp, status en open vraag.",
            B: "Maak de notitie korter voor overdracht.",
            C: "Vind het belangrijkste overdrachtsonderwerp.",
            D: "Herschrijf alles in een soepelere stijl.",
          },
          correct_answer: "A",
          feedback:
            "De prompt vraagt de AI-tool de overdracht in bruikbare categorieën te ordenen. Hij verkort of polijst niet alleen.",
        },
      },
      question_text:
        "A handover note mixes topics, status and open questions. Which instruction would create the most useful structure?",
      explanation:
        "The prompt asks AI to organise the handover into useful categories. It does not merely shorten or polish the note.",
      answers: [
        {
          id: "station4_q006_a",
          question_id: "station4_q006",
          answer_text: "Group by topic, status and open question.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q006_b",
          question_id: "station4_q006",
          answer_text: "Make the note shorter for handover.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q006_c",
          question_id: "station4_q006",
          answer_text: "Find the most important handover topic.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q006_d",
          question_id: "station4_q006",
          answer_text: "Rewrite everything in a smoother style.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q007",
      station_id: "station_4",
      stage: 1,
      complexity: 2,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "An internal request lacks some background. Which prompt would produce a useful first reply?",
          options: {
            A: "Answer the request as well as possible.",
            B: "Tell the requester what the final decision is.",
            C: "Make the reply friendly and confident.",
            D: "Draft a reply and list missing background.",
          },
          correct_answer: "D",
          feedback:
            "The prompt asks for a draft plus missing information. That helps the user continue the conversation instead of guessing.",
        },
        pt: {
          question:
            "Uma solicitação interna não traz todo o contexto. Qual prompt produziria uma primeira resposta útil?",
          options: {
            A: "Responda à solicitação da melhor forma possível.",
            B: "Diga ao solicitante qual é a decisão final.",
            C: "Deixe a resposta amigável e confiante.",
            D: "Rascunhe resposta e liste contexto faltante.",
          },
          correct_answer: "D",
          feedback:
            "O prompt pede um rascunho e informações faltantes. Isso ajuda o usuário a continuar a conversa sem adivinhar.",
        },
        sv: {
          question:
            "En intern förfrågan saknar viss bakgrund. Vilken prompt skulle ge ett användbart första svar?",
          options: {
            A: "Svara på förfrågan så bra som möjligt.",
            B: "Säg till frågeställaren vad slutbeslutet är.",
            C: "Gör svaret vänligt och självsäkert.",
            D: "Skapa svar och lista saknad bakgrund.",
          },
          correct_answer: "D",
          feedback:
            "Prompten ber om ett utkast och saknad information. Det hjälper användaren att fortsätta samtalet utan gissningar.",
        },
        nl: {
          question:
            "Een intern verzoek mist wat achtergrond. Welke prompt levert een bruikbaar eerste antwoord op?",
          options: {
            A: "Beantwoord het verzoek zo goed mogelijk.",
            B: "Vertel de aanvrager wat het eindbesluit is.",
            C: "Maak het antwoord vriendelijk en zeker.",
            D: "Maak antwoord en noem ontbrekende achtergrond.",
          },
          correct_answer: "D",
          feedback:
            "De prompt vraagt om een concept en ontbrekende informatie. Dat helpt de gebruiker verder zonder te gokken.",
        },
      },
      question_text:
        "An internal request lacks some background. Which prompt would produce a useful first reply?",
      explanation:
        "The prompt asks for a draft plus missing information. That helps the user continue the conversation instead of guessing.",
      answers: [
        {
          id: "station4_q007_a",
          question_id: "station4_q007",
          answer_text: "Answer the request as well as possible.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q007_b",
          question_id: "station4_q007",
          answer_text: "Tell the requester what the final decision is.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q007_c",
          question_id: "station4_q007",
          answer_text: "Make the reply friendly and confident.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q007_d",
          question_id: "station4_q007",
          answer_text: "Draft a reply and list missing background.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station4_q008",
      station_id: "station_4",
      stage: 1,
      complexity: 2,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You need a short guide for beginners using a new tool. Which prompt gives the best instruction?",
          options: {
            A: "Explain the tool in a complete way.",
            B: "Create a beginner guide with three clear steps.",
            C: "Make the tool sound simple and easy.",
            D: "Remove anything that may confuse beginners.",
          },
          correct_answer: "B",
          feedback:
            "The prompt defines audience, length and structure. That makes the output easier to review and use.",
        },
        pt: {
          question:
            "Você precisa de um guia curto para iniciantes em uma nova ferramenta. Qual prompt dá a melhor instrução?",
          options: {
            A: "Explique a ferramenta de forma completa.",
            B: "Crie guia para iniciantes com três passos claros.",
            C: "Faça a ferramenta parecer simples e fácil.",
            D: "Remova tudo que possa confundir iniciantes.",
          },
          correct_answer: "B",
          feedback:
            "O prompt define público, tamanho e estrutura. Isso torna o resultado mais fácil de revisar e usar.",
        },
        sv: {
          question:
            "Du behöver en kort guide för nybörjare i ett nytt verktyg. Vilken prompt ger bäst instruktion?",
          options: {
            A: "Förklara verktyget på ett komplett sätt.",
            B: "Skapa nybörjarguide med tre tydliga steg.",
            C: "Få verktyget att låta enkelt.",
            D: "Ta bort allt som kan förvirra nybörjare.",
          },
          correct_answer: "B",
          feedback:
            "Prompten anger målgrupp, längd och struktur. Det gör resultatet lättare att granska och använda.",
        },
        nl: {
          question:
            "Je hebt een korte gids nodig voor beginners met een nieuwe tool. Welke prompt geeft de beste instructie?",
          options: {
            A: "Leg de tool volledig uit.",
            B: "Maak beginnersgids met drie duidelijke stappen.",
            C: "Laat de tool simpel en makkelijk klinken.",
            D: "Verwijder alles wat beginners kan verwarren.",
          },
          correct_answer: "B",
          feedback:
            "De prompt bepaalt doelgroep, lengte en structuur. Daardoor is het resultaat makkelijker te beoordelen en te gebruiken.",
        },
      },
      question_text:
        "You need a short guide for beginners using a new tool. Which prompt gives the best instruction?",
      explanation:
        "The prompt defines audience, length and structure. That makes the output easier to review and use.",
      answers: [
        {
          id: "station4_q008_a",
          question_id: "station4_q008",
          answer_text: "Explain the tool in a complete way.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q008_b",
          question_id: "station4_q008",
          answer_text: "Create a beginner guide with three clear steps.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station4_q008_c",
          question_id: "station4_q008",
          answer_text: "Make the tool sound simple and easy.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q008_d",
          question_id: "station4_q008",
          answer_text: "Remove anything that may confuse beginners.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q009",
      station_id: "station_4",
      stage: 1,
      complexity: 2,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A process note must become a checklist for daily use. Which prompt gives the most useful format?",
          options: {
            A: "Make this process note shorter.",
            B: "Explain the process in plain words.",
            C: "Turn this into steps, checks and exceptions.",
            D: "Remove unusual cases from the checklist.",
          },
          correct_answer: "C",
          feedback:
            "The prompt asks for a checklist structure people can use. It includes steps, checks and exceptions, not only shorter wording.",
        },
        pt: {
          question:
            "Uma nota de processo precisa virar checklist para uso diário. Qual prompt dá o formato mais útil?",
          options: {
            A: "Deixe esta nota de processo mais curta.",
            B: "Explique o processo em palavras simples.",
            C: "Transforme em passos, verificações e exceções.",
            D: "Remova casos incomuns do checklist.",
          },
          correct_answer: "C",
          feedback:
            "O prompt pede uma estrutura de checklist utilizável. Ele inclui passos, verificações e exceções, não apenas texto mais curto.",
        },
        sv: {
          question:
            "En processanteckning ska bli en checklista för daglig användning. Vilken prompt ger mest användbart format?",
          options: {
            A: "Gör processanteckningen kortare.",
            B: "Förklara processen med enkla ord.",
            C: "Gör den till steg, kontroller och undantag.",
            D: "Ta bort ovanliga fall från checklistan.",
          },
          correct_answer: "C",
          feedback:
            "Prompten ber om en checklistsstruktur som kan användas. Den tar med steg, kontroller och undantag, inte bara kortare text.",
        },
        nl: {
          question:
            "Een procesnotitie moet een checklist voor dagelijks gebruik worden. Welke prompt geeft de nuttigste vorm?",
          options: {
            A: "Maak deze procesnotitie korter.",
            B: "Leg het proces in gewone woorden uit.",
            C: "Maak er stappen, checks en uitzonderingen van.",
            D: "Haal ongebruikelijke gevallen uit de checklist.",
          },
          correct_answer: "C",
          feedback:
            "De prompt vraagt om een bruikbare checkliststructuur. Hij bevat stappen, checks en uitzonderingen, niet alleen kortere tekst.",
        },
      },
      question_text:
        "A process note must become a checklist for daily use. Which prompt gives the most useful format?",
      explanation:
        "The prompt asks for a checklist structure people can use. It includes steps, checks and exceptions, not only shorter wording.",
      answers: [
        {
          id: "station4_q009_a",
          question_id: "station4_q009",
          answer_text: "Make this process note shorter.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q009_b",
          question_id: "station4_q009",
          answer_text: "Explain the process in plain words.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q009_c",
          question_id: "station4_q009",
          answer_text: "Turn this into steps, checks and exceptions.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station4_q009_d",
          question_id: "station4_q009",
          answer_text: "Remove unusual cases from the checklist.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q010",
      station_id: "station_4",
      stage: 1,
      complexity: 2,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A leader needs a quick update with risks first. Which prompt fits that audience best?",
          options: {
            A: "Rewrite for leaders, putting key risks first.",
            B: "Make the update sound more important.",
            C: "Create a detailed version with every point.",
            D: "Write a friendly message for the team.",
          },
          correct_answer: "A",
          feedback:
            "The prompt matches the audience and priority: leaders need the key risks first. The other prompts change style or detail wrongly.",
        },
        pt: {
          question:
            "Uma liderança precisa de atualização rápida com riscos primeiro. Qual prompt combina melhor com esse público?",
          options: {
            A: "Reescreva para líderes, colocando riscos principais primeiro.",
            B: "Faça a atualização parecer mais importante.",
            C: "Crie versão detalhada com todos os pontos.",
            D: "Escreva mensagem amigável para a equipe.",
          },
          correct_answer: "A",
          feedback:
            "O prompt combina público e prioridade: líderes precisam ver riscos principais primeiro. Os outros mudam estilo ou detalhe de forma inadequada.",
        },
        sv: {
          question:
            "En ledare behöver en snabb uppdatering med risker först. Vilken prompt passar målgruppen bäst?",
          options: {
            A: "Skriv om för ledare, med viktigaste risker först.",
            B: "Få uppdateringen att låta viktigare.",
            C: "Skapa detaljerad version med alla punkter.",
            D: "Skriv ett vänligt meddelande till teamet.",
          },
          correct_answer: "A",
          feedback:
            "Prompten matchar målgrupp och prioritet: ledare behöver se viktigaste risker först. De andra ändrar stil eller detaljnivå fel.",
        },
        nl: {
          question:
            "Een leider heeft een snelle update nodig met risico’s eerst. Welke prompt past het best bij die doelgroep?",
          options: {
            A: "Herschrijf voor leiders, met belangrijkste risico’s eerst.",
            B: "Laat de update belangrijker klinken.",
            C: "Maak een gedetailleerde versie met alle punten.",
            D: "Schrijf een vriendelijk bericht voor het team.",
          },
          correct_answer: "A",
          feedback:
            "De prompt past bij doelgroep en prioriteit: leiders moeten risico’s eerst zien. De andere opties veranderen toon of detail verkeerd.",
        },
      },
      question_text:
        "A leader needs a quick update with risks first. Which prompt fits that audience best?",
      explanation:
        "The prompt matches the audience and priority: leaders need the key risks first. The other prompts change style or detail wrongly.",
      answers: [
        {
          id: "station4_q010_a",
          question_id: "station4_q010",
          answer_text: "Rewrite for leaders, putting key risks first.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q010_b",
          question_id: "station4_q010",
          answer_text: "Make the update sound more important.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q010_c",
          question_id: "station4_q010",
          answer_text: "Create a detailed version with every point.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q010_d",
          question_id: "station4_q010",
          answer_text: "Write a friendly message for the team.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q011",
      station_id: "station_4",
      stage: 2,
      complexity: 2,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Before a stakeholder meeting, you want AI to prepare useful questions. Which prompt gives the strongest direction?",
          options: {
            A: "Act as a stakeholder and list likely questions and concerns.",
            B: "Write everything I should say in the meeting.",
            C: "Make me sound confident during the meeting.",
            D: "Create a short agenda without concerns.",
          },
          correct_answer: "A",
          feedback:
            "The strongest prompt uses a role and asks for questions plus concerns. It helps preparation without scripting the meeting.",
        },
        pt: {
          question:
            "Antes de reunião com stakeholders, você quer que a IA prepare perguntas úteis. Qual prompt dá a direção mais forte?",
          options: {
            A: "Aja como stakeholder e liste perguntas e preocupações prováveis.",
            B: "Escreva tudo que devo dizer na reunião.",
            C: "Faça eu parecer confiante durante a reunião.",
            D: "Crie agenda curta sem preocupações.",
          },
          correct_answer: "A",
          feedback:
            "O prompt mais forte usa um papel e pede perguntas e preocupações. Ele ajuda a preparação sem roteirizar a reunião.",
        },
        sv: {
          question:
            "Inför ett intressentmöte vill du att AI-verktyget förbereder användbara frågor. Vilken prompt ger starkast riktning?",
          options: {
            A: "Agera intressent och lista troliga frågor och farhågor.",
            B: "Skriv allt jag ska säga på mötet.",
            C: "Få mig att låta säker under mötet.",
            D: "Skapa kort agenda utan farhågor.",
          },
          correct_answer: "A",
          feedback:
            "Den starkaste prompten använder en roll och ber om frågor samt farhågor. Den stödjer förberedelse utan att skriva manus.",
        },
        nl: {
          question:
            "Voor een stakeholderoverleg wil je dat de AI-tool nuttige vragen voorbereidt. Welke prompt geeft de sterkste richting?",
          options: {
            A: "Doe alsof je stakeholder bent en noem waarschijnlijke vragen.",
            B: "Schrijf alles wat ik moet zeggen.",
            C: "Laat mij zelfverzekerd klinken tijdens het overleg.",
            D: "Maak korte agenda zonder zorgen.",
          },
          correct_answer: "A",
          feedback:
            "De sterkste prompt gebruikt een rol en vraagt om vragen plus zorgen. Dat helpt voorbereiding zonder het overleg te scripten.",
        },
      },
      question_text:
        "Before a stakeholder meeting, you want AI to prepare useful questions. Which prompt gives the strongest direction?",
      explanation:
        "The strongest prompt uses a role and asks for questions plus concerns. It helps preparation without scripting the meeting.",
      answers: [
        {
          id: "station4_q011_a",
          question_id: "station4_q011",
          answer_text:
            "Act as a stakeholder and list likely questions and concerns.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q011_b",
          question_id: "station4_q011",
          answer_text: "Write everything I should say in the meeting.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q011_c",
          question_id: "station4_q011",
          answer_text: "Make me sound confident during the meeting.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q011_d",
          question_id: "station4_q011",
          answer_text: "Create a short agenda without concerns.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q012",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Two draft updates describe the same change differently. Which prompt would make comparison easiest?",
          options: {
            A: "Rewrite both drafts into one polished update.",
            B: "Compare key differences, overlaps and unclear points.",
            C: "Choose the draft that sounds more complete.",
            D: "Make both drafts shorter and more direct.",
          },
          correct_answer: "B",
          feedback:
            "The prompt asks for a comparison structure. It helps the user see differences before choosing or rewriting anything.",
        },
        pt: {
          question:
            "Dois rascunhos descrevem a mesma mudança de formas diferentes. Qual prompt facilitaria a comparação?",
          options: {
            A: "Reescreva os dois em uma atualização polida.",
            B: "Compare diferenças, sobreposições e pontos pouco claros.",
            C: "Escolha o rascunho que parece mais completo.",
            D: "Deixe os dois rascunhos mais curtos.",
          },
          correct_answer: "B",
          feedback:
            "O prompt pede uma estrutura de comparação. Ele ajuda o usuário a ver diferenças antes de escolher ou reescrever.",
        },
        sv: {
          question:
            "Två utkast beskriver samma ändring på olika sätt. Vilken prompt gör jämförelsen enklast?",
          options: {
            A: "Skriv om båda till en polerad uppdatering.",
            B: "Jämför skillnader, överlapp och otydliga punkter.",
            C: "Välj utkastet som verkar mest komplett.",
            D: "Gör båda utkasten kortare och tydligare.",
          },
          correct_answer: "B",
          feedback:
            "Prompten ber om en jämförelsestruktur. Den hjälper användaren se skillnader innan något väljs eller skrivs om.",
        },
        nl: {
          question:
            "Twee conceptupdates beschrijven dezelfde wijziging anders. Welke prompt maakt vergelijken het makkelijkst?",
          options: {
            A: "Herschrijf beide tot één nette update.",
            B: "Vergelijk verschillen, overlap en onduidelijke punten.",
            C: "Kies het concept dat vollediger klinkt.",
            D: "Maak beide concepten korter en directer.",
          },
          correct_answer: "B",
          feedback:
            "De prompt vraagt om een vergelijkingsstructuur. Dat helpt de gebruiker verschillen zien vóór kiezen of herschrijven.",
        },
      },
      question_text:
        "Two draft updates describe the same change differently. Which prompt would make comparison easiest?",
      explanation:
        "The prompt asks for a comparison structure. It helps the user see differences before choosing or rewriting anything.",
      answers: [
        {
          id: "station4_q012_a",
          question_id: "station4_q012",
          answer_text: "Rewrite both drafts into one polished update.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q012_b",
          question_id: "station4_q012",
          answer_text: "Compare key differences, overlaps and unclear points.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station4_q012_c",
          question_id: "station4_q012",
          answer_text: "Choose the draft that sounds more complete.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q012_d",
          question_id: "station4_q012",
          answer_text: "Make both drafts shorter and more direct.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q013",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A handover has tasks, risks and unanswered questions in one paragraph. Which prompt gives the best structure?",
          options: {
            A: "Make the paragraph easier to read.",
            B: "Decide what the next owner should do.",
            C: "Remove anything not needed for handover.",
            D: "Separate tasks, risks and open questions.",
          },
          correct_answer: "D",
          feedback:
            "The prompt creates useful categories while keeping decisions with the user. It turns messy input into reviewable handover information.",
        },
        pt: {
          question:
            "Uma transição tem tarefas, riscos e perguntas abertas em um parágrafo. Qual prompt dá a melhor estrutura?",
          options: {
            A: "Deixe o parágrafo mais fácil de ler.",
            B: "Decida o que o próximo responsável fará.",
            C: "Remova o que não for necessário.",
            D: "Separe tarefas, riscos e dúvidas pendentes.",
          },
          correct_answer: "D",
          feedback:
            "O prompt cria categorias úteis mantendo decisões com o usuário. Ele transforma input confuso em informação revisável.",
        },
        sv: {
          question:
            "En överlämning har uppgifter, risker och obesvarade frågor i ett stycke. Vilken prompt ger bäst struktur?",
          options: {
            A: "Gör stycket lättare att läsa.",
            B: "Avgör vad nästa ansvariga ska göra.",
            C: "Ta bort sådant som inte behövs.",
            D: "Dela upp uppgifter, risker och öppna frågor.",
          },
          correct_answer: "D",
          feedback:
            "Prompten skapar användbara kategorier och låter beslut ligga kvar hos användaren. Den gör rörig input granskningsbar.",
        },
        nl: {
          question:
            "Een overdracht heeft taken, risico’s en open vragen in één alinea. Welke prompt geeft de beste structuur?",
          options: {
            A: "Maak de alinea makkelijker leesbaar.",
            B: "Bepaal wat de volgende eigenaar moet doen.",
            C: "Verwijder wat niet nodig is.",
            D: "Scheid taken, risico’s en open vragen.",
          },
          correct_answer: "D",
          feedback:
            "De prompt maakt bruikbare categorieën en laat besluiten bij de gebruiker. Rommelige input wordt zo beoordeelbare overdracht.",
        },
      },
      question_text:
        "A handover has tasks, risks and unanswered questions in one paragraph. Which prompt gives the best structure?",
      explanation:
        "The prompt creates useful categories while keeping decisions with the user. It turns messy input into reviewable handover information.",
      answers: [
        {
          id: "station4_q013_a",
          question_id: "station4_q013",
          answer_text: "Make the paragraph easier to read.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q013_b",
          question_id: "station4_q013",
          answer_text: "Decide what the next owner should do.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q013_c",
          question_id: "station4_q013",
          answer_text: "Remove anything not needed for handover.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q013_d",
          question_id: "station4_q013",
          answer_text: "Separate tasks, risks and open questions.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station4_q014",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A supplier email is factual but too blunt. Which prompt improves it without changing the message?",
          options: {
            A: "Make the supplier email nicer and shorter.",
            B: "Add a more positive explanation for the supplier.",
            C: "Rewrite politely, keeping all facts unchanged.",
            D: "Remove facts that may create tension.",
          },
          correct_answer: "C",
          feedback:
            "The prompt improves tone while preserving facts. That is the right instruction when wording is the problem, not content.",
        },
        pt: {
          question:
            "Um e-mail ao fornecedor está factual, mas direto demais. Qual prompt melhora sem mudar a mensagem?",
          options: {
            A: "Deixe o e-mail mais simpático e curto.",
            B: "Adicione explicação mais positiva ao fornecedor.",
            C: "Reescreva com educação, mantendo todos os fatos.",
            D: "Remova fatos que possam criar tensão.",
          },
          correct_answer: "C",
          feedback:
            "O prompt melhora o tom preservando fatos. Essa é a instrução correta quando o problema é redação, não conteúdo.",
        },
        sv: {
          question:
            "Ett leverantörsmejl är faktabaserat men för rakt. Vilken prompt förbättrar utan att ändra budskapet?",
          options: {
            A: "Gör leverantörsmejlet trevligare och kortare.",
            B: "Lägg till en mer positiv förklaring.",
            C: "Skriv artigt, men behåll alla fakta.",
            D: "Ta bort fakta som kan skapa spänning.",
          },
          correct_answer: "C",
          feedback:
            "Prompten förbättrar tonen och bevarar fakta. Det är rätt instruktion när problemet är formulering, inte innehåll.",
        },
        nl: {
          question:
            "Een leveranciersmail is feitelijk maar te bot. Welke prompt verbetert zonder de boodschap te veranderen?",
          options: {
            A: "Maak de leveranciersmail vriendelijker en korter.",
            B: "Voeg een positievere uitleg toe.",
            C: "Herschrijf beleefd, met alle feiten gelijk.",
            D: "Verwijder feiten die spanning kunnen geven.",
          },
          correct_answer: "C",
          feedback:
            "De prompt verbetert toon en bewaart feiten. Dat is de juiste instructie wanneer formulering het probleem is.",
        },
      },
      question_text:
        "A supplier email is factual but too blunt. Which prompt improves it without changing the message?",
      explanation:
        "The prompt improves tone while preserving facts. That is the right instruction when wording is the problem, not content.",
      answers: [
        {
          id: "station4_q014_a",
          question_id: "station4_q014",
          answer_text: "Make the supplier email nicer and shorter.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q014_b",
          question_id: "station4_q014",
          answer_text: "Add a more positive explanation for the supplier.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q014_c",
          question_id: "station4_q014",
          answer_text: "Rewrite politely, keeping all facts unchanged.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station4_q014_d",
          question_id: "station4_q014",
          answer_text: "Remove facts that may create tension.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q015",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A risk list looks too neat and misses weak spots. Which follow-up prompt would improve it?",
          options: {
            A: "Challenge the list and name missing assumptions.",
            B: "Make the risk list shorter and clearer.",
            C: "Rank the risks and choose the top one.",
            D: "Turn the risks into a polished slide.",
          },
          correct_answer: "A",
          feedback:
            "The prompt asks AI to critique the output and reveal assumptions. That is better than polishing or ranking too soon.",
        },
        pt: {
          question:
            "Uma lista de riscos parece arrumada demais e perde pontos fracos. Qual prompt de acompanhamento melhoraria isso?",
          options: {
            A: "Questione a lista e nomeie premissas faltantes.",
            B: "Deixe a lista de riscos mais curta.",
            C: "Classifique os riscos e escolha o principal.",
            D: "Transforme riscos em slide polido.",
          },
          correct_answer: "A",
          feedback:
            "O prompt pede que a IA critique o resultado e revele premissas. Isso é melhor que polir ou ranquear cedo demais.",
        },
        sv: {
          question:
            "En risklista ser för prydlig ut och missar svagheter. Vilken uppföljningsprompt skulle förbättra den?",
          options: {
            A: "Utmana listan och nämn saknade antaganden.",
            B: "Gör risklistan kortare och tydligare.",
            C: "Rangordna riskerna och välj den största.",
            D: "Gör riskerna till en polerad slide.",
          },
          correct_answer: "A",
          feedback:
            "Prompten ber AI-verktyget kritisera resultatet och visa antaganden. Det är bättre än att polera eller rangordna för tidigt.",
        },
        nl: {
          question:
            "Een risicolijst oogt te netjes en mist zwakke plekken. Welke opvolgprompt zou haar verbeteren?",
          options: {
            A: "Daag de lijst uit en noem ontbrekende aannames.",
            B: "Maak de risicolijst korter en duidelijker.",
            C: "Rangschik risico’s en kies de grootste.",
            D: "Maak van risico’s een nette slide.",
          },
          correct_answer: "A",
          feedback:
            "De prompt vraagt de AI-tool de output te bekritiseren en aannames zichtbaar te maken. Dat is beter dan te vroeg polijsten.",
        },
      },
      question_text:
        "A risk list looks too neat and misses weak spots. Which follow-up prompt would improve it?",
      explanation:
        "The prompt asks AI to critique the output and reveal assumptions. That is better than polishing or ranking too soon.",
      answers: [
        {
          id: "station4_q015_a",
          question_id: "station4_q015",
          answer_text: "Challenge the list and name missing assumptions.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q015_b",
          question_id: "station4_q015",
          answer_text: "Make the risk list shorter and clearer.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q015_c",
          question_id: "station4_q015",
          answer_text: "Rank the risks and choose the top one.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q015_d",
          question_id: "station4_q015",
          answer_text: "Turn the risks into a polished slide.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q016",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI drafts a vague status update from rough bullets. Which follow-up prompt would improve the output?",
          options: {
            A: "Make the status update sound more confident.",
            B: "Add progress, risks, blockers and next steps.",
            C: "Remove details so leaders read it quickly.",
            D: "Decide whether the project is on track.",
          },
          correct_answer: "B",
          feedback:
            "The follow-up prompt adds the missing structure. It tells AI what information the status update should contain.",
        },
        pt: {
          question:
            "A IA rascunha status vago a partir de tópicos. Qual prompt de acompanhamento melhoraria o resultado?",
          options: {
            A: "Deixe o status parecer mais confiante.",
            B: "Adicione progresso, riscos, bloqueios e próximos passos.",
            C: "Remova detalhes para líderes lerem rápido.",
            D: "Decida se o projeto está no caminho.",
          },
          correct_answer: "B",
          feedback:
            "O prompt de acompanhamento adiciona estrutura faltante. Ele diz à IA quais informações o status deve conter.",
        },
        sv: {
          question:
            "AI-verktyget skapar en vag status från punkter. Vilken uppföljningsprompt förbättrar resultatet?",
          options: {
            A: "Få statusen att låta mer självsäker.",
            B: "Lägg till framsteg, risker, hinder och nästa steg.",
            C: "Ta bort detaljer så ledare läser snabbt.",
            D: "Avgör om projektet är på rätt väg.",
          },
          correct_answer: "B",
          feedback:
            "Uppföljningsprompten lägger till saknad struktur. Den säger vilken information statusen ska innehålla.",
        },
        nl: {
          question:
            "De AI-tool maakt een vage status uit bullets. Welke opvolgprompt verbetert het resultaat?",
          options: {
            A: "Laat de status zelfverzekerder klinken.",
            B: "Voeg voortgang, risico’s, blokkades en vervolgstappen toe.",
            C: "Verwijder details zodat leiders snel lezen.",
            D: "Bepaal of het project op schema ligt.",
          },
          correct_answer: "B",
          feedback:
            "De opvolgprompt voegt ontbrekende structuur toe. Hij zegt welke informatie de statusupdate moet bevatten.",
        },
      },
      question_text:
        "AI drafts a vague status update from rough bullets. Which follow-up prompt would improve the output?",
      explanation:
        "The follow-up prompt adds the missing structure. It tells AI what information the status update should contain.",
      answers: [
        {
          id: "station4_q016_a",
          question_id: "station4_q016",
          answer_text: "Make the status update sound more confident.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q016_b",
          question_id: "station4_q016",
          answer_text: "Add progress, risks, blockers and next steps.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station4_q016_c",
          question_id: "station4_q016",
          answer_text: "Remove details so leaders read it quickly.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q016_d",
          question_id: "station4_q016",
          answer_text: "Decide whether the project is on track.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q017",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI simplified a technical note, but important caveats disappeared. Which follow-up prompt is most useful?",
          options: {
            A: "Make the text even shorter for beginners.",
            B: "Remove all caveats that slow reading.",
            C: "Turn the note into friendly marketing text.",
            D: "Restore key caveats in plain language.",
          },
          correct_answer: "D",
          feedback:
            "The follow-up keeps the simplification goal while recovering important meaning. That is stronger than making the text merely easier.",
        },
        pt: {
          question:
            "A IA simplificou uma nota técnica, mas ressalvas importantes sumiram. Qual prompt de acompanhamento é mais útil?",
          options: {
            A: "Deixe o texto ainda mais curto para iniciantes.",
            B: "Remova todas as ressalvas que atrasam leitura.",
            C: "Transforme a nota em texto promocional amigável.",
            D: "Restaure ressalvas importantes em linguagem simples.",
          },
          correct_answer: "D",
          feedback:
            "O acompanhamento mantém a simplificação e recupera significado importante. Isso é melhor que apenas deixar o texto mais fácil.",
        },
        sv: {
          question:
            "AI-verktyget förenklade en teknisk notis, men viktiga förbehåll försvann. Vilken uppföljningsprompt är mest användbar?",
          options: {
            A: "Gör texten ännu kortare för nybörjare.",
            B: "Ta bort alla förbehåll som bromsar läsning.",
            C: "Gör notisen till vänlig marknadstext.",
            D: "Återställ viktiga förbehåll med enkelt språk.",
          },
          correct_answer: "D",
          feedback:
            "Uppföljningen behåller förenklingen och återför viktig betydelse. Det är bättre än att bara göra texten lättare.",
        },
        nl: {
          question:
            "De AI-tool vereenvoudigde een technische notitie, maar belangrijke nuances verdwenen. Welke opvolgprompt is het nuttigst?",
          options: {
            A: "Maak de tekst nog korter voor beginners.",
            B: "Verwijder alle nuances die lezen vertragen.",
            C: "Maak er vriendelijke marketingtekst van.",
            D: "Herstel belangrijke nuances in gewone taal.",
          },
          correct_answer: "D",
          feedback:
            "De opvolging bewaart vereenvoudiging en herstelt belangrijke betekenis. Dat is beter dan tekst alleen makkelijker maken.",
        },
      },
      question_text:
        "AI simplified a technical note, but important caveats disappeared. Which follow-up prompt is most useful?",
      explanation:
        "The follow-up keeps the simplification goal while recovering important meaning. That is stronger than making the text merely easier.",
      answers: [
        {
          id: "station4_q017_a",
          question_id: "station4_q017",
          answer_text: "Make the text even shorter for beginners.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q017_b",
          question_id: "station4_q017",
          answer_text: "Remove all caveats that slow reading.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q017_c",
          question_id: "station4_q017",
          answer_text: "Turn the note into friendly marketing text.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q017_d",
          question_id: "station4_q017",
          answer_text: "Restore key caveats in plain language.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station4_q018",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Meeting notes contain decisions, blockers and next steps. Which prompt would create the most useful output?",
          options: {
            A: "Summarise the meeting in a professional tone.",
            B: "Decide which blockers the team should fix first.",
            C: "Extract decisions, blockers, owners and dates.",
            D: "Write a message saying the meeting went well.",
          },
          correct_answer: "C",
          feedback:
            "The prompt asks for a practical follow-up format. It turns notes into usable information without assigning priorities to AI.",
        },
        pt: {
          question:
            "Notas de reunião têm decisões, bloqueios e próximos passos. Qual prompt criaria o resultado mais útil?",
          options: {
            A: "Resuma a reunião em tom profissional.",
            B: "Decida quais bloqueios corrigir primeiro.",
            C: "Extraia decisões, bloqueios, responsáveis e datas.",
            D: "Escreva mensagem dizendo que a reunião foi boa.",
          },
          correct_answer: "C",
          feedback:
            "O prompt pede formato prático de acompanhamento. Ele transforma notas em informação útil sem entregar prioridades à IA.",
        },
        sv: {
          question:
            "Mötesanteckningar innehåller beslut, hinder och nästa steg. Vilken prompt skapar mest användbar output?",
          options: {
            A: "Sammanfatta mötet med professionell ton.",
            B: "Avgör vilka hinder teamet bör lösa först.",
            C: "Extrahera beslut, hinder, ansvariga och datum.",
            D: "Skriv meddelande om att mötet gick bra.",
          },
          correct_answer: "C",
          feedback:
            "Prompten ber om ett praktiskt uppföljningsformat. Den gör anteckningar användbara utan att ge prioritering till verktyget.",
        },
        nl: {
          question:
            "Vergadernotities bevatten besluiten, blokkades en vervolgstappen. Welke prompt maakt de nuttigste output?",
          options: {
            A: "Vat het overleg professioneel samen.",
            B: "Bepaal welke blokkades eerst opgelost worden.",
            C: "Haal besluiten, blokkades, eigenaars en datums eruit.",
            D: "Schrijf bericht dat het overleg goed ging.",
          },
          correct_answer: "C",
          feedback:
            "De prompt vraagt om een praktisch opvolgformaat. Notities worden bruikbaar zonder prioritering aan AI te geven.",
        },
      },
      question_text:
        "Meeting notes contain decisions, blockers and next steps. Which prompt would create the most useful output?",
      explanation:
        "The prompt asks for a practical follow-up format. It turns notes into usable information without assigning priorities to AI.",
      answers: [
        {
          id: "station4_q018_a",
          question_id: "station4_q018",
          answer_text: "Summarise the meeting in a professional tone.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q018_b",
          question_id: "station4_q018",
          answer_text: "Decide which blockers the team should fix first.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q018_c",
          question_id: "station4_q018",
          answer_text: "Extract decisions, blockers, owners and dates.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station4_q018_d",
          question_id: "station4_q018",
          answer_text: "Write a message saying the meeting went well.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q019",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A draft answer to an internal request sounds complete, but details are missing. Which prompt helps most?",
          options: {
            A: "Draft a reply and list details to confirm.",
            B: "Send a shorter answer with confident wording.",
            C: "Assume the missing details are not important.",
            D: "Rewrite the answer in a warmer tone.",
          },
          correct_answer: "A",
          feedback:
            "The prompt asks for both a reply and confirmation points. It supports progress without hiding missing information.",
        },
        pt: {
          question:
            "Um rascunho para solicitação interna parece completo, mas faltam detalhes. Qual prompt ajuda mais?",
          options: {
            A: "Rascunhe resposta e liste detalhes para confirmar.",
            B: "Envie resposta mais curta com tom confiante.",
            C: "Presuma que detalhes faltantes não importam.",
            D: "Reescreva a resposta em tom mais acolhedor.",
          },
          correct_answer: "A",
          feedback:
            "O prompt pede resposta e pontos de confirmação. Ele ajuda a avançar sem esconder informações faltantes.",
        },
        sv: {
          question:
            "Ett svar på en intern fråga verkar komplett, men detaljer saknas. Vilken prompt hjälper mest?",
          options: {
            A: "Skapa svar och lista detaljer att bekräfta.",
            B: "Skicka kortare svar med säker ton.",
            C: "Anta att saknade detaljer inte spelar roll.",
            D: "Skriv om svaret med varmare ton.",
          },
          correct_answer: "A",
          feedback:
            "Prompten ber om svar och bekräftelsepunkter. Den stödjer framdrift utan att dölja saknad information.",
        },
        nl: {
          question:
            "Een conceptantwoord op een intern verzoek lijkt compleet, maar details ontbreken. Welke prompt helpt het meest?",
          options: {
            A: "Maak antwoord en noem details om te bevestigen.",
            B: "Stuur korter antwoord met zekere toon.",
            C: "Neem aan dat ontbrekende details niet belangrijk zijn.",
            D: "Herschrijf het antwoord warmer.",
          },
          correct_answer: "A",
          feedback:
            "De prompt vraagt om antwoord én bevestigingspunten. Dat helpt vooruitgang zonder ontbrekende informatie te verbergen.",
        },
      },
      question_text:
        "A draft answer to an internal request sounds complete, but details are missing. Which prompt helps most?",
      explanation:
        "The prompt asks for both a reply and confirmation points. It supports progress without hiding missing information.",
      answers: [
        {
          id: "station4_q019_a",
          question_id: "station4_q019",
          answer_text: "Draft a reply and list details to confirm.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q019_b",
          question_id: "station4_q019",
          answer_text: "Send a shorter answer with confident wording.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q019_c",
          question_id: "station4_q019",
          answer_text: "Assume the missing details are not important.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q019_d",
          question_id: "station4_q019",
          answer_text: "Rewrite the answer in a warmer tone.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q020",
      station_id: "station_4",
      stage: 2,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI made a checklist, but some steps overlap. Which follow-up prompt would improve it?",
          options: {
            A: "Make the checklist more polished and official.",
            B: "Merge duplicates and flag unclear steps.",
            C: "Choose which steps the team can skip.",
            D: "Turn the checklist into a short paragraph.",
          },
          correct_answer: "B",
          feedback:
            "The follow-up asks AI to improve structure and clarity. It does not let AI remove steps or hide problems.",
        },
        pt: {
          question:
            "A IA criou um checklist, mas alguns passos se sobrepõem. Qual prompt de acompanhamento melhoraria isso?",
          options: {
            A: "Deixe o checklist mais polido e oficial.",
            B: "Una duplicados e marque passos pouco claros.",
            C: "Escolha quais passos a equipe pode pular.",
            D: "Transforme o checklist em parágrafo curto.",
          },
          correct_answer: "B",
          feedback:
            "O acompanhamento pede melhoria de estrutura e clareza. Ele não deixa a IA remover passos ou esconder problemas.",
        },
        sv: {
          question:
            "AI-verktyget skapade en checklista, men vissa steg överlappar. Vilken uppföljningsprompt förbättrar den?",
          options: {
            A: "Gör checklistan mer polerad och officiell.",
            B: "Slå ihop dubbletter och markera otydliga steg.",
            C: "Välj vilka steg teamet kan hoppa över.",
            D: "Gör checklistan till ett kort stycke.",
          },
          correct_answer: "B",
          feedback:
            "Uppföljningen ber om bättre struktur och tydlighet. Den låter inte verktyget ta bort steg eller dölja problem.",
        },
        nl: {
          question:
            "De AI-tool maakte een checklist, maar stappen overlappen. Welke opvolgprompt verbetert dit?",
          options: {
            A: "Maak de checklist netter en officiëler.",
            B: "Voeg dubbels samen en markeer onduidelijke stappen.",
            C: "Kies welke stappen het team kan overslaan.",
            D: "Maak van de checklist een korte alinea.",
          },
          correct_answer: "B",
          feedback:
            "De opvolgprompt vraagt om betere structuur en duidelijkheid. Hij laat AI geen stappen verwijderen of problemen verbergen.",
        },
      },
      question_text:
        "AI made a checklist, but some steps overlap. Which follow-up prompt would improve it?",
      explanation:
        "The follow-up asks AI to improve structure and clarity. It does not let AI remove steps or hide problems.",
      answers: [
        {
          id: "station4_q020_a",
          question_id: "station4_q020",
          answer_text: "Make the checklist more polished and official.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q020_b",
          question_id: "station4_q020",
          answer_text: "Merge duplicates and flag unclear steps.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station4_q020_c",
          question_id: "station4_q020",
          answer_text: "Choose which steps the team can skip.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q020_d",
          question_id: "station4_q020",
          answer_text: "Turn the checklist into a short paragraph.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q021",
      station_id: "station_4",
      stage: 3,
      complexity: 3,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A decision memo needs options, not a single recommendation. Which prompt gives the best AI task?",
          options: {
            A: "Recommend the option that sounds strongest.",
            B: "Write a persuasive memo for one option.",
            C: "Prepare options with pros, cons and assumptions.",
            D: "Summarise the memo in fewer words.",
          },
          correct_answer: "C",
          feedback:
            "The prompt asks AI to prepare options and assumptions. That supports judgment instead of pushing one recommendation too early.",
        },
        pt: {
          question:
            "Um memo de decisão precisa de opções, não de recomendação única. Qual prompt dá a melhor tarefa à IA?",
          options: {
            A: "Recomende a opção que parece mais forte.",
            B: "Escreva memo persuasivo para uma opção.",
            C: "Prepare opções com prós, contras e premissas.",
            D: "Resuma o memo em menos palavras.",
          },
          correct_answer: "C",
          feedback:
            "O prompt pede opções e premissas. Ele apoia o julgamento em vez de empurrar uma recomendação cedo demais.",
        },
        sv: {
          question:
            "Ett beslutsunderlag behöver alternativ, inte en enda rekommendation. Vilken prompt ger bäst uppgift?",
          options: {
            A: "Rekommendera alternativet som låter starkast.",
            B: "Skriv övertygande underlag för ett alternativ.",
            C: "Förbered alternativ med fördelar, nackdelar och antaganden.",
            D: "Sammanfatta underlaget med färre ord.",
          },
          correct_answer: "C",
          feedback:
            "Prompten ber om alternativ och antaganden. Den stödjer omdöme i stället för att driva en rekommendation för tidigt.",
        },
        nl: {
          question:
            "Een beslisnotitie heeft opties nodig, geen enkele aanbeveling. Welke prompt geeft de beste taak?",
          options: {
            A: "Beveel de optie aan die het sterkst klinkt.",
            B: "Schrijf overtuigende notitie voor één optie.",
            C: "Bereid opties voor met voor- en nadelen en aannames.",
            D: "Vat de notitie korter samen.",
          },
          correct_answer: "C",
          feedback:
            "De prompt vraagt om opties en aannames. Dat ondersteunt oordeel in plaats van één aanbeveling te vroeg te sturen.",
        },
      },
      question_text:
        "A decision memo needs options, not a single recommendation. Which prompt gives the best AI task?",
      explanation:
        "The prompt asks AI to prepare options and assumptions. That supports judgment instead of pushing one recommendation too early.",
      answers: [
        {
          id: "station4_q021_a",
          question_id: "station4_q021",
          answer_text: "Recommend the option that sounds strongest.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q021_b",
          question_id: "station4_q021",
          answer_text: "Write a persuasive memo for one option.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q021_c",
          question_id: "station4_q021",
          answer_text: "Prepare options with pros, cons and assumptions.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station4_q021_d",
          question_id: "station4_q021",
          answer_text: "Summarise the memo in fewer words.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q022",
      station_id: "station_4",
      stage: 3,
      complexity: 4,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Several teams give conflicting inputs for the same update. Which prompt helps you use AI without hiding the conflict?",
          options: {
            A: "Compare inputs and list conflicts to resolve.",
            B: "Write one clean version from all inputs.",
            C: "Choose the team with the clearest wording.",
            D: "Remove conflicting details from the update.",
          },
          correct_answer: "A",
          feedback:
            "The prompt keeps conflict visible and actionable. It helps the user resolve issues instead of smoothing them away.",
        },
        pt: {
          question:
            "Várias equipes dão inputs conflitantes para a mesma atualização. Qual prompt ajuda a usar IA sem esconder o conflito?",
          options: {
            A: "Compare inputs e liste conflitos a resolver.",
            B: "Escreva uma versão limpa com todos os inputs.",
            C: "Escolha a equipe com texto mais claro.",
            D: "Remova detalhes conflitantes da atualização.",
          },
          correct_answer: "A",
          feedback:
            "O prompt mantém o conflito visível e acionável. Ele ajuda o usuário a resolver problemas em vez de suavizá-los.",
        },
        sv: {
          question:
            "Flera team ger motstridig input till samma uppdatering. Vilken prompt hjälper utan att dölja konflikten?",
          options: {
            A: "Jämför input och lista konflikter att lösa.",
            B: "Skriv en ren version från all input.",
            C: "Välj teamet med tydligast formulering.",
            D: "Ta bort motstridiga detaljer ur uppdateringen.",
          },
          correct_answer: "A",
          feedback:
            "Prompten håller konflikten synlig och möjlig att agera på. Den hjälper användaren lösa problem i stället för att släta över dem.",
        },
        nl: {
          question:
            "Meerdere teams geven conflicterende input voor dezelfde update. Welke prompt helpt zonder het conflict te verbergen?",
          options: {
            A: "Vergelijk input en lijst conflicten om op te lossen.",
            B: "Schrijf één nette versie uit alle input.",
            C: "Kies het team met de duidelijkste tekst.",
            D: "Verwijder conflicterende details uit de update.",
          },
          correct_answer: "A",
          feedback:
            "De prompt houdt conflict zichtbaar en actiegericht. Hij helpt de gebruiker problemen oplossen in plaats van gladstrijken.",
        },
      },
      question_text:
        "Several teams give conflicting inputs for the same update. Which prompt helps you use AI without hiding the conflict?",
      explanation:
        "The prompt keeps conflict visible and actionable. It helps the user resolve issues instead of smoothing them away.",
      answers: [
        {
          id: "station4_q022_a",
          question_id: "station4_q022",
          answer_text: "Compare inputs and list conflicts to resolve.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q022_b",
          question_id: "station4_q022",
          answer_text: "Write one clean version from all inputs.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q022_c",
          question_id: "station4_q022",
          answer_text: "Choose the team with the clearest wording.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q022_d",
          question_id: "station4_q022",
          answer_text: "Remove conflicting details from the update.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q023",
      station_id: "station_4",
      stage: 3,
      complexity: 4,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Two audiences need the same update, but one wants detail and one wants speed. Which prompt is strongest?",
          options: {
            A: "Write one update that works for everyone.",
            B: "Make the update short so both groups read it.",
            C: "Let AI choose which audience gets detail.",
            D: "Draft two versions with different detail levels.",
          },
          correct_answer: "D",
          feedback:
            "The prompt asks for audience-specific versions. It handles the communication need without forcing one weak compromise.",
        },
        pt: {
          question:
            "Dois públicos precisam da mesma atualização, mas um quer detalhe e outro quer rapidez. Qual prompt é mais forte?",
          options: {
            A: "Escreva uma atualização que sirva para todos.",
            B: "Deixe a atualização curta para ambos lerem.",
            C: "Deixe a IA escolher quem recebe detalhe.",
            D: "Rascunhe duas versões com níveis diferentes.",
          },
          correct_answer: "D",
          feedback:
            "O prompt pede versões específicas por público. Ele atende a comunicação sem forçar um compromisso fraco.",
        },
        sv: {
          question:
            "Två målgrupper behöver samma uppdatering, men en vill ha detaljer och en vill ha snabbhet. Vilken prompt är starkast?",
          options: {
            A: "Skriv en uppdatering som fungerar för alla.",
            B: "Gör uppdateringen kort så båda läser.",
            C: "Låt verktyget välja vem som får detaljer.",
            D: "Skapa två versioner med olika detaljnivå.",
          },
          correct_answer: "D",
          feedback:
            "Prompten ber om målgruppsanpassade versioner. Den löser kommunikationsbehovet utan en svag kompromiss.",
        },
        nl: {
          question:
            "Twee doelgroepen hebben dezelfde update nodig, maar één wil detail en één snelheid. Welke prompt is het sterkst?",
          options: {
            A: "Schrijf één update die voor iedereen werkt.",
            B: "Maak de update kort zodat beide groepen lezen.",
            C: "Laat AI kiezen wie details krijgt.",
            D: "Maak twee versies met verschillend detailniveau.",
          },
          correct_answer: "D",
          feedback:
            "De prompt vraagt om doelgroepgerichte versies. Hij behandelt de communicatiebehoefte zonder een zwak compromis.",
        },
      },
      question_text:
        "Two audiences need the same update, but one wants detail and one wants speed. Which prompt is strongest?",
      explanation:
        "The prompt asks for audience-specific versions. It handles the communication need without forcing one weak compromise.",
      answers: [
        {
          id: "station4_q023_a",
          question_id: "station4_q023",
          answer_text: "Write one update that works for everyone.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q023_b",
          question_id: "station4_q023",
          answer_text: "Make the update short so both groups read it.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q023_c",
          question_id: "station4_q023",
          answer_text: "Let AI choose which audience gets detail.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q023_d",
          question_id: "station4_q023",
          answer_text: "Draft two versions with different detail levels.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station4_q024",
      station_id: "station_4",
      stage: 3,
      complexity: 4,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI gives a confident risk summary, but you suspect weak assumptions. Which follow-up prompt is most useful?",
          options: {
            A: "Make the risk summary more concise.",
            B: "Challenge assumptions behind each risk.",
            C: "Rank the risks from highest to lowest.",
            D: "Turn the risks into executive wording.",
          },
          correct_answer: "B",
          feedback:
            "The follow-up asks AI to test the reasoning behind the risks. That is stronger than polishing or ranking a weak summary.",
        },
        pt: {
          question:
            "A IA dá resumo de riscos confiante, mas você suspeita de premissas frágeis. Qual prompt de acompanhamento é mais útil?",
          options: {
            A: "Deixe o resumo de riscos mais conciso.",
            B: "Questione premissas por trás de cada risco.",
            C: "Classifique os riscos do maior ao menor.",
            D: "Transforme riscos em linguagem executiva.",
          },
          correct_answer: "B",
          feedback:
            "O acompanhamento pede que a IA teste o raciocínio por trás dos riscos. Isso é melhor que polir ou ranquear resumo frágil.",
        },
        sv: {
          question:
            "AI-verktyget ger en säker risksammanfattning, men du misstänker svaga antaganden. Vilken uppföljningsprompt är mest användbar?",
          options: {
            A: "Gör risksammanfattningen mer koncis.",
            B: "Utmana antaganden bakom varje risk.",
            C: "Rangordna riskerna från högst till lägst.",
            D: "Gör riskerna till ledningsspråk.",
          },
          correct_answer: "B",
          feedback:
            "Uppföljningen ber verktyget pröva resonemanget bakom riskerna. Det är starkare än att polera eller rangordna en svag sammanfattning.",
        },
        nl: {
          question:
            "De AI-tool geeft een zekere risicosamenvatting, maar je vermoedt zwakke aannames. Welke opvolgprompt is het nuttigst?",
          options: {
            A: "Maak de risicosamenvatting beknopter.",
            B: "Daag aannames achter elk risico uit.",
            C: "Rangschik risico’s van hoog naar laag.",
            D: "Maak risico’s geschikt voor executives.",
          },
          correct_answer: "B",
          feedback:
            "De opvolgprompt vraagt de redenering achter risico’s te testen. Dat is sterker dan een zwakke samenvatting polijsten of rangschikken.",
        },
      },
      question_text:
        "AI gives a confident risk summary, but you suspect weak assumptions. Which follow-up prompt is most useful?",
      explanation:
        "The follow-up asks AI to test the reasoning behind the risks. That is stronger than polishing or ranking a weak summary.",
      answers: [
        {
          id: "station4_q024_a",
          question_id: "station4_q024",
          answer_text: "Make the risk summary more concise.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q024_b",
          question_id: "station4_q024",
          answer_text: "Challenge assumptions behind each risk.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station4_q024_c",
          question_id: "station4_q024",
          answer_text: "Rank the risks from highest to lowest.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q024_d",
          question_id: "station4_q024",
          answer_text: "Turn the risks into executive wording.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q025",
      station_id: "station_4",
      stage: 3,
      complexity: 4,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A status update must be honest but not alarming. Which prompt gives AI the right balance?",
          options: {
            A: "Make the update sound positive and calm.",
            B: "Hide risks until there is more certainty.",
            C: "Use calm tone and keep key risks visible.",
            D: "Make the risks sound less serious.",
          },
          correct_answer: "C",
          feedback:
            "The prompt balances tone and substance. It improves readability while keeping important risks visible to the audience.",
        },
        pt: {
          question:
            "Uma atualização de status deve ser honesta sem alarmar. Qual prompt dá o equilíbrio certo à IA?",
          options: {
            A: "Deixe a atualização positiva e calma.",
            B: "Esconda riscos até haver mais certeza.",
            C: "Use tom calmo e mantenha riscos visíveis.",
            D: "Faça os riscos parecerem menos sérios.",
          },
          correct_answer: "C",
          feedback:
            "O prompt equilibra tom e substância. Ele melhora a leitura mantendo riscos importantes visíveis para o público.",
        },
        sv: {
          question:
            "En statusuppdatering ska vara ärlig utan att larma. Vilken prompt ger rätt balans?",
          options: {
            A: "Få uppdateringen att låta positiv och lugn.",
            B: "Dölj risker tills mer säkerhet finns.",
            C: "Använd lugn ton och behåll viktiga risker.",
            D: "Få riskerna att låta mindre allvarliga.",
          },
          correct_answer: "C",
          feedback:
            "Prompten balanserar ton och innehåll. Den förbättrar läsbarhet och håller viktiga risker synliga för målgruppen.",
        },
        nl: {
          question:
            "Een statusupdate moet eerlijk zijn zonder te alarmeren. Welke prompt geeft de juiste balans?",
          options: {
            A: "Laat de update positief en rustig klinken.",
            B: "Verberg risico’s tot er meer zekerheid is.",
            C: "Gebruik rustige toon en houd risico’s zichtbaar.",
            D: "Laat risico’s minder ernstig klinken.",
          },
          correct_answer: "C",
          feedback:
            "De prompt balanceert toon en inhoud. Hij verbetert leesbaarheid en houdt belangrijke risico’s zichtbaar voor de doelgroep.",
        },
      },
      question_text:
        "A status update must be honest but not alarming. Which prompt gives AI the right balance?",
      explanation:
        "The prompt balances tone and substance. It improves readability while keeping important risks visible to the audience.",
      answers: [
        {
          id: "station4_q025_a",
          question_id: "station4_q025",
          answer_text: "Make the update sound positive and calm.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q025_b",
          question_id: "station4_q025",
          answer_text: "Hide risks until there is more certainty.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q025_c",
          question_id: "station4_q025",
          answer_text: "Use calm tone and keep key risks visible.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station4_q025_d",
          question_id: "station4_q025",
          answer_text: "Make the risks sound less serious.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q026",
      station_id: "station_4",
      stage: 3,
      complexity: 4,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI compares two plans but misses trade-offs and unresolved questions. Which follow-up prompt would improve the comparison?",
          options: {
            A: "Add trade-offs, assumptions and open questions.",
            B: "Choose the plan with fewer disadvantages.",
            C: "Make the comparison shorter for leaders.",
            D: "Rewrite both plans in the same tone.",
          },
          correct_answer: "A",
          feedback:
            "The follow-up adds the missing decision support. It asks for trade-offs and open questions, not a premature choice.",
        },
        pt: {
          question:
            "A IA compara dois planos, mas perde trade-offs e perguntas abertas. Qual prompt de acompanhamento melhoraria a comparação?",
          options: {
            A: "Adicione trade-offs, premissas e perguntas abertas.",
            B: "Escolha o plano com menos desvantagens.",
            C: "Deixe a comparação mais curta para líderes.",
            D: "Reescreva os dois planos no mesmo tom.",
          },
          correct_answer: "A",
          feedback:
            "O acompanhamento adiciona apoio à decisão que faltava. Ele pede trade-offs e perguntas abertas, não uma escolha prematura.",
        },
        sv: {
          question:
            "AI-verktyget jämför två planer men missar avvägningar och öppna frågor. Vilken uppföljningsprompt förbättrar jämförelsen?",
          options: {
            A: "Lägg till avvägningar, antaganden och öppna frågor.",
            B: "Välj planen med färre nackdelar.",
            C: "Gör jämförelsen kortare för ledare.",
            D: "Skriv om båda planerna i samma ton.",
          },
          correct_answer: "A",
          feedback:
            "Uppföljningen lägger till saknat beslutsstöd. Den ber om avvägningar och öppna frågor, inte ett för tidigt val.",
        },
        nl: {
          question:
            "De AI-tool vergelijkt twee plannen maar mist afwegingen en open vragen. Welke opvolgprompt verbetert de vergelijking?",
          options: {
            A: "Voeg trade-offs, aannames en open vragen toe.",
            B: "Kies het plan met minder nadelen.",
            C: "Maak de vergelijking korter voor leiders.",
            D: "Herschrijf beide plannen in dezelfde toon.",
          },
          correct_answer: "A",
          feedback:
            "De opvolging voegt ontbrekende beslisondersteuning toe. Hij vraagt om afwegingen en open vragen, niet om een te vroege keuze.",
        },
      },
      question_text:
        "AI compares two plans but misses trade-offs and unresolved questions. Which follow-up prompt would improve the comparison?",
      explanation:
        "The follow-up adds the missing decision support. It asks for trade-offs and open questions, not a premature choice.",
      answers: [
        {
          id: "station4_q026_a",
          question_id: "station4_q026",
          answer_text: "Add trade-offs, assumptions and open questions.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q026_b",
          question_id: "station4_q026",
          answer_text: "Choose the plan with fewer disadvantages.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q026_c",
          question_id: "station4_q026",
          answer_text: "Make the comparison shorter for leaders.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q026_d",
          question_id: "station4_q026",
          answer_text: "Rewrite both plans in the same tone.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q027",
      station_id: "station_4",
      stage: 3,
      complexity: 5,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You expect a difficult sponsor meeting and want stronger preparation. Which prompt uses role prompting well?",
          options: {
            A: "List likely sponsor questions and short answers.",
            B: "Make our current position sound stronger.",
            C: "Summarise the project benefits in one paragraph.",
            D: "Act as a critical sponsor; list objections and evidence gaps.",
          },
          correct_answer: "D",
          feedback:
            "The role prompt asks for objections and evidence gaps. That creates stronger preparation than likely questions alone.",
        },
        pt: {
          question:
            "Você espera reunião difícil com patrocinador e quer preparação mais forte. Qual prompt usa bem um papel?",
          options: {
            A: "Liste perguntas prováveis do patrocinador e respostas curtas.",
            B: "Faça nossa posição atual parecer mais forte.",
            C: "Resuma benefícios do projeto em um parágrafo.",
            D: "Aja como patrocinador crítico; liste objeções e lacunas de evidência.",
          },
          correct_answer: "D",
          feedback:
            "O prompt com papel pede objeções e lacunas de evidência. Isso cria preparação mais forte que apenas perguntas prováveis.",
        },
        sv: {
          question:
            "Du väntar ett svårt sponsormöte och vill förbereda dig bättre. Vilken prompt använder roll på bra sätt?",
          options: {
            A: "Lista troliga sponsorfrågor och korta svar.",
            B: "Få vår nuvarande ståndpunkt att verka starkare.",
            C: "Sammanfatta projektfördelar i ett stycke.",
            D: "Agera kritisk sponsor; lista invändningar och bevisluckor.",
          },
          correct_answer: "D",
          feedback:
            "Rollprompten ber om invändningar och bevisluckor. Det ger starkare förberedelse än bara troliga frågor.",
        },
        nl: {
          question:
            "Je verwacht een lastig sponsoroverleg en wilt sterker voorbereiden. Welke prompt gebruikt een rol goed?",
          options: {
            A: "Noem waarschijnlijke sponsorvragen en korte antwoorden.",
            B: "Laat ons huidige standpunt sterker klinken.",
            C: "Vat projectvoordelen samen in één alinea.",
            D: "Doe alsof je kritische sponsor bent; noem bezwaren en bewijsleemtes.",
          },
          correct_answer: "D",
          feedback:
            "De rolprompt vraagt om bezwaren en bewijsleemtes. Dat geeft sterkere voorbereiding dan alleen waarschijnlijke vragen.",
        },
      },
      question_text:
        "You expect a difficult sponsor meeting and want stronger preparation. Which prompt uses role prompting well?",
      explanation:
        "The role prompt asks for objections and evidence gaps. That creates stronger preparation than likely questions alone.",
      answers: [
        {
          id: "station4_q027_a",
          question_id: "station4_q027",
          answer_text: "List likely sponsor questions and short answers.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q027_b",
          question_id: "station4_q027",
          answer_text: "Make our current position sound stronger.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q027_c",
          question_id: "station4_q027",
          answer_text: "Summarise the project benefits in one paragraph.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q027_d",
          question_id: "station4_q027",
          answer_text:
            "Act as a critical sponsor; list objections and evidence gaps.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station4_q028",
      station_id: "station_4",
      stage: 3,
      complexity: 5,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A handover draft looks clear, but two actions seem to have conflicting owners. Which follow-up prompt is strongest?",
          options: {
            A: "Make the handover shorter and easier to scan.",
            B: "Flag conflicting owners and responsibilities.",
            C: "Choose the owner with the clearest task.",
            D: "Rewrite the handover as a friendly note.",
          },
          correct_answer: "B",
          feedback:
            "The follow-up asks AI to expose a consistency problem. That helps the user resolve ownership instead of hiding conflict.",
        },
        pt: {
          question:
            "Um rascunho de transição parece claro, mas duas ações têm responsáveis conflitantes. Qual prompt de acompanhamento é mais forte?",
          options: {
            A: "Deixe a transição mais curta e fácil de ler.",
            B: "Marque responsáveis e responsabilidades conflitantes.",
            C: "Escolha o responsável com tarefa mais clara.",
            D: "Reescreva a transição como nota amigável.",
          },
          correct_answer: "B",
          feedback:
            "O acompanhamento pede que a IA exponha problema de consistência. Isso ajuda o usuário a resolver ownership sem esconder conflito.",
        },
        sv: {
          question:
            "Ett överlämningsutkast verkar tydligt, men två åtgärder har motstridiga ansvariga. Vilken uppföljningsprompt är starkast?",
          options: {
            A: "Gör överlämningen kortare och lättare att skanna.",
            B: "Markera motstridiga ansvariga och ansvar.",
            C: "Välj ansvarig med tydligast uppgift.",
            D: "Skriv överlämningen som en vänlig notis.",
          },
          correct_answer: "B",
          feedback:
            "Uppföljningen ber verktyget visa ett konsekvensproblem. Det hjälper användaren lösa ägarskap i stället för att dölja konflikt.",
        },
        nl: {
          question:
            "Een overdrachtsconcept lijkt duidelijk, maar twee acties hebben conflicterende eigenaars. Welke opvolgprompt is het sterkst?",
          options: {
            A: "Maak de overdracht korter en scanbaar.",
            B: "Markeer conflicterende eigenaars en verantwoordelijkheden.",
            C: "Kies de eigenaar met de duidelijkste taak.",
            D: "Herschrijf de overdracht als vriendelijke notitie.",
          },
          correct_answer: "B",
          feedback:
            "De opvolging vraagt de AI-tool een consistentieprobleem zichtbaar te maken. Dat helpt eigenaarschap oplossen zonder conflict te verbergen.",
        },
      },
      question_text:
        "A handover draft looks clear, but two actions seem to have conflicting owners. Which follow-up prompt is strongest?",
      explanation:
        "The follow-up asks AI to expose a consistency problem. That helps the user resolve ownership instead of hiding conflict.",
      answers: [
        {
          id: "station4_q028_a",
          question_id: "station4_q028",
          answer_text: "Make the handover shorter and easier to scan.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q028_b",
          question_id: "station4_q028",
          answer_text: "Flag conflicting owners and responsibilities.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station4_q028_c",
          question_id: "station4_q028",
          answer_text: "Choose the owner with the clearest task.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q028_d",
          question_id: "station4_q028",
          answer_text: "Rewrite the handover as a friendly note.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q029",
      station_id: "station_4",
      stage: 3,
      complexity: 5,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Teams disagree on dates, but a deadline message must go out soon. Which prompt supports the next useful step?",
          options: {
            A: "Write a confident deadline message now.",
            B: "Use the date mentioned most often.",
            C: "Map conflicts, assumptions and questions for alignment.",
            D: "Remove uncertain dates from the message.",
          },
          correct_answer: "C",
          feedback:
            "The prompt turns disagreement into an alignment task. It helps prepare a better message without pretending uncertainty is solved.",
        },
        pt: {
          question:
            "Equipes discordam sobre datas, mas mensagem de prazo sairá em breve. Qual prompt apoia o próximo passo útil?",
          options: {
            A: "Escreva agora mensagem de prazo confiante.",
            B: "Escolha a data mencionada mais vezes.",
            C: "Mapeie conflitos, premissas e perguntas para alinhamento.",
            D: "Remova datas incertas da mensagem.",
          },
          correct_answer: "C",
          feedback:
            "O prompt transforma discordância em tarefa de alinhamento. Ele prepara mensagem melhor sem fingir que a incerteza acabou.",
        },
        sv: {
          question:
            "Team är oense om datum, men ett deadline-meddelande ska snart skickas. Vilken prompt stödjer nästa användbara steg?",
          options: {
            A: "Skriv ett säkert deadline-meddelande nu.",
            B: "Använd datumet som nämns flest gånger.",
            C: "Kartlägg konflikter, antaganden och frågor för samsyn.",
            D: "Ta bort osäkra datum från meddelandet.",
          },
          correct_answer: "C",
          feedback:
            "Prompten gör oenighet till en samordningsuppgift. Den hjälper till att förbereda ett bättre meddelande utan låtsad säkerhet.",
        },
        nl: {
          question:
            "Teams verschillen over datums, maar een deadlinebericht moet snel uit. Welke prompt ondersteunt de volgende nuttige stap?",
          options: {
            A: "Schrijf nu een zeker deadlinebericht.",
            B: "Gebruik de datum die het vaakst voorkomt.",
            C: "Breng conflicten, aannames en vragen in kaart.",
            D: "Verwijder onzekere datums uit het bericht.",
          },
          correct_answer: "C",
          feedback:
            "De prompt maakt van verschil een afstemmingstaak. Hij helpt een beter bericht voorbereiden zonder onzekerheid te verbergen.",
        },
      },
      question_text:
        "Teams disagree on dates, but a deadline message must go out soon. Which prompt supports the next useful step?",
      explanation:
        "The prompt turns disagreement into an alignment task. It helps prepare a better message without pretending uncertainty is solved.",
      answers: [
        {
          id: "station4_q029_a",
          question_id: "station4_q029",
          answer_text: "Write a confident deadline message now.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station4_q029_b",
          question_id: "station4_q029",
          answer_text: "Use the date mentioned most often.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q029_c",
          question_id: "station4_q029",
          answer_text:
            "Map conflicts, assumptions and questions for alignment.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station4_q029_d",
          question_id: "station4_q029",
          answer_text: "Remove uncertain dates from the message.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station4_q030",
      station_id: "station_4",
      stage: 3,
      complexity: 5,
      core_concept:
        "Strong workplace prompts include task, context, audience, output format, constraints or useful follow-up instructions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI drafted two audience versions, but both hide uncertainty. Which follow-up prompt should you use?",
          options: {
            A: "Keep uncertainty visible and adapt wording by audience.",
            B: "Make both versions more confident and brief.",
            C: "Choose the version that sounds most positive.",
            D: "Remove caveats from the senior version.",
          },
          correct_answer: "A",
          feedback:
            "The follow-up preserves uncertainty while adapting language. It improves communication without making the output sound more certain than it is.",
        },
        pt: {
          question:
            "A IA rascunhou duas versões para públicos diferentes, mas ambas escondem incerteza. Qual prompt de acompanhamento usar?",
          options: {
            A: "Mantenha incerteza visível e adapte linguagem por público.",
            B: "Deixe as duas versões mais confiantes e breves.",
            C: "Escolha a versão que soa mais positiva.",
            D: "Remova ressalvas da versão para liderança.",
          },
          correct_answer: "A",
          feedback:
            "O acompanhamento preserva incerteza e adapta linguagem. Ele melhora a comunicação sem fazer o resultado parecer mais certo.",
        },
        sv: {
          question:
            "AI-verktyget skapade två målgruppsversioner, men båda döljer osäkerhet. Vilken uppföljningsprompt ska du använda?",
          options: {
            A: "Behåll osäkerhet synlig och anpassa språk per målgrupp.",
            B: "Gör båda versionerna säkrare och kortare.",
            C: "Välj versionen som låter mest positiv.",
            D: "Ta bort förbehåll från ledningsversionen.",
          },
          correct_answer: "A",
          feedback:
            "Uppföljningen bevarar osäkerhet och anpassar språk. Den förbättrar kommunikationen utan att låta resultatet säkrare än det är.",
        },
        nl: {
          question:
            "De AI-tool maakte twee publieksversies, maar beide verbergen onzekerheid. Welke opvolgprompt gebruik je?",
          options: {
            A: "Houd onzekerheid zichtbaar en pas taal per doelgroep aan.",
            B: "Maak beide versies zekerder en korter.",
            C: "Kies de versie die het positiefst klinkt.",
            D: "Verwijder nuances uit de senior versie.",
          },
          correct_answer: "A",
          feedback:
            "De opvolging bewaart onzekerheid en past taal aan. Zij verbetert communicatie zonder het resultaat zekerder te laten lijken.",
        },
      },
      question_text:
        "AI drafted two audience versions, but both hide uncertainty. Which follow-up prompt should you use?",
      explanation:
        "The follow-up preserves uncertainty while adapting language. It improves communication without making the output sound more certain than it is.",
      answers: [
        {
          id: "station4_q030_a",
          question_id: "station4_q030",
          answer_text:
            "Keep uncertainty visible and adapt wording by audience.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station4_q030_b",
          question_id: "station4_q030",
          answer_text: "Make both versions more confident and brief.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station4_q030_c",
          question_id: "station4_q030",
          answer_text: "Choose the version that sounds most positive.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station4_q030_d",
          question_id: "station4_q030",
          answer_text: "Remove caveats from the senior version.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q001",
      station_id: "station_5",
      stage: 1,
      complexity: 1,
      core_concept:
        "Email thread summaries should be checked against the original messages",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI summarises a long email thread and lists one decision. What should you check before using it?",
          options: {
            A: "Make the summary sound more formal.",
            B: "Ask AI to add more decisions.",
            C: "Check the decision in the original thread.",
            D: "Treat the summary as the final record.",
          },
          correct_answer: "C",
          feedback:
            "AI summaries are useful, but they are not the source of truth. Check important decisions in the original thread.",
        },
        pt: {
          question:
            "A IA resume uma longa troca de e-mails e lista uma decisão. O que verificar antes de usar?",
          options: {
            A: "Deixar o resumo mais formal.",
            B: "Pedir à IA que adicione mais decisões.",
            C: "Checar a decisão na troca original.",
            D: "Tratar o resumo como registro final.",
          },
          correct_answer: "C",
          feedback:
            "Resumos da IA são úteis, mas não são a fonte da verdade. Cheque decisões importantes na troca original.",
        },
        sv: {
          question:
            "AI-verktyget sammanfattar en lång mejltråd och listar ett beslut. Vad bör du kontrollera före användning?",
          options: {
            A: "Göra sammanfattningen mer formell.",
            B: "Be verktyget lägga till fler beslut.",
            C: "Kontrollera beslutet i ursprungstråden.",
            D: "Behandla sammanfattningen som slutligt register.",
          },
          correct_answer: "C",
          feedback:
            "AI-sammanfattningar är användbara, men de är inte sanningskällan. Kontrollera viktiga beslut i originaltråden.",
        },
        nl: {
          question:
            "De AI-tool vat een lange e-mailreeks samen en noemt één besluit. Wat controleer je vóór gebruik?",
          options: {
            A: "De samenvatting formeler maken.",
            B: "De AI-tool meer besluiten laten toevoegen.",
            C: "Het besluit in de originele reeks controleren.",
            D: "De samenvatting als definitief verslag gebruiken.",
          },
          correct_answer: "C",
          feedback:
            "AI-samenvattingen zijn nuttig, maar niet de bron van waarheid. Controleer belangrijke besluiten in de originele reeks.",
        },
      },
      question_text:
        "AI summarises a long email thread and lists one decision. What should you check before using it?",
      explanation:
        "AI summaries are useful, but they are not the source of truth. Check important decisions in the original thread.",
      answers: [
        {
          id: "station5_q001_a",
          question_id: "station5_q001",
          answer_text: "Make the summary sound more formal.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q001_b",
          question_id: "station5_q001",
          answer_text: "Ask AI to add more decisions.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q001_c",
          question_id: "station5_q001",
          answer_text: "Check the decision in the original thread.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station5_q001_d",
          question_id: "station5_q001",
          answer_text: "Treat the summary as the final record.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q002",
      station_id: "station_5",
      stage: 1,
      complexity: 1,
      core_concept: "Unsourced AI answers are leads, not final truth",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI answers a question about a document but shows no source. How should you treat the answer?",
          options: {
            A: "As a lead to check.",
            B: "As approved information.",
            C: "As stronger than the document.",
            D: "As a final decision.",
          },
          correct_answer: "A",
          feedback:
            "Without a source, the answer may still help, but it should be treated as a lead to verify.",
        },
        pt: {
          question:
            "A IA responde sobre um documento, mas não mostra fonte. Como tratar a resposta?",
          options: {
            A: "Como pista a verificar.",
            B: "Como informação aprovada.",
            C: "Como mais forte que o documento.",
            D: "Como decisão final.",
          },
          correct_answer: "A",
          feedback:
            "Sem fonte, a resposta ainda pode ajudar, mas deve ser tratada como pista a verificar.",
        },
        sv: {
          question:
            "AI-verktyget svarar om ett dokument men visar ingen källa. Hur bör du behandla svaret?",
          options: {
            A: "Som ett spår att kontrollera.",
            B: "Som godkänd information.",
            C: "Som starkare än dokumentet.",
            D: "Som ett slutligt beslut.",
          },
          correct_answer: "A",
          feedback:
            "Utan källa kan svaret ändå hjälpa, men det bör behandlas som ett spår att verifiera.",
        },
        nl: {
          question:
            "De AI-tool antwoordt over een document maar toont geen bron. Hoe behandel je het antwoord?",
          options: {
            A: "Als aanwijzing om te controleren.",
            B: "Als goedgekeurde informatie.",
            C: "Als sterker dan het document.",
            D: "Als een definitief besluit.",
          },
          correct_answer: "A",
          feedback:
            "Zonder bron kan het antwoord helpen, maar behandel het als aanwijzing die je moet verifiëren.",
        },
      },
      question_text:
        "AI answers a question about a document but shows no source. How should you treat the answer?",
      explanation:
        "Without a source, the answer may still help, but it should be treated as a lead to verify.",
      answers: [
        {
          id: "station5_q002_a",
          question_id: "station5_q002",
          answer_text: "As a lead to check.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station5_q002_b",
          question_id: "station5_q002",
          answer_text: "As approved information.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q002_c",
          question_id: "station5_q002",
          answer_text: "As stronger than the document.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q002_d",
          question_id: "station5_q002",
          answer_text: "As a final decision.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q003",
      station_id: "station_5",
      stage: 1,
      complexity: 1,
      core_concept:
        "Information search should include where the answer came from",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You ask AI where a topic was discussed. It gives an answer but no location. What should you ask for next?",
          options: {
            A: "A more confident wording.",
            B: "The file, message or meeting source.",
            C: "A shorter answer with no details.",
            D: "A decision based on the topic.",
          },
          correct_answer: "B",
          feedback:
            "Asking for the source helps you locate the material and judge whether the answer is grounded.",
        },
        pt: {
          question:
            "Você pergunta à IA onde um tema foi discutido. Ela responde sem indicar local. O que pedir em seguida?",
          options: {
            A: "Uma redação mais confiante.",
            B: "O arquivo, mensagem ou reunião de origem.",
            C: "Resposta mais curta sem detalhes.",
            D: "Uma decisão baseada no tema.",
          },
          correct_answer: "B",
          feedback:
            "Pedir a fonte ajuda a localizar o material e avaliar se a resposta está fundamentada.",
        },
        sv: {
          question:
            "Du frågar AI-verktyget var ett ämne diskuterades. Det svarar utan plats. Vad bör du be om?",
          options: {
            A: "En mer självsäker formulering.",
            B: "Fil, meddelande eller möte som källa.",
            C: "Ett kortare svar utan detaljer.",
            D: "Ett beslut baserat på ämnet.",
          },
          correct_answer: "B",
          feedback:
            "Att be om källa hjälper dig hitta materialet och bedöma om svaret är grundat.",
        },
        nl: {
          question:
            "Je vraagt de AI-tool waar een onderwerp is besproken. Het antwoord noemt geen plek. Wat vraag je daarna?",
          options: {
            A: "Een zelfverzekerdere formulering.",
            B: "Het bestand, bericht of overleg als bron.",
            C: "Een korter antwoord zonder details.",
            D: "Een besluit op basis van het onderwerp.",
          },
          correct_answer: "B",
          feedback:
            "Vragen om de bron helpt materiaal vinden en beoordelen of het antwoord onderbouwd is.",
        },
      },
      question_text:
        "You ask AI where a topic was discussed. It gives an answer but no location. What should you ask for next?",
      explanation:
        "Asking for the source helps you locate the material and judge whether the answer is grounded.",
      answers: [
        {
          id: "station5_q003_a",
          question_id: "station5_q003",
          answer_text: "A more confident wording.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q003_b",
          question_id: "station5_q003",
          answer_text: "The file, message or meeting source.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station5_q003_c",
          question_id: "station5_q003",
          answer_text: "A shorter answer with no details.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q003_d",
          question_id: "station5_q003",
          answer_text: "A decision based on the topic.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q004",
      station_id: "station_5",
      stage: 1,
      complexity: 2,
      core_concept:
        "Actions from meeting notes should be checked against the notes",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI extracts action owners from meeting notes. One owner looks surprising. What is the best check?",
          options: {
            A: "Ask AI to make the owner sound certain.",
            B: "Remove the surprising owner from the list.",
            C: "Use the list because notes were provided.",
            D: "Check the owner in the meeting notes.",
          },
          correct_answer: "D",
          feedback:
            "A surprising owner should be checked against the meeting notes. AI can extract information, but the source still matters.",
        },
        pt: {
          question:
            "A IA extrai responsáveis por ações de notas de reunião. Um responsável parece estranho. Qual é a melhor verificação?",
          options: {
            A: "Pedir que a IA deixe o responsável certo.",
            B: "Remover o responsável estranho da lista.",
            C: "Usar a lista porque havia notas.",
            D: "Checar o responsável nas notas da reunião.",
          },
          correct_answer: "D",
          feedback:
            "Um responsável inesperado deve ser checado nas notas. A IA pode extrair informação, mas a fonte ainda importa.",
        },
        sv: {
          question:
            "AI-verktyget hämtar ansvariga från mötesanteckningar. En ansvarig verkar oväntad. Vilken kontroll är bäst?",
          options: {
            A: "Be verktyget göra ansvarig säker.",
            B: "Ta bort den oväntade ansvariga.",
            C: "Använda listan eftersom anteckningar fanns.",
            D: "Kontrollera ansvarig i mötesanteckningarna.",
          },
          correct_answer: "D",
          feedback:
            "En oväntad ansvarig bör kontrolleras i anteckningarna. Verktyget kan extrahera information, men källan spelar roll.",
        },
        nl: {
          question:
            "De AI-tool haalt actie-eigenaars uit vergadernotities. Eén eigenaar lijkt verrassend. Wat is de beste check?",
          options: {
            A: "De eigenaar zekerder laten klinken.",
            B: "De verrassende eigenaar verwijderen.",
            C: "De lijst gebruiken omdat er notities waren.",
            D: "De eigenaar in de notities controleren.",
          },
          correct_answer: "D",
          feedback:
            "Een verrassende eigenaar moet in de notities worden gecontroleerd. AI kan informatie ophalen, maar de bron blijft belangrijk.",
        },
      },
      question_text:
        "AI extracts action owners from meeting notes. One owner looks surprising. What is the best check?",
      explanation:
        "A surprising owner should be checked against the meeting notes. AI can extract information, but the source still matters.",
      answers: [
        {
          id: "station5_q004_a",
          question_id: "station5_q004",
          answer_text: "Ask AI to make the owner sound certain.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q004_b",
          question_id: "station5_q004",
          answer_text: "Remove the surprising owner from the list.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q004_c",
          question_id: "station5_q004",
          answer_text: "Use the list because notes were provided.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q004_d",
          question_id: "station5_q004",
          answer_text: "Check the owner in the meeting notes.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station5_q005",
      station_id: "station_5",
      stage: 1,
      complexity: 2,
      core_concept:
        "Better information search uses topic, team and time context",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You need a previous decision but only remember the topic. What helps AI-supported search most?",
          options: {
            A: "Add likely team, date range and keywords.",
            B: "Ask for any decision that sounds similar.",
            C: "Use the first result that appears.",
            D: "Ask AI to decide again from scratch.",
          },
          correct_answer: "A",
          feedback:
            "Adding team, dates and keywords gives search useful context and reduces irrelevant results.",
        },
        pt: {
          question:
            "Você precisa de decisão anterior, mas só lembra o tema. O que mais ajuda a busca com IA?",
          options: {
            A: "Adicionar equipe provável, período e palavras-chave.",
            B: "Pedir qualquer decisão parecida.",
            C: "Usar o primeiro resultado encontrado.",
            D: "Pedir à IA que decida do zero.",
          },
          correct_answer: "A",
          feedback:
            "Adicionar equipe, datas e palavras-chave dá contexto útil à busca e reduz resultados irrelevantes.",
        },
        sv: {
          question:
            "Du behöver ett tidigare beslut men minns bara ämnet. Vad hjälper AI-stödd sökning mest?",
          options: {
            A: "Lägg till troligt team, period och nyckelord.",
            B: "Be om vilket beslut som helst som liknar.",
            C: "Använd första resultatet som visas.",
            D: "Be verktyget besluta från början.",
          },
          correct_answer: "A",
          feedback:
            "Team, datum och nyckelord ger sökningen användbar kontext och minskar irrelevanta träffar.",
        },
        nl: {
          question:
            "Je zoekt een eerder besluit maar herinnert alleen het onderwerp. Wat helpt AI-zoekhulp het meest?",
          options: {
            A: "Voeg waarschijnlijk team, periode en trefwoorden toe.",
            B: "Vraag om elk besluit dat erop lijkt.",
            C: "Gebruik het eerste resultaat dat verschijnt.",
            D: "Laat de AI-tool opnieuw beslissen.",
          },
          correct_answer: "A",
          feedback:
            "Team, datums en trefwoorden geven de zoekopdracht context en verminderen irrelevante resultaten.",
        },
      },
      question_text:
        "You need a previous decision but only remember the topic. What helps AI-supported search most?",
      explanation:
        "Adding team, dates and keywords gives search useful context and reduces irrelevant results.",
      answers: [
        {
          id: "station5_q005_a",
          question_id: "station5_q005",
          answer_text: "Add likely team, date range and keywords.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station5_q005_b",
          question_id: "station5_q005",
          answer_text: "Ask for any decision that sounds similar.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q005_c",
          question_id: "station5_q005",
          answer_text: "Use the first result that appears.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q005_d",
          question_id: "station5_q005",
          answer_text: "Ask AI to decide again from scratch.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q006",
      station_id: "station_5",
      stage: 1,
      complexity: 2,
      core_concept: "AI may miss information when it lacks access",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI cannot find a project update you know exists. Which explanation is most realistic?",
          options: {
            A: "The update probably never existed.",
            B: "AI is refusing to answer correctly.",
            C: "The tool may lack access or context.",
            D: "The newest update must be wrong.",
          },
          correct_answer: "C",
          feedback:
            "AI-supported tools depend on access and context. Missing information may reflect tool limits, not absence.",
        },
        pt: {
          question:
            "A IA não encontra uma atualização de projeto que você sabe que existe. Qual explicação é mais realista?",
          options: {
            A: "A atualização provavelmente nunca existiu.",
            B: "A IA está se recusando a responder.",
            C: "A ferramenta pode não ter acesso ou contexto.",
            D: "A atualização mais recente deve estar errada.",
          },
          correct_answer: "C",
          feedback:
            "Ferramentas com IA dependem de acesso e contexto. Informação ausente pode refletir limites, não inexistência.",
        },
        sv: {
          question:
            "AI-verktyget hittar inte en projektuppdatering som du vet finns. Vilken förklaring är mest realistisk?",
          options: {
            A: "Uppdateringen har nog aldrig funnits.",
            B: "Verktyget vägrar svara korrekt.",
            C: "Verktyget kan sakna åtkomst eller kontext.",
            D: "Den senaste uppdateringen måste vara fel.",
          },
          correct_answer: "C",
          feedback:
            "AI-stödda verktyg beror på åtkomst och kontext. Saknad information kan visa begränsningar, inte frånvaro.",
        },
        nl: {
          question:
            "De AI-tool vindt een projectupdate niet waarvan je weet dat die bestaat. Welke verklaring is het meest realistisch?",
          options: {
            A: "De update heeft waarschijnlijk nooit bestaan.",
            B: "De tool weigert juist te antwoorden.",
            C: "De tool mist mogelijk toegang of context.",
            D: "De nieuwste update moet fout zijn.",
          },
          correct_answer: "C",
          feedback:
            "AI-tools hangen af van toegang en context. Ontbrekende informatie kan beperking zijn, geen bewijs van afwezigheid.",
        },
      },
      question_text:
        "AI cannot find a project update you know exists. Which explanation is most realistic?",
      explanation:
        "AI-supported tools depend on access and context. Missing information may reflect tool limits, not absence.",
      answers: [
        {
          id: "station5_q006_a",
          question_id: "station5_q006",
          answer_text: "The update probably never existed.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q006_b",
          question_id: "station5_q006",
          answer_text: "AI is refusing to answer correctly.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q006_c",
          question_id: "station5_q006",
          answer_text: "The tool may lack access or context.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station5_q006_d",
          question_id: "station5_q006",
          answer_text: "The newest update must be wrong.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q007",
      station_id: "station_5",
      stage: 1,
      complexity: 2,
      core_concept:
        "Important summaries should be checked against the original source",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI summarises a procedure and the wording affects your task. How should you use the summary?",
          options: {
            A: "Use it as the only reference.",
            B: "Check the key point in the procedure.",
            C: "Ask AI to make it sound clearer.",
            D: "Ignore the summary entirely.",
          },
          correct_answer: "B",
          feedback:
            "When wording affects work, the summary should guide you to the original procedure, not replace it.",
        },
        pt: {
          question:
            "A IA resume um procedimento e a redação afeta sua tarefa. Como usar o resumo?",
          options: {
            A: "Usar como única referência.",
            B: "Checar o ponto-chave no procedimento.",
            C: "Pedir que a IA deixe mais claro.",
            D: "Ignorar o resumo completamente.",
          },
          correct_answer: "B",
          feedback:
            "Quando a redação afeta o trabalho, o resumo deve levar ao procedimento original, não substituí-lo.",
        },
        sv: {
          question:
            "AI-verktyget sammanfattar en rutin och formuleringen påverkar din uppgift. Hur bör du använda sammanfattningen?",
          options: {
            A: "Använda den som enda referens.",
            B: "Kontrollera nyckelpunkten i rutinen.",
            C: "Be verktyget göra den tydligare.",
            D: "Ignorera sammanfattningen helt.",
          },
          correct_answer: "B",
          feedback:
            "När formulering påverkar arbetet ska sammanfattningen leda till originalrutinen, inte ersätta den.",
        },
        nl: {
          question:
            "De AI-tool vat een procedure samen en de formulering raakt je taak. Hoe gebruik je de samenvatting?",
          options: {
            A: "Als enige referentie gebruiken.",
            B: "Het kernpunt in de procedure controleren.",
            C: "De tool vragen het duidelijker te maken.",
            D: "De samenvatting volledig negeren.",
          },
          correct_answer: "B",
          feedback:
            "Als formulering werk raakt, moet de samenvatting naar de originele procedure leiden, niet die vervangen.",
        },
      },
      question_text:
        "AI summarises a procedure and the wording affects your task. How should you use the summary?",
      explanation:
        "When wording affects work, the summary should guide you to the original procedure, not replace it.",
      answers: [
        {
          id: "station5_q007_a",
          question_id: "station5_q007",
          answer_text: "Use it as the only reference.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q007_b",
          question_id: "station5_q007",
          answer_text: "Check the key point in the procedure.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station5_q007_c",
          question_id: "station5_q007",
          answer_text: "Ask AI to make it sound clearer.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q007_d",
          question_id: "station5_q007",
          answer_text: "Ignore the summary entirely.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q008",
      station_id: "station_5",
      stage: 1,
      complexity: 2,
      core_concept: "Preparation answers should show the evidence used",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI gives three talking points for a meeting. You need confidence in where they came from. What should you ask next?",
          options: {
            A: "Make the talking points more persuasive.",
            B: "Turn them into a speech.",
            C: "Choose the strongest talking point.",
            D: "Show the notes or messages behind each point.",
          },
          correct_answer: "D",
          feedback:
            "Source notes or messages let you judge whether the talking points are grounded in real material.",
        },
        pt: {
          question:
            "A IA dá três pontos para reunião. Você precisa confiar na origem deles. O que pedir em seguida?",
          options: {
            A: "Deixar os pontos mais persuasivos.",
            B: "Transformá-los em discurso.",
            C: "Escolher o ponto mais forte.",
            D: "Mostrar notas ou mensagens por trás de cada ponto.",
          },
          correct_answer: "D",
          feedback:
            "Notas ou mensagens de origem permitem avaliar se os pontos estão fundamentados em material real.",
        },
        sv: {
          question:
            "AI-verktyget ger tre samtalspunkter inför ett möte. Du behöver veta ursprunget. Vad ber du om?",
          options: {
            A: "Gör punkterna mer övertygande.",
            B: "Gör dem till ett tal.",
            C: "Välj den starkaste punkten.",
            D: "Visa anteckningar eller meddelanden bakom varje punkt.",
          },
          correct_answer: "D",
          feedback:
            "Källanteckningar eller meddelanden låter dig bedöma om punkterna bygger på verkligt material.",
        },
        nl: {
          question:
            "De AI-tool geeft drie bespreekpunten voor een overleg. Je wilt weten waar ze vandaan komen. Wat vraag je?",
          options: {
            A: "Maak de punten overtuigender.",
            B: "Maak er een toespraak van.",
            C: "Kies het sterkste punt.",
            D: "Toon notities of berichten achter elk punt.",
          },
          correct_answer: "D",
          feedback:
            "Bronnotities of berichten helpen beoordelen of bespreekpunten op echt materiaal zijn gebaseerd.",
        },
      },
      question_text:
        "AI gives three talking points for a meeting. You need confidence in where they came from. What should you ask next?",
      explanation:
        "Source notes or messages let you judge whether the talking points are grounded in real material.",
      answers: [
        {
          id: "station5_q008_a",
          question_id: "station5_q008",
          answer_text: "Make the talking points more persuasive.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q008_b",
          question_id: "station5_q008",
          answer_text: "Turn them into a speech.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q008_c",
          question_id: "station5_q008",
          answer_text: "Choose the strongest talking point.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q008_d",
          question_id: "station5_q008",
          answer_text: "Show the notes or messages behind each point.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station5_q009",
      station_id: "station_5",
      stage: 1,
      complexity: 2,
      core_concept:
        "Comparisons should show differences and where evidence appears",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You compare two versions of a guide. What is the most useful AI-supported output?",
          options: {
            A: "A table of differences with page references.",
            B: "A rewritten guide with no comparison.",
            C: "A guess about which guide is better.",
            D: "A shorter version of both guides.",
          },
          correct_answer: "A",
          feedback:
            "A table with references helps you understand differences and check where they appear.",
        },
        pt: {
          question:
            "Você compara duas versões de um guia. Qual saída com IA é mais útil?",
          options: {
            A: "Tabela de diferenças com referências de página.",
            B: "Guia reescrito sem comparação.",
            C: "Palpite sobre qual guia é melhor.",
            D: "Versão mais curta dos dois guias.",
          },
          correct_answer: "A",
          feedback:
            "Uma tabela com referências ajuda a entender diferenças e verificar onde elas aparecem.",
        },
        sv: {
          question:
            "Du jämför två versioner av en guide. Vilken AI-stödd output är mest användbar?",
          options: {
            A: "Tabell med skillnader och sidreferenser.",
            B: "Omskriven guide utan jämförelse.",
            C: "Gissning om vilken guide som är bättre.",
            D: "Kortare version av båda guiderna.",
          },
          correct_answer: "A",
          feedback:
            "En tabell med referenser hjälper dig förstå skillnader och kontrollera var de finns.",
        },
        nl: {
          question:
            "Je vergelijkt twee versies van een gids. Welke AI-output is het nuttigst?",
          options: {
            A: "Tabel met verschillen en paginaverwijzingen.",
            B: "Herschreven gids zonder vergelijking.",
            C: "Gok over welke gids beter is.",
            D: "Kortere versie van beide gidsen.",
          },
          correct_answer: "A",
          feedback:
            "Een tabel met verwijzingen helpt verschillen begrijpen en controleren waar ze staan.",
        },
      },
      question_text:
        "You compare two versions of a guide. What is the most useful AI-supported output?",
      explanation:
        "A table with references helps you understand differences and check where they appear.",
      answers: [
        {
          id: "station5_q009_a",
          question_id: "station5_q009",
          answer_text: "A table of differences with page references.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station5_q009_b",
          question_id: "station5_q009",
          answer_text: "A rewritten guide with no comparison.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q009_c",
          question_id: "station5_q009",
          answer_text: "A guess about which guide is better.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q009_d",
          question_id: "station5_q009",
          answer_text: "A shorter version of both guides.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q010",
      station_id: "station_5",
      stage: 1,
      complexity: 2,
      core_concept:
        "Dates and owners from threads should be checked before use",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI extracts owners and dates from a long thread. One date affects your plan. What should you do first?",
          options: {
            A: "Ask for a nicer table.",
            B: "Use the date to update the plan.",
            C: "Check the date in the original message.",
            D: "Ask AI to choose a new date.",
          },
          correct_answer: "C",
          feedback:
            "A planning date should be checked in the original message before it changes your work plan.",
        },
        pt: {
          question:
            "A IA extrai responsáveis e datas de uma longa troca. Uma data afeta seu plano. O que fazer primeiro?",
          options: {
            A: "Pedir tabela mais bonita.",
            B: "Usar a data para atualizar o plano.",
            C: "Checar a data na mensagem original.",
            D: "Pedir à IA que escolha nova data.",
          },
          correct_answer: "C",
          feedback:
            "Uma data de planejamento deve ser checada na mensagem original antes de mudar seu plano.",
        },
        sv: {
          question:
            "AI-verktyget hämtar ansvariga och datum ur en lång tråd. Ett datum påverkar planen. Vad gör du först?",
          options: {
            A: "Be om en snyggare tabell.",
            B: "Använda datumet för att uppdatera planen.",
            C: "Kontrollera datumet i originalmeddelandet.",
            D: "Be verktyget välja nytt datum.",
          },
          correct_answer: "C",
          feedback:
            "Ett planeringsdatum bör kontrolleras i originalmeddelandet innan arbetsplanen ändras.",
        },
        nl: {
          question:
            "De AI-tool haalt eigenaars en datums uit een lange reeks. Eén datum raakt je planning. Wat doe je eerst?",
          options: {
            A: "Om een mooiere tabel vragen.",
            B: "De datum gebruiken om het plan bij te werken.",
            C: "De datum in het originele bericht controleren.",
            D: "De tool een nieuwe datum laten kiezen.",
          },
          correct_answer: "C",
          feedback:
            "Een planningsdatum moet in het originele bericht worden gecontroleerd voordat je plan verandert.",
        },
      },
      question_text:
        "AI extracts owners and dates from a long thread. One date affects your plan. What should you do first?",
      explanation:
        "A planning date should be checked in the original message before it changes your work plan.",
      answers: [
        {
          id: "station5_q010_a",
          question_id: "station5_q010",
          answer_text: "Ask for a nicer table.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q010_b",
          question_id: "station5_q010",
          answer_text: "Use the date to update the plan.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q010_c",
          question_id: "station5_q010",
          answer_text: "Check the date in the original message.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station5_q010_d",
          question_id: "station5_q010",
          answer_text: "Ask AI to choose a new date.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q011",
      station_id: "station_5",
      stage: 2,
      complexity: 2,
      core_concept: "AI search can miss material outside its access or context",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI searches shared files and finds no answer. You know some teams keep separate notes. What is realistic?",
          options: {
            A: "The answer cannot exist anywhere.",
            B: "Another source or team may still have it.",
            C: "AI should invent a likely answer.",
            D: "The search result is enough to decide.",
          },
          correct_answer: "B",
          feedback:
            "A connected tool can only use what it can access and interpret. Other sources may still matter.",
        },
        pt: {
          question:
            "A IA busca em arquivos compartilhados e não encontra resposta. Algumas equipes mantêm notas separadas. O que é realista?",
          options: {
            A: "A resposta não pode existir em lugar nenhum.",
            B: "Outra fonte ou equipe ainda pode ter.",
            C: "A IA deve inventar resposta provável.",
            D: "O resultado basta para decidir.",
          },
          correct_answer: "B",
          feedback:
            "Uma ferramenta conectada só usa o que consegue acessar e interpretar. Outras fontes ainda podem importar.",
        },
        sv: {
          question:
            "AI-verktyget söker i delade filer och hittar inget svar. Vissa team har separata anteckningar. Vad är realistiskt?",
          options: {
            A: "Svaret kan inte finnas någonstans.",
            B: "En annan källa eller team kan ha det.",
            C: "Verktyget bör hitta på troligt svar.",
            D: "Sökresultatet räcker för beslut.",
          },
          correct_answer: "B",
          feedback:
            "Ett anslutet verktyg kan bara använda åtkomligt och tolkningsbart material. Andra källor kan fortfarande spela roll.",
        },
        nl: {
          question:
            "De AI-tool zoekt in gedeelde bestanden en vindt geen antwoord. Sommige teams hebben aparte notities. Wat is realistisch?",
          options: {
            A: "Het antwoord kan nergens bestaan.",
            B: "Een andere bron of team kan het hebben.",
            C: "De tool moet een waarschijnlijk antwoord verzinnen.",
            D: "Het zoekresultaat is genoeg om te beslissen.",
          },
          correct_answer: "B",
          feedback:
            "Een gekoppelde tool gebruikt alleen wat toegankelijk en interpreteerbaar is. Andere bronnen kunnen nog belangrijk zijn.",
        },
      },
      question_text:
        "AI searches shared files and finds no answer. You know some teams keep separate notes. What is realistic?",
      explanation:
        "A connected tool can only use what it can access and interpret. Other sources may still matter.",
      answers: [
        {
          id: "station5_q011_a",
          question_id: "station5_q011",
          answer_text: "The answer cannot exist anywhere.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q011_b",
          question_id: "station5_q011",
          answer_text: "Another source or team may still have it.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station5_q011_c",
          question_id: "station5_q011",
          answer_text: "AI should invent a likely answer.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q011_d",
          question_id: "station5_q011",
          answer_text: "The search result is enough to decide.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q012",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept:
        "Comparison outputs should be checked against cited locations",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI compares two documents and says a requirement changed. What should you verify before sharing that conclusion?",
          options: {
            A: "Whether the wording sounds objective.",
            B: "Whether the change makes work easier.",
            C: "Whether AI can shorten the comparison.",
            D: "Where the changed requirement appears in both documents.",
          },
          correct_answer: "D",
          feedback:
            "The conclusion matters only if the changed requirement can be traced to both documents.",
        },
        pt: {
          question:
            "A IA compara dois documentos e diz que um requisito mudou. O que verificar antes de compartilhar a conclusão?",
          options: {
            A: "Se a redação soa objetiva.",
            B: "Se a mudança facilita o trabalho.",
            C: "Se a IA pode encurtar a comparação.",
            D: "Onde o requisito mudou nos dois documentos.",
          },
          correct_answer: "D",
          feedback:
            "A conclusão só importa se o requisito alterado puder ser rastreado nos dois documentos.",
        },
        sv: {
          question:
            "AI-verktyget jämför två dokument och säger att ett krav ändrats. Vad bör du kontrollera före delning?",
          options: {
            A: "Om formuleringen låter objektiv.",
            B: "Om ändringen gör arbetet lättare.",
            C: "Om jämförelsen kan kortas.",
            D: "Var kravet ändrats i båda dokumenten.",
          },
          correct_answer: "D",
          feedback:
            "Slutsatsen spelar roll först när det ändrade kravet kan spåras i båda dokumenten.",
        },
        nl: {
          question:
            "De AI-tool vergelijkt twee documenten en zegt dat een eis is gewijzigd. Wat controleer je vóór delen?",
          options: {
            A: "Of de formulering objectief klinkt.",
            B: "Of de wijziging werk makkelijker maakt.",
            C: "Of de vergelijking korter kan.",
            D: "Waar de gewijzigde eis in beide documenten staat.",
          },
          correct_answer: "D",
          feedback:
            "De conclusie telt pas als de gewijzigde eis in beide documenten te traceren is.",
        },
      },
      question_text:
        "AI compares two documents and says a requirement changed. What should you verify before sharing that conclusion?",
      explanation:
        "The conclusion matters only if the changed requirement can be traced to both documents.",
      answers: [
        {
          id: "station5_q012_a",
          question_id: "station5_q012",
          answer_text: "Whether the wording sounds objective.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q012_b",
          question_id: "station5_q012",
          answer_text: "Whether the change makes work easier.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q012_c",
          question_id: "station5_q012",
          answer_text: "Whether AI can shorten the comparison.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q012_d",
          question_id: "station5_q012",
          answer_text:
            "Where the changed requirement appears in both documents.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station5_q013",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept:
        "Useful information summaries separate known, unknown and unclear points",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI summarises background for a meeting, but the answer feels incomplete. What is the best next request?",
          options: {
            A: "Separate what is known, unknown and unclear.",
            B: "Make the answer sound more complete.",
            C: "Turn the answer into a final recommendation.",
            D: "Remove details that create doubt.",
          },
          correct_answer: "A",
          feedback:
            "Separating known, unknown and unclear information shows what is grounded and what still needs checking.",
        },
        pt: {
          question:
            "A IA resume contexto para reunião, mas a resposta parece incompleta. Qual é o melhor próximo pedido?",
          options: {
            A: "Separe o que é conhecido, desconhecido e incerto.",
            B: "Faça a resposta parecer mais completa.",
            C: "Transforme a resposta em recomendação final.",
            D: "Remova detalhes que geram dúvida.",
          },
          correct_answer: "A",
          feedback:
            "Separar conhecido, desconhecido e incerto mostra o que está fundamentado e o que ainda precisa checagem.",
        },
        sv: {
          question:
            "AI-verktyget sammanfattar bakgrund inför möte, men svaret känns ofullständigt. Vad är bästa nästa begäran?",
          options: {
            A: "Dela upp känt, okänt och oklart.",
            B: "Få svaret att verka mer komplett.",
            C: "Gör svaret till slutrekommendation.",
            D: "Ta bort detaljer som skapar tvekan.",
          },
          correct_answer: "A",
          feedback:
            "Att dela upp känt, okänt och oklart visar vad som är grundat och vad som kräver kontroll.",
        },
        nl: {
          question:
            "De AI-tool vat achtergrond samen voor een overleg, maar het antwoord voelt onvolledig. Wat vraag je daarna?",
          options: {
            A: "Scheid wat bekend, onbekend en onduidelijk is.",
            B: "Laat het antwoord vollediger klinken.",
            C: "Maak er een eindaanbeveling van.",
            D: "Verwijder details die twijfel geven.",
          },
          correct_answer: "A",
          feedback:
            "Bekend, onbekend en onduidelijk scheiden toont wat onderbouwd is en wat controle vraagt.",
        },
      },
      question_text:
        "AI summarises background for a meeting, but the answer feels incomplete. What is the best next request?",
      explanation:
        "Separating known, unknown and unclear information shows what is grounded and what still needs checking.",
      answers: [
        {
          id: "station5_q013_a",
          question_id: "station5_q013",
          answer_text: "Separate what is known, unknown and unclear.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station5_q013_b",
          question_id: "station5_q013",
          answer_text: "Make the answer sound more complete.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q013_c",
          question_id: "station5_q013",
          answer_text: "Turn the answer into a final recommendation.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q013_d",
          question_id: "station5_q013",
          answer_text: "Remove details that create doubt.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q014",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept:
        "Search results may miss decisions when access or keywords are incomplete",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI finds one old decision from a broad search. You suspect newer notes exist. What is the main risk?",
          options: {
            A: "The old decision may be too detailed.",
            B: "The answer may use too many sources.",
            C: "Relevant newer information may be missing.",
            D: "The search may take too little time.",
          },
          correct_answer: "C",
          feedback:
            "A broad search can find something real while still missing newer or less accessible material.",
        },
        pt: {
          question:
            "A IA encontra uma decisão antiga em busca ampla. Você suspeita que há notas mais novas. Qual é o principal risco?",
          options: {
            A: "A decisão antiga pode ser detalhada demais.",
            B: "A resposta pode usar fontes demais.",
            C: "Informação mais nova relevante pode faltar.",
            D: "A busca pode ser rápida demais.",
          },
          correct_answer: "C",
          feedback:
            "Uma busca ampla pode encontrar algo real e ainda perder material mais novo ou menos acessível.",
        },
        sv: {
          question:
            "AI-verktyget hittar ett gammalt beslut i bred sökning. Du misstänker nyare anteckningar. Vad är huvudrisken?",
          options: {
            A: "Det gamla beslutet kan vara för detaljerat.",
            B: "Svaret kan använda för många källor.",
            C: "Nyare relevant information kan saknas.",
            D: "Sökningen kan gå för snabbt.",
          },
          correct_answer: "C",
          feedback:
            "En bred sökning kan hitta något verkligt men ändå missa nyare eller mindre åtkomligt material.",
        },
        nl: {
          question:
            "De AI-tool vindt één oud besluit in een brede zoekopdracht. Je vermoedt nieuwere notities. Wat is het grootste risico?",
          options: {
            A: "Het oude besluit kan te gedetailleerd zijn.",
            B: "Het antwoord kan te veel bronnen gebruiken.",
            C: "Relevante nieuwere informatie kan ontbreken.",
            D: "De zoekopdracht kan te snel gaan.",
          },
          correct_answer: "C",
          feedback:
            "Een brede zoekactie kan iets echts vinden en toch nieuwer of minder toegankelijk materiaal missen.",
        },
      },
      question_text:
        "AI finds one old decision from a broad search. You suspect newer notes exist. What is the main risk?",
      explanation:
        "A broad search can find something real while still missing newer or less accessible material.",
      answers: [
        {
          id: "station5_q014_a",
          question_id: "station5_q014",
          answer_text: "The old decision may be too detailed.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q014_b",
          question_id: "station5_q014",
          answer_text: "The answer may use too many sources.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q014_c",
          question_id: "station5_q014",
          answer_text: "Relevant newer information may be missing.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station5_q014_d",
          question_id: "station5_q014",
          answer_text: "The search may take too little time.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q015",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept: "Confident information answers still need evidence",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI gives a confident answer about project status but cites no file. What behaviour shows good information judgment?",
          options: {
            A: "Use it because it sounds informed.",
            B: "Treat it as a lead until evidence is shown.",
            C: "Ask AI to make it more concise.",
            D: "Turn it into a decision for the team.",
          },
          correct_answer: "B",
          feedback:
            "A confident answer without evidence can guide your search, but it should not be treated as verified.",
        },
        pt: {
          question:
            "A IA dá resposta confiante sobre status do projeto, mas não cita arquivo. Que comportamento mostra bom julgamento?",
          options: {
            A: "Usar porque parece bem informada.",
            B: "Tratar como pista até haver evidência.",
            C: "Pedir que a IA deixe mais concisa.",
            D: "Transformar em decisão para a equipe.",
          },
          correct_answer: "B",
          feedback:
            "Uma resposta confiante sem evidência pode orientar a busca, mas não deve ser tratada como verificada.",
        },
        sv: {
          question:
            "AI-verktyget ger säkert svar om projektstatus men citerar ingen fil. Vilket beteende visar gott omdöme?",
          options: {
            A: "Använda det eftersom det låter kunnigt.",
            B: "Behandla som spår tills evidens visas.",
            C: "Be verktyget göra det mer koncist.",
            D: "Göra det till beslut för teamet.",
          },
          correct_answer: "B",
          feedback:
            "Ett säkert svar utan evidens kan styra sökningen, men bör inte behandlas som verifierat.",
        },
        nl: {
          question:
            "De AI-tool geeft zeker antwoord over projectstatus maar noemt geen bestand. Welk gedrag toont goed oordeel?",
          options: {
            A: "Gebruiken omdat het goed geïnformeerd klinkt.",
            B: "Als aanwijzing behandelen tot bewijs zichtbaar is.",
            C: "De tool vragen het beknopter te maken.",
            D: "Er een teambesluit van maken.",
          },
          correct_answer: "B",
          feedback:
            "Een zeker antwoord zonder bewijs kan je zoekrichting geven, maar is nog niet geverifieerd.",
        },
      },
      question_text:
        "AI gives a confident answer about project status but cites no file. What behaviour shows good information judgment?",
      explanation:
        "A confident answer without evidence can guide your search, but it should not be treated as verified.",
      answers: [
        {
          id: "station5_q015_a",
          question_id: "station5_q015",
          answer_text: "Use it because it sounds informed.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q015_b",
          question_id: "station5_q015",
          answer_text: "Treat it as a lead until evidence is shown.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station5_q015_c",
          question_id: "station5_q015",
          answer_text: "Ask AI to make it more concise.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q015_d",
          question_id: "station5_q015",
          answer_text: "Turn it into a decision for the team.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q016",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept: "AI can compare documents while keeping evidence visible",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Two documents disagree on a deadline. Which AI-supported comparison is most useful?",
          options: {
            A: "Pick the deadline that appears first.",
            B: "Use the shorter document as the source.",
            C: "Rewrite both documents into one version.",
            D: "Show both deadlines and where each appears.",
          },
          correct_answer: "D",
          feedback:
            "Showing both deadlines and locations keeps the conflict visible for human resolution.",
        },
        pt: {
          question:
            "Dois documentos discordam sobre um prazo. Qual comparação com IA é mais útil?",
          options: {
            A: "Escolher o prazo que aparece primeiro.",
            B: "Usar o documento mais curto como fonte.",
            C: "Reescrever os dois em uma versão.",
            D: "Mostrar os dois prazos e onde aparecem.",
          },
          correct_answer: "D",
          feedback:
            "Mostrar os dois prazos e locais mantém o conflito visível para resolução humana.",
        },
        sv: {
          question:
            "Två dokument säger olika om en deadline. Vilken AI-stödd jämförelse är mest användbar?",
          options: {
            A: "Välj deadline som visas först.",
            B: "Använd kortare dokument som källa.",
            C: "Skriv om båda till en version.",
            D: "Visa båda datum och var de står.",
          },
          correct_answer: "D",
          feedback:
            "Att visa båda datum och platser håller konflikten synlig för mänsklig lösning.",
        },
        nl: {
          question:
            "Twee documenten spreken elkaar tegen over een deadline. Welke AI-vergelijking is het nuttigst?",
          options: {
            A: "Kies de deadline die eerst verschijnt.",
            B: "Gebruik het kortste document als bron.",
            C: "Herschrijf beide tot één versie.",
            D: "Toon beide deadlines en waar ze staan.",
          },
          correct_answer: "D",
          feedback:
            "Beide deadlines en locaties tonen houdt het conflict zichtbaar voor menselijke oplossing.",
        },
      },
      question_text:
        "Two documents disagree on a deadline. Which AI-supported comparison is most useful?",
      explanation:
        "Showing both deadlines and locations keeps the conflict visible for human resolution.",
      answers: [
        {
          id: "station5_q016_a",
          question_id: "station5_q016",
          answer_text: "Pick the deadline that appears first.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q016_b",
          question_id: "station5_q016",
          answer_text: "Use the shorter document as the source.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q016_c",
          question_id: "station5_q016",
          answer_text: "Rewrite both documents into one version.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q016_d",
          question_id: "station5_q016",
          answer_text: "Show both deadlines and where each appears.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station5_q017",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept:
        "Action lists should preserve evidence for owners and dates",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI creates an action list from meeting notes. Before sharing it, what should you verify?",
          options: {
            A: "Owners and dates against the notes.",
            B: "Whether the list sounds polite.",
            C: "Whether AI agrees with every action.",
            D: "Whether the list has enough color.",
          },
          correct_answer: "A",
          feedback:
            "Owners and dates affect follow-up, so they should be checked against the source notes.",
        },
        pt: {
          question:
            "A IA cria lista de ações a partir de notas de reunião. Antes de compartilhar, o que verificar?",
          options: {
            A: "Responsáveis e datas nas notas.",
            B: "Se a lista soa educada.",
            C: "Se a IA concorda com cada ação.",
            D: "Se a lista tem cor suficiente.",
          },
          correct_answer: "A",
          feedback:
            "Responsáveis e datas afetam o acompanhamento, então devem ser checados nas notas de origem.",
        },
        sv: {
          question:
            "AI-verktyget skapar åtgärdslista från mötesanteckningar. Vad bör du kontrollera före delning?",
          options: {
            A: "Ansvariga och datum mot anteckningarna.",
            B: "Om listan låter artig.",
            C: "Om verktyget håller med varje åtgärd.",
            D: "Om listan har tillräckligt med färg.",
          },
          correct_answer: "A",
          feedback:
            "Ansvariga och datum påverkar uppföljning och bör därför kontrolleras mot källanteckningarna.",
        },
        nl: {
          question:
            "De AI-tool maakt een actielijst uit vergadernotities. Wat controleer je vóór delen?",
          options: {
            A: "Eigenaars en datums tegen de notities.",
            B: "Of de lijst beleefd klinkt.",
            C: "Of de tool elke actie goedkeurt.",
            D: "Of de lijst genoeg kleur heeft.",
          },
          correct_answer: "A",
          feedback:
            "Eigenaars en datums beïnvloeden opvolging en moeten daarom tegen de bronnotities worden gecontroleerd.",
        },
      },
      question_text:
        "AI creates an action list from meeting notes. Before sharing it, what should you verify?",
      explanation:
        "Owners and dates affect follow-up, so they should be checked against the source notes.",
      answers: [
        {
          id: "station5_q017_a",
          question_id: "station5_q017",
          answer_text: "Owners and dates against the notes.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station5_q017_b",
          question_id: "station5_q017",
          answer_text: "Whether the list sounds polite.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q017_c",
          question_id: "station5_q017",
          answer_text: "Whether AI agrees with every action.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q017_d",
          question_id: "station5_q017",
          answer_text: "Whether the list has enough color.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q018",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept:
        "When AI cannot ground an answer, ask for missing context or another source",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A colleague asks for an answer from past chats. AI finds only partial context. What should you do first?",
          options: {
            A: "Send the partial answer quickly.",
            B: "Ask AI to fill the gaps.",
            C: "Check another source or ask the colleague.",
            D: "Turn the partial answer into a decision.",
          },
          correct_answer: "C",
          feedback:
            "Partial context is a signal to look for another source or ask a person before relying on it.",
        },
        pt: {
          question:
            "Um colega pede resposta baseada em chats antigos. A IA encontra só contexto parcial. O que fazer primeiro?",
          options: {
            A: "Enviar a resposta parcial rapidamente.",
            B: "Pedir que a IA preencha lacunas.",
            C: "Checar outra fonte ou perguntar ao colega.",
            D: "Transformar a resposta parcial em decisão.",
          },
          correct_answer: "C",
          feedback:
            "Contexto parcial é sinal para buscar outra fonte ou perguntar a alguém antes de confiar.",
        },
        sv: {
          question:
            "En kollega frågar om svar från gamla chattar. AI-verktyget hittar bara delvis kontext. Vad gör du först?",
          options: {
            A: "Skicka det partiella svaret snabbt.",
            B: "Be verktyget fylla luckorna.",
            C: "Kontrollera annan källa eller fråga kollegan.",
            D: "Gör det partiella svaret till beslut.",
          },
          correct_answer: "C",
          feedback:
            "Delvis kontext är en signal att söka annan källa eller fråga en person före användning.",
        },
        nl: {
          question:
            "Een collega vraagt om antwoord uit oude chats. De AI-tool vindt alleen gedeeltelijke context. Wat doe je eerst?",
          options: {
            A: "Stuur het gedeeltelijke antwoord snel.",
            B: "Laat de tool de gaten invullen.",
            C: "Controleer andere bron of vraag de collega.",
            D: "Maak van het gedeeltelijke antwoord een besluit.",
          },
          correct_answer: "C",
          feedback:
            "Gedeeltelijke context is een signaal om een andere bron te zoeken of iemand te vragen.",
        },
      },
      question_text:
        "A colleague asks for an answer from past chats. AI finds only partial context. What should you do first?",
      explanation:
        "Partial context is a signal to look for another source or ask a person before relying on it.",
      answers: [
        {
          id: "station5_q018_a",
          question_id: "station5_q018",
          answer_text: "Send the partial answer quickly.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q018_b",
          question_id: "station5_q018",
          answer_text: "Ask AI to fill the gaps.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q018_c",
          question_id: "station5_q018",
          answer_text: "Check another source or ask the colleague.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station5_q018_d",
          question_id: "station5_q018",
          answer_text: "Turn the partial answer into a decision.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q019",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept:
        "Classification can miss material outside available sources",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI groups search results into themes, but only from one folder. What is the main risk?",
          options: {
            A: "The themes may be too easy to read.",
            B: "Other relevant material may be excluded.",
            C: "The folder may have too many files.",
            D: "The answer may be too structured.",
          },
          correct_answer: "B",
          feedback:
            "Themes may look complete while reflecting only one folder, not all relevant material.",
        },
        pt: {
          question:
            "A IA agrupa resultados em temas, mas usa só uma pasta. Qual é o principal risco?",
          options: {
            A: "Os temas podem ser fáceis demais de ler.",
            B: "Outro material relevante pode ficar fora.",
            C: "A pasta pode ter arquivos demais.",
            D: "A resposta pode ser estruturada demais.",
          },
          correct_answer: "B",
          feedback:
            "Os temas podem parecer completos, mas refletir só uma pasta, não todo material relevante.",
        },
        sv: {
          question:
            "AI-verktyget grupperar sökresultat i teman, men bara från en mapp. Vad är huvudrisken?",
          options: {
            A: "Teman kan bli för lätta att läsa.",
            B: "Annat relevant material kan saknas.",
            C: "Mappen kan ha för många filer.",
            D: "Svaret kan vara för strukturerat.",
          },
          correct_answer: "B",
          feedback:
            "Teman kan verka kompletta men bara spegla en mapp, inte allt relevant material.",
        },
        nl: {
          question:
            "De AI-tool groepeert zoekresultaten in thema’s, maar alleen uit één map. Wat is het grootste risico?",
          options: {
            A: "Thema’s kunnen te makkelijk leesbaar zijn.",
            B: "Ander relevant materiaal kan ontbreken.",
            C: "De map kan te veel bestanden hebben.",
            D: "Het antwoord kan te gestructureerd zijn.",
          },
          correct_answer: "B",
          feedback:
            "Thema’s kunnen compleet lijken maar slechts één map weerspiegelen, niet al het relevante materiaal.",
        },
      },
      question_text:
        "AI groups search results into themes, but only from one folder. What is the main risk?",
      explanation:
        "Themes may look complete while reflecting only one folder, not all relevant material.",
      answers: [
        {
          id: "station5_q019_a",
          question_id: "station5_q019",
          answer_text: "The themes may be too easy to read.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q019_b",
          question_id: "station5_q019",
          answer_text: "Other relevant material may be excluded.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station5_q019_c",
          question_id: "station5_q019",
          answer_text: "The folder may have too many files.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q019_d",
          question_id: "station5_q019",
          answer_text: "The answer may be too structured.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q020",
      station_id: "station_5",
      stage: 2,
      complexity: 3,
      core_concept: "Conflicting sources should be made visible before action",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI finds two team notes with different numbers for the same metric. What should you do before using the number?",
          options: {
            A: "Average the two numbers.",
            B: "Use the newer-looking note.",
            C: "Pick the number with clearer wording.",
            D: "Compare sources and resolve the conflict.",
          },
          correct_answer: "D",
          feedback:
            "Conflicting evidence should be visible and resolved before one number is used in work.",
        },
        pt: {
          question:
            "A IA encontra duas notas de equipes com números diferentes para a mesma métrica. O que fazer antes de usar o número?",
          options: {
            A: "Calcular média dos dois números.",
            B: "Usar a nota que parece mais nova.",
            C: "Escolher o número com texto mais claro.",
            D: "Comparar fontes e resolver o conflito.",
          },
          correct_answer: "D",
          feedback:
            "Evidências conflitantes devem ficar visíveis e ser resolvidas antes de usar um número no trabalho.",
        },
        sv: {
          question:
            "AI-verktyget hittar två teamanteckningar med olika siffror för samma mått. Vad gör du före användning?",
          options: {
            A: "Räkna genomsnitt av siffrorna.",
            B: "Använd anteckningen som verkar nyare.",
            C: "Välj siffran med tydligare text.",
            D: "Jämför källor och lös konflikten.",
          },
          correct_answer: "D",
          feedback:
            "Motstridig evidens bör synas och lösas innan en siffra används i arbetet.",
        },
        nl: {
          question:
            "De AI-tool vindt twee teamnotities met verschillende cijfers voor dezelfde maatstaf. Wat doe je vóór gebruik?",
          options: {
            A: "Neem het gemiddelde van beide cijfers.",
            B: "Gebruik de notitie die nieuwer lijkt.",
            C: "Kies het cijfer met duidelijkere tekst.",
            D: "Vergelijk bronnen en los het conflict op.",
          },
          correct_answer: "D",
          feedback:
            "Tegenstrijdig bewijs moet zichtbaar zijn en opgelost worden voordat één cijfer wordt gebruikt.",
        },
      },
      question_text:
        "AI finds two team notes with different numbers for the same metric. What should you do before using the number?",
      explanation:
        "Conflicting evidence should be visible and resolved before one number is used in work.",
      answers: [
        {
          id: "station5_q020_a",
          question_id: "station5_q020",
          answer_text: "Average the two numbers.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q020_b",
          question_id: "station5_q020",
          answer_text: "Use the newer-looking note.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q020_c",
          question_id: "station5_q020",
          answer_text: "Pick the number with clearer wording.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q020_d",
          question_id: "station5_q020",
          answer_text: "Compare sources and resolve the conflict.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station5_q021",
      station_id: "station_5",
      stage: 3,
      complexity: 3,
      core_concept:
        "Finding information is not the same as deciding what to do",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI gathers evidence for a decision memo. What should remain outside the information-finding task?",
          options: {
            A: "Choosing the final action.",
            B: "Listing relevant documents.",
            C: "Summarising evidence from sources.",
            D: "Showing where facts appear.",
          },
          correct_answer: "A",
          feedback:
            "AI can gather and explain evidence, but people still own the final action choice.",
        },
        pt: {
          question:
            "A IA reúne evidências para um memo de decisão. O que fica fora da tarefa de encontrar informação?",
          options: {
            A: "Escolher a ação final.",
            B: "Listar documentos relevantes.",
            C: "Resumir evidências das fontes.",
            D: "Mostrar onde os fatos aparecem.",
          },
          correct_answer: "A",
          feedback:
            "A IA pode reunir e explicar evidências, mas pessoas continuam donas da escolha da ação final.",
        },
        sv: {
          question:
            "AI-verktyget samlar evidens till beslutsunderlag. Vad ligger utanför informationssökningen?",
          options: {
            A: "Välja slutlig åtgärd.",
            B: "Lista relevanta dokument.",
            C: "Sammanfatta evidens från källor.",
            D: "Visa var fakta finns.",
          },
          correct_answer: "A",
          feedback:
            "Verktyget kan samla och förklara evidens, men människor äger valet av slutlig åtgärd.",
        },
        nl: {
          question:
            "De AI-tool verzamelt bewijs voor een beslisnotitie. Wat valt buiten informatie zoeken?",
          options: {
            A: "De uiteindelijke actie kiezen.",
            B: "Relevante documenten noemen.",
            C: "Bewijs uit bronnen samenvatten.",
            D: "Tonen waar feiten staan.",
          },
          correct_answer: "A",
          feedback:
            "De tool kan bewijs verzamelen en uitleggen, maar mensen blijven eigenaar van de eindactie.",
        },
      },
      question_text:
        "AI gathers evidence for a decision memo. What should remain outside the information-finding task?",
      explanation:
        "AI can gather and explain evidence, but people still own the final action choice.",
      answers: [
        {
          id: "station5_q021_a",
          question_id: "station5_q021",
          answer_text: "Choosing the final action.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station5_q021_b",
          question_id: "station5_q021",
          answer_text: "Listing relevant documents.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q021_c",
          question_id: "station5_q021",
          answer_text: "Summarising evidence from sources.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q021_d",
          question_id: "station5_q021",
          answer_text: "Showing where facts appear.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q022",
      station_id: "station_5",
      stage: 3,
      complexity: 4,
      core_concept:
        "Risk conclusions without evidence should not be treated as verified",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI flags a risk after reading several notes, but shows no evidence. What is the main risk?",
          options: {
            A: "The risk may reflect only some notes.",
            B: "The risk may be relevant but unsupported.",
            C: "The conclusion may not be grounded.",
            D: "The source notes may contradict the label.",
          },
          correct_answer: "C",
          feedback:
            "A risk can be useful to investigate, but without evidence it is not yet grounded.",
        },
        pt: {
          question:
            "A IA aponta um risco após ler várias notas, mas não mostra evidência. Qual é o principal risco?",
          options: {
            A: "O risco pode refletir só algumas notas.",
            B: "O risco pode ser relevante, mas sem suporte.",
            C: "A conclusão pode não estar fundamentada.",
            D: "As notas podem contradizer o rótulo.",
          },
          correct_answer: "C",
          feedback:
            "Um risco pode ser útil para investigar, mas sem evidência ainda não está fundamentado.",
        },
        sv: {
          question:
            "AI-verktyget markerar en risk efter flera anteckningar men visar ingen evidens. Vad är huvudrisken?",
          options: {
            A: "Risken kan spegla bara vissa anteckningar.",
            B: "Risken kan vara relevant men ostödd.",
            C: "Slutsatsen kanske inte är grundad.",
            D: "Källanteckningarna kan motsäga etiketten.",
          },
          correct_answer: "C",
          feedback:
            "En risk kan vara nyttig att undersöka, men utan evidens är den ännu inte grundad.",
        },
        nl: {
          question:
            "De AI-tool markeert een risico na meerdere notities, maar toont geen bewijs. Wat is het grootste risico?",
          options: {
            A: "Het risico kan slechts enkele notities weerspiegelen.",
            B: "Het risico kan relevant maar niet onderbouwd zijn.",
            C: "De conclusie is mogelijk niet onderbouwd.",
            D: "Bronnotities kunnen het label tegenspreken.",
          },
          correct_answer: "C",
          feedback:
            "Een risico kan nuttig zijn om te onderzoeken, maar zonder bewijs is het nog niet onderbouwd.",
        },
      },
      question_text:
        "AI flags a risk after reading several notes, but shows no evidence. What is the main risk?",
      explanation:
        "A risk can be useful to investigate, but without evidence it is not yet grounded.",
      answers: [
        {
          id: "station5_q022_a",
          question_id: "station5_q022",
          answer_text: "The risk may reflect only some notes.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q022_b",
          question_id: "station5_q022",
          answer_text: "The risk may be relevant but unsupported.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q022_c",
          question_id: "station5_q022",
          answer_text: "The conclusion may not be grounded.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station5_q022_d",
          question_id: "station5_q022",
          answer_text: "The source notes may contradict the label.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q023",
      station_id: "station_5",
      stage: 3,
      complexity: 4,
      core_concept:
        "Conflicting sources require evidence mapping before interpretation",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Two sources explain a delay differently, and both look credible. What should you ask AI to prepare first?",
          options: {
            A: "A merged explanation with uncertain points hidden.",
            B: "A source map with differences and evidence.",
            C: "The explanation from the newer source only.",
            D: "A summary that removes contradictions.",
          },
          correct_answer: "B",
          feedback:
            "A source map helps compare evidence before deciding which explanation is stronger.",
        },
        pt: {
          question:
            "Duas fontes explicam um atraso de formas diferentes, e ambas parecem confiáveis. O que pedir à IA primeiro?",
          options: {
            A: "Explicação única com incertezas escondidas.",
            B: "Mapa de fontes com diferenças e evidências.",
            C: "A explicação só da fonte mais nova.",
            D: "Resumo que remove contradições.",
          },
          correct_answer: "B",
          feedback:
            "Um mapa de fontes ajuda a comparar evidências antes de decidir qual explicação é mais forte.",
        },
        sv: {
          question:
            "Två källor förklarar en försening olika och båda verkar trovärdiga. Vad ber du verktyget förbereda först?",
          options: {
            A: "En sammanslagen förklaring där osäkerhet döljs.",
            B: "Källkarta med skillnader och evidens.",
            C: "Förklaringen bara från nyare källa.",
            D: "Sammanfattning som tar bort motsägelser.",
          },
          correct_answer: "B",
          feedback:
            "En källkarta hjälper jämföra evidens innan du avgör vilken förklaring som är starkare.",
        },
        nl: {
          question:
            "Twee bronnen verklaren een vertraging anders en beide lijken geloofwaardig. Wat laat je eerst voorbereiden?",
          options: {
            A: "Samengevoegde uitleg waarin onzekerheid verdwijnt.",
            B: "Bronnenkaart met verschillen en bewijs.",
            C: "Alleen de uitleg uit de nieuwere bron.",
            D: "Samenvatting die tegenstrijdigheden verwijdert.",
          },
          correct_answer: "B",
          feedback:
            "Een bronnenkaart helpt bewijs vergelijken voordat je bepaalt welke verklaring sterker is.",
        },
      },
      question_text:
        "Two sources explain a delay differently, and both look credible. What should you ask AI to prepare first?",
      explanation:
        "A source map helps compare evidence before deciding which explanation is stronger.",
      answers: [
        {
          id: "station5_q023_a",
          question_id: "station5_q023",
          answer_text: "A merged explanation with uncertain points hidden.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q023_b",
          question_id: "station5_q023",
          answer_text: "A source map with differences and evidence.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station5_q023_c",
          question_id: "station5_q023",
          answer_text: "The explanation from the newer source only.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q023_d",
          question_id: "station5_q023",
          answer_text: "A summary that removes contradictions.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q024",
      station_id: "station_5",
      stage: 3,
      complexity: 4,
      core_concept:
        "Information used in stakeholder messages should remain traceable",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI drafts a stakeholder update using several documents. A key claim may affect expectations. What should you verify?",
          options: {
            A: "Whether related documents mention the topic.",
            B: "Whether the claim appears in the draft.",
            C: "Whether the wording fits stakeholders.",
            D: "Which source supports the claim.",
          },
          correct_answer: "D",
          feedback:
            "A claim that shapes expectations should be traceable to evidence before it is shared.",
        },
        pt: {
          question:
            "A IA rascunha atualização para stakeholders usando vários documentos. Uma afirmação pode afetar expectativas. O que verificar?",
          options: {
            A: "Se documentos relacionados mencionam o tema.",
            B: "Se a afirmação aparece no rascunho.",
            C: "Se a redação serve aos stakeholders.",
            D: "Qual fonte sustenta a afirmação.",
          },
          correct_answer: "D",
          feedback:
            "Uma afirmação que molda expectativas deve ser rastreável a evidência antes de ser compartilhada.",
        },
        sv: {
          question:
            "AI-verktyget skapar intressentuppdatering från flera dokument. Ett påstående kan påverka förväntningar. Vad bör du kontrollera?",
          options: {
            A: "Om relaterade dokument nämner ämnet.",
            B: "Om påståendet finns i utkastet.",
            C: "Om formuleringen passar intressenter.",
            D: "Vilken källa stöder påståendet.",
          },
          correct_answer: "D",
          feedback:
            "Ett påstående som påverkar förväntningar bör kunna spåras till evidens före delning.",
        },
        nl: {
          question:
            "De AI-tool maakt een stakeholderupdate uit meerdere documenten. Een claim kan verwachtingen beïnvloeden. Wat controleer je?",
          options: {
            A: "Of verwante documenten het onderwerp noemen.",
            B: "Of de claim in het concept staat.",
            C: "Of de wording bij stakeholders past.",
            D: "Welke bron de claim ondersteunt.",
          },
          correct_answer: "D",
          feedback:
            "Een claim die verwachtingen beïnvloedt moet vóór delen te herleiden zijn tot bewijs.",
        },
      },
      question_text:
        "AI drafts a stakeholder update using several documents. A key claim may affect expectations. What should you verify?",
      explanation:
        "A claim that shapes expectations should be traceable to evidence before it is shared.",
      answers: [
        {
          id: "station5_q024_a",
          question_id: "station5_q024",
          answer_text: "Whether related documents mention the topic.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q024_b",
          question_id: "station5_q024",
          answer_text: "Whether the claim appears in the draft.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q024_c",
          question_id: "station5_q024",
          answer_text: "Whether the wording fits stakeholders.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q024_d",
          question_id: "station5_q024",
          answer_text: "Which source supports the claim.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station5_q025",
      station_id: "station_5",
      stage: 3,
      complexity: 4,
      core_concept: "AI can organise evidence but not own a decision",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI organises evidence into options for a decision. What should the user still own?",
          options: {
            A: "Judging which option to recommend.",
            B: "Listing the documents used.",
            C: "Showing evidence under each option.",
            D: "Grouping facts by topic.",
          },
          correct_answer: "A",
          feedback:
            "Organising evidence is support work. The recommendation still requires human judgment and accountability.",
        },
        pt: {
          question:
            "A IA organiza evidências em opções para uma decisão. O que o usuário ainda deve assumir?",
          options: {
            A: "Julgar qual opção recomendar.",
            B: "Listar documentos usados.",
            C: "Mostrar evidências em cada opção.",
            D: "Agrupar fatos por tema.",
          },
          correct_answer: "A",
          feedback:
            "Organizar evidências é apoio. A recomendação ainda exige julgamento e responsabilidade humanos.",
        },
        sv: {
          question:
            "AI-verktyget ordnar evidens i alternativ inför beslut. Vad ska användaren fortfarande äga?",
          options: {
            A: "Bedöma vilket alternativ som rekommenderas.",
            B: "Lista använda dokument.",
            C: "Visa evidens under varje alternativ.",
            D: "Gruppera fakta efter ämne.",
          },
          correct_answer: "A",
          feedback:
            "Att ordna evidens är stödarbete. Rekommendationen kräver fortfarande mänskligt omdöme och ansvar.",
        },
        nl: {
          question:
            "De AI-tool ordent bewijs in opties voor een besluit. Wat blijft van de gebruiker?",
          options: {
            A: "Beoordelen welke optie wordt aanbevolen.",
            B: "Gebruikte documenten opsommen.",
            C: "Bewijs onder elke optie tonen.",
            D: "Feiten per onderwerp groeperen.",
          },
          correct_answer: "A",
          feedback:
            "Bewijs ordenen is ondersteunend werk. De aanbeveling vraagt nog menselijk oordeel en verantwoordelijkheid.",
        },
      },
      question_text:
        "AI organises evidence into options for a decision. What should the user still own?",
      explanation:
        "Organising evidence is support work. The recommendation still requires human judgment and accountability.",
      answers: [
        {
          id: "station5_q025_a",
          question_id: "station5_q025",
          answer_text: "Judging which option to recommend.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station5_q025_b",
          question_id: "station5_q025",
          answer_text: "Listing the documents used.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q025_c",
          question_id: "station5_q025",
          answer_text: "Showing evidence under each option.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q025_d",
          question_id: "station5_q025",
          answer_text: "Grouping facts by topic.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q026",
      station_id: "station_5",
      stage: 3,
      complexity: 4,
      core_concept: "Incomplete access can make confident answers misleading",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI gives a clear answer from available files, but the team uses another system too. What is the main risk?",
          options: {
            A: "The answer may need a clearer date range.",
            B: "The other system may use different labels.",
            C: "Important evidence may be outside access.",
            D: "The team may treat the answer as final.",
          },
          correct_answer: "C",
          feedback:
            "A clear answer can still miss evidence from systems the tool cannot access.",
        },
        pt: {
          question:
            "A IA dá resposta clara com arquivos disponíveis, mas a equipe também usa outro sistema. Qual é o principal risco?",
          options: {
            A: "A resposta pode precisar de período mais claro.",
            B: "O outro sistema pode usar rótulos diferentes.",
            C: "Evidência importante pode estar fora do acesso.",
            D: "A equipe pode tratar a resposta como final.",
          },
          correct_answer: "C",
          feedback:
            "Uma resposta clara ainda pode perder evidências de sistemas que a ferramenta não acessa.",
        },
        sv: {
          question:
            "AI-verktyget ger tydligt svar från tillgängliga filer, men teamet använder också ett annat system. Vad är huvudrisken?",
          options: {
            A: "Svaret kan behöva tydligare datumintervall.",
            B: "Det andra systemet kan ha andra etiketter.",
            C: "Viktig evidens kan ligga utanför åtkomst.",
            D: "Teamet kan behandla svaret som slutligt.",
          },
          correct_answer: "C",
          feedback:
            "Ett tydligt svar kan ändå missa evidens från system som verktyget inte når.",
        },
        nl: {
          question:
            "De AI-tool geeft duidelijk antwoord uit beschikbare bestanden, maar het team gebruikt ook een ander systeem. Wat is het grootste risico?",
          options: {
            A: "Het antwoord kan een duidelijkere periode nodig hebben.",
            B: "Het andere systeem kan andere labels gebruiken.",
            C: "Belangrijk bewijs kan buiten toegang liggen.",
            D: "Het team kan het antwoord als definitief zien.",
          },
          correct_answer: "C",
          feedback:
            "Een duidelijk antwoord kan nog bewijs missen uit systemen waar de tool niet bij kan.",
        },
      },
      question_text:
        "AI gives a clear answer from available files, but the team uses another system too. What is the main risk?",
      explanation:
        "A clear answer can still miss evidence from systems the tool cannot access.",
      answers: [
        {
          id: "station5_q026_a",
          question_id: "station5_q026",
          answer_text: "The answer may need a clearer date range.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q026_b",
          question_id: "station5_q026",
          answer_text: "The other system may use different labels.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q026_c",
          question_id: "station5_q026",
          answer_text: "Important evidence may be outside access.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station5_q026_d",
          question_id: "station5_q026",
          answer_text: "The team may treat the answer as final.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q027",
      station_id: "station_5",
      stage: 3,
      complexity: 4,
      core_concept:
        "Evidence should support information-based risk conclusions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI says a risk increased since last month. The answer sounds precise. What should you ask for next?",
          options: {
            A: "Evidence from the current month only.",
            B: "Evidence showing the month-to-month change.",
            C: "A sharper risk title for leaders.",
            D: "A forecast of next month’s risk.",
          },
          correct_answer: "B",
          feedback:
            "The precise claim needs trend evidence, especially because it compares change over time.",
        },
        pt: {
          question:
            "A IA diz que um risco aumentou desde o mês passado. A resposta parece precisa. O que pedir em seguida?",
          options: {
            A: "Evidência apenas do mês atual.",
            B: "Evidência da mudança mês a mês.",
            C: "Título de risco mais forte para líderes.",
            D: "Previsão do risco do próximo mês.",
          },
          correct_answer: "B",
          feedback:
            "A afirmação precisa de evidência de tendência, especialmente porque compara mudança ao longo do tempo.",
        },
        sv: {
          question:
            "AI-verktyget säger att en risk ökat sedan förra månaden. Svaret låter exakt. Vad ber du om?",
          options: {
            A: "Evidens bara från aktuell månad.",
            B: "Evidens som visar förändring månad för månad.",
            C: "Skarpare risktitel för ledare.",
            D: "Prognos för nästa månads risk.",
          },
          correct_answer: "B",
          feedback:
            "Det exakta påståendet behöver trend-evidens, särskilt eftersom det jämför förändring över tid.",
        },
        nl: {
          question:
            "De AI-tool zegt dat een risico sinds vorige maand is gestegen. Het antwoord klinkt precies. Wat vraag je daarna?",
          options: {
            A: "Bewijs alleen uit de huidige maand.",
            B: "Bewijs voor de maand-op-maand verandering.",
            C: "Scherpere risicotitel voor leiders.",
            D: "Voorspelling van het risico volgende maand.",
          },
          correct_answer: "B",
          feedback:
            "De precieze claim vraagt trendbewijs, zeker omdat hij verandering over tijd vergelijkt.",
        },
      },
      question_text:
        "AI says a risk increased since last month. The answer sounds precise. What should you ask for next?",
      explanation:
        "The precise claim needs trend evidence, especially because it compares change over time.",
      answers: [
        {
          id: "station5_q027_a",
          question_id: "station5_q027",
          answer_text: "Evidence from the current month only.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q027_b",
          question_id: "station5_q027",
          answer_text: "Evidence showing the month-to-month change.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station5_q027_c",
          question_id: "station5_q027",
          answer_text: "A sharper risk title for leaders.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q027_d",
          question_id: "station5_q027",
          answer_text: "A forecast of next month’s risk.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q028",
      station_id: "station_5",
      stage: 3,
      complexity: 5,
      core_concept:
        "Ambiguous information should be resolved before decisions depend on it",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI finds conflicting evidence about whether a milestone is complete. A decision depends on it today. What is the safest next step?",
          options: {
            A: "Use the newest source and note uncertainty.",
            B: "Ask AI to choose the likely status.",
            C: "Pause the decision until tomorrow.",
            D: "Review sources and confirm with the owner.",
          },
          correct_answer: "D",
          feedback:
            "When a decision depends on conflicting evidence, review sources and confirm with the accountable owner.",
        },
        pt: {
          question:
            "A IA encontra evidências conflitantes sobre um marco estar concluído. Uma decisão depende disso hoje. Qual é o próximo passo mais seguro?",
          options: {
            A: "Usar a fonte mais nova e notar incerteza.",
            B: "Pedir que a IA escolha o status provável.",
            C: "Pausar a decisão até amanhã.",
            D: "Revisar fontes e confirmar com o responsável.",
          },
          correct_answer: "D",
          feedback:
            "Quando uma decisão depende de evidência conflitante, revise fontes e confirme com o responsável.",
        },
        sv: {
          question:
            "AI-verktyget hittar motstridig evidens om en milstolpe är klar. Ett beslut kräver svar idag. Vad är säkrast?",
          options: {
            A: "Använd nyaste källan och notera osäkerhet.",
            B: "Be AI välja trolig status.",
            C: "Pausa beslutet till i morgon.",
            D: "Granska källor och bekräfta med ansvarig.",
          },
          correct_answer: "D",
          feedback:
            "När beslut beror på motstridig evidens, granska källor och bekräfta med ansvarig.",
        },
        nl: {
          question:
            "De AI-tool vindt tegenstrijdig bewijs over een voltooide mijlpaal. Vandaag hangt er een besluit vanaf. Wat is het veiligst?",
          options: {
            A: "De nieuwste bron gebruiken en onzekerheid noteren.",
            B: "AI de waarschijnlijke status laten kiezen.",
            C: "Het besluit tot morgen pauzeren.",
            D: "Bronnen bekijken en met eigenaar bevestigen.",
          },
          correct_answer: "D",
          feedback:
            "Als een besluit afhangt van tegenstrijdig bewijs, controleer bronnen en bevestig met de eigenaar.",
        },
      },
      question_text:
        "AI finds conflicting evidence about whether a milestone is complete. A decision depends on it today. What is the safest next step?",
      explanation:
        "When a decision depends on conflicting evidence, review sources and confirm with the accountable owner.",
      answers: [
        {
          id: "station5_q028_a",
          question_id: "station5_q028",
          answer_text: "Use the newest source and note uncertainty.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q028_b",
          question_id: "station5_q028",
          answer_text: "Ask AI to choose the likely status.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q028_c",
          question_id: "station5_q028",
          answer_text: "Pause the decision until tomorrow.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q028_d",
          question_id: "station5_q028",
          answer_text: "Review sources and confirm with the owner.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station5_q029",
      station_id: "station_5",
      stage: 3,
      complexity: 5,
      core_concept: "Decision preparation can be distorted by missing evidence",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI prepares options from available evidence, but two key regions have no documents in the tool. What is the main risk?",
          options: {
            A: "The options may reflect incomplete evidence.",
            B: "The options may overrepresent available regions.",
            C: "Missing regions may change the trade-off.",
            D: "The memo may hide where evidence is absent.",
          },
          correct_answer: "A",
          feedback:
            "Missing regional evidence can make options look balanced while excluding important context.",
        },
        pt: {
          question:
            "A IA prepara opções com evidências disponíveis, mas duas regiões-chave não têm documentos na ferramenta. Qual é o principal risco?",
          options: {
            A: "As opções podem refletir evidência incompleta.",
            B: "As opções podem supervalorizar regiões disponíveis.",
            C: "Regiões ausentes podem mudar o trade-off.",
            D: "O memo pode esconder onde falta evidência.",
          },
          correct_answer: "A",
          feedback:
            "Evidência regional ausente pode fazer opções parecerem equilibradas enquanto excluem contexto importante.",
        },
        sv: {
          question:
            "AI-verktyget förbereder alternativ från tillgänglig evidens, men två viktiga regioner saknar dokument. Vad är huvudrisken?",
          options: {
            A: "Alternativen kan spegla ofullständig evidens.",
            B: "Alternativen kan överrepresentera tillgängliga regioner.",
            C: "Saknade regioner kan ändra avvägningen.",
            D: "Underlaget kan dölja var evidens saknas.",
          },
          correct_answer: "A",
          feedback:
            "Saknad regional evidens kan få alternativ att verka balanserade men utesluta viktig kontext.",
        },
        nl: {
          question:
            "De AI-tool bereidt opties voor uit beschikbaar bewijs, maar twee sleutelregio’s hebben geen documenten. Wat is het grootste risico?",
          options: {
            A: "De opties kunnen onvolledig bewijs weerspiegelen.",
            B: "De opties kunnen beschikbare regio’s oververtegenwoordigen.",
            C: "Ontbrekende regio’s kunnen de afweging veranderen.",
            D: "De notitie kan verbergen waar bewijs ontbreekt.",
          },
          correct_answer: "A",
          feedback:
            "Ontbrekend regionaal bewijs kan opties evenwichtig laten lijken terwijl belangrijke context ontbreekt.",
        },
      },
      question_text:
        "AI prepares options from available evidence, but two key regions have no documents in the tool. What is the main risk?",
      explanation:
        "Missing regional evidence can make options look balanced while excluding important context.",
      answers: [
        {
          id: "station5_q029_a",
          question_id: "station5_q029",
          answer_text: "The options may reflect incomplete evidence.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station5_q029_b",
          question_id: "station5_q029",
          answer_text: "The options may overrepresent available regions.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q029_c",
          question_id: "station5_q029",
          answer_text: "Missing regions may change the trade-off.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station5_q029_d",
          question_id: "station5_q029",
          answer_text: "The memo may hide where evidence is absent.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station5_q030",
      station_id: "station_5",
      stage: 3,
      complexity: 5,
      core_concept:
        "AI can explain evidence conflicts but humans own interpretation and action",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI summarises conflicting sources and explains possible implications. What should remain human-owned before action is taken?",
          options: {
            A: "Finding the source names.",
            B: "Listing possible implications.",
            C: "Judging the interpretation and next action.",
            D: "Formatting the evidence for readability.",
          },
          correct_answer: "C",
          feedback:
            "AI can organise conflicting information, but interpretation and action require human ownership.",
        },
        pt: {
          question:
            "A IA resume fontes conflitantes e explica possíveis implicações. O que deve continuar humano antes de agir?",
          options: {
            A: "Encontrar nomes das fontes.",
            B: "Listar possíveis implicações.",
            C: "Julgar a interpretação e próxima ação.",
            D: "Formatar evidências para leitura.",
          },
          correct_answer: "C",
          feedback:
            "A IA pode organizar informação conflitante, mas interpretação e ação exigem responsabilidade humana.",
        },
        sv: {
          question:
            "AI-verktyget sammanfattar motstridiga källor och förklarar möjliga följder. Vad ska förbli mänskligt före åtgärd?",
          options: {
            A: "Hitta källnamnen.",
            B: "Lista möjliga följder.",
            C: "Bedöma tolkning och nästa åtgärd.",
            D: "Formatera evidens för läsbarhet.",
          },
          correct_answer: "C",
          feedback:
            "Verktyget kan ordna motstridig information, men tolkning och åtgärd kräver mänskligt ägarskap.",
        },
        nl: {
          question:
            "De AI-tool vat conflicterende bronnen samen en legt mogelijke gevolgen uit. Wat blijft menselijk vóór actie?",
          options: {
            A: "De bronnamen vinden.",
            B: "Mogelijke gevolgen opsommen.",
            C: "Interpretatie en volgende actie beoordelen.",
            D: "Bewijs leesbaar formatteren.",
          },
          correct_answer: "C",
          feedback:
            "De tool kan conflicterende informatie ordenen, maar interpretatie en actie vragen menselijk eigenaarschap.",
        },
      },
      question_text:
        "AI summarises conflicting sources and explains possible implications. What should remain human-owned before action is taken?",
      explanation:
        "AI can organise conflicting information, but interpretation and action require human ownership.",
      answers: [
        {
          id: "station5_q030_a",
          question_id: "station5_q030",
          answer_text: "Finding the source names.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station5_q030_b",
          question_id: "station5_q030",
          answer_text: "Listing possible implications.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station5_q030_c",
          question_id: "station5_q030",
          answer_text: "Judging the interpretation and next action.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station5_q030_d",
          question_id: "station5_q030",
          answer_text: "Formatting the evidence for readability.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q001",
      station_id: "station_6",
      stage: 1,
      complexity: 1,
      core_concept:
        "Sensitive information should only be used in allowed tools",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You want AI to summarise a file with personal data. What should you do first?",
          options: {
            A: "Paste it into any available AI tool.",
            B: "Check approved tool and handling rules.",
            C: "Remove names after the summary.",
            D: "Ask AI not to remember it.",
          },
          correct_answer: "B",
          feedback:
            "Sensitive information belongs only in approved tools and within handling rules. Removing details afterward may still expose data.",
        },
        pt: {
          question:
            "Você quer que a IA resuma um arquivo com dados pessoais. O que fazer primeiro?",
          options: {
            A: "Colar em qualquer ferramenta de IA.",
            B: "Checar ferramenta aprovada e regras de uso.",
            C: "Remover nomes depois do resumo.",
            D: "Pedir que a IA não lembre.",
          },
          correct_answer: "B",
          feedback:
            "Informação sensível só deve ir para ferramentas aprovadas e conforme as regras. Remover detalhes depois ainda pode expor dados.",
        },
        sv: {
          question:
            "Du vill låta AI sammanfatta en fil med personuppgifter. Vad gör du först?",
          options: {
            A: "Klistra in i valfritt AI-verktyg.",
            B: "Kontrollera godkänt verktyg och regler.",
            C: "Ta bort namn efter sammanfattningen.",
            D: "Be verktyget att inte minnas.",
          },
          correct_answer: "B",
          feedback:
            "Känslig information hör bara hemma i godkända verktyg och enligt regler. Att ta bort detaljer senare kan ändå exponera data.",
        },
        nl: {
          question:
            "Je wilt AI een bestand met persoonsgegevens laten samenvatten. Wat doe je eerst?",
          options: {
            A: "Plakken in elke beschikbare AI-tool.",
            B: "Goedgekeurde tool en regels controleren.",
            C: "Namen na de samenvatting verwijderen.",
            D: "Vragen dat AI het niet onthoudt.",
          },
          correct_answer: "B",
          feedback:
            "Gevoelige informatie hoort alleen in goedgekeurde tools en volgens regels. Details later verwijderen kan data alsnog blootstellen.",
        },
      },
      question_text:
        "You want AI to summarise a file with personal data. What should you do first?",
      explanation:
        "Sensitive information belongs only in approved tools and within handling rules. Removing details afterward may still expose data.",
      answers: [
        {
          id: "station6_q001_a",
          question_id: "station6_q001",
          answer_text: "Paste it into any available AI tool.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q001_b",
          question_id: "station6_q001",
          answer_text: "Check approved tool and handling rules.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station6_q001_c",
          question_id: "station6_q001",
          answer_text: "Remove names after the summary.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q001_d",
          question_id: "station6_q001",
          answer_text: "Ask AI not to remember it.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q002",
      station_id: "station_6",
      stage: 1,
      complexity: 1,
      core_concept:
        "Important external messages should be checked before sending",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI drafts a customer update with a delivery date. What should you verify before sending it?",
          options: {
            A: "Whether the tone sounds friendly.",
            B: "Whether the message is short.",
            C: "Whether the date is correct.",
            D: "Whether AI likes the wording.",
          },
          correct_answer: "C",
          feedback:
            "Delivery dates can create real expectations. Check the agreed fact, not only the wording.",
        },
        pt: {
          question:
            "A IA rascunha atualização ao cliente com data de entrega. O que verificar antes de enviar?",
          options: {
            A: "Se o tom soa amigável.",
            B: "Se a mensagem está curta.",
            C: "Se a data está correta.",
            D: "Se a IA gosta da redação.",
          },
          correct_answer: "C",
          feedback:
            "Datas de entrega criam expectativas reais. Cheque o fato acordado, não apenas a redação.",
        },
        sv: {
          question:
            "AI skapar kunduppdatering med leveransdatum. Vad bör du kontrollera före skick?",
          options: {
            A: "Om tonen låter vänlig.",
            B: "Om meddelandet är kort.",
            C: "Om datumet är korrekt.",
            D: "Om AI gillar formuleringen.",
          },
          correct_answer: "C",
          feedback:
            "Leveransdatum skapar verkliga förväntningar. Kontrollera den överenskomna uppgiften, inte bara formuleringen.",
        },
        nl: {
          question:
            "AI maakt een klantupdate met leverdatum. Wat controleer je vóór verzenden?",
          options: {
            A: "Of de toon vriendelijk klinkt.",
            B: "Of het bericht kort is.",
            C: "Of de datum klopt.",
            D: "Of AI de formulering goed vindt.",
          },
          correct_answer: "C",
          feedback:
            "Leverdatums scheppen echte verwachtingen. Controleer het afgesproken feit, niet alleen de formulering.",
        },
      },
      question_text:
        "AI drafts a customer update with a delivery date. What should you verify before sending it?",
      explanation:
        "Delivery dates can create real expectations. Check the agreed fact, not only the wording.",
      answers: [
        {
          id: "station6_q002_a",
          question_id: "station6_q002",
          answer_text: "Whether the tone sounds friendly.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q002_b",
          question_id: "station6_q002",
          answer_text: "Whether the message is short.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q002_c",
          question_id: "station6_q002",
          answer_text: "Whether the date is correct.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station6_q002_d",
          question_id: "station6_q002",
          answer_text: "Whether AI likes the wording.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q003",
      station_id: "station_6",
      stage: 1,
      complexity: 1,
      core_concept: "Users remain accountable for using AI output",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You use AI to draft a message you will send. Which mindset is responsible?",
          options: {
            A: "I am accountable for what I send.",
            B: "AI owns any mistake in the message.",
            C: "A fluent draft is already safe.",
            D: "Drafts never need checking.",
          },
          correct_answer: "A",
          feedback:
            "AI can help draft, but you remain accountable for what you send and how it is understood.",
        },
        pt: {
          question:
            "Você usa IA para rascunhar mensagem que enviará. Qual mentalidade é responsável?",
          options: {
            A: "Sou responsável pelo que envio.",
            B: "A IA assume qualquer erro na mensagem.",
            C: "Rascunho fluente já é seguro.",
            D: "Rascunhos nunca precisam checagem.",
          },
          correct_answer: "A",
          feedback:
            "A IA pode ajudar no rascunho, mas você continua responsável pelo que envia e como será entendido.",
        },
        sv: {
          question:
            "Du använder AI för att skriva ett meddelande du ska skicka. Vilket tankesätt är ansvarsfullt?",
          options: {
            A: "Jag ansvarar för det jag skickar.",
            B: "AI äger alla fel i meddelandet.",
            C: "Ett flytande utkast är redan säkert.",
            D: "Utkast behöver aldrig kontrolleras.",
          },
          correct_answer: "A",
          feedback:
            "AI kan hjälpa med utkast, men du ansvarar för det du skickar och hur det förstås.",
        },
        nl: {
          question:
            "Je gebruikt AI voor een bericht dat je gaat sturen. Welke houding is verantwoordelijk?",
          options: {
            A: "Ik ben verantwoordelijk voor wat ik stuur.",
            B: "AI bezit elke fout in het bericht.",
            C: "Een vloeiend concept is al veilig.",
            D: "Concepten hoeven nooit gecontroleerd te worden.",
          },
          correct_answer: "A",
          feedback:
            "AI kan helpen met een concept, maar jij blijft verantwoordelijk voor wat je verstuurt en hoe het wordt begrepen.",
        },
      },
      question_text:
        "You use AI to draft a message you will send. Which mindset is responsible?",
      explanation:
        "AI can help draft, but you remain accountable for what you send and how it is understood.",
      answers: [
        {
          id: "station6_q003_a",
          question_id: "station6_q003",
          answer_text: "I am accountable for what I send.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station6_q003_b",
          question_id: "station6_q003",
          answer_text: "AI owns any mistake in the message.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q003_c",
          question_id: "station6_q003",
          answer_text: "A fluent draft is already safe.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q003_d",
          question_id: "station6_q003",
          answer_text: "Drafts never need checking.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q004",
      station_id: "station_6",
      stage: 1,
      complexity: 2,
      core_concept:
        "Work-sensitive material should not be put into private accounts",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "Your approved work AI is unavailable. A private AI account is open. You need to summarise work-sensitive notes. What is safest?",
          options: {
            A: "Use the private account because it is faster.",
            B: "Paste only the most important parts.",
            C: "Ask AI to delete the notes afterward.",
            D: "Wait or use an approved route.",
          },
          correct_answer: "D",
          feedback:
            "Work-sensitive notes should not move to a private account for convenience. Use an approved route or wait.",
        },
        pt: {
          question:
            "A IA aprovada do trabalho está indisponível. Uma conta privada está aberta. Você precisa resumir notas sensíveis. O que é mais seguro?",
          options: {
            A: "Usar a conta privada porque é mais rápido.",
            B: "Colar só as partes mais importantes.",
            C: "Pedir que a IA apague as notas depois.",
            D: "Esperar ou usar rota aprovada.",
          },
          correct_answer: "D",
          feedback:
            "Notas sensíveis do trabalho não devem ir para uma conta privada por conveniência. Escolha uma rota aprovada ou espere.",
        },
        sv: {
          question:
            "Godkänt arbetsverktyg för AI är nere. Ett privat konto är öppet. Du behöver sammanfatta känsliga arbetsanteckningar. Vad är säkrast?",
          options: {
            A: "Använda privata kontot eftersom det går snabbare.",
            B: "Klistra in bara viktigaste delarna.",
            C: "Be AI radera anteckningarna efteråt.",
            D: "Vänta eller använda godkänd väg.",
          },
          correct_answer: "D",
          feedback:
            "Arbetskänsliga anteckningar ska inte flyttas till ett privat konto för bekvämlighet. Välj en godkänd väg eller vänta.",
        },
        nl: {
          question:
            "De goedgekeurde werk-AI is niet beschikbaar. Een privéaccount staat open. Je moet gevoelige werknotities samenvatten. Wat is het veiligst?",
          options: {
            A: "Het privéaccount gebruiken omdat het sneller is.",
            B: "Alleen de belangrijkste delen plakken.",
            C: "AI vragen de notities daarna te verwijderen.",
            D: "Wachten of een goedgekeurde route gebruiken.",
          },
          correct_answer: "D",
          feedback:
            "Werkgevoelige notities horen niet in een privéaccount voor gemak. Kies een goedgekeurde route of wacht.",
        },
      },
      question_text:
        "Your approved work AI is unavailable. A private AI account is open. You need to summarise work-sensitive notes. What is safest?",
      explanation:
        "Work-sensitive notes should not move to a private account for convenience. Use an approved route or wait.",
      answers: [
        {
          id: "station6_q004_a",
          question_id: "station6_q004",
          answer_text: "Use the private account because it is faster.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q004_b",
          question_id: "station6_q004",
          answer_text: "Paste only the most important parts.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q004_c",
          question_id: "station6_q004",
          answer_text: "Ask AI to delete the notes afterward.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q004_d",
          question_id: "station6_q004",
          answer_text: "Wait or use an approved route.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station6_q005",
      station_id: "station_6",
      stage: 1,
      complexity: 2,
      core_concept:
        "People-related AI outputs can carry bias and require caution",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI ranks team performance comments and may influence people decisions. What is the main responsible-use risk?",
          options: {
            A: "The ranking may need clearer labels.",
            B: "The ranking may reflect bias.",
            C: "The comments may need shorter summaries.",
            D: "The output may need manager formatting.",
          },
          correct_answer: "B",
          feedback:
            "People-related rankings can reflect biased patterns. Treat them with caution and human review.",
        },
        pt: {
          question:
            "A IA ranqueia comentários de desempenho e pode influenciar decisões sobre pessoas. Qual é o principal risco?",
          options: {
            A: "O ranking pode precisar de rótulos mais claros.",
            B: "O ranking pode refletir viés.",
            C: "Os comentários podem precisar de resumos menores.",
            D: "A saída pode precisar de formato para gestores.",
          },
          correct_answer: "B",
          feedback:
            "Rankings sobre pessoas podem refletir padrões enviesados. Trate-os com cautela e revisão humana.",
        },
        sv: {
          question:
            "AI rangordnar kommentarer om prestation och kan påverka personbeslut. Vilken är huvudrisken?",
          options: {
            A: "Rangordningen kan behöva tydligare etiketter.",
            B: "Rangordningen kan spegla bias.",
            C: "Kommentarerna kan behöva kortare sammanfattningar.",
            D: "Resultatet kan behöva chefsformat.",
          },
          correct_answer: "B",
          feedback:
            "Rangordningar om människor kan spegla bias. Hantera dem försiktigt och med mänsklig granskning.",
        },
        nl: {
          question:
            "AI rangschikt opmerkingen over prestaties en kan mensbesluiten beïnvloeden. Wat is het belangrijkste risico?",
          options: {
            A: "De ranglijst kan duidelijkere labels nodig hebben.",
            B: "De ranglijst kan bias weerspiegelen.",
            C: "De opmerkingen kunnen kortere samenvattingen nodig hebben.",
            D: "De output kan managementopmaak nodig hebben.",
          },
          correct_answer: "B",
          feedback:
            "Ranglijsten over mensen kunnen bias weerspiegelen. Behandel ze voorzichtig en met menselijke beoordeling.",
        },
      },
      question_text:
        "AI ranks team performance comments and may influence people decisions. What is the main responsible-use risk?",
      explanation:
        "People-related rankings can reflect biased patterns. Treat them with caution and human review.",
      answers: [
        {
          id: "station6_q005_a",
          question_id: "station6_q005",
          answer_text: "The ranking may need clearer labels.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q005_b",
          question_id: "station6_q005",
          answer_text: "The ranking may reflect bias.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station6_q005_c",
          question_id: "station6_q005",
          answer_text: "The comments may need shorter summaries.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q005_d",
          question_id: "station6_q005",
          answer_text: "The output may need manager formatting.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q006",
      station_id: "station_6",
      stage: 1,
      complexity: 2,
      core_concept: "AI drafts should be checked before external use",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI drafts a supplier email with a claim about costs. What should you do first?",
          options: {
            A: "Send it because it sounds professional.",
            B: "Ask AI to make it firmer.",
            C: "Check the cost claim yourself.",
            D: "Let AI negotiate the supplier response.",
          },
          correct_answer: "C",
          feedback:
            "Cost claims can affect negotiation and trust. Verify the claim yourself before sending it.",
        },
        pt: {
          question:
            "A IA rascunha e-mail ao fornecedor com afirmação sobre custos. O que fazer primeiro?",
          options: {
            A: "Enviar porque soa profissional.",
            B: "Pedir que a IA deixe mais firme.",
            C: "Checar a afirmação de custo.",
            D: "Deixar a IA negociar a resposta.",
          },
          correct_answer: "C",
          feedback:
            "Afirmações sobre custo podem afetar negociação e confiança. Verifique a afirmação antes de enviar.",
        },
        sv: {
          question:
            "AI skapar leverantörsmejl med påstående om kostnader. Vad gör du först?",
          options: {
            A: "Skicka eftersom det låter professionellt.",
            B: "Be AI göra det mer bestämt.",
            C: "Kontrollera kostnadspåståendet själv.",
            D: "Låt AI förhandla svaret.",
          },
          correct_answer: "C",
          feedback:
            "Kostnadspåståenden kan påverka förhandling och förtroende. Kontrollera påståendet själv före utskick.",
        },
        nl: {
          question:
            "AI maakt een leveranciersmail met een kostenclaim. Wat doe je eerst?",
          options: {
            A: "Versturen omdat het professioneel klinkt.",
            B: "AI vragen het steviger te maken.",
            C: "De kostenclaim zelf controleren.",
            D: "AI de reactie laten onderhandelen.",
          },
          correct_answer: "C",
          feedback:
            "Kostenclaims kunnen onderhandeling en vertrouwen beïnvloeden. Controleer de claim zelf vóór verzending.",
        },
      },
      question_text:
        "AI drafts a supplier email with a claim about costs. What should you do first?",
      explanation:
        "Cost claims can affect negotiation and trust. Verify the claim yourself before sending it.",
      answers: [
        {
          id: "station6_q006_a",
          question_id: "station6_q006",
          answer_text: "Send it because it sounds professional.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q006_b",
          question_id: "station6_q006",
          answer_text: "Ask AI to make it firmer.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q006_c",
          question_id: "station6_q006",
          answer_text: "Check the cost claim yourself.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station6_q006_d",
          question_id: "station6_q006",
          answer_text: "Let AI negotiate the supplier response.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q007",
      station_id: "station_6",
      stage: 1,
      complexity: 2,
      core_concept:
        "When unsure about risky use, ask for guidance before using AI",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You are unsure whether a work file can be used with AI. What is the responsible first step?",
          options: {
            A: "Check guidance or ask for help.",
            B: "Try it and remove details later.",
            C: "Use AI only for the conclusion.",
            D: "Assume internal files are always allowed.",
          },
          correct_answer: "A",
          feedback:
            "When permission is unclear, guidance comes first. Guessing can expose information or create policy risk.",
        },
        pt: {
          question:
            "Você não sabe se um arquivo de trabalho pode ser usado com IA. Qual é o primeiro passo responsável?",
          options: {
            A: "Checar orientação ou pedir ajuda.",
            B: "Testar e remover detalhes depois.",
            C: "Usar IA só para a conclusão.",
            D: "Presumir que arquivos internos são sempre permitidos.",
          },
          correct_answer: "A",
          feedback:
            "Quando a permissão não está clara, a orientação vem primeiro. Adivinhar pode expor informação ou criar risco.",
        },
        sv: {
          question:
            "Du är osäker om en arbetsfil får användas med AI. Vad är ansvarsfullt första steg?",
          options: {
            A: "Kontrollera vägledning eller be om hjälp.",
            B: "Testa och ta bort detaljer senare.",
            C: "Använd AI bara för slutsatsen.",
            D: "Anta att interna filer alltid är tillåtna.",
          },
          correct_answer: "A",
          feedback:
            "När tillåtelse är oklar kommer vägledning först. Gissningar kan exponera information eller skapa risk.",
        },
        nl: {
          question:
            "Je twijfelt of een werkbestand met AI mag worden gebruikt. Wat is de verantwoordelijke eerste stap?",
          options: {
            A: "Richtlijn checken of hulp vragen.",
            B: "Testen en details later verwijderen.",
            C: "AI alleen voor de conclusie gebruiken.",
            D: "Aannemen dat interne bestanden altijd mogen.",
          },
          correct_answer: "A",
          feedback:
            "Als toestemming onduidelijk is, komt begeleiding eerst. Gokken kan informatie blootstellen of risico creëren.",
        },
      },
      question_text:
        "You are unsure whether a work file can be used with AI. What is the responsible first step?",
      explanation:
        "When permission is unclear, guidance comes first. Guessing can expose information or create policy risk.",
      answers: [
        {
          id: "station6_q007_a",
          question_id: "station6_q007",
          answer_text: "Check guidance or ask for help.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station6_q007_b",
          question_id: "station6_q007",
          answer_text: "Try it and remove details later.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q007_c",
          question_id: "station6_q007",
          answer_text: "Use AI only for the conclusion.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q007_d",
          question_id: "station6_q007",
          answer_text: "Assume internal files are always allowed.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q008",
      station_id: "station_6",
      stage: 1,
      complexity: 2,
      core_concept: "Fluent AI outputs can contain unsupported claims",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI adds a strong claim to your update, but you did not provide that fact. What should you verify?",
          options: {
            A: "Whether the claim sounds confident.",
            B: "Whether the update is shorter.",
            C: "Whether the tone is positive.",
            D: "Whether the claim is supported.",
          },
          correct_answer: "D",
          feedback:
            "A strong claim needs support. Confident wording does not make an unsupported fact safe to use.",
        },
        pt: {
          question:
            "A IA adiciona afirmação forte à sua atualização, mas você não forneceu esse fato. O que verificar?",
          options: {
            A: "Se a afirmação soa confiante.",
            B: "Se a atualização ficou mais curta.",
            C: "Se o tom está positivo.",
            D: "Se a afirmação tem suporte.",
          },
          correct_answer: "D",
          feedback:
            "Uma afirmação forte precisa de suporte. Redação confiante não torna seguro usar um fato sem evidência.",
        },
        sv: {
          question:
            "AI lägger till ett starkt påstående i din uppdatering, men du gav inte den faktan. Vad bör du kontrollera?",
          options: {
            A: "Om påståendet låter säkert.",
            B: "Om uppdateringen är kortare.",
            C: "Om tonen är positiv.",
            D: "Om påståendet har stöd.",
          },
          correct_answer: "D",
          feedback:
            "Ett starkt påstående behöver stöd. Säker formulering gör inte ett ostött faktum säkert att använda.",
        },
        nl: {
          question:
            "AI voegt een sterke claim toe aan je update, maar jij gaf dat feit niet. Wat controleer je?",
          options: {
            A: "Of de claim zelfverzekerd klinkt.",
            B: "Of de update korter is.",
            C: "Of de toon positief is.",
            D: "Of de claim onderbouwd is.",
          },
          correct_answer: "D",
          feedback:
            "Een sterke claim heeft steun nodig. Zeker taalgebruik maakt een onbewezen feit niet veilig.",
        },
      },
      question_text:
        "AI adds a strong claim to your update, but you did not provide that fact. What should you verify?",
      explanation:
        "A strong claim needs support. Confident wording does not make an unsupported fact safe to use.",
      answers: [
        {
          id: "station6_q008_a",
          question_id: "station6_q008",
          answer_text: "Whether the claim sounds confident.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q008_b",
          question_id: "station6_q008",
          answer_text: "Whether the update is shorter.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q008_c",
          question_id: "station6_q008",
          answer_text: "Whether the tone is positive.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q008_d",
          question_id: "station6_q008",
          answer_text: "Whether the claim is supported.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station6_q009",
      station_id: "station_6",
      stage: 1,
      complexity: 2,
      core_concept: "External communication remains human-owned",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI rewrites a customer reply and changes the promise slightly. What is the safest next step?",
          options: {
            A: "Send it because the tone improved.",
            B: "Restore the promise to agreed facts.",
            C: "Ask AI to make it warmer.",
            D: "Let AI choose the final promise.",
          },
          correct_answer: "B",
          feedback:
            "A warmer reply is not safer if it changes the promise. Keep commitments tied to agreed facts.",
        },
        pt: {
          question:
            "A IA reescreve resposta ao cliente e muda levemente a promessa. Qual é o próximo passo mais seguro?",
          options: {
            A: "Enviar porque o tom melhorou.",
            B: "Restaurar a promessa aos fatos acordados.",
            C: "Pedir tom mais acolhedor à IA.",
            D: "Deixar a IA escolher a promessa final.",
          },
          correct_answer: "B",
          feedback:
            "Uma resposta mais cordial não é mais segura se muda a promessa. Mantenha compromissos ligados aos fatos acordados.",
        },
        sv: {
          question:
            "AI skriver om ett kundsvar och ändrar löftet lite. Vad är säkrast nästa steg?",
          options: {
            A: "Skicka eftersom tonen blev bättre.",
            B: "Återställ löftet till överenskomna fakta.",
            C: "Be AI göra tonen varmare.",
            D: "Låt AI välja slutligt löfte.",
          },
          correct_answer: "B",
          feedback:
            "Ett varmare svar är inte säkrare om löftet ändras. Håll åtaganden kopplade till överenskomna fakta.",
        },
        nl: {
          question:
            "AI herschrijft een klantantwoord en verandert de belofte iets. Wat is het veiligst?",
          options: {
            A: "Versturen omdat de toon beter is.",
            B: "De belofte terugzetten naar afgesproken feiten.",
            C: "AI om warmere toon vragen.",
            D: "AI de definitieve belofte laten kiezen.",
          },
          correct_answer: "B",
          feedback:
            "Een vriendelijker antwoord is niet veiliger als de belofte verandert. Houd toezeggingen bij afgesproken feiten.",
        },
      },
      question_text:
        "AI rewrites a customer reply and changes the promise slightly. What is the safest next step?",
      explanation:
        "A warmer reply is not safer if it changes the promise. Keep commitments tied to agreed facts.",
      answers: [
        {
          id: "station6_q009_a",
          question_id: "station6_q009",
          answer_text: "Send it because the tone improved.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q009_b",
          question_id: "station6_q009",
          answer_text: "Restore the promise to agreed facts.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station6_q009_c",
          question_id: "station6_q009",
          answer_text: "Ask AI to make it warmer.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q009_d",
          question_id: "station6_q009",
          answer_text: "Let AI choose the final promise.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q010",
      station_id: "station_6",
      stage: 1,
      complexity: 2,
      core_concept: "AI can help spot patterns, but bias must be considered",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI groups feedback into positive and negative themes. What is the responsible way to use it?",
          options: {
            A: "Accept the grouping as objective.",
            B: "Delete comments that do not fit.",
            C: "Review themes for bias or missed nuance.",
            D: "Use only the negative theme list.",
          },
          correct_answer: "C",
          feedback:
            "Feedback themes can hide nuance or bias. Review examples before treating the grouping as representative.",
        },
        pt: {
          question:
            "A IA agrupa feedback em temas positivos e negativos. Qual é o uso responsável?",
          options: {
            A: "Aceitar o agrupamento como objetivo.",
            B: "Apagar comentários que não se encaixam.",
            C: "Revisar temas por viés ou nuance perdida.",
            D: "Usar só a lista de temas negativos.",
          },
          correct_answer: "C",
          feedback:
            "Temas de feedback podem esconder nuance ou viés. Revise exemplos antes de tratar o agrupamento como representativo.",
        },
        sv: {
          question:
            "AI grupperar feedback i positiva och negativa teman. Vad är ansvarsfull användning?",
          options: {
            A: "Acceptera grupperingen som objektiv.",
            B: "Ta bort kommentarer som inte passar.",
            C: "Granska teman för bias eller tappad nyans.",
            D: "Använd bara negativa teman.",
          },
          correct_answer: "C",
          feedback:
            "Feedbackteman kan dölja nyans eller bias. Granska exempel innan grupperingen ses som representativ.",
        },
        nl: {
          question:
            "AI groepeert feedback in positieve en negatieve thema’s. Wat is verantwoordelijk gebruik?",
          options: {
            A: "De groepering als objectief accepteren.",
            B: "Reacties verwijderen die niet passen.",
            C: "Thema’s controleren op bias of verloren nuance.",
            D: "Alleen de negatieve themalijst gebruiken.",
          },
          correct_answer: "C",
          feedback:
            "Feedbackthema’s kunnen nuance of bias verbergen. Controleer voorbeelden voordat de groepering representatief lijkt.",
        },
      },
      question_text:
        "AI groups feedback into positive and negative themes. What is the responsible way to use it?",
      explanation:
        "Feedback themes can hide nuance or bias. Review examples before treating the grouping as representative.",
      answers: [
        {
          id: "station6_q010_a",
          question_id: "station6_q010",
          answer_text: "Accept the grouping as objective.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q010_b",
          question_id: "station6_q010",
          answer_text: "Delete comments that do not fit.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q010_c",
          question_id: "station6_q010",
          answer_text: "Review themes for bias or missed nuance.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station6_q010_d",
          question_id: "station6_q010",
          answer_text: "Use only the negative theme list.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q011",
      station_id: "station_6",
      stage: 2,
      complexity: 2,
      core_concept: "Sensitive business information requires approved handling",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A colleague suggests pasting supplier pricing into an unapproved AI tool for a quick summary. What is the main risk?",
          options: {
            A: "Sensitive business information may be exposed.",
            B: "The pricing may be summarized without context.",
            C: "The supplier may need an approved channel.",
            D: "The output may be reused outside control.",
          },
          correct_answer: "A",
          feedback:
            "Supplier pricing is sensitive business information. Speed does not justify using an unapproved tool.",
        },
        pt: {
          question:
            "Um colega sugere colar preços de fornecedor em ferramenta de IA não aprovada para resumo rápido. Qual é o principal risco?",
          options: {
            A: "Informação comercial sensível pode ser exposta.",
            B: "O preço pode ser resumido sem contexto.",
            C: "O fornecedor pode exigir canal aprovado.",
            D: "A saída pode ser reutilizada sem controle.",
          },
          correct_answer: "A",
          feedback:
            "Preços de fornecedores são informação comercial sensível. Rapidez não justifica ferramenta não aprovada.",
        },
        sv: {
          question:
            "En kollega vill klistra in leverantörspriser i ett ej godkänt AI-verktyg för snabb sammanfattning. Vad är huvudrisken?",
          options: {
            A: "Känslig affärsinformation kan exponeras.",
            B: "Prissättningen kan sammanfattas utan kontext.",
            C: "Leverantören kan kräva godkänd kanal.",
            D: "Resultatet kan återanvändas utan kontroll.",
          },
          correct_answer: "A",
          feedback:
            "Leverantörspriser är känslig affärsinformation. Hastighet motiverar inte ett icke godkänt verktyg.",
        },
        nl: {
          question:
            "Een collega wil leveranciersprijzen in een niet-goedgekeurde AI-tool plakken voor een snelle samenvatting. Wat is het grootste risico?",
          options: {
            A: "Gevoelige bedrijfsinformatie kan worden blootgesteld.",
            B: "De prijs kan zonder context worden samengevat.",
            C: "De leverancier kan een goedgekeurd kanaal vereisen.",
            D: "De output kan ongecontroleerd worden hergebruikt.",
          },
          correct_answer: "A",
          feedback:
            "Leveranciersprijzen zijn gevoelige bedrijfsinformatie. Snelheid rechtvaardigt geen niet-goedgekeurde tool.",
        },
      },
      question_text:
        "A colleague suggests pasting supplier pricing into an unapproved AI tool for a quick summary. What is the main risk?",
      explanation:
        "Supplier pricing is sensitive business information. Speed does not justify using an unapproved tool.",
      answers: [
        {
          id: "station6_q011_a",
          question_id: "station6_q011",
          answer_text: "Sensitive business information may be exposed.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station6_q011_b",
          question_id: "station6_q011",
          answer_text: "The pricing may be summarized without context.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q011_c",
          question_id: "station6_q011",
          answer_text: "The supplier may need an approved channel.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q011_d",
          question_id: "station6_q011",
          answer_text: "The output may be reused outside control.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q012",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept: "People-related decisions require human accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI prepares notes about employee performance patterns. What must remain human-owned?",
          options: {
            A: "Checking whether examples are representative.",
            B: "Grouping repeated themes for review.",
            C: "Summarising patterns without naming people.",
            D: "Judging any people-related action.",
          },
          correct_answer: "D",
          feedback:
            "AI may organise performance notes, but people-related actions need human judgment, fairness and accountability.",
        },
        pt: {
          question:
            "A IA prepara notas sobre padrões de desempenho de colaboradores. O que deve continuar humano?",
          options: {
            A: "Checar se exemplos são representativos.",
            B: "Agrupar temas repetidos para revisão.",
            C: "Resumir padrões sem nomear pessoas.",
            D: "Julgar qualquer ação sobre pessoas.",
          },
          correct_answer: "D",
          feedback:
            "A IA pode organizar notas de desempenho, mas ações sobre pessoas exigem julgamento, justiça e responsabilidade humanos.",
        },
        sv: {
          question:
            "AI förbereder anteckningar om mönster i medarbetares prestation. Vad måste förbli mänskligt?",
          options: {
            A: "Kontrollera om exemplen är representativa.",
            B: "Gruppera upprepade teman för granskning.",
            C: "Sammanfatta mönster utan att namnge personer.",
            D: "Bedöma åtgärder som rör människor.",
          },
          correct_answer: "D",
          feedback:
            "AI kan ordna prestationsanteckningar, men åtgärder om människor kräver mänskligt omdöme, rättvisa och ansvar.",
        },
        nl: {
          question:
            "AI bereidt notities voor over patronen in medewerkersprestaties. Wat moet menselijk blijven?",
          options: {
            A: "Controleren of voorbeelden representatief zijn.",
            B: "Herhaalde thema’s groeperen voor review.",
            C: "Patronen samenvatten zonder mensen te noemen.",
            D: "Oordelen over mensgerelateerde acties.",
          },
          correct_answer: "D",
          feedback:
            "AI kan prestatienotities ordenen, maar acties over mensen vragen menselijk oordeel, eerlijkheid en verantwoordelijkheid.",
        },
      },
      question_text:
        "AI prepares notes about employee performance patterns. What must remain human-owned?",
      explanation:
        "AI may organise performance notes, but people-related actions need human judgment, fairness and accountability.",
      answers: [
        {
          id: "station6_q012_a",
          question_id: "station6_q012",
          answer_text: "Checking whether examples are representative.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q012_b",
          question_id: "station6_q012",
          answer_text: "Grouping repeated themes for review.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q012_c",
          question_id: "station6_q012",
          answer_text: "Summarising patterns without naming people.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q012_d",
          question_id: "station6_q012",
          answer_text: "Judging any people-related action.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station6_q013",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept: "Unsupported AI conclusions should be checked before use",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI writes a confident conclusion that supports your preferred view. What should you do first?",
          options: {
            A: "Use it if the evidence looks familiar.",
            B: "Check evidence and possible counterpoints.",
            C: "Ask for a stronger supporting argument.",
            D: "Remove uncertainty from the conclusion.",
          },
          correct_answer: "B",
          feedback:
            "A conclusion that fits your view can still be weak. Check evidence and counterpoints before relying on it.",
        },
        pt: {
          question:
            "A IA escreve conclusão confiante que apoia sua visão preferida. O que fazer primeiro?",
          options: {
            A: "Utilizar se a evidência parecer familiar.",
            B: "Checar evidências e possíveis contrapontos.",
            C: "Pedir um argumento de apoio mais forte.",
            D: "Remover incerteza da conclusão.",
          },
          correct_answer: "B",
          feedback:
            "Uma conclusão que combina com sua visão ainda pode ser fraca. Cheque evidências e contrapontos antes de confiar.",
        },
        sv: {
          question:
            "AI skriver en säker slutsats som stödjer din föredragna syn. Vad gör du först?",
          options: {
            A: "Använda det om evidensen känns bekant.",
            B: "Kontrollera evidens och möjliga motargument.",
            C: "Be om ett starkare stödargument.",
            D: "Ta bort osäkerhet från slutsatsen.",
          },
          correct_answer: "B",
          feedback:
            "En slutsats som passar din syn kan ändå vara svag. Kontrollera evidens och motargument före användning.",
        },
        nl: {
          question:
            "AI schrijft een zekere conclusie die jouw voorkeur ondersteunt. Wat doe je eerst?",
          options: {
            A: "Gebruiken als het bewijs bekend lijkt.",
            B: "Bewijs en mogelijke tegenargumenten controleren.",
            C: "Om een sterker steunargument vragen.",
            D: "Onzekerheid uit de conclusie verwijderen.",
          },
          correct_answer: "B",
          feedback:
            "Een conclusie die jouw mening steunt kan zwak zijn. Controleer bewijs en tegenargumenten voordat je erop vertrouwt.",
        },
      },
      question_text:
        "AI writes a confident conclusion that supports your preferred view. What should you do first?",
      explanation:
        "A conclusion that fits your view can still be weak. Check evidence and counterpoints before relying on it.",
      answers: [
        {
          id: "station6_q013_a",
          question_id: "station6_q013",
          answer_text: "Use it if the evidence looks familiar.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q013_b",
          question_id: "station6_q013",
          answer_text: "Check evidence and possible counterpoints.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station6_q013_c",
          question_id: "station6_q013",
          answer_text: "Ask for a stronger supporting argument.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q013_d",
          question_id: "station6_q013",
          answer_text: "Remove uncertainty from the conclusion.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q014",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept: "External AI-assisted communication should be verified",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI drafts a partner update that mentions a new commitment. You are unsure it was agreed. What is safest?",
          options: {
            A: "Send it with cautious wording.",
            B: "Soften the commitment language.",
            C: "Confirm the commitment before sending.",
            D: "Mark the commitment as likely agreed.",
          },
          correct_answer: "C",
          feedback:
            "External commitments should match what was actually agreed. Softer wording does not fix an unconfirmed promise.",
        },
        pt: {
          question:
            "A IA rascunha atualização a parceiro com novo compromisso. Você não sabe se foi acordado. O que é mais seguro?",
          options: {
            A: "Enviar com redação cautelosa.",
            B: "Suavizar a linguagem do compromisso.",
            C: "Confirmar o compromisso antes de enviar.",
            D: "Marcar o compromisso como provável.",
          },
          correct_answer: "C",
          feedback:
            "Compromissos externos devem refletir o que foi acordado. Redação suave não corrige promessa não confirmada.",
        },
        sv: {
          question:
            "AI skriver partneruppdatering med ett nytt åtagande. Du är osäker om det avtalats. Vad är säkrast?",
          options: {
            A: "Skicka med försiktig formulering.",
            B: "Mjuka upp åtagandets formulering.",
            C: "Bekräfta åtagandet före utskick.",
            D: "Markera åtagandet som troligen avtalat.",
          },
          correct_answer: "C",
          feedback:
            "Externa åtaganden ska spegla vad som faktiskt avtalats. Mildare formulering löser inte ett obekräftat löfte.",
        },
        nl: {
          question:
            "AI maakt een partnerupdate met een nieuwe toezegging. Je weet niet of die is afgesproken. Wat is het veiligst?",
          options: {
            A: "Versturen met voorzichtige formulering.",
            B: "De toezegging zachter formuleren.",
            C: "De toezegging bevestigen vóór verzending.",
            D: "De toezegging als waarschijnlijk afgesproken markeren.",
          },
          correct_answer: "C",
          feedback:
            "Externe toezeggingen moeten kloppen met afspraken. Zachtere woorden maken een onbevestigde belofte niet veilig.",
        },
      },
      question_text:
        "AI drafts a partner update that mentions a new commitment. You are unsure it was agreed. What is safest?",
      explanation:
        "External commitments should match what was actually agreed. Softer wording does not fix an unconfirmed promise.",
      answers: [
        {
          id: "station6_q014_a",
          question_id: "station6_q014",
          answer_text: "Send it with cautious wording.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q014_b",
          question_id: "station6_q014",
          answer_text: "Soften the commitment language.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q014_c",
          question_id: "station6_q014",
          answer_text: "Confirm the commitment before sending.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station6_q014_d",
          question_id: "station6_q014",
          answer_text: "Mark the commitment as likely agreed.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q015",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept: "AI prioritisation can encode bias or unfair assumptions",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI prioritises support tickets and puts one region last every time. What risk should you consider?",
          options: {
            A: "The criteria may fit one region poorly.",
            B: "The data may miss regional context.",
            C: "The queue may need human review.",
            D: "The prioritisation may be biased.",
          },
          correct_answer: "D",
          feedback:
            "Repeatedly ranking one region last may indicate biased data, assumptions or criteria. Investigate before using the ranking.",
        },
        pt: {
          question:
            "A IA prioriza chamados e coloca uma região por último toda vez. Qual risco considerar?",
          options: {
            A: "Os critérios podem servir mal a uma região.",
            B: "Os dados podem faltar contexto regional.",
            C: "A fila pode precisar de revisão humana.",
            D: "A priorização pode ser enviesada.",
          },
          correct_answer: "D",
          feedback:
            "Colocar sempre uma região por último pode indicar viés em dados, premissas ou critérios. Investigue antes de usar.",
        },
        sv: {
          question:
            "AI prioriterar supportärenden och placerar en region sist varje gång. Vilken risk bör du tänka på?",
          options: {
            A: "Kriterierna kan passa en region dåligt.",
            B: "Data kan sakna regional kontext.",
            C: "Kön kan behöva mänsklig granskning.",
            D: "Prioriteringen kan vara partisk.",
          },
          correct_answer: "D",
          feedback:
            "Att alltid rangordna en region sist kan visa bias i data, antaganden eller kriterier. Undersök före användning.",
        },
        nl: {
          question:
            "AI prioriteert supporttickets en zet één regio steeds onderaan. Welk risico moet je overwegen?",
          options: {
            A: "De criteria passen mogelijk slecht bij één regio.",
            B: "De data mist mogelijk regionale context.",
            C: "De wachtrij kan menselijke review nodig hebben.",
            D: "De prioritering kan bevooroordeeld zijn.",
          },
          correct_answer: "D",
          feedback:
            "Eén regio steeds onderaan zetten kan bias in data, aannames of criteria tonen. Onderzoek dit vóór gebruik.",
        },
      },
      question_text:
        "AI prioritises support tickets and puts one region last every time. What risk should you consider?",
      explanation:
        "Repeatedly ranking one region last may indicate biased data, assumptions or criteria. Investigate before using the ranking.",
      answers: [
        {
          id: "station6_q015_a",
          question_id: "station6_q015",
          answer_text: "The criteria may fit one region poorly.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q015_b",
          question_id: "station6_q015",
          answer_text: "The data may miss regional context.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q015_c",
          question_id: "station6_q015",
          answer_text: "The queue may need human review.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q015_d",
          question_id: "station6_q015",
          answer_text: "The prioritisation may be biased.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station6_q016",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept:
        "Summaries of people feedback need nuance and fairness checks",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI summarises team feedback into one negative theme. What is the responsible use?",
          options: {
            A: "Check nuance and examples before sharing.",
            B: "Share it with a note about uncertainty.",
            C: "Ask for themes by subgroup.",
            D: "Remove examples that weaken the theme.",
          },
          correct_answer: "A",
          feedback:
            "A single negative theme can oversimplify feedback. Check examples and nuance before sharing it as a team view.",
        },
        pt: {
          question:
            "A IA resume feedback da equipe em um tema negativo. Qual é o uso responsável?",
          options: {
            A: "Checar nuances e exemplos antes de compartilhar.",
            B: "Compartilhar com nota de incerteza.",
            C: "Pedir temas por subgrupo.",
            D: "Remover exemplos que enfraquecem o tema.",
          },
          correct_answer: "A",
          feedback:
            "Um único tema negativo pode simplificar demais o feedback. Cheque exemplos e nuances antes de compartilhar.",
        },
        sv: {
          question:
            "AI sammanfattar teamfeedback till ett negativt tema. Vad är ansvarsfull användning?",
          options: {
            A: "Kontrollera nyanser och exempel före delning.",
            B: "Dela med en osäkerhetsnotering.",
            C: "Be om teman per undergrupp.",
            D: "Ta bort exempel som försvagar temat.",
          },
          correct_answer: "A",
          feedback:
            "Ett enda negativt tema kan förenkla feedback för mycket. Kontrollera exempel och nyanser före delning.",
        },
        nl: {
          question:
            "AI vat teamfeedback samen tot één negatief thema. Wat is verantwoordelijk gebruik?",
          options: {
            A: "Nuance en voorbeelden controleren vóór delen.",
            B: "Delen met een opmerking over onzekerheid.",
            C: "Om thema’s per subgroep vragen.",
            D: "Voorbeelden verwijderen die het thema verzwakken.",
          },
          correct_answer: "A",
          feedback:
            "Eén negatief thema kan feedback te sterk vereenvoudigen. Controleer voorbeelden en nuance vóór delen.",
        },
      },
      question_text:
        "AI summarises team feedback into one negative theme. What is the responsible use?",
      explanation:
        "A single negative theme can oversimplify feedback. Check examples and nuance before sharing it as a team view.",
      answers: [
        {
          id: "station6_q016_a",
          question_id: "station6_q016",
          answer_text: "Check nuance and examples before sharing.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station6_q016_b",
          question_id: "station6_q016",
          answer_text: "Share it with a note about uncertainty.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q016_c",
          question_id: "station6_q016",
          answer_text: "Ask for themes by subgroup.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q016_d",
          question_id: "station6_q016",
          answer_text: "Remove examples that weaken the theme.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q017",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept: "Use approved tools for work-sensitive information",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You need AI help with internal figures. Two tools are available: one approved, one public. What should you do first?",
          options: {
            A: "Use the public tool with rounded figures.",
            B: "Use the approved tool for the figures.",
            C: "Share only figures already in slides.",
            D: "Ask both tools and compare outputs.",
          },
          correct_answer: "B",
          feedback:
            "Internal figures should stay in approved tools. Public tools may expose information or use it in unsafe ways.",
        },
        pt: {
          question:
            "Você precisa de ajuda da IA com números internos. Há duas ferramentas: uma aprovada e uma pública. O que fazer primeiro?",
          options: {
            A: "Utilizar a ferramenta pública com números arredondados.",
            B: "Utilizar a ferramenta aprovada para os números.",
            C: "Compartilhar apenas números já em slides.",
            D: "Perguntar às duas ferramentas e comparar.",
          },
          correct_answer: "B",
          feedback:
            "Números internos devem ficar em ferramentas aprovadas. Ferramentas públicas podem expor ou usar informação indevidamente.",
        },
        sv: {
          question:
            "Du behöver AI-hjälp med interna siffror. Två verktyg finns: ett godkänt, ett publikt. Vad gör du först?",
          options: {
            A: "Använda publikt verktyg med avrundade siffror.",
            B: "Använda godkänt verktyg för siffrorna.",
            C: "Dela bara siffror som finns i bilder.",
            D: "Fråga båda verktygen och jämföra.",
          },
          correct_answer: "B",
          feedback:
            "Interna siffror ska stanna i godkända verktyg. Publika verktyg kan exponera eller använda information fel.",
        },
        nl: {
          question:
            "Je hebt AI-hulp nodig met interne cijfers. Er zijn twee tools: één goedgekeurd, één publiek. Wat doe je eerst?",
          options: {
            A: "Publieke tool gebruiken met afgeronde cijfers.",
            B: "De goedgekeurde tool gebruiken voor de cijfers.",
            C: "Alleen cijfers uit slides delen.",
            D: "Beide tools vragen en vergelijken.",
          },
          correct_answer: "B",
          feedback:
            "Interne cijfers horen in goedgekeurde tools. Publieke tools kunnen informatie blootstellen of verkeerd gebruiken.",
        },
      },
      question_text:
        "You need AI help with internal figures. Two tools are available: one approved, one public. What should you do first?",
      explanation:
        "Internal figures should stay in approved tools. Public tools may expose information or use it in unsafe ways.",
      answers: [
        {
          id: "station6_q017_a",
          question_id: "station6_q017",
          answer_text: "Use the public tool with rounded figures.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q017_b",
          question_id: "station6_q017",
          answer_text: "Use the approved tool for the figures.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station6_q017_c",
          question_id: "station6_q017",
          answer_text: "Share only figures already in slides.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q017_d",
          question_id: "station6_q017",
          answer_text: "Ask both tools and compare outputs.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q018",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept: "Unsupported details should not be treated as reliable",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI adds a customer quote you do not recognise. How should you treat it?",
          options: {
            A: "Use it if the quote supports the message.",
            B: "Treat it as a draft until approved.",
            C: "Treat it as unsupported until verified.",
            D: "Paraphrase it to reduce quotation risk.",
          },
          correct_answer: "C",
          feedback:
            "A specific quote can still be invented. Verify it before using it in customer communication.",
        },
        pt: {
          question:
            "A IA adiciona citação de cliente que você não reconhece. Como tratá-la?",
          options: {
            A: "Utilizar se a citação apoiar a mensagem.",
            B: "Tratar como rascunho até aprovação.",
            C: "Tratar como sem suporte até verificar.",
            D: "Parafrasear para reduzir risco de citação.",
          },
          correct_answer: "C",
          feedback:
            "Uma citação específica ainda pode ser inventada. Verifique antes de usar em comunicação com cliente.",
        },
        sv: {
          question:
            "AI lägger till ett kundcitat du inte känner igen. Hur bör du behandla det?",
          options: {
            A: "Använda det om citatet stödjer budskapet.",
            B: "Behandla det som utkast tills godkänt.",
            C: "Behandla det som ostött tills verifierat.",
            D: "Parafrasera för att minska citatrisk.",
          },
          correct_answer: "C",
          feedback:
            "Ett specifikt citat kan ändå vara påhittat. Verifiera det före kundkommunikation.",
        },
        nl: {
          question:
            "AI voegt een klantquote toe die je niet herkent. Hoe behandel je die?",
          options: {
            A: "Gebruiken als het citaat de boodschap steunt.",
            B: "Als concept behandelen tot goedkeuring.",
            C: "Als niet onderbouwd behandelen tot verificatie.",
            D: "Parafraseren om citaatrisico te beperken.",
          },
          correct_answer: "C",
          feedback:
            "Een specifiek citaat kan nog steeds verzonnen zijn. Verifieer het vóór klantcommunicatie.",
        },
      },
      question_text:
        "AI adds a customer quote you do not recognise. How should you treat it?",
      explanation:
        "A specific quote can still be invented. Verify it before using it in customer communication.",
      answers: [
        {
          id: "station6_q018_a",
          question_id: "station6_q018",
          answer_text: "Use it if the quote supports the message.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q018_b",
          question_id: "station6_q018",
          answer_text: "Treat it as a draft until approved.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q018_c",
          question_id: "station6_q018",
          answer_text: "Treat it as unsupported until verified.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station6_q018_d",
          question_id: "station6_q018",
          answer_text: "Paraphrase it to reduce quotation risk.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q019",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept: "High-impact decisions should not be delegated to AI",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI prepares options for a high-impact supplier decision. What must remain human-owned?",
          options: {
            A: "Ranking options by expected benefit.",
            B: "Listing pros, cons and assumptions.",
            C: "Summarising available facts for review.",
            D: "Making the final decision.",
          },
          correct_answer: "D",
          feedback:
            "AI can prepare options, but a high-impact supplier decision needs accountable human ownership.",
        },
        pt: {
          question:
            "A IA prepara opções para decisão importante sobre fornecedor. O que deve continuar humano?",
          options: {
            A: "Ranquear opções por benefício esperado.",
            B: "Listar prós, contras e premissas.",
            C: "Resumir fatos disponíveis para revisão.",
            D: "Tomar a decisão final.",
          },
          correct_answer: "D",
          feedback:
            "A IA pode preparar opções, mas uma decisão importante sobre fornecedor exige responsabilidade humana.",
        },
        sv: {
          question:
            "AI förbereder alternativ för viktigt leverantörsbeslut. Vad måste förbli mänskligt?",
          options: {
            A: "Rangordna alternativ efter förväntad nytta.",
            B: "Lista fördelar, nackdelar och antaganden.",
            C: "Sammanfatta tillgängliga fakta för granskning.",
            D: "Fatta det slutliga beslutet.",
          },
          correct_answer: "D",
          feedback:
            "AI kan förbereda alternativ, men ett viktigt leverantörsbeslut kräver mänskligt ansvar.",
        },
        nl: {
          question:
            "AI bereidt opties voor voor een belangrijk leveranciersbesluit. Wat moet menselijk blijven?",
          options: {
            A: "Opties rangschikken op verwacht voordeel.",
            B: "Voor- en nadelen en aannames opsommen.",
            C: "Beschikbare feiten samenvatten voor review.",
            D: "De uiteindelijke beslissing nemen.",
          },
          correct_answer: "D",
          feedback:
            "AI kan opties voorbereiden, maar een grote leveranciersbeslissing vraagt menselijke verantwoordelijkheid.",
        },
      },
      question_text:
        "AI prepares options for a high-impact supplier decision. What must remain human-owned?",
      explanation:
        "AI can prepare options, but a high-impact supplier decision needs accountable human ownership.",
      answers: [
        {
          id: "station6_q019_a",
          question_id: "station6_q019",
          answer_text: "Ranking options by expected benefit.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q019_b",
          question_id: "station6_q019",
          answer_text: "Listing pros, cons and assumptions.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q019_c",
          question_id: "station6_q019",
          answer_text: "Summarising available facts for review.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q019_d",
          question_id: "station6_q019",
          answer_text: "Making the final decision.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station6_q020",
      station_id: "station_6",
      stage: 2,
      complexity: 3,
      core_concept: "Unclear risky uses should be escalated before action",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "You are unsure whether a dataset can be processed with AI, and the deadline is close. What is safest?",
          options: {
            A: "Pause and ask for guidance.",
            B: "Test with a small sample first.",
            C: "Remove obvious identifiers before uploading.",
            D: "Process only the least sensitive rows.",
          },
          correct_answer: "A",
          feedback:
            "Urgency does not remove data-handling risk. Pause and confirm what is allowed before processing.",
        },
        pt: {
          question:
            "Você não sabe se um conjunto de dados pode ser processado com IA, e o prazo está perto. O que é mais seguro?",
          options: {
            A: "Pausar e pedir orientação.",
            B: "Testar primeiro com pequena amostra.",
            C: "Remover identificadores óbvios antes de enviar.",
            D: "Processar só linhas menos sensíveis.",
          },
          correct_answer: "A",
          feedback:
            "Urgência não remove risco no tratamento de dados. Pare e confirme o que é permitido antes de processar.",
        },
        sv: {
          question:
            "Du är osäker om ett dataset får behandlas med AI, och deadline är nära. Vad är säkrast?",
          options: {
            A: "Pausa och be om vägledning.",
            B: "Testa först med ett litet urval.",
            C: "Ta bort tydliga identifierare före uppladdning.",
            D: "Bearbeta bara minst känsliga rader.",
          },
          correct_answer: "A",
          feedback:
            "Brådska tar inte bort datarisk. Pausa och bekräfta vad som är tillåtet före behandling.",
        },
        nl: {
          question:
            "Je weet niet of een dataset met AI verwerkt mag worden, en de deadline is dichtbij. Wat is het veiligst?",
          options: {
            A: "Pauzeren en om begeleiding vragen.",
            B: "Eerst met een kleine steekproef testen.",
            C: "Duidelijke identifiers verwijderen vóór upload.",
            D: "Alleen minst gevoelige rijen verwerken.",
          },
          correct_answer: "A",
          feedback:
            "Urgentie neemt datarisico niet weg. Pauzeer en bevestig wat mag vóór verwerking.",
        },
      },
      question_text:
        "You are unsure whether a dataset can be processed with AI, and the deadline is close. What is safest?",
      explanation:
        "Urgency does not remove data-handling risk. Pause and confirm what is allowed before processing.",
      answers: [
        {
          id: "station6_q020_a",
          question_id: "station6_q020",
          answer_text: "Pause and ask for guidance.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station6_q020_b",
          question_id: "station6_q020",
          answer_text: "Test with a small sample first.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q020_c",
          question_id: "station6_q020",
          answer_text: "Remove obvious identifiers before uploading.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q020_d",
          question_id: "station6_q020",
          answer_text: "Process only the least sensitive rows.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q021",
      station_id: "station_6",
      stage: 3,
      complexity: 3,
      core_concept:
        "AI can help challenge assumptions but people must judge fairness",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI supports your preferred supplier ranking. You want a responsible check. What should you ask it to do?",
          options: {
            A: "Ask for evidence supporting the ranking.",
            B: "Challenge assumptions and possible bias.",
            C: "Compare the top two suppliers only.",
            D: "Rewrite the ranking for leaders.",
          },
          correct_answer: "B",
          feedback:
            "Responsible use includes challenging a preferred answer. Bias checks can reveal weak assumptions before decisions.",
        },
        pt: {
          question:
            "A IA apoia seu ranking preferido de fornecedores. Você quer checagem responsável. O que pedir?",
          options: {
            A: "Pedir evidências que sustentem o ranking.",
            B: "Desafiar premissas e possível viés.",
            C: "Comparar apenas os dois melhores fornecedores.",
            D: "Reescrever o ranking para líderes.",
          },
          correct_answer: "B",
          feedback:
            "Uso responsável inclui desafiar uma resposta preferida. Checagens de viés podem revelar premissas fracas.",
        },
        sv: {
          question:
            "AI stödjer din föredragna leverantörsranking. Du vill göra ansvarsfull kontroll. Vad ber du om?",
          options: {
            A: "Be om evidens som stödjer rangordningen.",
            B: "Utmana antaganden och möjlig bias.",
            C: "Jämföra bara de två bästa leverantörerna.",
            D: "Skriva om rangordningen för ledare.",
          },
          correct_answer: "B",
          feedback:
            "Ansvarsfull användning inkluderar att utmana ett önskat svar. Bias-kontroller kan avslöja svaga antaganden.",
        },
        nl: {
          question:
            "AI ondersteunt jouw voorkeursranglijst van leveranciers. Je wilt verantwoordelijk checken. Wat vraag je?",
          options: {
            A: "Om bewijs voor de ranglijst vragen.",
            B: "Aannames en mogelijke bias uitdagen.",
            C: "Alleen de twee beste leveranciers vergelijken.",
            D: "De ranglijst herschrijven voor leiders.",
          },
          correct_answer: "B",
          feedback:
            "Verantwoord gebruik betekent ook een gewenst antwoord uitdagen. Biaschecks kunnen zwakke aannames tonen.",
        },
      },
      question_text:
        "AI supports your preferred supplier ranking. You want a responsible check. What should you ask it to do?",
      explanation:
        "Responsible use includes challenging a preferred answer. Bias checks can reveal weak assumptions before decisions.",
      answers: [
        {
          id: "station6_q021_a",
          question_id: "station6_q021",
          answer_text: "Ask for evidence supporting the ranking.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q021_b",
          question_id: "station6_q021",
          answer_text: "Challenge assumptions and possible bias.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station6_q021_c",
          question_id: "station6_q021",
          answer_text: "Compare the top two suppliers only.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q021_d",
          question_id: "station6_q021",
          answer_text: "Rewrite the ranking for leaders.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q022",
      station_id: "station_6",
      stage: 3,
      complexity: 4,
      core_concept: "Important claims need verification even when fluent",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI writes that a process is fully compliant, but you only gave partial notes. What should you verify?",
          options: {
            A: "Whether the wording makes compliance sound too certain.",
            B: "Whether missing evidence should be named.",
            C: "Whether the claim is actually supported.",
            D: "Whether the claim uses approved wording.",
          },
          correct_answer: "C",
          feedback:
            "A compliance claim based on partial notes can be misleading. Verify support against the right material before sharing.",
        },
        pt: {
          question:
            "A IA escreve que um processo está totalmente conforme, mas você deu notas parciais. O que verificar?",
          options: {
            A: "Se a redação torna a conformidade certa demais.",
            B: "Se evidências ausentes devem ser citadas.",
            C: "Se a afirmação tem suporte real.",
            D: "Se a afirmação usa redação aprovada.",
          },
          correct_answer: "C",
          feedback:
            "Uma afirmação de conformidade baseada em notas parciais pode enganar. Verifique o suporte no material correto.",
        },
        sv: {
          question:
            "AI skriver att en process är helt compliant, men du gav bara delvisa anteckningar. Vad bör du kontrollera?",
          options: {
            A: "Om formuleringen gör efterlevnaden för säker.",
            B: "Om saknad evidens bör nämnas.",
            C: "Om påståendet faktiskt har stöd.",
            D: "Om påståendet använder godkänd formulering.",
          },
          correct_answer: "C",
          feedback:
            "Ett efterlevnadspåstående baserat på delvisa anteckningar kan vilseleda. Kontrollera stöd i rätt material.",
        },
        nl: {
          question:
            "AI schrijft dat een proces volledig compliant is, maar je gaf alleen gedeeltelijke notities. Wat controleer je?",
          options: {
            A: "Of de formulering compliance te zeker maakt.",
            B: "Of ontbrekend bewijs genoemd moet worden.",
            C: "Of de claim echt wordt ondersteund.",
            D: "Of de claim goedgekeurde formulering gebruikt.",
          },
          correct_answer: "C",
          feedback:
            "Een complianceclaim op basis van gedeeltelijke notities kan misleiden. Controleer steun in het juiste materiaal.",
        },
      },
      question_text:
        "AI writes that a process is fully compliant, but you only gave partial notes. What should you verify?",
      explanation:
        "A compliance claim based on partial notes can be misleading. Verify support against the right material before sharing.",
      answers: [
        {
          id: "station6_q022_a",
          question_id: "station6_q022",
          answer_text:
            "Whether the wording makes compliance sound too certain.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q022_b",
          question_id: "station6_q022",
          answer_text: "Whether missing evidence should be named.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q022_c",
          question_id: "station6_q022",
          answer_text: "Whether the claim is actually supported.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station6_q022_d",
          question_id: "station6_q022",
          answer_text: "Whether the claim uses approved wording.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q023",
      station_id: "station_6",
      stage: 3,
      complexity: 4,
      core_concept: "People-related AI classification can create fairness risk",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI labels several employees as “low potential” from short comments. What is the main responsible-use risk?",
          options: {
            A: "The comments may need clearer grouping.",
            B: "The label may need a better explanation.",
            C: "The pattern may need more examples.",
            D: "The labels may be biased or unfair.",
          },
          correct_answer: "D",
          feedback:
            "People labels from short comments can be biased and unfair. This requires caution, not fast classification.",
        },
        pt: {
          question:
            "A IA rotula alguns colaboradores como “baixo potencial” a partir de comentários curtos. Qual é o principal risco?",
          options: {
            A: "Os comentários podem precisar de agrupamento melhor.",
            B: "O rótulo pode precisar de explicação melhor.",
            C: "O padrão pode precisar de mais exemplos.",
            D: "Os rótulos podem ser enviesados ou injustos.",
          },
          correct_answer: "D",
          feedback:
            "Rótulos sobre pessoas a partir de comentários curtos podem ser enviesados e injustos. Isso exige cautela.",
        },
        sv: {
          question:
            "AI märker flera medarbetare som ”låg potential” från korta kommentarer. Vad är huvudrisken?",
          options: {
            A: "Kommentarerna kan behöva tydligare gruppering.",
            B: "Etiketten kan behöva bättre förklaring.",
            C: "Mönstret kan behöva fler exempel.",
            D: "Etiketterna kan vara partiska eller orättvisa.",
          },
          correct_answer: "D",
          feedback:
            "Personetiketter från korta kommentarer kan vara partiska och orättvisa. Det kräver försiktighet.",
        },
        nl: {
          question:
            "AI labelt enkele medewerkers als “laag potentieel” op basis van korte opmerkingen. Wat is het grootste risico?",
          options: {
            A: "De opmerkingen kunnen duidelijkere groepering nodig hebben.",
            B: "Het label kan betere uitleg nodig hebben.",
            C: "Het patroon kan meer voorbeelden nodig hebben.",
            D: "De labels kunnen bevooroordeeld of oneerlijk zijn.",
          },
          correct_answer: "D",
          feedback:
            "Labels over mensen uit korte opmerkingen kunnen bevooroordeeld en oneerlijk zijn. Dit vraagt voorzichtigheid.",
        },
      },
      question_text:
        "AI labels several employees as “low potential” from short comments. What is the main responsible-use risk?",
      explanation:
        "People labels from short comments can be biased and unfair. This requires caution, not fast classification.",
      answers: [
        {
          id: "station6_q023_a",
          question_id: "station6_q023",
          answer_text: "The comments may need clearer grouping.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q023_b",
          question_id: "station6_q023",
          answer_text: "The label may need a better explanation.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q023_c",
          question_id: "station6_q023",
          answer_text: "The pattern may need more examples.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q023_d",
          question_id: "station6_q023",
          answer_text: "The labels may be biased or unfair.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station6_q024",
      station_id: "station_6",
      stage: 3,
      complexity: 4,
      core_concept: "External escalation messages require human ownership",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI drafts a firm customer escalation using uncertain facts. The message may affect trust. What is safest?",
          options: {
            A: "Verify facts and approve wording yourself.",
            B: "Send it with uncertainty clearly stated.",
            C: "Ask AI for a less firm version.",
            D: "Remove uncertain facts from the message.",
          },
          correct_answer: "A",
          feedback:
            "Customer escalations can affect trust. Facts and wording need human approval before the message goes out.",
        },
        pt: {
          question:
            "A IA rascunha escalonamento firme ao cliente com fatos incertos. A mensagem pode afetar confiança. O que é mais seguro?",
          options: {
            A: "Verificar fatos e aprovar a redação.",
            B: "Enviar com incerteza declarada claramente.",
            C: "Pedir à IA uma versão menos firme.",
            D: "Remover fatos incertos da mensagem.",
          },
          correct_answer: "A",
          feedback:
            "Escalações para clientes podem afetar confiança. Fatos e redação precisam de aprovação humana antes do envio.",
        },
        sv: {
          question:
            "AI skapar tydlig kundeskalering med osäkra fakta. Meddelandet kan påverka förtroende. Vad är säkrast?",
          options: {
            A: "Verifiera fakta och godkänn formuleringen själv.",
            B: "Skicka med osäkerhet tydligt angiven.",
            C: "Be AI om en mindre bestämd version.",
            D: "Ta bort osäkra fakta från meddelandet.",
          },
          correct_answer: "A",
          feedback:
            "Kundeskaleringar kan påverka förtroende. Fakta och formulering behöver mänskligt godkännande före utskick.",
        },
        nl: {
          question:
            "AI maakt een stevige klantescalatie met onzekere feiten. Het bericht kan vertrouwen raken. Wat is het veiligst?",
          options: {
            A: "Feiten verifiëren en tekst zelf goedkeuren.",
            B: "Versturen met onzekerheid duidelijk vermeld.",
            C: "AI om een minder stellige versie vragen.",
            D: "Onzekere feiten uit het bericht verwijderen.",
          },
          correct_answer: "A",
          feedback:
            "Klantescalaties kunnen vertrouwen beïnvloeden. Feiten en formulering vragen menselijke goedkeuring vóór verzending.",
        },
      },
      question_text:
        "AI drafts a firm customer escalation using uncertain facts. The message may affect trust. What is safest?",
      explanation:
        "Customer escalations can affect trust. Facts and wording need human approval before the message goes out.",
      answers: [
        {
          id: "station6_q024_a",
          question_id: "station6_q024",
          answer_text: "Verify facts and approve wording yourself.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station6_q024_b",
          question_id: "station6_q024",
          answer_text: "Send it with uncertainty clearly stated.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q024_c",
          question_id: "station6_q024",
          answer_text: "Ask AI for a less firm version.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q024_d",
          question_id: "station6_q024",
          answer_text: "Remove uncertain facts from the message.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q025",
      station_id: "station_6",
      stage: 3,
      complexity: 4,
      core_concept: "Unclear high-risk use cases should be escalated",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A use case involves customer complaints and possible personal details. Guidance is unclear. What should you do first?",
          options: {
            A: "Use only complaints with names removed.",
            B: "Test with one low-risk example.",
            C: "Ask for guidance before using AI.",
            D: "Summarise themes without storing details.",
          },
          correct_answer: "C",
          feedback:
            "Customer complaints with personal details need clear guidance first. Removing names later may not remove the risk.",
        },
        pt: {
          question:
            "Um caso envolve reclamações de clientes e possíveis dados pessoais. A orientação é incerta. O que fazer primeiro?",
          options: {
            A: "Utilizar só reclamações sem nomes.",
            B: "Testar com um exemplo de baixo risco.",
            C: "Pedir orientação antes de usar IA.",
            D: "Resumir temas sem guardar detalhes.",
          },
          correct_answer: "C",
          feedback:
            "Reclamações com detalhes pessoais precisam de orientação clara primeiro. Remover nomes depois pode não remover o risco.",
        },
        sv: {
          question:
            "Ett fall gäller kundklagomål och möjliga personuppgifter. Vägledning är oklar. Vad gör du först?",
          options: {
            A: "Använd bara klagomål utan namn.",
            B: "Testa med ett lågriskexempel.",
            C: "Be om vägledning innan AI används.",
            D: "Sammanfatta teman utan att lagra detaljer.",
          },
          correct_answer: "C",
          feedback:
            "Kundklagomål med personuppgifter behöver tydlig vägledning först. Att ta bort namn senare räcker kanske inte.",
        },
        nl: {
          question:
            "Een use case gaat over klantklachten en mogelijke persoonsgegevens. Richtlijn is onduidelijk. Wat doe je eerst?",
          options: {
            A: "Alleen klachten zonder namen gebruiken.",
            B: "Met één laag-risicovoorbeeld testen.",
            C: "Om begeleiding vragen vóór AI-gebruik.",
            D: "Thema’s samenvatten zonder details op te slaan.",
          },
          correct_answer: "C",
          feedback:
            "Klachten met persoonlijke details vragen eerst duidelijke richtlijnen. Namen later verwijderen neemt het risico niet altijd weg.",
        },
      },
      question_text:
        "A use case involves customer complaints and possible personal details. Guidance is unclear. What should you do first?",
      explanation:
        "Customer complaints with personal details need clear guidance first. Removing names later may not remove the risk.",
      answers: [
        {
          id: "station6_q025_a",
          question_id: "station6_q025",
          answer_text: "Use only complaints with names removed.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q025_b",
          question_id: "station6_q025",
          answer_text: "Test with one low-risk example.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q025_c",
          question_id: "station6_q025",
          answer_text: "Ask for guidance before using AI.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station6_q025_d",
          question_id: "station6_q025",
          answer_text: "Summarise themes without storing details.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q026",
      station_id: "station_6",
      stage: 3,
      complexity: 4,
      core_concept: "AI can support judgment but not replace accountability",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI lists risks for a launch and ranks them clearly. How should you use the ranking responsibly?",
          options: {
            A: "Treat the top risk as the decision.",
            B: "Use it as input for human review.",
            C: "Focus only on high-ranked risks.",
            D: "Ask AI for a launch recommendation.",
          },
          correct_answer: "B",
          feedback:
            "A ranked risk list is useful input, not approval. Humans still review context and decide what to do.",
        },
        pt: {
          question:
            "A IA lista riscos de lançamento e ranqueia claramente. Como usar o ranking com responsabilidade?",
          options: {
            A: "Tratar o maior risco como decisão.",
            B: "Utilizar como insumo para revisão humana.",
            C: "Focar só nos riscos mais altos.",
            D: "Pedir recomendação de lançamento à IA.",
          },
          correct_answer: "B",
          feedback:
            "Uma lista ranqueada de riscos é insumo útil, não aprovação. Humanos revisam contexto e decidem ações.",
        },
        sv: {
          question:
            "AI listar lanseringsrisker och rangordnar tydligt. Hur använder du rankingen ansvarsfullt?",
          options: {
            A: "Behandla största risken som beslutet.",
            B: "Använda den som input för mänsklig granskning.",
            C: "Fokusera bara på högt rankade risker.",
            D: "Be AI om lanseringsrekommendation.",
          },
          correct_answer: "B",
          feedback:
            "En rangordnad risklista är användbar input, inte godkännande. Människor granskar kontext och beslutar åtgärder.",
        },
        nl: {
          question:
            "AI noemt lanceringsrisico’s en rangschikt ze duidelijk. Hoe gebruik je de ranglijst verantwoordelijk?",
          options: {
            A: "Het hoogste risico als besluit behandelen.",
            B: "Als input voor menselijke review gebruiken.",
            C: "Alleen op hoog gerangschikte risico’s focussen.",
            D: "AI om lanceringsadvies vragen.",
          },
          correct_answer: "B",
          feedback:
            "Een gerangschikte risicolijst is nuttige input, geen goedkeuring. Mensen beoordelen context en bepalen acties.",
        },
      },
      question_text:
        "AI lists risks for a launch and ranks them clearly. How should you use the ranking responsibly?",
      explanation:
        "A ranked risk list is useful input, not approval. Humans still review context and decide what to do.",
      answers: [
        {
          id: "station6_q026_a",
          question_id: "station6_q026",
          answer_text: "Treat the top risk as the decision.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q026_b",
          question_id: "station6_q026",
          answer_text: "Use it as input for human review.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station6_q026_c",
          question_id: "station6_q026",
          answer_text: "Focus only on high-ranked risks.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q026_d",
          question_id: "station6_q026",
          answer_text: "Ask AI for a launch recommendation.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q027",
      station_id: "station_6",
      stage: 3,
      complexity: 4,
      core_concept: "AI can hallucinate convincing details",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI creates a detailed project timeline with events you do not recognise. What should you do before relying on it?",
          options: {
            A: "Make the timeline easier to read.",
            B: "Use only events with recent dates.",
            C: "Ask AI to add more milestones.",
            D: "Check events against sources or owners.",
          },
          correct_answer: "D",
          feedback:
            "Unrecognised timeline events may be invented or from the wrong context. Check sources or owners before relying on the plan.",
        },
        pt: {
          question:
            "A IA cria uma linha do tempo com eventos que você não reconhece. O que fazer antes de confiar?",
          options: {
            A: "Tornar a linha do tempo mais legível.",
            B: "Usar só eventos com datas recentes.",
            C: "Pedir à IA para adicionar marcos.",
            D: "Checar eventos com fontes ou responsáveis.",
          },
          correct_answer: "D",
          feedback:
            "Eventos desconhecidos podem ser inventados ou vir do contexto errado. Cheque fontes ou responsáveis antes de confiar no plano.",
        },
        sv: {
          question:
            "AI skapar en detaljerad projekttidslinje med händelser du inte känner igen. Vad bör du göra före användning?",
          options: {
            A: "Göra tidslinjen lättare att läsa.",
            B: "Använda bara händelser med nya datum.",
            C: "Be AI lägga till fler milstolpar.",
            D: "Kontrollera händelser mot källor eller ägare.",
          },
          correct_answer: "D",
          feedback:
            "Okända tidslinjehändelser kan vara påhittade eller från fel kontext. Kontrollera källor eller ägare före användning.",
        },
        nl: {
          question:
            "AI maakt een projecttijdlijn met gebeurtenissen die je niet herkent. Wat moet je doen vóór gebruik?",
          options: {
            A: "De tijdlijn leesbaarder maken.",
            B: "Alleen gebeurtenissen met recente datums gebruiken.",
            C: "AI meer mijlpalen laten toevoegen.",
            D: "Gebeurtenissen controleren bij bronnen of eigenaars.",
          },
          correct_answer: "D",
          feedback:
            "Onbekende gebeurtenissen kunnen verzonnen zijn of uit verkeerde context komen. Controleer bronnen of eigenaars vóór gebruik.",
        },
      },
      question_text:
        "AI creates a detailed project timeline with events you do not recognise. What should you do before relying on it?",
      explanation:
        "Unrecognised timeline events may be invented or from the wrong context. Check sources or owners before relying on the plan.",
      answers: [
        {
          id: "station6_q027_a",
          question_id: "station6_q027",
          answer_text: "Make the timeline easier to read.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q027_b",
          question_id: "station6_q027",
          answer_text: "Use only events with recent dates.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q027_c",
          question_id: "station6_q027",
          answer_text: "Ask AI to add more milestones.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q027_d",
          question_id: "station6_q027",
          answer_text: "Check events against sources or owners.",
          option_key: "D",
          order_index: 4,
          is_correct: true,
        },
      ],
    },
    {
      id: "station6_q028",
      station_id: "station_6",
      stage: 3,
      complexity: 5,
      core_concept: "People-related ambiguous use cases require escalation",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A manager wants AI to suggest who should get fewer development opportunities. The input is brief and subjective. What is safest?",
          options: {
            A: "Stop and seek guidance before using AI.",
            B: "Ask AI to explain the ranking better.",
            C: "Remove names and run the ranking.",
            D: "Use AI only to flag development risk.",
          },
          correct_answer: "A",
          feedback:
            "Limiting development opportunities is a people-related decision with fairness and accountability risks. Seek guidance before using AI.",
        },
        pt: {
          question:
            "Um gestor quer que a IA sugira quem deve receber menos oportunidades de desenvolvimento. O input é breve e subjetivo. O que é mais seguro?",
          options: {
            A: "Parar e buscar orientação antes de usar IA.",
            B: "Pedir à IA para explicar melhor o ranking.",
            C: "Remover nomes e rodar o ranking.",
            D: "Usar IA só para sinalizar risco de desenvolvimento.",
          },
          correct_answer: "A",
          feedback:
            "Limitar oportunidades de desenvolvimento é decisão sobre pessoas, com riscos de justiça e responsabilidade. Busque orientação antes.",
        },
        sv: {
          question:
            "En chef vill att AI föreslår vem som ska få färre utvecklingsmöjligheter. Inputen är kort och subjektiv. Vad är säkrast?",
          options: {
            A: "Stoppa och sök vägledning innan AI används.",
            B: "Be AI förklara rangordningen bättre.",
            C: "Ta bort namn och kör rangordningen.",
            D: "Använd AI bara för att flagga utvecklingsrisk.",
          },
          correct_answer: "A",
          feedback:
            "Att begränsa utvecklingsmöjligheter är ett personbeslut med rättvise- och ansvarsrisk. Sök vägledning först.",
        },
        nl: {
          question:
            "Een manager wil AI laten voorstellen wie minder ontwikkelkansen krijgt. De input is kort en subjectief. Wat is het veiligst?",
          options: {
            A: "Stoppen en begeleiding vragen vóór AI-gebruik.",
            B: "AI de ranglijst beter laten uitleggen.",
            C: "Namen verwijderen en rangschikken.",
            D: "AI alleen ontwikkelrisico laten signaleren.",
          },
          correct_answer: "A",
          feedback:
            "Ontwikkelkansen beperken is een mensgerelateerde beslissing met eerlijkheids- en verantwoordelijkheidsrisico. Vraag eerst begeleiding.",
        },
      },
      question_text:
        "A manager wants AI to suggest who should get fewer development opportunities. The input is brief and subjective. What is safest?",
      explanation:
        "Limiting development opportunities is a people-related decision with fairness and accountability risks. Seek guidance before using AI.",
      answers: [
        {
          id: "station6_q028_a",
          question_id: "station6_q028",
          answer_text: "Stop and seek guidance before using AI.",
          option_key: "A",
          order_index: 1,
          is_correct: true,
        },
        {
          id: "station6_q028_b",
          question_id: "station6_q028",
          answer_text: "Ask AI to explain the ranking better.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q028_c",
          question_id: "station6_q028",
          answer_text: "Remove names and run the ranking.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q028_d",
          question_id: "station6_q028",
          answer_text: "Use AI only to flag development risk.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q029",
      station_id: "station_6",
      stage: 3,
      complexity: 5,
      core_concept:
        "High-stakes external communication requires human approval",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "AI drafts a public partner statement after a serious issue. What must remain human-owned?",
          options: {
            A: "Checking whether the message is concise.",
            B: "Adapting the draft for each audience.",
            C: "Approving facts, tone and release.",
            D: "Asking AI to list likely questions.",
          },
          correct_answer: "C",
          feedback:
            "A public statement after a serious issue needs accountable approval of facts, tone and timing.",
        },
        pt: {
          question:
            "A IA rascunha comunicado público a parceiro após problema sério. O que deve continuar humano?",
          options: {
            A: "Checar se a mensagem está concisa.",
            B: "Adaptar o rascunho para cada público.",
            C: "Aprovar fatos, tom e divulgação.",
            D: "Pedir à IA perguntas prováveis.",
          },
          correct_answer: "C",
          feedback:
            "Uma declaração pública após problema sério precisa de aprovação responsável de fatos, tom e momento.",
        },
        sv: {
          question:
            "AI skapar offentligt partneruttalande efter ett allvarligt problem. Vad måste förbli mänskligt?",
          options: {
            A: "Kontrollera om budskapet är kortfattat.",
            B: "Anpassa utkastet för varje målgrupp.",
            C: "Godkänna fakta, ton och publicering.",
            D: "Be AI lista troliga frågor.",
          },
          correct_answer: "C",
          feedback:
            "Ett offentligt uttalande efter en allvarlig händelse kräver ansvarigt godkännande av fakta, ton och tidpunkt.",
        },
        nl: {
          question:
            "AI maakt een publieke partnerverklaring na een ernstig probleem. Wat moet menselijk blijven?",
          options: {
            A: "Controleren of het bericht beknopt is.",
            B: "Het concept per doelgroep aanpassen.",
            C: "Feiten, toon en vrijgave goedkeuren.",
            D: "AI waarschijnlijke vragen laten noemen.",
          },
          correct_answer: "C",
          feedback:
            "Een publieke verklaring na een ernstig probleem vraagt verantwoordelijke goedkeuring van feiten, toon en timing.",
        },
      },
      question_text:
        "AI drafts a public partner statement after a serious issue. What must remain human-owned?",
      explanation:
        "A public statement after a serious issue needs accountable approval of facts, tone and timing.",
      answers: [
        {
          id: "station6_q029_a",
          question_id: "station6_q029",
          answer_text: "Checking whether the message is concise.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q029_b",
          question_id: "station6_q029",
          answer_text: "Adapting the draft for each audience.",
          option_key: "B",
          order_index: 2,
          is_correct: false,
        },
        {
          id: "station6_q029_c",
          question_id: "station6_q029",
          answer_text: "Approving facts, tone and release.",
          option_key: "C",
          order_index: 3,
          is_correct: true,
        },
        {
          id: "station6_q029_d",
          question_id: "station6_q029",
          answer_text: "Asking AI to list likely questions.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
    {
      id: "station6_q030",
      station_id: "station_6",
      stage: 3,
      complexity: 5,
      core_concept: "Ambiguous high-risk AI use should be escalated",
      question_type: "multiple_choice_single_answer",
      status: "approved",
      human_review: {
        decision: "approved",
      },
      languages: {
        en: {
          question:
            "A team wants AI to classify customer complaints by likely legal exposure. The rules are unclear. What is safest?",
          options: {
            A: "Classify only cases with clear wording.",
            B: "Escalate before running the analysis.",
            C: "Ask AI to show uncertainty levels.",
            D: "Run the analysis without storing output.",
          },
          correct_answer: "B",
          feedback:
            "Likely legal exposure is a high-risk classification. Escalate before running unclear analysis with AI.",
        },
        pt: {
          question:
            "Uma equipe quer que a IA classifique reclamações de clientes por possível exposição legal. As regras são incertas. O que é mais seguro?",
          options: {
            A: "Classificar só casos com redação clara.",
            B: "Escalar antes de rodar a análise.",
            C: "Pedir à IA níveis de incerteza.",
            D: "Rodar a análise sem guardar saída.",
          },
          correct_answer: "B",
          feedback:
            "Exposição legal provável é classificação de alto risco. Escale antes de rodar análise incerta com IA.",
        },
        sv: {
          question:
            "Ett team vill att AI klassificerar kundklagomål efter möjlig juridisk exponering. Reglerna är oklara. Vad är säkrast?",
          options: {
            A: "Klassificera bara ärenden med tydlig text.",
            B: "Eskalera innan analysen körs.",
            C: "Be AI visa osäkerhetsnivåer.",
            D: "Kör analysen utan att lagra resultat.",
          },
          correct_answer: "B",
          feedback:
            "Trolig juridisk exponering är en högriskklassificering. Eskalera innan oklar analys körs med AI.",
        },
        nl: {
          question:
            "Een team wil AI klantklachten laten classificeren op mogelijke juridische blootstelling. Regels zijn onduidelijk. Wat is het veiligst?",
          options: {
            A: "Alleen gevallen met duidelijke tekst classificeren.",
            B: "Escaleren vóór de analyse draait.",
            C: "AI onzekerheidsniveaus laten tonen.",
            D: "Analyse draaien zonder output op te slaan.",
          },
          correct_answer: "B",
          feedback:
            "Mogelijke juridische blootstelling is een hoog-risicoclassificatie. Escaleer vóór onduidelijke AI-analyse.",
        },
      },
      question_text:
        "A team wants AI to classify customer complaints by likely legal exposure. The rules are unclear. What is safest?",
      explanation:
        "Likely legal exposure is a high-risk classification. Escalate before running unclear analysis with AI.",
      answers: [
        {
          id: "station6_q030_a",
          question_id: "station6_q030",
          answer_text: "Classify only cases with clear wording.",
          option_key: "A",
          order_index: 1,
          is_correct: false,
        },
        {
          id: "station6_q030_b",
          question_id: "station6_q030",
          answer_text: "Escalate before running the analysis.",
          option_key: "B",
          order_index: 2,
          is_correct: true,
        },
        {
          id: "station6_q030_c",
          question_id: "station6_q030",
          answer_text: "Ask AI to show uncertainty levels.",
          option_key: "C",
          order_index: 3,
          is_correct: false,
        },
        {
          id: "station6_q030_d",
          question_id: "station6_q030",
          answer_text: "Run the analysis without storing output.",
          option_key: "D",
          order_index: 4,
          is_correct: false,
        },
      ],
    },
  ],
} satisfies LocalQuestionBank;
